/**
 * BoardRenderer3D - Babylon.js WebGL 3D Cribbage Board Renderer
 * Uses Babylon.js coordinate system (left-handed: +X right, +Y up, +Z forward)
 * to natively load GLB board models and position pegs directly from Babylon-exported coordinates.
 */

import { Board3DCatalog } from './Board3DCatalog.js';
import { getTrackXY as get2DTrackXY, isInPegThroughZone, isInLast5PegOut, isInBunker, isInStinkHole } from './BoardGeometry.js';
import { ObstacleGeometry } from './ObstacleGeometry.js';
import { AvatarManager } from '../ui/AvatarManager.js';
import { RoomCatalog3D } from './RoomCatalog3D.js';
import { CardEngine3D } from './CardMesh3D.js';
import { SeatManager3D, PlayerAvatar3D } from './SeatManager3D.js';

export class BoardRenderer3D {
  /**
   * @param {HTMLElement} container - DOM element to mount the Babylon.js canvas into.
   * @param {Object} options
   */
  constructor(container, options = {}) {
    this.container = container;
    this.options = options;
    this.boardConfig = options.boardConfig || Board3DCatalog.getBoardById('board-14') || Board3DCatalog.getBoards()[0];
    this.activeTracks = options.activeTracks || ['p1', 'p2'];
    this.settings = options.settings || {};
    this.targetHoles = Number(this.settings.target) || 121;
    this.viewBoxW = Number(options.boardViewW) || 820;
    this.viewBoxH = Number(options.boardViewH) || 216;

    this.canvas = null;
    this.engine = null;
    this.scene = null;
    this.camera = null;
    this.boardMeshes = [];
    this.boardRoot = null;
    this.isDraggingBoard = false;
    this.pegMeshes = new Map(); // key: `${who}_${role}` -> TransformNode / Mesh
    this.pegState = {};         // key: `${who}` -> { front: 0, rear: 0 }
    this.holeLookup = null;
    this.animatingPegs = [];
    this.isDestroyed = false;
    this.BABYLON = null;
    this._avatarMaterials = new Map();
    this._avatarTextures = new Map();
    this.clashObstaclesRoot = null;
    this.glowLayer = null;
    this._obstacleMaterials = null;
    this.pintsObjectsRoot = null;
    this._pintsMaterials = null;
    this.adultObjectsRoot = null;
    this._adultMaterials = null;
    this.zoneHighlightsRoot = null;
    this._zoneMaterials = null;
    this._lastZoneHighlightKey = null;

    this.boardBounds = null;
    this.boardScale = 0.5; // Scale cribbage board down by 50% in 3D room
    let savedScale = 1.0;
    try {
      if (typeof localStorage !== 'undefined') {
        const s = parseFloat(localStorage.getItem('cc_board_3d_scale'));
        if (!isNaN(s) && s >= 0.20 && s <= 2.5) {
          savedScale = s;
        }
      }
    } catch (_) {}
    this.boardScaleMultiplier = savedScale;
    this.dirLight = null;
    this.fillLight = null;
    this.hemiLight = null;
    this.shadowGenerator = null;
    this.ssaoPipeline = null;
    this.activePreset = 'overview';
    this.isPanMode = false;
    this.presetsToolbar = null;
    this._currentCameraAnim = null;
    this.resizeObserver = null;
    this._resizeRaf = null;
    this._userHasAdjustedCamera = false;

    this.roomData = null;
    this.tableMesh = null;
    this._tableTexture = null;
    this.cardEngine = null;
    this.seatManager = null;
    this.hud3DOverlay = null;
    this.dealerButtonMesh = null;
    this.dealerSeat = null;
    this._dealerButtonMat = null;
    this._dealerButtonTex = null;
    this._dealerButtonAnim = null;
    this._tableMaterial = null;
    this.tableEnvironment = this.settings?.tableEnvironment || 'auto';
    this.pegStyle = this.settings?.pegStyle || 'classic';
    this._activeTrailParticles = [];
    this._isActionCamActive = false;
    this._savedActionCamState = null;
    this._actionCamTimer = null;
    this.actionCamOverlay = null;
    this._onFullscreenChange = null;
    this._onGlobalKeydown = null;
    this._fullscreenListenersBound = false;

    let savedBrightness = 100;
    try {
      if (typeof localStorage !== 'undefined') {
        const stored = parseInt(localStorage.getItem('cc_board_3d_brightness'), 10);
        if (!isNaN(stored) && stored >= 30 && stored <= 220) {
          savedBrightness = stored;
        }
        const storedEnv = localStorage.getItem('cc_board_3d_table_env');
        if (storedEnv) this.tableEnvironment = storedEnv;
        const storedPegStyle = localStorage.getItem('cc_board_3d_peg_style');
        if (storedPegStyle) this.pegStyle = storedPegStyle;
      }
    } catch (_) {}
    this.brightnessPercent = savedBrightness;

    this.init();
  }

  /**
   * Emits loading progress updates to options.onProgress if registered.
   * @param {string} statusText
   * @param {number} percent
   */
  notifyProgress(statusText, percent) {
    if (typeof this.options.onProgress === 'function') {
      try { this.options.onProgress(statusText, percent); } catch (_) {}
    }
  }

  /**
   * Asynchronously loads Babylon.js and initializes the 3D scene.
   */
  async init() {
    try {
      this.notifyProgress('Loading 3D WebGL engine…', 0.20);
      this.BABYLON = await this.loadBabylon();
      if (!this.BABYLON || this.isDestroyed) return;

      this.notifyProgress('Configuring camera & dynamic lighting…', 0.35);
      this.setupEngineAndScene();
      this.setupCamera();
      this.setupLighting();

      this.notifyProgress('Loading 3D cribbage board model…', 0.52);
      await this.loadBoardModel();

      this.notifyProgress('Constructing 3D room, table & surroundings…', 0.72);
      this.setupPostProcessing();
      this.setupTableEnvironment();
      this.fitBoardToViewBox(true);

      this.notifyProgress('Setting up pegs, tracks & 3D cards…', 0.88);
      await this.setupHoleLookup();
      this.setupPegs();
      this.setupClashObstacles();
      this.setupPintsObjects();
      this.setupAdultObjects();
      this.setupZoneHighlights();
      this.setupCameraPresetsUI();
      this.setupTopLeftLogoUI();
      this.syncFromGameState();
      this.startRenderLoop();

      this.notifyProgress('3D Room ready to play!', 1.0);
      if (typeof this.options.onReady === 'function') {
        this.options.onReady(this);
      }
    } catch (err) {
      console.error('[BoardRenderer3D] Babylon.js initialization failed, falling back to 2D:', err);
      if (typeof this.options.onError === 'function') {
        this.options.onError(err);
      }
    }
  }

  /**
   * Checks whether the GLTF/GLB loader plugin is registered in Babylon.js.
   * @returns {boolean}
   */
  static hasGlbLoader() {
    return Boolean(
      typeof window !== 'undefined' &&
      window.BABYLON &&
      window.BABYLON.Engine &&
      (window.BABYLON.GLTFFileLoader ||
        (window.BABYLON.SceneLoader &&
         typeof window.BABYLON.SceneLoader.IsPluginForExtensionAvailable === 'function' &&
         window.BABYLON.SceneLoader.IsPluginForExtensionAvailable('.glb')))
    );
  }

  /**
   * Helper to sequentially attempt loading a script from a list of candidate URLs.
   * @param {string[]} urls - Array of candidate URLs in order of preference.
   * @param {string} dataAttr - Dataset attribute for script tag identification.
   * @returns {Promise<void>}
   */
  static _loadScriptWithFallbacks(urls, dataAttr) {
    return new Promise((resolve, reject) => {
      let index = 0;

      const tryNext = () => {
        if (index >= urls.length) {
          return reject(new Error(`Failed to load script from all candidate URLs for [${dataAttr}]: ${urls.join(', ')}`));
        }

        const url = urls[index++];
        const prevScript = document.querySelector(`script[${dataAttr}]`);
        if (prevScript) {
          prevScript.remove();
        }

        const script = document.createElement('script');
        script.setAttribute(dataAttr, 'loading');
        script.crossOrigin = 'anonymous';
        script.async = false;
        script.src = url;

        script.onload = () => {
          script.setAttribute(dataAttr, 'loaded');
          resolve();
        };

        script.onerror = () => {
          script.remove();
          tryNext();
        };

        document.head.appendChild(script);
      };

      tryNext();
    });
  }

  /**
   * Dynamic loader for Babylon.js core and GLTF loaders.
   * Pinned version 9.28.0 loaded directly from reliable CDN on production/Shopify,
   * guaranteeing that both Babylon core and the GLTF/GLB loader plugin are loaded and ready.
   */
  async loadBabylon() {
    if (BoardRenderer3D.hasGlbLoader()) {
      return window.BABYLON;
    }
    if (BoardRenderer3D._babylonPromise) {
      return BoardRenderer3D._babylonPromise;
    }

    BoardRenderer3D._babylonPromise = new Promise(async (resolve, reject) => {
      if (typeof document === 'undefined') return reject(new Error('No DOM environment'));
      if (BoardRenderer3D.hasGlbLoader()) return resolve(window.BABYLON);

      // Only attempt local relative dist/ path on localhost or file: protocols.
      // On live production sites (like Shopify), always use CDN to avoid 404 network errors.
      const isLocal = typeof window !== 'undefined' && (
        window.location.hostname === 'localhost' ||
        window.location.hostname === '127.0.0.1' ||
        window.location.protocol === 'file:'
      );

      const customCore = typeof window !== 'undefined' && window.BABYLON_CORE_URL ? [window.BABYLON_CORE_URL] : [];
      const customLoader = typeof window !== 'undefined' && window.BABYLON_LOADER_URL ? [window.BABYLON_LOADER_URL] : [];

      const coreUrls = [
        ...customCore,
        ...(isLocal ? ['dist/babylon.min.js'] : []),
        'https://unpkg.com/babylonjs@9.28.0/babylon.js',
        'https://cdn.babylonjs.com/babylon.js'
      ];

      const loaderUrls = [
        ...customLoader,
        ...(isLocal ? ['dist/babylonjs.loaders.min.js'] : []),
        'https://unpkg.com/babylonjs-loaders@9.28.0/babylonjs.loaders.min.js',
        'https://cdn.babylonjs.com/loaders/babylonjs.loaders.min.js'
      ];

      try {
        if (!window.BABYLON || !window.BABYLON.Engine) {
          await BoardRenderer3D._loadScriptWithFallbacks(coreUrls, 'data-babylon-engine');
        }

        if (!BoardRenderer3D.hasGlbLoader()) {
          await BoardRenderer3D._loadScriptWithFallbacks(loaderUrls, 'data-babylon-loader');
        }

        if (BoardRenderer3D.hasGlbLoader()) {
          resolve(window.BABYLON);
        } else {
          BoardRenderer3D._babylonPromise = null;
          reject(new Error('Babylon.js GLTF/GLB loader plugin was not found after loading scripts.'));
        }
      } catch (err) {
        BoardRenderer3D._babylonPromise = null;
        reject(err);
      }
    });

    return BoardRenderer3D._babylonPromise;
  }

  /**
   * Sets up hole coordinate resolver using the Babylon.js coordinate system.
   */
  async setupHoleLookup() {
    const scale = this.boardScale || 0.5;
    // If coordinatesData (or direct holeData) is specified, use it
    const directCoords = this.boardConfig?.coordinatesData || this.boardConfig?.holeData;
    if (directCoords) {
      let parsed = directCoords;
      if (typeof parsed === 'string') {
        try {
          parsed = JSON.parse(parsed);
        } catch (e) {
          try {
            parsed = JSON.parse(parsed.replace(/,(\s*[\]}])/g, '$1'));
          } catch (_) {}
        }
      }
      if (parsed && typeof parsed === 'object') {
        this.boardConfig.holeData = parsed;
        if (this.hasDistinctCoordinates(parsed)) {
          this.holeLookup = Board3DCatalog.createHoleLookup(parsed, scale);
          return;
        }
      }
    }

    // Surface-mapped or procedural 3D coordinates derived from 2D geometry
    this.holeLookup = (who, hole) => {
      const p2d = get2DTrackXY(hole, who, this.targetHoles, this.settings);
      if (!p2d) return null;
      if (this.boardBounds && this.boardConfig?.modelUrl) {
        const b = this.boardBounds;
        const normX = Math.max(0, Math.min(1, (p2d.x - 40) / 740));
        const normZ = Math.max(0, Math.min(1, (p2d.y - 30) / 156));
        const x3d = b.min.x + normX * b.size.x;
        const z3d = b.max.z - normZ * b.size.z;
        const y3d = b.max.y + 0.2 * scale;
        return { x: x3d, y: y3d, z: z3d, normal: [0, 1, 0] };
      }
      const x3d = (((p2d.x - 410) / 410) * 30) * scale;
      const z3d = (((108 - p2d.y) / 108) * 8.5) * scale; // Invert Y to +Z forward
      const y3d = 0.8 * scale; // Board surface height
      return { x: x3d, y: y3d, z: z3d, normal: [0, 1, 0] };
    };
  }

  /**
   * Helper to verify if coordinate data has real distinct hole positions.
   */
  hasDistinctCoordinates(data) {
    if (!data) return false;
    const pts = Array.isArray(data) ? data : (data.tracks ? Object.values(data.tracks)[0] : Object.values(data)[0]);
    if (!Array.isArray(pts) || pts.length < 5) return false;
    let minX = Infinity, maxX = -Infinity, minZ = Infinity, maxZ = -Infinity;
    for (const p of pts) {
      const px = typeof p?.x === 'number' ? p.x : (typeof p?.[' x'] === 'number' ? p[' x'] : Number(p?.x ?? p?.[' x']));
      const pz = typeof p?.z === 'number' ? p.z : (typeof p?.[' z'] === 'number' ? p[' z'] : Number(p?.z ?? p?.[' z']));
      if (!isNaN(px)) {
        minX = Math.min(minX, px);
        maxX = Math.max(maxX, px);
      }
      if (!isNaN(pz)) {
        minZ = Math.min(minZ, pz);
        maxZ = Math.max(maxZ, pz);
      }
    }
    return (maxX - minX > 2.0) || (maxZ - minZ > 2.0);
  }

  /**
   * Creates the canvas, Babylon Engine, Scene, and sets up responsive observers.
   */
  setupEngineAndScene() {
    const BABYLON = this.BABYLON;
    // Remove existing canvases while preserving any active 3D loading overlay
    const existingCanvases = this.container.querySelectorAll('canvas');
    existingCanvases.forEach(c => c.remove());

    this.canvas = document.createElement('canvas');
    this.canvas.style.width = '100%';
    this.canvas.style.height = '100%';
    this.canvas.style.display = 'block';
    this.canvas.style.outline = 'none';
    this.canvas.style.touchAction = 'none';

    const activeOverlay = this.container.querySelector('.room3d-loading-overlay');
    if (activeOverlay) {
      this.container.insertBefore(this.canvas, activeOverlay);
    } else {
      this.container.appendChild(this.canvas);
    }

    this.engine = new BABYLON.Engine(this.canvas, true, {
      preserveDrawingBuffer: true,
      stencil: true,
      antialias: true,
      adaptToDeviceRatio: true
    });

    this.scene = new BABYLON.Scene(this.engine);
    this.scene.clearColor = new BABYLON.Color4(0, 0, 0, 0); // Transparent canvas background
    this.boardRoot = new BABYLON.TransformNode('boardRoot', this.scene);
    if (this.boardScaleMultiplier && this.boardScaleMultiplier !== 1.0) {
      this.boardRoot.scaling.set(this.boardScaleMultiplier, this.boardScaleMultiplier, this.boardScaleMultiplier);
    }

    // ResizeObserver watches for any changes to container dimensions (e.g. tableSplitter dragging)
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver((entries) => {
        if (this.isDestroyed || !this.engine) return;
        for (const entry of entries) {
          if (entry.contentRect.width > 0 && entry.contentRect.height > 0) {
            if (this._resizeRaf) cancelAnimationFrame(this._resizeRaf);
            this._resizeRaf = requestAnimationFrame(() => {
              if (!this.isDestroyed && this.engine) {
                this.handleResize();
              }
            });
            break;
          }
        }
      });
      this.resizeObserver.observe(this.container);
    }

    this.resizeHandler = () => {
      if (this._resizeRaf) cancelAnimationFrame(this._resizeRaf);
      this._resizeRaf = requestAnimationFrame(() => {
        if (!this.isDestroyed && this.engine) {
          this.handleResize();
        }
      });
    };
    window.addEventListener('resize', this.resizeHandler);
  }

  /**
   * Configures Babylon ArcRotateCamera with orbit, zoom, and pan controls.
   */
  setupCamera() {
    const BABYLON = this.BABYLON;
    const camDef = this.boardConfig?.cameraDefaults || {};

    const target = new BABYLON.Vector3(camDef.target?.[0] || 0, camDef.target?.[1] || 0, camDef.target?.[2] || 0);
    const defaultAlpha = camDef.alpha !== undefined
      ? Number(camDef.alpha)
      : (camDef.alphaDeg !== undefined ? (Number(camDef.alphaDeg) * Math.PI) / 180 : -Math.PI / 2);
    const defaultBeta = camDef.beta !== undefined
      ? Number(camDef.beta)
      : (camDef.betaDeg !== undefined ? (Number(camDef.betaDeg) * Math.PI) / 180 : Math.PI / 5.2);
    this.camera = new BABYLON.ArcRotateCamera(
      'boardCamera',
      defaultAlpha,       // Alpha: horizontal orbit angle (yaw)
      defaultBeta,        // Beta: elevated vantage point showing all tracks and tiers (pitch)
      55,                 // Will be auto-computed by fitBoardToViewBox
      target,
      this.scene
    );

    this.camera.fov = (camDef.fov ? (camDef.fov * Math.PI) / 180 : 0.8);
    this.camera.fovMode = BABYLON.Camera.FOVMODE_VERTICAL_FIXED;
    this.camera.attachControl(this.canvas, true);
    // Smooth, calibrated proportional mouse wheel and pinch zoom for touch devices
    this.camera.wheelDeltaPercentage = 0.035; // Smooth 3.5% proportional zoom per wheel tick
    this.camera.pinchDeltaPercentage = 0;     // Disabled in favor of natural pinch zoom (avoids squared-pixel jumpiness)
    this.camera.useNaturalPinchZoom = true;   // 1-to-1 natural pinch zoom matching finger motion
    this.camera.pinchPrecision = 40;          // Smooth fallback pinch precision
    this.camera.wheelPrecision = 12;          // Smooth fallback wheel precision (prevents coarse jumps)
    this.camera.inertia = 0.88;               // High smooth camera momentum damping
    this.camera.panningSensibility = 25;      // Responsive camera panning sensibility (calibrated for room scale)
    this.camera.panningInertia = 0.85;        // Smooth camera panning momentum damping
    this.camera.angularSensibilityX = 1000;   // Smooth horizontal orbit
    this.camera.angularSensibilityY = 1000;   // Smooth vertical pitch
    this.camera.upperBetaLimit = Math.PI / 2.05; // Prevent camera flipping below board table
    if (this.camera.inputs?.attached?.pointers) {
      this.camera.inputs.attached.pointers.useNaturalPinchZoom = true;
      this.camera.inputs.attached.pointers.pinchDeltaPercentage = 0;
      this.camera.inputs.attached.pointers.pinchPrecision = 40;
      this.camera.inputs.attached.pointers.panningSensibility = 25;
    }

    // Enable Babylon.js built-in collision engine and configure camera collision bounds
    this.scene.collisionsEnabled = true;
    this.camera.checkCollisions = true;
    this.camera.collisionRadius = new BABYLON.Vector3(3.0, 3.0, 3.0);
    this.camera.onCollide = (collidedMesh) => {
      this.camera.inertialAlphaOffset = 0;
      this.camera.inertialBetaOffset = 0;
      this.camera.inertialRadiusOffset = 0;
      this.camera.inertialPanningX = 0;
      this.camera.inertialPanningY = 0;
      if (typeof this.camera.rebuildAnglesAndRadius === 'function') {
        this.camera.rebuildAnglesAndRadius();
      }
    };

    // Contain camera target and position strictly inside the room volume during orbit/pan/zoom
    this.scene.onBeforeRenderObservable.add(() => {
      if (this.camera && this.roomData?.dims) {
        const { roomW, roomD, floorY, ceilingY, tableSurfaceY } = this.roomData.dims;
        const marginX = roomW * 0.44;
        const marginZ = roomD * 0.44;
        const minY = (tableSurfaceY != null ? tableSurfaceY - 10 : floorY + 15);
        if (this.camera.target) {
          if (this.camera.target.x > marginX) this.camera.target.x = marginX;
          if (this.camera.target.x < -marginX) this.camera.target.x = -marginX;
          if (this.camera.target.z > marginZ) this.camera.target.z = marginZ;
          if (this.camera.target.z < -marginZ) this.camera.target.z = -marginZ;
          if (this.camera.target.y < minY) this.camera.target.y = minY;
          if (this.camera.target.y > ceilingY - 25) this.camera.target.y = ceilingY - 25;
        }

        if (this.camera.position) {
          const camMarginX = roomW * 0.46;
          const camMarginZ = roomD * 0.46;
          const camMinY = floorY + 8.0;
          const camMaxY = ceilingY - 12.0;
          let clamped = false;
          let px = this.camera.position.x;
          let py = this.camera.position.y;
          let pz = this.camera.position.z;

          if (px > camMarginX) { px = camMarginX; clamped = true; }
          else if (px < -camMarginX) { px = -camMarginX; clamped = true; }
          if (pz > camMarginZ) { pz = camMarginZ; clamped = true; }
          else if (pz < -camMarginZ) { pz = -camMarginZ; clamped = true; }
          if (py < camMinY) { py = camMinY; clamped = true; }
          else if (py > camMaxY) { py = camMaxY; clamped = true; }

          if (clamped) {
            this.camera.position.set(px, py, pz);
            this.camera.inertialAlphaOffset = 0;
            this.camera.inertialBetaOffset = 0;
            this.camera.inertialRadiusOffset = 0;
            this.camera.inertialPanningX = 0;
            this.camera.inertialPanningY = 0;
            if (typeof this.camera.rebuildAnglesAndRadius === 'function') {
              this.camera.rebuildAnglesAndRadius();
            }
          }
        }
      }
    });

    // Track user camera manipulation to avoid clobbering manual inspections
    this.canvas.addEventListener('wheel', (e) => {
      // Ctrl + Wheel or Alt + Wheel over board scales the cribbage board directly
      if (e.ctrlKey || e.altKey) {
        const pick = this.scene ? this.scene.pick(this.scene.pointerX, this.scene.pointerY) : null;
        if (pick && pick.hit && this.isBoardMesh(pick.pickedMesh)) {
          e.preventDefault();
          e.stopPropagation();
          const delta = e.deltaY < 0 ? 0.05 : -0.05;
          this.scaleBoard(delta, false);
          return;
        }
      }
      this._userHasAdjustedCamera = true;
      this.activePreset = null;
      this.updatePresetButtonsUI(null);
    }, { passive: false });

    const activePointers = new Map();
    let isDraggingBoard = false;
    let isTwoFingerRotating = false;
    let twoFingerStart = null;
    let cameraOrbitDetached = false;
    let boardDragPlane = null;
    let boardDragStart = null;
    let pointerStart = null;

    const detachCameraOrbit = () => {
      if (!cameraOrbitDetached) {
        cameraOrbitDetached = true;
        if (this.camera) {
          if (typeof this.camera.detachControl === 'function') {
            this.camera.detachControl();
          }
          if (this.camera.inputs?.attached?.pointers) {
            try { this.camera.inputs.attached.pointers.detachControl(); } catch (_) {}
          }
          if (typeof this.camera._reset === 'function') {
            this.camera._reset();
          }
          this.camera.inertialAlphaOffset = 0;
          this.camera.inertialBetaOffset = 0;
          this.camera.inertialRadiusOffset = 0;
          this.camera.inertialPanningX = 0;
          this.camera.inertialPanningY = 0;
        }
      }
    };

    const attachCameraOrbit = () => {
      if (cameraOrbitDetached && !this.isPanMode && !isTwoFingerRotating) {
        cameraOrbitDetached = false;
        if (this.camera) {
          if (typeof this.camera.attachControl === 'function') {
            this.camera.attachControl(this.canvas, true);
          } else if (this.camera.inputs?.attached?.pointers) {
            try { this.camera.inputs.attached.pointers.attachControl(this.canvas, true); } catch (_) {}
          }
          if (this.camera.inputs?.attached?.pointers) {
            this.camera.inputs.attached.pointers.useNaturalPinchZoom = true;
            this.camera.inputs.attached.pointers.pinchDeltaPercentage = 0;
            this.camera.inputs.attached.pointers.pinchPrecision = 40;
            this.camera.inputs.attached.pointers.panningSensibility = 25;
          }
        }
      }
    };

    const endTwoFingerRotate = () => {
      isTwoFingerRotating = false;
      twoFingerStart = null;
      if (activePointers.size === 0 && !isDraggingBoard) {
        attachCameraOrbit();
      }
    };

    const endBoardDrag = (e) => {
      if (isDraggingBoard) {
        if (e && typeof e.pointerId === 'number') {
          try {
            this.canvas.releasePointerCapture(e.pointerId);
          } catch (_) {}
        }
        isDraggingBoard = false;
        this.isDraggingBoard = false;
        boardDragStart = null;
        boardDragPlane = null;

        // Re-attach camera controls if all pointers are released and not rotating
        if (activePointers.size === 0 && !isTwoFingerRotating) {
          attachCameraOrbit();
        }
      }
      if (this.canvas) {
        this.canvas.classList.remove('is-pan-grabbing');
        this.canvas.style.cursor = 'default';
      }
    };

    this.canvas.addEventListener('pointerdown', (e) => {
      activePointers.set(e.pointerId, { x: e.clientX, y: e.clientY, type: e.pointerType });
      pointerStart = { x: e.clientX, y: e.clientY };

      const rect = this.canvas.getBoundingClientRect();
      const px = e.clientX - rect.left;
      const py = e.clientY - rect.top;
      const pick = this.scene ? this.scene.pick(px, py) : null;
      let hitBoard = Boolean(pick && pick.hit && this.isBoardMesh(pick.pickedMesh));
      if (!hitBoard && this.scene) {
        const boardPick = this.scene.pick(px, py, (m) => this.isBoardMesh(m));
        if (boardPick && boardPick.hit) {
          hitBoard = true;
        }
      }

      // If 2 pointers are active: check for two-finger twist rotate gesture on cribbage board
      if (activePointers.size === 2) {
        if (isDraggingBoard || hitBoard || this.isPanMode) {
          const pts = Array.from(activePointers.values());
          const p1 = pts[0];
          const p2 = pts[1];
          const initialAngle = Math.atan2(p2.y - p1.y, p2.x - p1.x);
          const initialDist = Math.hypot(p2.x - p1.x, p2.y - p1.y);

          twoFingerStart = {
            initialAngle,
            lastAngle: initialAngle,
            initialDist,
            startRotY: this.boardRoot ? (this.boardRoot.rotation.y || 0) : 0,
            accumulatedAngleDelta: 0,
            isConfirmedRotation: false,
            cameraAlpha: this.camera ? this.camera.alpha : null,
            cameraBeta: this.camera ? this.camera.beta : null
          };

          // Freeze camera orbit so room view doesn't shake while evaluating twist vs pinch
          detachCameraOrbit();
          return;
        }

        // Neither touch is on board -> yield to camera pinch-to-zoom / 2-finger pan
        if (isDraggingBoard) {
          endBoardDrag(e);
        }
        endTwoFingerRotate();
        attachCameraOrbit();
        return;
      }

      // If more than 2 pointers:
      if (activePointers.size > 2) {
        if (isDraggingBoard) endBoardDrag(e);
        endTwoFingerRotate();
        attachCameraOrbit();
        return;
      }

      // Left click (0) or touch/pen pointer
      if (e.button === 0 || e.pointerType === 'touch' || e.pointerType === 'pen') {
        if (this.isPanMode || hitBoard) {
          isDraggingBoard = true;
          this.isDraggingBoard = true;
          this._userHasAdjustedCamera = true;
          this.activePreset = null;
          this.updatePresetButtonsUI(null);

          // Detach camera orbit and zero inertia while dragging the board so the 3D room stays completely stable
          detachCameraOrbit();

          // Horizontal plane at tabletop height (or board Y)
          const surfaceY = (this.roomData?.dims?.tableSurfaceY != null)
            ? this.roomData.dims.tableSurfaceY
            : (this.boardRoot?.position.y || 0);

          boardDragPlane = this.BABYLON.Plane.FromPositionAndNormal(
            new this.BABYLON.Vector3(0, surfaceY, 0),
            new this.BABYLON.Vector3(0, 1, 0)
          );

          const ray = this.scene.createPickingRay(
            px,
            py,
            this.BABYLON.Matrix.Identity(),
            this.camera
          );
          const dist = ray ? ray.intersectsPlane(boardDragPlane) : null;
          const initialHit = (dist !== null && isFinite(dist))
            ? ray.origin.add(ray.direction.scale(dist))
            : new this.BABYLON.Vector3(0, 0, 0);

          boardDragStart = {
            pointerId: e.pointerId,
            screenX: e.clientX,
            screenY: e.clientY,
            hit: initialHit,
            startPos: this.boardRoot ? this.boardRoot.position.clone() : new this.BABYLON.Vector3(0, 0, 0),
            startRotY: this.boardRoot ? this.boardRoot.rotation.y : 0,
            startScale: this.boardScaleMultiplier || 1.0,
            cameraAlpha: this.camera ? this.camera.alpha : null,
            cameraBeta: this.camera ? this.camera.beta : null,
            isRotating: Boolean(e.shiftKey),
            isScaling: Boolean(e.ctrlKey || e.metaKey)
          };

          this.canvas.classList.add('is-pan-grabbing');
          try {
            this.canvas.setPointerCapture(e.pointerId);
          } catch (_) {}
        }
      }
    });

    this.canvas.addEventListener('pointermove', (e) => {
      if (activePointers.has(e.pointerId)) {
        activePointers.set(e.pointerId, { x: e.clientX, y: e.clientY, type: e.pointerType });
      }

      // Two-finger twist gesture handling on board:
      if (activePointers.size === 2) {
        if (twoFingerStart && this.boardRoot) {
          const pts = Array.from(activePointers.values());
          const p1 = pts[0];
          const p2 = pts[1];
          const currentAngle = Math.atan2(p2.y - p1.y, p2.x - p1.x);
          const currentDist = Math.hypot(p2.x - p1.x, p2.y - p1.y);

          let deltaAngle = currentAngle - twoFingerStart.lastAngle;
          while (deltaAngle < -Math.PI) deltaAngle += Math.PI * 2;
          while (deltaAngle > Math.PI) deltaAngle -= Math.PI * 2;

          twoFingerStart.accumulatedAngleDelta += Math.abs(deltaAngle);

          // Disambiguate twist rotation from camera pinch zoom:
          if (!twoFingerStart.isConfirmedRotation) {
            const distChange = Math.abs(currentDist - twoFingerStart.initialDist);
            if (twoFingerStart.accumulatedAngleDelta > 0.04) {
              // Confirmed rotation gesture on board!
              twoFingerStart.isConfirmedRotation = true;
              isTwoFingerRotating = true;
              if (isDraggingBoard) {
                isDraggingBoard = false;
                this.isDraggingBoard = false;
              }
            } else if (distChange > 32 && twoFingerStart.accumulatedAngleDelta < 0.02) {
              // User pinched with straight finger distance change -> cancel board twist and yield to camera pinch zoom
              endTwoFingerRotate();
              if (isDraggingBoard) endBoardDrag(e);
              attachCameraOrbit();
              return;
            }
          }

          if (twoFingerStart.isConfirmedRotation || isTwoFingerRotating) {
            if (e.cancelable) e.preventDefault();
            detachCameraOrbit();

            if (this.camera && typeof twoFingerStart.cameraAlpha === 'number') {
              this.camera.alpha = twoFingerStart.cameraAlpha;
              this.camera.beta = twoFingerStart.cameraBeta;
              this.camera.inertialAlphaOffset = 0;
              this.camera.inertialBetaOffset = 0;
              this.camera.inertialRadiusOffset = 0;
              this.camera.inertialPanningX = 0;
              this.camera.inertialPanningY = 0;
            }

            this.setBoardRotation((this.boardRoot.rotation.y || 0) + deltaAngle, false);
            twoFingerStart.lastAngle = currentAngle;
            this._userHasAdjustedCamera = true;
            this.activePreset = null;
            this.updatePresetButtonsUI(null);
            return;
          }
        } else {
          // Two fingers active outside board: yield to camera
          if (isDraggingBoard) {
            endBoardDrag(e);
          }
          return;
        }
      }

      // If more than 2 pointers:
      if (activePointers.size > 2) {
        if (isDraggingBoard) endBoardDrag(e);
        endTwoFingerRotate();
        return;
      }

      if (isDraggingBoard && boardDragStart && boardDragStart.pointerId === e.pointerId && this.boardRoot && boardDragPlane) {
        if (e.cancelable) {
          e.preventDefault();
        }

        // Lock camera orientation and suppress any inertia so the 3D room remains motionless during board drag
        if (this.camera && typeof boardDragStart.cameraAlpha === 'number') {
          this.camera.alpha = boardDragStart.cameraAlpha;
          this.camera.beta = boardDragStart.cameraBeta;
          this.camera.inertialAlphaOffset = 0;
          this.camera.inertialBetaOffset = 0;
          this.camera.inertialRadiusOffset = 0;
          this.camera.inertialPanningX = 0;
          this.camera.inertialPanningY = 0;
        }

        const dx = e.clientX - boardDragStart.screenX;
        const dy = e.clientY - boardDragStart.screenY;
        if (Math.abs(dx) > 3 || Math.abs(dy) > 3) {
          this._userHasAdjustedCamera = true;
          this.activePreset = null;
          this.updatePresetButtonsUI(null);
        }

        if (boardDragStart.isScaling || e.ctrlKey || e.metaKey) {
          // Ctrl + drag scales the board: dragging up or right scales up, down or left scales down
          const scaleDelta = ((boardDragStart.screenY - e.clientY) + (e.clientX - boardDragStart.screenX)) * 0.005;
          this.setBoardScale(boardDragStart.startScale + scaleDelta, false);
          this.canvas.style.cursor = 'nwse-resize';
        } else if (boardDragStart.isRotating || e.shiftKey) {
          // Shift + drag rotates the board around Y
          const rotSpeed = 0.015;
          this.setBoardRotation(boardDragStart.startRotY + dx * rotSpeed);
          this.canvas.style.cursor = 'ew-resize';
        } else {
          // Normal drag slides the board across the table
          const rect = this.canvas.getBoundingClientRect();
          const px = e.clientX - rect.left;
          const py = e.clientY - rect.top;
          const ray = this.scene.createPickingRay(
            px,
            py,
            this.BABYLON.Matrix.Identity(),
            this.camera
          );
          const dist = ray ? ray.intersectsPlane(boardDragPlane) : null;
          if (dist !== null && isFinite(dist)) {
            const currentHit = ray.origin.add(ray.direction.scale(dist));
            const deltaX = currentHit.x - boardDragStart.hit.x;
            const deltaZ = currentHit.z - boardDragStart.hit.z;
            this.setBoardPosition(
              boardDragStart.startPos.x + deltaX,
              boardDragStart.startPos.z + deltaZ,
              false
            );
          }
          this.canvas.style.cursor = 'grabbing';
        }
      } else if (!isDraggingBoard && this.scene && this.canvas) {
        // Cursor hover feedback (throttled via requestAnimationFrame with board/obstacle predicate):
        // 1. Obstacles -> 'pointer'
        // 2. Cribbage Board -> 'nwse-resize' (with Ctrl), 'ew-resize' (with Shift), 'grab'
        // 3. Otherwise -> default (or grab if in pan mode)
        if (!this._hoverRaf) {
          const ctrl = Boolean(e.ctrlKey || e.metaKey);
          const shift = Boolean(e.shiftKey);
          this._hoverRaf = requestAnimationFrame(() => {
            this._hoverRaf = null;
            if (this.isDestroyed || !this.scene || !this.canvas || isDraggingBoard) return;
            const pick = this.scene.pick(this.scene.pointerX, this.scene.pointerY, (m) => {
              return m && (m.metadata?.obstacle || this.isBoardMesh(m));
            });
            if (pick && pick.hit) {
              if (pick.pickedMesh?.metadata?.obstacle) {
                this.canvas.style.cursor = 'pointer';
              } else if (this.isBoardMesh(pick.pickedMesh)) {
                if (ctrl) {
                  this.canvas.style.cursor = 'nwse-resize';
                } else if (shift) {
                  this.canvas.style.cursor = 'ew-resize';
                } else {
                  this.canvas.style.cursor = 'grab';
                }
              } else if (this.isPanMode) {
                this.canvas.style.cursor = 'grab';
              } else {
                this.canvas.style.cursor = 'default';
              }
            } else if (this.isPanMode) {
              this.canvas.style.cursor = 'grab';
            } else {
              this.canvas.style.cursor = 'default';
            }
          });
        }
      }
    });

    this.canvas.addEventListener('pointerup', (e) => {
      activePointers.delete(e.pointerId);
      if (activePointers.size < 2) {
        endTwoFingerRotate();
      }
      if (isDraggingBoard && boardDragStart && boardDragStart.pointerId === e.pointerId) {
        endBoardDrag(e);
      }
      if (activePointers.size === 0 && !isDraggingBoard && !isTwoFingerRotating) {
        attachCameraOrbit();
      }
      if (pointerStart) {
        const dx = Math.abs(e.clientX - pointerStart.x);
        const dy = Math.abs(e.clientY - pointerStart.y);
        if (dx > 6 || dy > 6) {
          this._userHasAdjustedCamera = true;
          this.activePreset = null;
          this.updatePresetButtonsUI(null);
        } else if (activePointers.size === 0) {
          this._handleBoard3DClick(e);
        }
        pointerStart = null;
      }
    });

    this.canvas.addEventListener('pointercancel', (e) => {
      activePointers.delete(e.pointerId);
      endTwoFingerRotate();
      endBoardDrag(e);
      if (activePointers.size === 0 && !isDraggingBoard && !isTwoFingerRotating) {
        attachCameraOrbit();
      }
      pointerStart = null;
    });

    this.canvas.addEventListener('lostpointercapture', (e) => {
      activePointers.delete(e.pointerId);
      if (activePointers.size < 2) {
        endTwoFingerRotate();
      }
      if (isDraggingBoard && boardDragStart && boardDragStart.pointerId === e.pointerId) {
        endBoardDrag(e);
      }
    });

    this.canvas.addEventListener('pointerleave', (e) => {
      if (!isDraggingBoard && this.canvas) {
        this.canvas.classList.remove('is-pan-grabbing');
      }
    });

    // Double-click on board centers it (or resets size if Ctrl/Shift held); double-click elsewhere resets overview camera
    this.canvas.addEventListener('dblclick', (e) => {
      const pick = this.scene ? this.scene.pick(this.scene.pointerX, this.scene.pointerY) : null;
      if (pick && pick.hit && this.isBoardMesh(pick.pickedMesh)) {
        if (e.ctrlKey) {
          this.resetBoardScale(true);
        } else if (e.shiftKey) {
          this.resetBoardTransform(true);
        } else {
          this.resetBoardPosition(true);
        }
      } else {
        this._userHasAdjustedCamera = false;
        this.setCameraPreset('overview');
      }
    });

    // Keyboard shortcuts for zooming (+/-), pan/move toggle (P/M), center board (R/0), size ([/]), and board nudge (Arrows)
    this.canvas.setAttribute('tabindex', '0');
    this.canvas.addEventListener('keydown', (e) => {
      if (e.key === '+' || e.key === '=') {
        if (e.ctrlKey) {
          e.preventDefault();
          this.scaleBoard(0.1, true);
        } else {
          e.preventDefault();
          this.zoomIn();
        }
      } else if (e.key === '-' || e.key === '_') {
        if (e.ctrlKey) {
          e.preventDefault();
          this.scaleBoard(-0.1, true);
        } else {
          e.preventDefault();
          this.zoomOut();
        }
      } else if (e.key === '[' || e.key === '{') {
        e.preventDefault();
        this.scaleBoard(-0.1, true);
      } else if (e.key === ']' || e.key === '}') {
        e.preventDefault();
        this.scaleBoard(0.1, true);
      } else if (e.key === '0') {
        e.preventDefault();
        if (e.ctrlKey) {
          this.resetBoardScale(true);
        } else {
          this.setCameraPreset('overview');
        }
      } else if (e.key === 'r' || e.key === 'R') {
        e.preventDefault();
        if (e.shiftKey) {
          this.resetBoardTransform(true);
        } else {
          this.resetBoardPosition(true);
        }
      } else if (e.key === 'p' || e.key === 'P' || e.key === 'm' || e.key === 'M') {
        e.preventDefault();
        this.togglePanMode();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        if (e.shiftKey) this.rotateBoard(-Math.PI / 16);
        else this.moveBoard(-5, 0);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        if (e.shiftKey) this.rotateBoard(Math.PI / 16);
        else this.moveBoard(5, 0);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        this.moveBoard(0, -5);
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        this.moveBoard(0, 5);
      } else if (e.key === 'f' || e.key === 'F') {
        e.preventDefault();
        this.toggleFullScreen();
      } else if (e.key === 'Escape' && this.isFullScreen()) {
        e.preventDefault();
        this.exitFullScreen();
      }
    });
  }

  /**
   * Sets up bright, radiant Babylon lighting:
   * - High-intensity ambient hemisphere bounce illuminating all angles and recesses
   * - Brilliant overhead table light casting clean, soft contact shadows into hole recesses
   * - Vibrant opposing rim/fill light highlighting wood grain and metallic peg bevels
   */
  setupLighting() {
    const BABYLON = this.BABYLON;
    const isPub = this.boardConfig?.lightingPreset === 'warm_pub';

    // 1. Ambient Hemisphere Light (bright, warm room bounce - keeps entire board luminous)
    this.hemiLight = new BABYLON.HemisphericLight(
      'hemiLight',
      new BABYLON.Vector3(0, 1, 0),
      this.scene
    );
    this.hemiLight.intensity = isPub ? 2.1 : 1.95;
    this.hemiLight.diffuse = isPub ? new BABYLON.Color3(1.0, 0.96, 0.90) : new BABYLON.Color3(1.0, 1.0, 1.0);
    this.hemiLight.groundColor = isPub ? new BABYLON.Color3(0.70, 0.62, 0.52) : new BABYLON.Color3(0.68, 0.68, 0.70);

    // 2. Key Sun / Overhead Table Light (crisp, brilliant illumination)
    this.dirLight = new BABYLON.DirectionalLight(
      'dirLight',
      new BABYLON.Vector3(-0.75, -2.2, -0.65).normalize(),
      this.scene
    );
    this.dirLight.position = new BABYLON.Vector3(30, 80, 35);
    this.dirLight.intensity = isPub ? 3.6 : 3.4;
    this.dirLight.diffuse = isPub ? new BABYLON.Color3(1.0, 0.98, 0.92) : new BABYLON.Color3(1.0, 1.0, 1.0);
    this.dirLight.specular = new BABYLON.Color3(1.0, 1.0, 1.0);

    // 3. Fill / Rim Directional Light (bright fill so shadows don't dim the board)
    this.fillLight = new BABYLON.DirectionalLight(
      'fillLight',
      new BABYLON.Vector3(0.85, -1.3, 0.8).normalize(),
      this.scene
    );
    this.fillLight.position = new BABYLON.Vector3(-30, 60, -25);
    this.fillLight.intensity = isPub ? 2.0 : 1.85;
    this.fillLight.diffuse = isPub ? new BABYLON.Color3(1.0, 0.92, 0.82) : new BABYLON.Color3(0.95, 0.97, 1.0);
    this.fillLight.specular = new BABYLON.Color3(0.85, 0.85, 0.9);

    // 4. Soft Contact Shadows into Hole Recesses (gentle darkness to maintain high brightness)
    this.shadowGenerator = new BABYLON.ShadowGenerator(2048, this.dirLight);
    if (BABYLON.ShadowGenerator.FILTER_PCF !== undefined) {
      this.shadowGenerator.filter = BABYLON.ShadowGenerator.FILTER_PCF;
      this.shadowGenerator.filteringQuality = BABYLON.ShadowGenerator.QUALITY_HIGH;
    } else {
      this.shadowGenerator.useBlurExponentialShadowMap = true;
      this.shadowGenerator.blurKernel = 16;
    }
    this.shadowGenerator.bias = 0.0005;
    this.shadowGenerator.normalBias = 0.0012;
    this.shadowGenerator.darkness = 0.30; // Soft, luminous contact shadows without dimming board
    this.shadowGenerator.transparencyShadow = true;

    // Apply saved user brightness setting
    this.setBrightness(this.brightnessPercent, false);
  }

  /**
   * Adjusts 3D board lighting brightness dynamically and persists preference.
   * @param {number} percent - Brightness percentage (40 to 180, default 100)
   * @param {boolean} [save=true] - Whether to save to localStorage
   */
  setBrightness(percent, save = true) {
    const val = Math.max(30, Math.min(220, Number(percent) || 100));
    this.brightnessPercent = val;

    if (save && typeof localStorage !== 'undefined') {
      try {
        localStorage.setItem('cc_board_3d_brightness', String(Math.round(val)));
      } catch (_) {}
    }

    if (!this.scene) return;

    const mult = val / 100;
    const isPub = this.boardConfig?.lightingPreset === 'warm_pub';

    // Baseline calibrated lighting values (100%)
    const baseHemi = isPub ? 1.45 : 1.30;
    const baseDir = isPub ? 2.6 : 2.4;
    const baseFill = isPub ? 1.2 : 1.0;
    const baseExposure = 1.15;

    if (this.hemiLight) {
      this.hemiLight.intensity = baseHemi * mult;
    }
    if (this.dirLight) {
      this.dirLight.intensity = baseDir * mult;
    }
    if (this.fillLight) {
      this.fillLight.intensity = baseFill * mult;
    }
    if (this.scene.imageProcessingConfiguration) {
      this.scene.imageProcessingConfiguration.exposure = baseExposure * Math.pow(mult, 0.4);
    }

    // Update UI slider & readout if present
    if (this.presetsToolbar) {
      const slider = this.presetsToolbar.querySelector('.slider-3d-brightness');
      const label = this.presetsToolbar.querySelector('.val-3d-brightness');
      if (slider && Number(slider.value) !== val) {
        slider.value = String(Math.round(val));
      }
      if (label) {
        label.textContent = `${Math.round(val)}%`;
      }
    }
  }

  /**
   * Post-processing configuration.
   */
  setupPostProcessing() {
    // Keep post-processing clean and bright without darkening ambient occlusion passes
    if (this.scene && this.scene.imageProcessingConfiguration) {
      this.scene.imageProcessingConfiguration.exposure = 1.35;
      this.scene.imageProcessingConfiguration.contrast = 1.1;
    }
  }

  /**
   * Resolves the active table environment setting.
   * If 'auto', detects current brand mood (Pints -> Pub, Clash -> Raceway, Adult -> Lounge, Crazy -> Vegas).
   * @returns {'pub'|'vegas'|'raceway'|'lounge'|'studio'}
   */
  getTableEnvironment() {
    let env = this.tableEnvironment || this.settings?.tableEnvironment || 'auto';
    if (env === 'auto') {
      const brand = this.options.brand || this.settings?.brand || (typeof window !== 'undefined' && typeof window.getAppBrand === 'function' ? window.getAppBrand() : 'crazy');
      if (brand === 'pints') return 'pub';
      if (brand === 'clash') return 'raceway';
      if (brand === 'adult') return 'lounge';
      return 'vegas';
    }
    if (env === 'thunderdome') return 'raceway';
    if (env === 'velvet') return 'lounge';
    return env;
  }

  /**
   * Sets and applies the active 3D table environment in real-time.
   * @param {string} envId - 'auto' | 'pub' | 'vegas' | 'raceway' | 'lounge' | 'studio'
   */
  setTableEnvironment(envId) {
    if (!envId) return;
    this.tableEnvironment = envId;
    if (this.settings) this.settings.tableEnvironment = envId;
    if (typeof localStorage !== 'undefined') {
      try { localStorage.setItem('cc_board_3d_table_env', envId); } catch (_) {}
    }
    const domSel = typeof document !== 'undefined' ? document.getElementById('optTableEnvironment') : null;
    if (domSel && domSel.value !== envId) {
      domSel.value = envId;
    }
    this.setupTableEnvironment();
    this.updateTableEnvironmentToolbarUI();
  }

  /**
   * Synchronizes active table environment state with DOM selector or toolbar if present.
   */
  updateTableEnvironmentToolbarUI() {
    const domSel = typeof document !== 'undefined' ? document.getElementById('optTableEnvironment') : null;
    if (domSel && this.tableEnvironment && domSel.value !== this.tableEnvironment) {
      domSel.value = this.tableEnvironment;
    }
  }

  /**
   * Constructs or updates the large 3D grounded table slab underneath the board
   * with custom themed PBR surfaces, coasters, card baize, raceway asphalt, or wood grain.
   */
  setupTableEnvironment() {
    if (!this.scene || !this.BABYLON || this.isDestroyed) return;
    this.clearTableEnvironment();

    const BABYLON = this.BABYLON;
    const envId = this.getTableEnvironment();

    const bounds = this.boardBounds || this.getBoardBounds();
    const activeTracks = this.activeTracks || ['p1', 'p2'];

    // Construct high-fidelity realistic 3D Room, Floor, Walls, Ceiling, Table with legs, and radial Chairs
    this.roomData = RoomCatalog3D.buildRoom(this.scene, BABYLON, envId, {
      bounds,
      activeTracks,
      seatCount: activeTracks.length,
      settings: this.settings
    });

    if (this.roomData && this.roomData.tableMesh) {
      this.tableMesh = this.roomData.tableMesh;
    }

    this.applyEnvironmentAtmosphere(envId);
    if (this.camera && this.roomData?.dims) {
      const { roomW, roomD } = this.roomData.dims;
      const roomLimit = Math.min(roomW, roomD) * 0.46;
      if (!this.camera.upperRadiusLimit || this.camera.upperRadiusLimit < roomLimit) {
        this.camera.upperRadiusLimit = roomLimit;
      }
      if (this.camera.upperRadiusLimit && this.camera.radius > this.camera.upperRadiusLimit) {
        this.camera.radius = this.camera.upperRadiusLimit;
      }
    }

    // Initialize high-fidelity 3D Playing Cards Engine
    try {
      const dims = this.roomData?.dims || {};
      const tableSurfaceY = dims.tableSurfaceY ?? (bounds?.min?.y != null ? bounds.min.y - 0.08 : -0.88);
      const tableW = dims.tableW ?? 140.0;
      const tableD = dims.tableD ?? 100.0;

      this.cardEngine = new CardEngine3D(this.scene, BABYLON, {
        tableSurfaceY,
        tableW,
        tableD,
        boardBounds: bounds,
        chairRoots: this.roomData?.chairRoots || [],
        backStyle: this.options.cardBack || this.settings?.cardBack || 'classic-blue',
        onCardClick: (card, cardMesh) => this.handle3DCardClick(card, cardMesh),
        onCardHover: (card, isHovered) => this.handle3DCardHover(card, isHovered)
      });
    } catch (e) {
      console.warn('[BoardRenderer3D] Could not initialize CardEngine3D:', e);
    }

    // Initialize high-fidelity 3D Seating & Player Avatars Engine
    try {
      this.seatManager = new SeatManager3D(this.scene, BABYLON, {
        dims: this.roomData?.dims,
        chairRoots: this.roomData?.chairRoots || []
      });
      const initialPlayers = this.getInitialSeatedPlayers();
      this.seatManager.setupSeats(initialPlayers, activeTracks.length, this.roomData?.chairRoots);
      const curScores = (typeof window !== 'undefined' ? window.state?.scores : null) || this.lastSyncedState?.scores;
      if (curScores) {
        this.seatManager.updateScores(curScores);
      }
      if (this.cardEngine) {
        this.cardEngine.seatManager = this.seatManager;
        this.cardEngine.chairRoots = this.roomData?.chairRoots || [];
      }
    } catch (e) {
      console.warn('[BoardRenderer3D] Could not initialize SeatManager3D:', e);
    }
  }

  /**
   * Resolves initial player avatars, display names, and scores for active seats.
   * @returns {Array<Object>}
   */
  getInitialSeatedPlayers() {
    const activeTracks = this.activeTracks || ['p1', 'p2'];
    const players = [];
    for (let i = 0; i < activeTracks.length; i++) {
      const seat = activeTracks[i] || `p${i + 1}`;
      const avatarId = this.options.avatarMap?.[seat] || (seat === 'p1' ? (this.settings?.avatar || 'joker') : (seat === 'p2' ? 'king' : 'shark'));
      const name = this.options.playerNames?.[seat] || (seat === 'p1' ? (this.settings?.playerName || 'You') : (seat === 'p2' ? 'Computer' : `Player ${i + 1}`));
      const score = this.pegState?.[seat]?.front || 0;
      players.push({
        id: seat,
        name,
        avatarId,
        score,
        isTurn: (i === 0)
      });
    }
    return players;
  }

  /**
   * Updates player data on a 3D seated avatar.
   * @param {string} seat - 'p1', 'p2', etc.
   * @param {Object} data
   */
  updateSeatPlayer(seat, data) {
    if (this.seatManager) this.seatManager.updatePlayer(seat, data);
  }

  /**
   * Sets the active player's turn halo and status badge.
   * @param {string} seat - 'p1', 'p2', etc.
   */
  setActiveTurn(seat) {
    if (this.seatManager) this.seatManager.setActiveTurn(seat);
  }

  /**
   * Updates scores displayed on 3D floating nameplates.
   * @param {Object} scores - { p1: 12, p2: 18, ... }
   */
  updateSeatScores(scores) {
    if (this.seatManager) this.seatManager.updateScores(scores);
  }

  /**
   * Shows an emoji reaction bubble above a seated player's avatar.
   * @param {string} seat
   * @param {string} emoji
   */
  showSeatReaction(seat, emoji) {
    if (this.seatManager) this.seatManager.showReaction(seat, emoji);
  }

  /**
   * Toggles visibility of the local human player 3D avatar figure.
   * @param {boolean} visible
   */
  setLocalPlayerVisible(visible) {
    if (this.seatManager) this.seatManager.setLocalPlayerVisible(visible);
  }

  /**
   * Sets transparency across all computer player avatars in the 3D scene.
   * @param {boolean} isTransparent
   * @param {number} [alpha=0.6]
   */
  setComputerAvatarsTransparent(isTransparent, alpha = 0.6) {
    if (this.seatManager) this.seatManager.setComputerAvatarsTransparent(isTransparent, alpha);
  }

  /**
   * Toggles avatar wrapping on head sphere across all computer player avatars.
   * @param {boolean} wrap
   */
  setComputerAvatarsWrapOnHead(wrap) {
    if (this.seatManager) this.seatManager.setComputerAvatarsWrapOnHead(wrap);
  }

  /**
   * Renders a player's hand fan in 3D in front of their seat.
   * @param {string} seat - 'p1', 'p2', etc.
   * @param {Array<Object>} cards
   * @param {Object} [options={}]
   * @returns {Array<CardMesh3D>}
   */
  renderPlayerHand(seat, cards, options = {}) {
    if (this.cardEngine) return this.cardEngine.renderHandFan(seat, cards, options);
    return [];
  }

  /**
   * Renders played cards in the central pegging pile on the table.
   * @param {Array<Object>} cards
   */
  renderPlayPile(cards, options = {}) {
    if (!this.cardEngine) return;
    const state = (typeof window !== 'undefined' ? window.state : null) || {};
    const me = this.mySeat || 'p1';
    const isPlayPhase = (state.phase === 'play' || state.phase === 'pegging');
    const normalizeSeat = (s) => {
      if (!s) return '';
      if (this.seatManager && typeof this.seatManager.normalizeSeatId === 'function') {
        return this.seatManager.normalizeSeatId(s);
      }
      if (s === 'you' || s === 'player1' || s === 'team1') return 'p1';
      if (s === 'cpu' || s === 'player2' || s === 'team2') return 'p2';
      if (s === 'player3' || s === 'team3') return 'p3';
      if (s === 'player4' || s === 'team4') return 'p4';
      return s;
    };
    const activeSeat = (isPlayPhase && state.turn)
      ? normalizeSeat(state.turn)
      : (options.turnSeat || me);
    return this.cardEngine.renderPlayPile(cards, {
      localSeat: me,
      turnSeat: activeSeat || me,
      faceUp: (options.faceUp !== undefined) ? options.faceUp : !state.pileFaceDown,
      ...options
    });
  }

  /**
   * Renders the starter deck and cut starter card on the table.
   * @param {Object} starterCard
   * @param {Object} [options={}]
   */
  renderStarterDeck(starterCard, options = {}) {
    if (this.cardEngine) return this.cardEngine.renderStarterDeck(starterCard, options);
  }

  /**
   * Performs cutting the starter deck in the 3D room.
   * Plays the 3D cut animation, snap audio, and invokes the game engine cut logic.
   * @param {Object} [context={}]
   */
  do3DCutStarter(context = {}) {
    if (this._isCuttingStarter) return;
    this._isCuttingStarter = true;

    // Trigger visual 3D cut animation on deck if available
    if (this.cardEngine && typeof this.cardEngine.animateDeckCut === 'function') {
      try {
        this.cardEngine.animateDeckCut();
      } catch (_) {}
    }

    // Trigger audio snap if available
    try {
      const sfx = context.sfx || (typeof window !== 'undefined' ? window.sfx : null);
      if (sfx && typeof sfx.snap === 'function') sfx.snap();
    } catch (_) {}

    // Trigger game cut logic
    const domCut = (typeof document !== 'undefined')
      ? (document.getElementById('btnCutStarter') || document.getElementById('dealDeck'))
      : null;
    if (domCut && typeof domCut.click === 'function' && domCut.offsetParent !== null) {
      domCut.click();
    } else if (typeof window !== 'undefined' && typeof window.poneDoCutStarter === 'function') {
      window.poneDoCutStarter(context);
    } else {
      const poneCut = context.poneDoCutStarter || (typeof window !== 'undefined' ? window.CrazyCribbage?.GameEngine?.poneDoCutStarter : null);
      if (typeof poneCut === 'function') poneCut(context);
    }

    setTimeout(() => {
      this._isCuttingStarter = false;
    }, 1200);
  }

  /**
   * Performs picking a card from the cut fan for initial deal in the 3D room.
   * @param {number} index
   * @param {Object} [context={}]
   */
  do3DPickCut(index, context = {}) {
    const isOnline = typeof context.isOnline === 'function' ? context.isOnline : (typeof window !== 'undefined' && typeof window.isOnline === 'function' ? window.isOnline : () => false);
    const mySeat = typeof context.mySeat === 'function' ? context.mySeat : (typeof window !== 'undefined' && typeof window.mySeat === 'function' ? window.mySeat : () => 'p1');
    const curState = (typeof window !== 'undefined' ? window.state : null) || this.lastSyncedState;
    const curTurn = curState?.cutTurn || 'p1';
    const seat = isOnline() ? mySeat() : curTurn;
    const pickCut = context.pickCut || (typeof window !== 'undefined' ? (window.pickCut || window.CrazyCribbage?.GameEngine?.pickCut) : null);
    if (typeof pickCut === 'function') {
      pickCut(index, seat, context);
    }
  }

  /**
   * Renders the crib cards in the crib zone.
   * @param {Array<Object>} cards
   * @param {boolean} [isRevealed=false]
   */
  renderCrib(cards, isRevealed = false) {
    if (this.cardEngine) return this.cardEngine.renderCrib(cards, isRevealed);
  }

  /**
   * Synchronizes all 3D cards in the scene based on game state.
   * @param {Object} cardState - { hands, playPile, starter, starterOptions, crib, cribRevealed, revealAll }
   */
  syncCards(cardState = {}) {
    if (!this.cardEngine) return;
    if (this.boardBounds && !this.cardEngine.boardBounds) {
      this.cardEngine.boardBounds = this.boardBounds;
    }
    if (cardState.hands) {
      for (const [seat, cards] of Object.entries(cardState.hands)) {
        this.cardEngine.renderHandFan(seat, cards, { faceUp: seat === 'p1' || cardState.revealAll, isLocal: seat === 'p1' });
      }
    }
    if (cardState.playPile) {
      this.cardEngine.renderPlayPile(cardState.playPile);
    }
    if (cardState.starters !== undefined || cardState.starter !== undefined) {
      const starters = (cardState.starters && cardState.starters.length)
        ? cardState.starters
        : (cardState.starter ? [cardState.starter] : []);
      this.cardEngine.renderStarterDeck(starters.length ? starters : null, cardState.starterOptions || {});
    }
    if (cardState.crib) {
      this.cardEngine.renderCrib(cardState.crib, cardState.cribRevealed);
    }
  }

  /**
   * Handles user interaction when clicking a 3D playing card in their hand or on the table.
   * Routes the action into deck cutting, discard picking, pegging card play, or custom callbacks.
   * @param {Object} card
   * @param {Object} cardMesh
   */
  handle3DCardClick(card, cardMesh) {
    if (cardMesh && (cardMesh.isDeck || cardMesh.owner === 'deck' || cardMesh.isCutPrompt)) {
      const curState = (typeof window !== 'undefined' ? window.state : null) || this.lastSyncedState;
      if (curState && curState.phase === 'cut') {
        const cutDeck = curState.cutDeck || [];
        const midIdx = Math.floor(cutDeck.length / 2);
        this.do3DPickCut(midIdx);
        return;
      }
      this.do3DCutStarter();
      return;
    }

    if (cardMesh && (cardMesh.isCutFan || cardMesh.cutIndex != null)) {
      this.do3DPickCut(cardMesh.cutIndex);
      return;
    }

    if (typeof this.options.onCardClick === 'function') {
      this.options.onCardClick(card, cardMesh);
      return;
    }

    if (typeof window === 'undefined') return;
    const state = window.state;
    const me = typeof window.mySeat === 'function' ? window.mySeat() : 'p1';
    if (!state || !card) return;

    const isDrawPhase = (state.phase === 'draw-1' || state.phase === 'draw-2' || state.phase === 'draw-1-p2' || state.phase === 'draw-2-p2');
    if (state.phase === 'discard' || state.phase === 'discard-p2' || isDrawPhase) {
      if (typeof window.onYouCard === 'function') {
        window.onYouCard(card);
      }
    } else if (state.phase === 'play' || state.phase === 'pegging') {
      if (state.frozen || state.paused || state.pausedForSign || state.pausedForAdultTimer || state.pausedForAdultStrip || state.pausedForPintsBonus || !state.turn) return;
      const cardOwner = cardMesh?.owner || card?.owner;
      const isOnline = typeof this.options.isOnline === 'function' ? this.options.isOnline : (typeof window !== 'undefined' && typeof window.isOnline === 'function' ? window.isOnline : () => false);
      const isSeatHuman = typeof this.options.isSeatHuman === 'function' ? this.options.isSeatHuman : (typeof window !== 'undefined' && typeof window.isSeatHuman === 'function' ? window.isSeatHuman : (s) => (s ? s === me : false));
      const isMyTurn = (state.turn === me || (!isOnline() && (state.turn === 'p1' || state.turn === 'you')));

      if (isMyTurn) {
        if (cardOwner && cardOwner !== me && cardOwner !== 'p1' && cardOwner !== 'you') return;
        if (typeof window.onYouCard === 'function') {
          window.onYouCard(card);
        }
      } else if (!isOnline() && isSeatHuman(state.turn)) {
        if (cardOwner && cardOwner !== state.turn) return;
        if (state.turn === 'p2' && typeof window.onCpuCard === 'function') {
          window.onCpuCard(card);
        } else if (typeof window.playCard === 'function') {
          window.playCard(state.turn, card);
        }
      }
    } else if (state.phase === 'assign' || state.phase === 'assign-p2') {
      if (state.assignTarget === 'starter') {
        const starters = (state.starters && state.starters.length) ? state.starters : (state.starter ? [state.starter] : []);
        const unassignedWild = starters.find(c => Boolean(c && c.wild) && !c.playAs);
        if (unassignedWild && typeof window.openWildPick === 'function') {
          window.openWildPick(unassignedWild, 'starter');
        }
      } else {
        if (state.phase === 'assign-p2' && typeof window.onCpuCard === 'function') {
          window.onCpuCard(card);
        } else if (typeof window.onYouCard === 'function') {
          window.onYouCard(card);
        }
      }
    } else if (state.phase === 'pick-crib' && typeof window.onPickCribCard === 'function') {
      const pickSeat = cardMesh?.owner || state.currentPickPone || 'p2';
      window.onPickCribCard(pickSeat, card);
    }
  }

  /**
   * Handles hovering over 3D playing cards (e.g. coach preview during discard).
   * @param {Object} card
   * @param {boolean} isHovered
   */
  handle3DCardHover(card, isHovered) {
    if (typeof this.options.onCardHover === 'function') {
      this.options.onCardHover(card, isHovered);
      return;
    }

    if (typeof window === 'undefined') return;
    const state = window.state;
    const settings = window.settings;
    if (settings?.coach === 'on' && state?.phase === 'discard' && typeof window.updateCoachDisplay === 'function') {
      if (isHovered && card) {
        const me = typeof window.mySeat === 'function' ? window.mySeat() : 'p1';
        const getHand = typeof window.getSeatHand === 'function' ? window.getSeatHand : () => [];
        let sim = getHand(me).filter(x => state.selected?.includes(x.id));
        if (!state.selected?.includes(card.id)) {
          sim = sim.concat([card]).slice(0, 2);
        }
        if (sim.length === 2) {
          window.updateCoachDisplay(sim);
        }
      } else {
        window.updateCoachDisplay();
      }
    }
  }

  /**
   * Mounts the floating in-game action HUD overlay on top of the 3D canvas.
   */
  create3DHUD() {
    if (typeof document === 'undefined' || !this.container || this.hud3DOverlay) return;

    const hud = document.createElement('div');
    hud.className = 'board-3d-hud-wrap';
    hud.setAttribute('aria-label', '3D In-Game Tabletop HUD');

    // 0. Active Dealer Pill
    const dealerPill = document.createElement('div');
    dealerPill.className = 'hud-3d-dealer';
    dealerPill.style.display = 'none';
    hud.appendChild(dealerPill);

    // 0b. Current Player Score Pill
    const scorePill = document.createElement('div');
    scorePill.className = 'hud-3d-player-score';
    scorePill.style.display = 'none';
    hud.appendChild(scorePill);

    // 1. Pegging Count Pill
    const countPill = document.createElement('div');
    countPill.className = 'hud-3d-count';
    countPill.style.display = 'none';
    hud.appendChild(countPill);

    // 1b. Play a Card Prompt Pill
    const playPromptPill = document.createElement('div');
    playPromptPill.className = 'hud-3d-play-prompt';
    playPromptPill.style.display = 'none';
    hud.appendChild(playPromptPill);

    // 2. Cut Starter Button
    const cutBtn = document.createElement('button');
    cutBtn.type = 'button';
    cutBtn.className = 'btn-3d-hud-action btn-3d-hud-cut';
    cutBtn.textContent = '🎯 Cut Starter Card';
    cutBtn.style.display = 'none';
    cutBtn.addEventListener('click', () => {
      const curState = (typeof window !== 'undefined' ? window.state : null) || this.lastSyncedState;
      if (curState && curState.phase === 'cut') {
        const cutDeck = curState.cutDeck || [];
        const midIdx = Math.floor(cutDeck.length / 2);
        this.do3DPickCut(midIdx);
        return;
      }
      this.do3DCutStarter();
    });
    hud.appendChild(cutBtn);

    // 3. Discard Button
    const discardBtn = document.createElement('button');
    discardBtn.type = 'button';
    discardBtn.className = 'btn-3d-hud-action btn-3d-hud-discard';
    discardBtn.textContent = 'Discard Selected';
    discardBtn.style.display = 'none';
    discardBtn.addEventListener('click', () => {
      const curState = (typeof window !== 'undefined' ? window.state : null);
      if (curState && curState.phase === 'pick-crib') {
        if (typeof window !== 'undefined' && typeof window.confirmPickCrib === 'function') {
          window.confirmPickCrib();
          return;
        }
      }
      if (curState && (curState.phase === 'draw-1' || curState.phase === 'draw-2' || curState.phase === 'draw-1-p2' || curState.phase === 'draw-2-p2')) {
        if (typeof window !== 'undefined' && typeof window.confirmDraw === 'function') {
          window.confirmDraw();
          return;
        }
      }
      const domDiscard = document.getElementById('btnDiscard');
      if (domDiscard && typeof domDiscard.click === 'function') {
        domDiscard.click();
      } else if (typeof window !== 'undefined' && typeof window.confirmDiscard === 'function') {
        window.confirmDiscard();
      }
    });
    hud.appendChild(discardBtn);

    // 4. Next / Continue Button
    const nextBtn = document.createElement('button');
    nextBtn.type = 'button';
    nextBtn.className = 'btn-3d-hud-action btn-3d-hud-next';
    nextBtn.textContent = 'Continue ▶';
    nextBtn.style.display = 'none';
    nextBtn.addEventListener('click', () => {
      const domNext = document.getElementById('btnNext');
      if (domNext) domNext.click();
    });
    hud.appendChild(nextBtn);

    // 5. Hand / Crib Counting Pill
    const showCountPill = document.createElement('div');
    showCountPill.className = 'hud-3d-show-count';
    showCountPill.style.display = 'none';
    hud.appendChild(showCountPill);

    // 6. Next Hand Button
    const nextHandBtn = document.createElement('button');
    nextHandBtn.type = 'button';
    nextHandBtn.className = 'btn-3d-hud-action btn-3d-hud-next-hand';
    nextHandBtn.textContent = 'Next Hand ▶';
    nextHandBtn.style.display = 'none';
    nextHandBtn.addEventListener('click', () => {
      const domNextHand = document.getElementById('btnNextHand');
      if (domNextHand && typeof domNextHand.click === 'function') {
        domNextHand.click();
      } else {
        const curState = (typeof window !== 'undefined' ? window.state : null) || state;
        if (curState) {
          curState.showModal = null;
          curState.chutesAndLaddersTriggered = false;
          curState.chutesAndLaddersTiedScore = null;
        }
        if (typeof window !== 'undefined') {
          if (typeof window.hideModal === 'function') window.hideModal();
          if (typeof window.nextSeat === 'function' && curState) curState.dealer = window.nextSeat(curState.dealer);
          if (curState) curState.revealCrib = false;
          if (typeof window.newHand === 'function') window.newHand();
          if (typeof window.netPush === 'function') window.netPush({ modal: 'hide' });
        }
      }
    });
    hud.appendChild(nextHandBtn);

    this.container.appendChild(hud);
    this.hud3DOverlay = hud;
  }

  /**
   * Updates state-driven visibility and labels of the floating 3D HUD controls.
   * @param {Object} state
   * @param {Object} context
   */
  update3DHUD(state = {}, context = {}) {
    if (typeof document === 'undefined' || !this.container) return;
    if (!this.hud3DOverlay) {
      this.create3DHUD();
    }
    if (!this.hud3DOverlay) return;

    const me = context.me || (typeof window !== 'undefined' && typeof window.mySeat === 'function' ? window.mySeat() : 'p1');
    const isPlayPhase = (state.phase === 'play' || state.phase === 'pegging');
    const isCutStarter = (state.phase === 'cut-starter');
    const isDiscard = (state.phase === 'discard' || state.phase === 'discard-p2');

    // Dealer Pill
    const dealerEl = this.hud3DOverlay.querySelector('.hud-3d-dealer');
    if (dealerEl) {
      if (state.dealer) {
        dealerEl.style.display = 'inline-flex';
        const isMeDeal = (state.dealer === me || state.dealer === 'you' || state.dealer === 'p1');
        const pName = context.pName || (typeof window !== 'undefined' && typeof window.pName === 'function' ? window.pName : (s) => (s === 'p1' ? 'You' : (s === 'p2' ? 'Opponent' : String(s).toUpperCase())));
        const dealerLabel = isMeDeal ? 'You (Your Crib)' : `${pName(state.dealer)} (Crib)`;
        dealerEl.innerHTML = `<span class="hud-3d-dealer-star" aria-hidden="true">★</span> <span>Dealer: ${dealerLabel}</span>`;
        dealerEl.classList.toggle('is-me', isMeDeal);
        dealerEl.title = isMeDeal
          ? 'You are the dealer. You count your hand and the crib at the end of the round.'
          : `${pName(state.dealer)} is the dealer and counts the crib.`;
      } else {
        dealerEl.style.display = 'none';
      }
    }

    // Count Pill
    const countEl = this.hud3DOverlay.querySelector('.hud-3d-count');
    if (countEl) {
      if (isPlayPhase) {
        countEl.style.display = 'inline-flex';
        const curCount = state.count != null ? state.count : 0;
        countEl.textContent = `Count: ${curCount} / 31`;
        countEl.classList.toggle('is-fifteen', curCount === 15);
        countEl.classList.toggle('is-thirtyone', curCount === 31);
      } else {
        countEl.style.display = 'none';
      }
    }

    // Player Score Pill (shows points for the active player / local player)
    const scoreEl = this.hud3DOverlay.querySelector('.hud-3d-player-score');
    if (scoreEl) {
      const activeSeat = isPlayPhase ? (state.passingTo || state.turn || me || 'p1') : (me || 'p1');
      const isMyTurn = (activeSeat === me || activeSeat === 'p1' || activeSeat === 'you');
      const seatNameFn = typeof context.seatName === 'function' ? context.seatName : (typeof window !== 'undefined' && typeof window.seatName === 'function' ? window.seatName : (s) => (s === 'p1' ? 'You' : (s === 'p2' ? 'Player 2' : String(s).toUpperCase())));
      const currentName = isMyTurn ? 'You' : seatNameFn(activeSeat);
      const currentScore = this.seatManager
        ? this.seatManager.resolveSeatScore(activeSeat, state.scores)
        : (this.getTrackScore(state.scores, activeSeat) || 0);
      const targetLimit = Number(this.targetHoles) || (typeof window !== 'undefined' && window.settings && Number(window.settings.target)) || 121;

      scoreEl.style.display = 'inline-flex';
      scoreEl.innerHTML = `<span class="hud-3d-score-icon" aria-hidden="true">🏆</span> <span><strong>${currentName}:</strong> ${currentScore} / ${targetLimit} pts</span>`;
      scoreEl.title = `${currentName}'s score: ${currentScore} pts (Goal: ${targetLimit})`;
    }

    // Play Card Prompt Pill
    const playPromptEl = this.hud3DOverlay.querySelector('.hud-3d-play-prompt');
    if (playPromptEl) {
      const activeTurn = state.passingTo || state.turn;
      const isSeatHuman = typeof context.isSeatHuman === 'function' ? context.isSeatHuman : (typeof window !== 'undefined' && typeof window.isSeatHuman === 'function' ? window.isSeatHuman : () => true);
      const isOnline = typeof context.isOnline === 'function' ? context.isOnline : (typeof window !== 'undefined' && typeof window.isOnline === 'function' ? window.isOnline : () => false);
      const seatName = typeof context.seatName === 'function' ? context.seatName : (typeof window !== 'undefined' && typeof window.seatName === 'function' ? window.seatName : (s) => (s === 'p1' ? 'You' : (s === 'p2' ? 'Opponent' : String(s).toUpperCase())));
      const isHumanTurn = isPlayPhase && !state.frozen && (isOnline() ? (activeTurn === me) : isSeatHuman(activeTurn));

      if (isHumanTurn) {
        playPromptEl.style.display = 'inline-flex';
        const isMyTurn = (activeTurn === me) || (!isOnline() && (activeTurn === 'p1' || activeTurn === 'you'));
        const turnScore = this.seatManager
          ? this.seatManager.resolveSeatScore(activeTurn, state.scores)
          : (this.getTrackScore(state.scores, activeTurn) || 0);
        const label = isMyTurn ? `Your Turn: Play a Card (${turnScore} pts)` : `${seatName(activeTurn)}: Play a Card (${turnScore} pts)`;
        playPromptEl.innerHTML = `<span class="hud-3d-prompt-icon" aria-hidden="true">🎴</span> <span><strong>${label}</strong></span>`;
      } else {
        playPromptEl.style.display = 'none';
      }
    }

    // Hand & Crib Counting Pill
    const showCountEl = this.hud3DOverlay.querySelector('.hud-3d-show-count');
    const isShowPhase = (state.phase === 'show' || state.phase === 'show-crib');
    if (showCountEl) {
      if (isShowPhase && state.showAct && state.showAct !== 'done') {
        showCountEl.style.display = 'inline-flex';
        const isCrib = state.showAct === 'crib';
        const pName = context.pName || (typeof window !== 'undefined' && typeof window.pName === 'function' ? window.pName : (s) => (s === 'p1' ? 'You' : (s === 'p2' ? 'Opponent' : String(s).toUpperCase())));
        const pack = (state.lastShow && state.lastShow[state.showAct]) || null;
        const icon = isCrib ? '🧺' : '🎴';
        const actTitle = pack?.title || (isCrib ? 'The Crib' : `${pName(state.showAct)} Hand`);
        const total = (pack?.score?.total != null) ? pack.score.total : (state.showRun || 0);
        const line = state.showMeter?.line || '';
        showCountEl.innerHTML = `<span class="hud-3d-show-icon">${icon}</span> <span><strong>${actTitle}:</strong> ${total} pts${line ? ` (${line})` : ''}</span>`;
      } else {
        showCountEl.style.display = 'none';
      }
    }

    // Cut Starter / Deal Cut Button
    const cutBtn = this.hud3DOverlay.querySelector('.btn-3d-hud-cut');
    if (cutBtn) {
      const isDealCut = (state.phase === 'cut');
      const isSeatHuman = typeof context.isSeatHuman === 'function' ? context.isSeatHuman : (typeof window !== 'undefined' && typeof window.isSeatHuman === 'function' ? window.isSeatHuman : () => true);
      const isOnline = typeof context.isOnline === 'function' ? context.isOnline : (typeof window !== 'undefined' && typeof window.isOnline === 'function' ? window.isOnline : () => false);

      if (isDealCut) {
        const isPassingDevice = !isOnline() && Boolean(state.passingTo);
        const curTurn = state.cutTurn || 'p1';
        const isMyDealCut = !isPassingDevice && (isOnline()
          ? (!state.cutPicks || !state.cutPicks[me]) && state.cutTurn === me
          : (isSeatHuman(curTurn) && (!state.cutPicks || !state.cutPicks[curTurn])));
        cutBtn.style.display = isMyDealCut ? 'inline-flex' : 'none';
        cutBtn.textContent = '✂️ Cut the Deck';
      } else {
        const pone = (state.cutTurn && isCutStarter) ? state.cutTurn : (state.dealer === 'p1' ? 'p2' : 'p1');
        const isMyCut = isCutStarter && (pone === me || (!isOnline() && isSeatHuman(pone)) || pone === 'p1');
        cutBtn.style.display = isMyCut ? 'inline-flex' : 'none';
        const settings = context.settings || (typeof window !== 'undefined' ? window.settings : null) || this.settings || {};
        const isProgCut = Boolean(settings && settings.optProgressiveCut && (settings.playerCount || 2) === 2);
        const cutCount = isProgCut ? Math.max(1, Math.floor((state.handsCompleted || 0) / 2) + 1) : 1;
        cutBtn.textContent = cutCount > 1 ? `🎯 Cut ${cutCount} Starter Cards` : '🎯 Cut Starter Card';
      }
    }

    // Discard Button
    const discardBtn = this.hud3DOverlay.querySelector('.btn-3d-hud-discard');
    if (discardBtn) {
      const selLen = state.selected?.length || 0;
      const isDrawPhase = (state.phase === 'draw-1' || state.phase === 'draw-2' || state.phase === 'draw-1-p2' || state.phase === 'draw-2-p2');
      if (state.phase === 'pick-crib') {
        const pickN = state.pickN || 2;
        const canConfirmPick = (selLen === pickN);
        discardBtn.style.display = canConfirmPick ? 'inline-flex' : 'none';
        if (canConfirmPick) {
          discardBtn.textContent = `Take (${pickN}) to your crib`;
        }
      } else if (isDrawPhase) {
        discardBtn.style.display = 'inline-flex';
        if (selLen === 0) {
          discardBtn.textContent = 'Keep Hand (Draw 0)';
        } else {
          discardBtn.textContent = `Discard & Draw (${selLen})`;
        }
      } else {
        const settings = context.settings || (typeof window !== 'undefined' ? window.settings : null) || this.settings || {};
        const count = Number(context.count || (settings && settings.playerCount) || (typeof window !== 'undefined' && typeof window.getPlayerCount === 'function' ? window.getPlayerCount(settings) : 2)) || 2;
        const isCanadianDoubles = Boolean(settings && settings.optCanadianDoubles && count === 4);
        const is7Card = Boolean(settings && settings.opt7CardCribbage && count === 2);
        let requiredDiscardN = isCanadianDoubles ? 2 : (count >= 3 ? 1 : (is7Card ? 2 : (settings && settings.deal === 'super' ? 3 : 2)));
        if (settings && (settings.optSpeedoCribbage || settings.optTwistedCribbage) && count === 2) {
          const curSeat = state.currentHumanDiscard || me;
          requiredDiscardN = (curSeat === state.dealer) ? (settings.deal === 'super' ? 6 : 4) : (settings.deal === 'super' ? 3 : 2);
        }
        const canDiscard = isDiscard && (selLen === requiredDiscardN);
        discardBtn.style.display = canDiscard ? 'inline-flex' : 'none';
        if (canDiscard) {
          if (settings && (settings.optSpeedoCribbage || settings.optTwistedCribbage) && count === 2) {
            const curSeat = state.currentHumanDiscard || me;
            const target = (curSeat === state.dealer) ? 'Crib' : "Dealer's Hand";
            discardBtn.textContent = `Discard (${selLen}) to ${target}`;
          } else {
            discardBtn.textContent = requiredDiscardN === 1 ? 'Discard Selected' : `Discard Selected (${selLen})`;
          }
        }
      }
    }

    // Next / Continue Button
    const nextBtn = this.hud3DOverlay.querySelector('.btn-3d-hud-next');
    if (nextBtn) {
      const showNext = (state.phase === 'await');
      nextBtn.style.display = showNext ? 'inline-flex' : 'none';
    }

    // Next Hand Button
    const nextHandBtn = this.hud3DOverlay.querySelector('.btn-3d-hud-next-hand');
    if (nextHandBtn) {
      const isShowOver = (state.showAct === 'done') || Boolean(state.showModal && state.showModal.open) || (state.phase === 'show' && Boolean(document.getElementById('btnNextHand')));
      const isMatchOver = Boolean(state.over);
      nextHandBtn.style.display = (isShowOver && !isMatchOver) ? 'inline-flex' : 'none';
    }
  }

  /**
   * Synchronizes 3D cards, seating avatars, active turns, scores, and HUD directly from GameState.
   * @param {Object} context
   */
  syncFromGameState(context = {}) {
    if (this.isDestroyed || !this.scene) return;

    const state = context.state || (typeof window !== 'undefined' ? window.state : null) || {};
    const settings = typeof this.getResolvedSettings === 'function'
      ? this.getResolvedSettings(context.settings)
      : (context.settings || (typeof window !== 'undefined' ? window.settings : null) || this.settings || {});
    const target = Number(settings.target || this.targetHoles || 121) || 121;
    const key = `${Boolean(settings.optPegThroughZones)}_${Boolean(settings.optLast5PegOut)}_${Boolean(settings.optBunkers)}_${Boolean(settings.optStinkHole)}_${target}`;
    if (this._lastZoneHighlightKey !== key || !this.zoneHighlightsRoot) {
      if (typeof this.updateZoneHighlights === 'function') this.updateZoneHighlights(settings);
    }
    const me = context.me || (typeof window !== 'undefined' && typeof window.mySeat === 'function' ? window.mySeat() : 'p1');
    const oppSeats = context.oppSeats || (this.activeTracks || ['p1', 'p2']).filter(s => s !== me);

    const getSeatHand = context.getSeatHand || (typeof window !== 'undefined' && typeof window.getSeatHand === 'function' ? window.getSeatHand : () => []);
    const getSeatKept = context.getSeatKept || (typeof window !== 'undefined' && typeof window.getSeatKept === 'function' ? window.getSeatKept : () => []);
    const sortCards = context.sortCards || (typeof window !== 'undefined' && typeof window.sortCards === 'function' ? window.sortCards : (c) => c);

    const showHands = Boolean(state.phase === 'show' || state.phase === 'show-crib' || state.over);

    // 1. Sync 3D Cards
    if (this.cardEngine) {
      if (this.boardBounds && !this.cardEngine.boardBounds) {
        this.cardEngine.boardBounds = this.boardBounds;
      }
      // Local player's hand
      const myKept = getSeatKept(me);
      const myHandCards = (showHands && myKept && myKept.length) ? myKept : getSeatHand(me);
      const myHand = sortCards(myHandCards || []);

      const isPlayPhase = (state.phase === 'play' || state.phase === 'pegging');
      const isDrawPhase = (state.phase === 'draw-1' || state.phase === 'draw-2' || state.phase === 'draw-1-p2' || state.phase === 'draw-2-p2');
      const isDiscardPhase = (state.phase === 'discard' || state.phase === 'discard-p2');
      const isAssignPhase = (state.phase === 'assign' || state.phase === 'assign-p2');
      const isOnlineFn = typeof context.isOnline === 'function' ? context.isOnline : (typeof window !== 'undefined' && typeof window.isOnline === 'function' ? window.isOnline : () => false);
      const isOnlineGame = isOnlineFn();

      let isLocalHandInteractive = false;
      if (state.frozen || state.paused || state.pausedForSign || state.pausedForAdultTimer || state.pausedForAdultStrip || state.pausedForPintsBonus || state.over) {
        isLocalHandInteractive = false;
      } else if (isDiscardPhase) {
        const hasDiscarded = Boolean(state.seatDiscards && state.seatDiscards[me]);
        isLocalHandInteractive = !hasDiscarded;
      } else if (isDrawPhase || isAssignPhase) {
        isLocalHandInteractive = true;
      } else if (isPlayPhase) {
        const isMyTurn = Boolean(state.turn && (state.turn === me || (!isOnlineGame && (state.turn === 'p1' || state.turn === 'you'))));
        isLocalHandInteractive = isMyTurn;
      }

      const p1Meshes = this.cardEngine.renderHandFan(me, myHand, {
        faceUp: true,
        interactive: isLocalHandInteractive,
        isLocal: true
      });

      // Synchronize selection and show-count highlights
      if (p1Meshes) {
        p1Meshes.forEach(mesh => {
          const isSel = (Array.isArray(state.selected) && state.selected.includes(mesh.card?.id)) ||
                        (Array.isArray(state.highlightIds) && state.highlightIds.includes(mesh.card?.id));
          if (mesh.isSelected !== isSel) {
            mesh.setSelected(isSel);
          }
        });
      }

      // Opponents' hands
      oppSeats.forEach(oppSeat => {
        const oppKept = getSeatKept(oppSeat);
        const oppHand = (showHands && oppKept && oppKept.length) ? oppKept : getSeatHand(oppSeat);
        const isThisOppShowing = state.phase === 'show' && (state.showAct === oppSeat || state.showAct === 'done' || state.showAct === 'crib' || state.over);
        const isPickingThisOpp = (state.phase === 'pick-crib' && state.currentPickPone === oppSeat);
        const oppMeshes = this.cardEngine.renderHandFan(oppSeat, oppHand || [], {
          faceUp: showHands && (isThisOppShowing || state.over),
          interactive: isPickingThisOpp,
          isLocal: false
        });
        if (oppMeshes) {
          oppMeshes.forEach(mesh => {
            const isHigh = Boolean(Array.isArray(state.highlightIds) && state.highlightIds.includes(mesh.card?.id));
            const isSel = Boolean(isPickingThisOpp && Array.isArray(state.selected) && state.selected.includes(mesh.card?.id));
            const active = isHigh || isSel;
            if (mesh.isSelected !== active) {
              mesh.setSelected(active);
            }
          });
        }
      });

      // Pegging Play Pile (rendered face up in front of the player whose turn it is to play a card)
      const normalizeSeat = (s) => {
        if (!s) return '';
        if (this.seatManager && typeof this.seatManager.normalizeSeatId === 'function') {
          return this.seatManager.normalizeSeatId(s);
        }
        if (s === 'you' || s === 'player1' || s === 'team1') return 'p1';
        if (s === 'cpu' || s === 'player2' || s === 'team2') return 'p2';
        if (s === 'player3' || s === 'team3') return 'p3';
        if (s === 'player4' || s === 'team4') return 'p4';
        return s;
      };
      const activeSeat = (isPlayPhase && state.turn) ? normalizeSeat(state.turn) : me;
      const playPileCards = (state.pile && state.pile.length)
        ? state.pile
        : ((state.played && state.played.length && (state.phase !== 'play' || state.turn || !state.frozen)) ? state.played : (state.pile || []));
      this.cardEngine.renderPlayPile(playPileCards, {
        localSeat: me,
        turnSeat: activeSeat || me,
        faceUp: !state.pileFaceDown
      });

      // Starter Deck & Cut Card (with 3D cutting interactivity)
      const isCutStarter = (state.phase === 'cut-starter');
      const pone = (state.cutTurn && isCutStarter) ? state.cutTurn : (state.dealer === 'p1' ? 'p2' : 'p1');
      const isSeatHuman = typeof context.isSeatHuman === 'function' ? context.isSeatHuman : (typeof window !== 'undefined' && typeof window.isSeatHuman === 'function' ? window.isSeatHuman : () => true);
      const isOnline = typeof context.isOnline === 'function' ? context.isOnline : (typeof window !== 'undefined' && typeof window.isOnline === 'function' ? window.isOnline : () => false);
      const isMyCut = isCutStarter && (pone === me || (!isOnline() && isSeatHuman(pone)));
      const settings = context.settings || (typeof window !== 'undefined' ? window.settings : null) || this.settings || {};
      const isProgCut = Boolean(settings && settings.optProgressiveCut && (settings.playerCount || 2) === 2);
      const cutCount = isProgCut ? Math.max(1, Math.floor((state.handsCompleted || 0) / 2) + 1) : 1;

      const starters = (state.starters && state.starters.length) ? state.starters : (state.starter ? [state.starter] : []);
      this.cardEngine.renderStarterDeck(starters.length ? starters : null, {
        isCuttable: isMyCut,
        cutCount: cutCount,
        phase: state.phase,
        cutTurn: pone,
        starters: starters,
        starterFace: state.starterFace !== false,
        onCut: () => this.do3DCutStarter(context)
      });
      const starterMeshes = this.cardEngine.starterCards && this.cardEngine.starterCards.length
        ? this.cardEngine.starterCards
        : (this.cardEngine.starterCardMesh ? [this.cardEngine.starterCardMesh] : []);
      starterMeshes.forEach(mesh => {
        if (mesh && mesh.cardData) {
          const isStarterHigh = Boolean(Array.isArray(state.highlightIds) && mesh.cardData.id && state.highlightIds.includes(mesh.cardData.id));
          if (mesh.isSelected !== isStarterHigh) {
            mesh.setSelected(isStarterHigh);
          }
        }
      });

      // Initial Deal Cut Fan
      if ((state.phase === 'cut' || state.phase === 'cut-reveal') && Array.isArray(state.cutDeck)) {
        const isPassingDevice = !isOnline() && Boolean(state.passingTo);
        const curTurn = state.cutTurn || 'p1';
        const isMyDealCut = !isPassingDevice && (isOnline()
          ? (!state.cutPicks || !state.cutPicks[me]) && state.cutTurn === me
          : (isSeatHuman(curTurn) && (!state.cutPicks || !state.cutPicks[curTurn])));

        this.cardEngine.renderCutDeckFan(state.cutDeck, state.cutPicks || {}, {
          isMyCut: isMyDealCut,
          cutTurn: curTurn,
          phase: state.phase,
          onPickCut: (index) => this.do3DPickCut(index, context)
        });
      } else if (typeof this.cardEngine.clearCutDeckFan === 'function') {
        this.cardEngine.clearCutDeckFan();
      }

      // Crib Stack (revealed during crib counting, after show, or when match is over)
      const isCribRevealed = Boolean(
        state.phase === 'show-crib' ||
        (state.phase === 'show' && (state.turn === 'crib' || state.showAct === 'crib' || state.revealCrib || state.showAct === 'done')) ||
        state.over
      );
      const cribMeshes = this.cardEngine.renderCrib(state.crib || [], isCribRevealed);
      if (cribMeshes && Array.isArray(state.highlightIds) && state.highlightIds.length > 0) {
        cribMeshes.forEach(mesh => {
          const isHigh = state.highlightIds.includes(mesh.card?.id);
          if (mesh.isSelected !== isHigh) {
            mesh.setSelected(isHigh);
          }
        });
      }
    }

    // 2. Sync Seats, Avatars, Turn Glow, Dealer & Scores
    if (state.dealer && typeof this.setDealer === 'function') {
      this.setDealer(state.dealer, true);
    }
    if (this.seatManager) {
      if (state.turn) {
        this.seatManager.setActiveTurn(state.turn);
      }
      if (state.scores) {
        this.seatManager.updateScores(state.scores);
      }
    }

    // Safety net: ensure 3D pegs stay synced with game scores if out of sync
    if (state && state.scores && typeof this.updatePegs === 'function') {
      const tracks = Array.isArray(this.activeTracks) ? this.activeTracks : ['p1', 'p2'];
      let outOfSync = false;
      for (const who of tracks) {
        const targetLimit = Number(this.targetHoles) || 121;
        const sc = Math.min(targetLimit, typeof this.getTrackScore === 'function' ? this.getTrackScore(state.scores, who) : (state.scores[who] || 0));
        const cur = (this.pegState && this.pegState[who]) ? this.pegState[who].front : 0;
        if (sc !== cur) {
          outOfSync = true;
          break;
        }
      }
      if (outOfSync) {
        this.updatePegs(state.scores, state.prev || {}, false, state.lastScoringEvent || null);
      }
    }

    // 3. Sync Floating 3D Action HUD
    this.update3DHUD(state, context);
  }

  /**
   * Generates a high-definition PBR material for the chosen table environment
   * featuring procedural pub coasters, casino felt baize, or raceway tire markings.
   * @param {string} envId
   * @param {number} width
   * @param {number} depth
   * @returns {BABYLON.PBRMaterial}
   */
  createTableEnvironmentMaterial(envId, width, depth) {
    const BABYLON = this.BABYLON;
    const mat = new BABYLON.PBRMaterial(`matTableEnv_${envId}`, this.scene);

    if (!BABYLON.DynamicTexture) {
      mat.albedoColor = new BABYLON.Color3(0.18, 0.12, 0.08);
      mat.roughness = 0.5;
      return mat;
    }

    const dynTex = new BABYLON.DynamicTexture(`texTableEnv_${envId}`, { width: 1024, height: 1024 }, this.scene, true);
    this._tableTexture = dynTex;
    const ctx = dynTex.getContext();

    if (envId === 'pub') {
      // 1. THE PUB (Dark tavern oak planks, beer coasters, condensation rings, warm pub feel)
      mat.roughness = 0.45;
      mat.metallic = 0.04;

      // Dark oak wood background
      ctx.fillStyle = '#22150c';
      ctx.fillRect(0, 0, 1024, 1024);

      // Oak plank horizontal seams and subtle grain
      for (let y = 0; y < 1024; y += 128) {
        ctx.fillStyle = (Math.floor(y / 128) % 2 === 0) ? '#281a10' : '#1c1109';
        ctx.fillRect(0, y, 1024, 126);

        // Plank joint line
        ctx.fillStyle = '#0f0804';
        ctx.fillRect(0, y + 126, 1024, 2);

        // Subtle wood grain streaks
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.03)';
        ctx.lineWidth = 1;
        for (let i = 0; i < 4; i++) {
          ctx.beginPath();
          ctx.moveTo(0, y + 20 + i * 26);
          ctx.bezierCurveTo(340, y + 25 + i * 26, 680, y + 15 + i * 26, 1024, y + 22 + i * 26);
          ctx.stroke();
        }
      }

      // Draw Coaster 1: Left Coaster ("PINTS & PEGS" brass & emerald crest)
      const cx1 = 200, cy1 = 220, r1 = 110;
      ctx.beginPath();
      ctx.arc(cx1, cy1, r1, 0, Math.PI * 2);
      ctx.fillStyle = '#103822';
      ctx.fill();
      ctx.strokeStyle = '#d4af37';
      ctx.lineWidth = 8;
      ctx.stroke();

      ctx.beginPath();
      ctx.arc(cx1, cy1, r1 - 12, 0, Math.PI * 2);
      ctx.strokeStyle = 'rgba(212, 175, 55, 0.4)';
      ctx.lineWidth = 2;
      ctx.stroke();

      ctx.fillStyle = '#ffd700';
      ctx.font = 'bold 20px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('PINTS & PEGS', cx1, cy1 - 18);
      ctx.font = '14px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
      ctx.fillStyle = '#e2e8f0';
      ctx.fillText('EST. CRAZY CRIBBAGE', cx1, cy1 + 10);
      ctx.fillText('🍺 TAVERN PUB 🍺', cx1, cy1 + 32);

      // Draw Coaster 2: Right Coaster (Kraft cardboard brew coaster)
      const cx2 = 824, cy2 = 804, r2 = 100;
      ctx.beginPath();
      ctx.arc(cx2, cy2, r2, 0, Math.PI * 2);
      ctx.fillStyle = '#9c7348';
      ctx.fill();
      ctx.strokeStyle = '#5a3d1e';
      ctx.lineWidth = 5;
      ctx.stroke();

      ctx.fillStyle = '#3a2512';
      ctx.font = 'bold 18px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
      ctx.fillText('CRAFT BREWED', cx2, cy2 - 14);
      ctx.font = '13px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
      ctx.fillText('121 PEGS • 31 TO THE GO', cx2, cy2 + 12);

      // Condensation Water Rings
      const drawRing = (rx, ry, rad) => {
        ctx.beginPath();
        ctx.arc(rx, ry, rad, 0, Math.PI * 2);
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.14)';
        ctx.lineWidth = 4;
        ctx.stroke();
        ctx.beginPath();
        ctx.arc(rx, ry, rad - 2, 0, Math.PI * 2);
        ctx.strokeStyle = 'rgba(0, 0, 0, 0.35)';
        ctx.lineWidth = 2;
        ctx.stroke();
      };
      drawRing(280, 800, 65);
      drawRing(760, 240, 72);

    } else if (envId === 'vegas') {
      // 2. VEGAS HIGH ROLLER SUITE (Emerald casino felt, gold card suits, mahogany railing)
      mat.roughness = 0.65;
      mat.metallic = 0.12;

      // Outer rich dark mahogany wood railing
      ctx.fillStyle = '#261108';
      ctx.fillRect(0, 0, 1024, 1024);

      // Casino Felt Inset
      const inset = 64;
      ctx.fillStyle = '#0a3d24';
      ctx.fillRect(inset, inset, 1024 - inset * 2, 1024 - inset * 2);

      // Gold Inlay Rail Border
      ctx.strokeStyle = '#d4af37';
      ctx.lineWidth = 6;
      ctx.strokeRect(inset, inset, 1024 - inset * 2, 1024 - inset * 2);
      ctx.strokeStyle = 'rgba(212, 175, 55, 0.35)';
      ctx.lineWidth = 2;
      ctx.strokeRect(inset + 10, inset + 10, 1024 - (inset + 10) * 2, 1024 - (inset + 10) * 2);

      // Subtle Diamond Harlequin Felt Watermark
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.035)';
      ctx.lineWidth = 1.5;
      const step = 80;
      for (let x = -1024; x < 2048; x += step) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x + 1024, 1024);
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(x, 1024);
        ctx.lineTo(x + 1024, 0);
        ctx.stroke();
      }

      // Vegas High Roller Gold Card Suit Inscriptions (♠ ♥ ♦ ♣)
      ctx.fillStyle = '#ffd700';
      ctx.font = 'bold 36px serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('♠   ♥   ♦   ♣', 512, 110);
      ctx.fillText('♠   ♥   ♦   ♣', 512, 914);

      ctx.font = 'bold 22px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
      ctx.fillStyle = '#fef08a';
      ctx.fillText('CRAZY CRIBBAGE • HIGH ROLLER VIP SUITE', 512, 150);
      ctx.fillText('CRAZY CRIBBAGE • HIGH ROLLER VIP SUITE', 512, 874);

    } else if (envId === 'raceway') {
      // 3. THE THUNDERDOME / RACEWAY (Asphalt tarmac, tire skid marks, hazard chevrons, speedway stencil)
      mat.roughness = 0.80;
      mat.metallic = 0.04;

      // Dark asphalt ground
      ctx.fillStyle = '#16191d';
      ctx.fillRect(0, 0, 1024, 1024);

      // Asphalt texture speckles
      ctx.fillStyle = 'rgba(255, 255, 255, 0.04)';
      for (let i = 0; i < 300; i++) {
        const sx = (i * 37) % 1024;
        const sy = (i * 83) % 1024;
        ctx.fillRect(sx, sy, 3, 2);
      }

      // Black & Safety-Yellow Hazard Stripes Border
      const stripeW = 32;
      const drawHazardBar = (x, y, w, h) => {
        ctx.save();
        ctx.beginPath();
        ctx.rect(x, y, w, h);
        ctx.clip();
        ctx.fillStyle = '#ffd600';
        ctx.fillRect(x, y, w, h);
        ctx.fillStyle = '#0f172a';
        for (let i = -h; i < w + h; i += stripeW * 2) {
          ctx.beginPath();
          ctx.moveTo(x + i, y);
          ctx.lineTo(x + i + stripeW, y);
          ctx.lineTo(x + i + stripeW - h, y + h);
          ctx.lineTo(x + i - h, y + h);
          ctx.closePath();
          ctx.fill();
        }
        ctx.restore();
      };
      drawHazardBar(0, 0, 1024, 32);
      drawHazardBar(0, 992, 1024, 32);

      // Checkered Flag Finish Line Strips
      const checkSize = 24;
      for (let cy = 120; cy < 904; cy += checkSize) {
        for (let col = 0; col < 2; col++) {
          const isBlack = (Math.floor(cy / checkSize) + col) % 2 === 0;
          ctx.fillStyle = isBlack ? '#000000' : '#ffffff';
          ctx.fillRect(40 + col * checkSize, cy, checkSize, checkSize);
          ctx.fillRect(936 + col * checkSize, cy, checkSize, checkSize);
        }
      }

      // Curved Rubber Tire Burnout Skid Marks
      ctx.strokeStyle = 'rgba(0, 0, 0, 0.65)';
      ctx.lineWidth = 14;
      ctx.beginPath();
      ctx.moveTo(140, 320);
      ctx.bezierCurveTo(380, 260, 680, 360, 880, 290);
      ctx.stroke();

      ctx.beginPath();
      ctx.moveTo(160, 350);
      ctx.bezierCurveTo(400, 290, 700, 390, 900, 320);
      ctx.stroke();

      ctx.beginPath();
      ctx.moveTo(120, 720);
      ctx.bezierCurveTo(340, 780, 660, 690, 890, 760);
      ctx.stroke();

      // Bold Speedway Stencil
      ctx.fillStyle = '#f8fafc';
      ctx.font = '900 36px "Arial Black", -apple-system, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('🏁 CLASH 481 MOTOR SPEEDWAY 🏁', 512, 180);
      ctx.fillStyle = '#ef4444';
      ctx.font = 'bold 20px -apple-system, BlinkMacSystemFont, sans-serif';
      ctx.fillText('HAZARDS • JUMPS • STOP SIGNS • SPEED TRAPS', 512, 840);

    } else if (envId === 'lounge') {
      // 4. VELVET LOUNGE (Obsidian satin, ruby red velvet inlays, rose gold borders)
      mat.roughness = 0.50;
      mat.metallic = 0.22;

      ctx.fillStyle = '#09090b';
      ctx.fillRect(0, 0, 1024, 1024);

      // Center ruby velvet inlay
      ctx.fillStyle = '#4c0519';
      ctx.fillRect(72, 72, 880, 880);

      // Rose gold pinstripe frame
      ctx.strokeStyle = '#fb7185';
      ctx.lineWidth = 6;
      ctx.strokeRect(72, 72, 880, 880);

      ctx.strokeStyle = 'rgba(251, 113, 133, 0.35)';
      ctx.lineWidth = 2;
      ctx.strokeRect(84, 84, 856, 856);

      ctx.fillStyle = '#f43f5e';
      ctx.font = 'bold 26px -apple-system, BlinkMacSystemFont, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('🌹 SKUNKED & STRIPPED LOUNGE 🌹', 512, 140);
      ctx.font = '16px -apple-system, BlinkMacSystemFont, sans-serif';
      ctx.fillStyle = '#fda4af';
      ctx.fillText('ADULT PARTY • DARE YOU TO SKUNK', 512, 884);

    } else {
      // 5. STUDIO WOOD (Clean Scandinavian natural blond maple with satin sheen)
      mat.roughness = 0.38;
      mat.metallic = 0.02;

      ctx.fillStyle = '#d2b48c';
      ctx.fillRect(0, 0, 1024, 1024);

      // Fine maple planks and grain
      for (let y = 0; y < 1024; y += 96) {
        ctx.fillStyle = (Math.floor(y / 96) % 2 === 0) ? '#deb887' : '#d2b48c';
        ctx.fillRect(0, y, 1024, 94);
        ctx.fillStyle = 'rgba(139, 69, 19, 0.2)';
        ctx.fillRect(0, y + 94, 1024, 2);
      }

      ctx.strokeStyle = 'rgba(139, 69, 19, 0.08)';
      ctx.lineWidth = 1;
      for (let y = 0; y < 1024; y += 24) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.bezierCurveTo(340, y + 8, 680, y - 8, 1024, y);
        ctx.stroke();
      }
    }

    dynTex.update();
    mat.albedoTexture = dynTex;
    return mat;
  }

  /**
   * Adjusts scene atmospheric light color temperatures and clear color
   * to match the chosen environment's mood.
   * @param {string} envId
   */
  applyEnvironmentAtmosphere(envId) {
    if (!this.BABYLON || !this.scene) return;
    const BABYLON = this.BABYLON;
    const theme = RoomCatalog3D.getThemeById(envId);

    if (theme && theme.ambientColor && BABYLON.Color3) {
      try {
        this.scene.ambientColor = new BABYLON.Color3(
          theme.ambientColor.r * 0.4,
          theme.ambientColor.g * 0.4,
          theme.ambientColor.b * 0.4
        );
      } catch (_) {}
    }

    if (theme && BABYLON.Color4) {
      try {
        if (theme.id === 'none') {
          this.scene.clearColor = new BABYLON.Color4(0.09, 0.10, 0.12, 1.0);
        } else if (theme.id === 'vegas') {
          this.scene.clearColor = new BABYLON.Color4(0.05, 0.07, 0.10, 1.0);
        } else if (theme.id === 'pub' || theme.id === 'cabin') {
          this.scene.clearColor = new BABYLON.Color4(0.08, 0.05, 0.03, 1.0);
        } else {
          this.scene.clearColor = new BABYLON.Color4(0.07, 0.08, 0.10, 1.0);
        }
      } catch (_) {}
    }

    const tid = theme ? theme.id : envId;

    if (tid === 'pub') {
      // Warm amber tavern candlelight bounce
      if (this.hemiLight) {
        this.hemiLight.diffuse = new BABYLON.Color3(1.0, 0.88, 0.72);
        this.hemiLight.groundColor = new BABYLON.Color3(0.55, 0.40, 0.28);
      }
      if (this.dirLight) {
        this.dirLight.diffuse = new BABYLON.Color3(1.0, 0.94, 0.82);
      }
    } else if (tid === 'vegas') {
      // Emerald / crystal chandelier brilliance with neon rim
      if (this.hemiLight) {
        this.hemiLight.diffuse = new BABYLON.Color3(0.92, 1.0, 0.95);
        this.hemiLight.groundColor = new BABYLON.Color3(0.35, 0.50, 0.42);
      }
      if (this.fillLight) {
        this.fillLight.diffuse = new BABYLON.Color3(0.98, 0.88, 1.0);
      }
    } else if (tid === 'thunderdome' || tid === 'raceway') {
      // Crisp cool stadium arc floodlights
      if (this.hemiLight) {
        this.hemiLight.diffuse = new BABYLON.Color3(0.90, 0.96, 1.0);
        this.hemiLight.groundColor = new BABYLON.Color3(0.40, 0.45, 0.52);
      }
      if (this.dirLight) {
        this.dirLight.diffuse = new BABYLON.Color3(1.0, 1.0, 1.0);
      }
    } else if (tid === 'velvet' || tid === 'lounge') {
      // Sultry rose / violet ambient
      if (this.hemiLight) {
        this.hemiLight.diffuse = new BABYLON.Color3(0.95, 0.82, 0.92);
        this.hemiLight.groundColor = new BABYLON.Color3(0.45, 0.25, 0.35);
      }
      if (this.fillLight) {
        this.fillLight.diffuse = new BABYLON.Color3(0.90, 0.65, 0.85);
      }
    } else {
      // Studio neutral daylight
      if (this.hemiLight) {
        this.hemiLight.diffuse = new BABYLON.Color3(1.0, 1.0, 1.0);
        this.hemiLight.groundColor = new BABYLON.Color3(0.68, 0.68, 0.70);
      }
    }
  }

  /**
   * Cleans up 3D table mesh, texture, and materials.
   */
  clearTableEnvironment() {
    if (this.seatManager) {
      try { this.seatManager.dispose(); } catch (_) {}
      this.seatManager = null;
    }
    if (this.cardEngine) {
      try { this.cardEngine.dispose(); } catch (_) {}
      this.cardEngine = null;
    }
    if (this.hud3DOverlay) {
      try { this.hud3DOverlay.remove(); } catch (_) {}
      this.hud3DOverlay = null;
    }
    if (this.roomData) {
      RoomCatalog3D.disposeRoom(this.roomData);
      this.roomData = null;
    }
    if (this.tableMesh) {
      try { this.tableMesh.dispose(false, true); } catch (_) {}
      this.tableMesh = null;
    }
    if (this._tableTexture) {
      try { this._tableTexture.dispose(); } catch (_) {}
      this._tableTexture = null;
    }
    if (this._tableMaterial) {
      try { this._tableMaterial.dispose(); } catch (_) {}
      this._tableMaterial = null;
    }
    this.clearDealerButton();
  }

  /**
   * Loads custom GLB model or generates a procedural 3D board in Babylon.js.
   */
  async loadBoardModel() {
    const BABYLON = this.BABYLON;

    if (this.boardConfig?.modelUrl) {
      try {
        if (!BoardRenderer3D.hasGlbLoader()) {
          await this.loadBabylon();
        }

        const fullUrl = this.boardConfig.modelUrl;
        const lastSlash = fullUrl.lastIndexOf('/');
        const rootUrl = lastSlash !== -1 ? fullUrl.substring(0, lastSlash + 1) : '';
        const fileName = lastSlash !== -1 ? fullUrl.substring(lastSlash + 1) : fullUrl;
        const ext = fullUrl.toLowerCase().includes('.glb') ? '.glb' : (fullUrl.toLowerCase().includes('.gltf') ? '.gltf' : undefined);

        const result = await BABYLON.SceneLoader.ImportMeshAsync('', rootUrl, fileName, this.scene, null, ext);
        this.boardMeshes = result.meshes || [];
        this.boardScale = 0.5;

        if (!this.boardRoot || this.boardRoot.isDisposed) {
          this.boardRoot = new BABYLON.TransformNode('boardRoot', this.scene);
        }
        if (this.boardScaleMultiplier && this.boardScaleMultiplier !== 1.0) {
          this.boardRoot.scaling.set(this.boardScaleMultiplier, this.boardScaleMultiplier, this.boardScaleMultiplier);
        }

        // Scale cribbage board down by 50% so it is in authentic realistic proportion with table, cards, and props
        const rootNode = (this.boardMeshes[0]?.name === '__root__') ? this.boardMeshes[0] : null;
        if (rootNode) {
          if (this.boardRoot) rootNode.parent = this.boardRoot;
          rootNode.scaling.scaleInPlace(this.boardScale);
        } else {
          this.boardMeshes.forEach(m => {
            if (!m.parent) {
              if (this.boardRoot) m.parent = this.boardRoot;
              m.scaling.scaleInPlace(this.boardScale);
            }
          });
        }

        // Enable shadow casting and receiving on imported geometry and enhance wood grain sheen
        this.boardMeshes.forEach(m => {
          m.receiveShadows = true;
          if (this.shadowGenerator && m.name !== '__root__') {
            this.shadowGenerator.addShadowCaster(m);
          }
          if (m.material) {
            // Wood grain satin sheen and specular reflections
            if (m.material.roughness !== undefined && m.material.roughness > 0.45) {
              m.material.roughness = 0.40;
            }
            if (m.material.metallic !== undefined && m.material.metallic > 0.08) {
              m.material.metallic = 0.04;
            }
          }
        });
        return;
      } catch (err) {
        console.warn('[BoardRenderer3D] Custom GLB failed to load in Babylon, falling back to procedural board:', err);
      }
    }

    if (!this.boardRoot || this.boardRoot.isDisposed) {
      this.boardRoot = new BABYLON.TransformNode('boardRoot', this.scene);
    }
    if (this.boardScaleMultiplier && this.boardScaleMultiplier !== 1.0) {
      this.boardRoot.scaling.set(this.boardScaleMultiplier, this.boardScaleMultiplier, this.boardScaleMultiplier);
    }

    // Procedural 3D Timber Slab with Carved Peg Holes (scaled down by 50%)
    this.boardScale = 0.5;
    const board = BABYLON.MeshBuilder.CreateBox('proceduralBoard', {
      width: 65 * this.boardScale,
      height: 1.6 * this.boardScale,
      depth: 21 * this.boardScale
    }, this.scene);
    board.position.y = 0;
    board.receiveShadows = true;
    if (this.shadowGenerator) this.shadowGenerator.addShadowCaster(board);
    if (this.boardRoot) board.parent = this.boardRoot;

    const mat = new BABYLON.PBRMaterial('woodMat', this.scene);
    mat.albedoColor = this.getBabylonWoodColor();
    mat.roughness = 0.4;
    mat.metallic = 0.05;
    board.material = mat;

    // Brass rim border
    const rim = BABYLON.MeshBuilder.CreateBox('brassRim', {
      width: 65.6 * this.boardScale,
      height: 0.3 * this.boardScale,
      depth: 21.6 * this.boardScale
    }, this.scene);
    rim.position.y = -0.65 * this.boardScale;
    if (this.boardRoot) rim.parent = this.boardRoot;
    const rimMat = new BABYLON.PBRMaterial('rimMat', this.scene);
    rimMat.albedoColor = new BABYLON.Color3(0.85, 0.65, 0.1);
    rimMat.metallic = 0.9;
    rimMat.roughness = 0.25;
    rim.material = rimMat;

    this.boardMeshes = [board, rim];
  }

  /**
   * Computes the world bounding box encompassing all board meshes.
   * @returns {{ min: BABYLON.Vector3, max: BABYLON.Vector3, center: BABYLON.Vector3, size: BABYLON.Vector3 } | null}
   */
  getBoardBounds() {
    if (!this.boardMeshes || this.boardMeshes.length === 0) return null;
    const BABYLON = this.BABYLON;
    let min = new BABYLON.Vector3(Infinity, Infinity, Infinity);
    let max = new BABYLON.Vector3(-Infinity, -Infinity, -Infinity);
    let count = 0;

    for (const mesh of this.boardMeshes) {
      if (!mesh || mesh.name === '__root__') continue;
      mesh.computeWorldMatrix(true);
      const bInfo = mesh.getBoundingInfo ? mesh.getBoundingInfo() : null;
      if (bInfo && bInfo.boundingBox) {
        min = BABYLON.Vector3.Minimize(min, bInfo.boundingBox.minimumWorld);
        max = BABYLON.Vector3.Maximize(max, bInfo.boundingBox.maximumWorld);
        count++;
      }
    }

    if (count === 0) {
      for (const mesh of this.boardMeshes) {
        if (!mesh) continue;
        mesh.computeWorldMatrix(true);
        const bInfo = mesh.getBoundingInfo ? mesh.getBoundingInfo() : null;
        if (bInfo && bInfo.boundingBox) {
          min = BABYLON.Vector3.Minimize(min, bInfo.boundingBox.minimumWorld);
          max = BABYLON.Vector3.Maximize(max, bInfo.boundingBox.maximumWorld);
          count++;
        }
      }
    }

    if (count === 0 || !isFinite(min.x) || !isFinite(max.x)) {
      return null;
    }

    const center = min.add(max).scale(0.5);
    const size = max.subtract(min);
    return { min, max, center, size };
  }

  /**
   * Adapts the cribbage board container's aspect ratio to match the natural
   * physical proportions of the active 3D model.
   * @param {Object} bounds - Bounding box { size, center }
   */
  updateContainerAspectRatio(bounds) {
    if (!this.container || !bounds) return;
    // If container has an explicit user-adjusted height, do not override it with aspect-ratio
    if (this.container.style.height && this.container.style.height !== 'auto') {
      return;
    }
    const beta = this.camera ? this.camera.beta : Math.PI / 5.2;
    const horizSpan = Math.max(1, bounds.size.x);
    const vertSpan = Math.abs(bounds.size.z * Math.cos(beta)) + Math.abs(bounds.size.y * Math.sin(beta));
    const modelAspect = horizSpan / Math.max(0.1, vertSpan);

    // Provide comfortable framing margins and clamp between 1.25 (compact 5:4) and 3.8 (classic banner)
    const targetAspect = Math.max(1.25, Math.min(3.8, modelAspect * 1.15));
    const aspectStr = `${targetAspect.toFixed(3)} / 1`;

    if (this.container.style.aspectRatio !== aspectStr) {
      this.container.style.aspectRatio = aspectStr;
    }
  }

  /**
   * Automatically calculates camera framing distance and target so the 3D model
   * fits comfortably into the cribbage board view box, adjusting dynamically on resize.
   * @param {boolean} [force=false] - If true, recalculates even if user manually panned/zoomed.
   */
  fitBoardToViewBox(force = false) {
    if (!this.camera || !this.engine || !this.scene || this.isDestroyed) return;
    if (this._userHasAdjustedCamera && !force) return;

    const bounds = this.getBoardBounds();
    if (!bounds) return;
    this.boardBounds = bounds;
    if (this.cardEngine) {
      this.cardEngine.boardBounds = bounds;
    }

    // Adapt container aspect ratio to match the 3D model's visual dimensions
    this.updateContainerAspectRatio(bounds);

    // Center camera on the model's 3D bounding center
    this.camera.setTarget(bounds.center);

    const canvas = this.canvas || this.engine.getRenderingCanvas();
    const width = (canvas && canvas.clientWidth > 0) ? canvas.clientWidth : this.viewBoxW;
    const height = (canvas && canvas.clientHeight > 0) ? canvas.clientHeight : this.viewBoxH;
    if (width <= 0 || height <= 0) return;

    const aspect = width / height;
    const fov = this.camera.fov || 0.8;
    const halfVFOV = fov / 2;
    const halfHFOV = Math.atan(Math.tan(halfVFOV) * aspect);

    // Compute bounding dimensions projected onto camera view plane
    const beta = this.camera.beta !== undefined ? this.camera.beta : Math.PI / 4;
    const alpha = this.camera.alpha !== undefined ? this.camera.alpha : -Math.PI / 2;
    const hasRoom = Boolean(this.roomData || this.tableMesh || this.cardEngine);
    const dims = this.roomData?.dims;
    const tableW = dims?.tableW ?? (this.cardEngine?.tableW || 120);
    const tableD = dims?.tableD ?? (this.cardEngine?.tableD || 100);

    let spanTargetX = bounds.size.x;
    let spanTargetZ = bounds.size.z;

    const isMobile = (aspect < 0.9 || (typeof window !== 'undefined' && (window.innerWidth <= 768 || window.matchMedia?.('(max-width: 768px)')?.matches)));

    if (hasRoom && tableW && tableD) {
      // Include table boundaries so all chairs and player hands in front of chairs are fully visible
      const spanFactor = isMobile ? 1.25 : 1.0;
      spanTargetX = Math.max(bounds.size.x, tableW * spanFactor);
      spanTargetZ = Math.max(bounds.size.z, tableD * spanFactor);
    }

    const sizeX = Math.max(1, spanTargetX);
    const sizeY = Math.max(0.1, bounds.size.y);
    const sizeZ = Math.max(1, spanTargetZ);

    // Horizontal width across the camera screen accounting for orbit azimuth (alpha)
    const horizSpan = Math.abs(sizeX * Math.sin(alpha)) + Math.abs(sizeZ * Math.cos(alpha));
    // Depth span along camera line-of-sight
    const depthSpan = Math.abs(sizeZ * Math.sin(alpha)) + Math.abs(sizeX * Math.cos(alpha));
    // Projected height combining board depth and thickness at camera pitch
    const vertSpan = Math.abs(depthSpan * Math.cos(beta)) + Math.abs(sizeY * Math.sin(beta));

    const distH = (horizSpan / 2) / Math.tan(halfHFOV);
    const distV = (vertSpan / 2) / Math.tan(halfVFOV);

    // Provide breathing room padding so the board, tabletop cards, and deck sit gracefully inside the view box
    const padding = hasRoom ? (isMobile ? 1.38 : 1.25) : 1.18;
    const fitRadius = Math.max(distH, distV) * padding;

    this.camera.radius = fitRadius;
    this.fitRadius = fitRadius;
    this.camera.lowerRadiusLimit = fitRadius * 0.15;
    let maxRadius = fitRadius * 4.5;
    if (this.roomData?.dims) {
      const { roomW, roomD } = this.roomData.dims;
      maxRadius = Math.min(maxRadius, Math.min(roomW, roomD) * 0.46);
    }
    this.camera.upperRadiusLimit = Math.max(fitRadius * 1.5, maxRadius);
    this.camera.minZ = Math.max(0.1, fitRadius * 0.005);
    this.camera.maxZ = Math.max(10000, fitRadius * 20);
    this.camera.panningSensibility = 25;

    // Reposition key light and rim light relative to the model size so shadows and highlights are crisp
    if (this.dirLight) {
      const maxDim = Math.max(sizeX, sizeY, sizeZ);
      this.dirLight.position = bounds.center.add(new this.BABYLON.Vector3(maxDim * 0.35, maxDim * 1.2, maxDim * 0.5));
    }
    if (this.fillLight) {
      const maxDim = Math.max(sizeX, sizeY, sizeZ);
      this.fillLight.position = bounds.center.add(new this.BABYLON.Vector3(-maxDim * 0.4, maxDim * 0.9, -maxDim * 0.4));
    }
  }

  /**
   * Handles resizing of the view box or window.
   */
  handleResize() {
    if (!this.engine || !this.scene || !this.camera || this.isDestroyed) return;
    this.engine.resize();
    this.fitBoardToViewBox(false);
  }

  /**
   * Instantiates front and rear 3D pegs for each active track.
   */
  setupPegs() {
    for (const who of this.activeTracks) {
      const hasMinusOne = this.hasHole(who, -1);
      const initialRearHole = hasMinusOne ? -1 : 0;
      this.pegState[who] = { front: 0, rear: initialRearHole };

      for (const role of ['rear', 'front']) {
        const pegMesh = this.createPegMesh(who, role);
        if (this.boardRoot) {
          pegMesh.parent = this.boardRoot;
        }
        this.pegMeshes.set(`${who}_${role}`, pegMesh);

        // Position initial pegs: front at Hole 0, rear at Hole -1 (if specified in data) or Hole 0
        const holeToUse = role === 'rear' ? initialRearHole : 0;
        const pt = this.getHoleCoords(who, holeToUse);
        if (pt) {
          pegMesh.position.set(pt.x, pt.y, pt.z);
        }
      }
    }
  }

  /**
   * Checks whether Cribbage Clash or hazard obstacle rules are active.
   * @returns {boolean}
   */
  isClashMode() {
    const brand = this.options.brand || this.settings?.brand || (typeof window !== 'undefined' && (window.CRAZY_BRAND || (typeof window.getAppBrand === 'function' && window.getAppBrand())));
    if (brand && brand !== 'clash') return false;
    const isClashTarget = [241, 361, 481].includes(Number(this.settings?.target)) || [241, 361, 481].includes(Number(this.targetHoles));
    const isClashBoard = this.boardConfig?.brand === 'clash' || this.boardConfig?.id === 'board-cribbage-clash' || this.boardConfig?.id === 'board-cribbage-clash-square';
    const hasClashSettings = Boolean(
      this.settings && (
        this.settings.optStopSign ||
        this.settings.optJump ||
        this.settings.optJail ||
        this.settings.optBar ||
        this.settings.optTimeout ||
        this.settings.optDetour ||
        this.settings.optRoundAbout ||
        this.settings.optNoPassingLanes ||
        this.settings.optNoPassing ||
        this.settings.optSigns
      )
    );
    const hasObstacleConfigs = Boolean(
      this.options.obstacleConfigs && (
        (this.options.obstacleConfigs.stopSigns && this.options.obstacleConfigs.stopSigns.length) ||
        (this.options.obstacleConfigs.jumps && this.options.obstacleConfigs.jumps.length) ||
        (this.options.obstacleConfigs.jails && this.options.obstacleConfigs.jails.length) ||
        (this.options.obstacleConfigs.bars && this.options.obstacleConfigs.bars.length) ||
        (this.options.obstacleConfigs.timeouts && this.options.obstacleConfigs.timeouts.length) ||
        (this.options.obstacleConfigs.detours && this.options.obstacleConfigs.detours.length) ||
        (this.options.obstacleConfigs.roundabouts && this.options.obstacleConfigs.roundabouts.length) ||
        (this.options.obstacleConfigs.noPassingLanes && this.options.obstacleConfigs.noPassingLanes.length) ||
        (this.options.obstacleConfigs.signs && this.options.obstacleConfigs.signs.length)
      )
    );
    return brand === 'clash' || isClashTarget || isClashBoard || hasClashSettings || hasObstacleConfigs;
  }

  /**
   * Resolves active game settings from options, instance state, window, and DOM checkboxes.
   * Prevents empty sync contexts from clearing active zone variant highlights.
   * @param {Object} [extraSettings=null]
   * @returns {Object}
   */
  getResolvedSettings(extraSettings = null) {
    const domSettings = {};
    if (typeof document !== 'undefined') {
      ['optPegThroughZones', 'optLast5PegOut', 'optBunkers', 'optStinkHole'].forEach(id => {
        const el = document.getElementById(id);
        if (el && el.checked) domSettings[id] = true;
      });
    }
    const resolved = Object.assign(
      {},
      this.options?.settings || {},
      this.settings || {},
      (typeof window !== 'undefined' && window.settings) ? window.settings : {},
      domSettings
    );
    if (extraSettings && typeof extraSettings === 'object' && Object.keys(extraSettings).length > 0) {
      ['optPegThroughZones', 'optLast5PegOut', 'optBunkers', 'optStinkHole', 'target'].forEach(k => {
        if (extraSettings[k] !== undefined) resolved[k] = extraSettings[k];
      });
    }
    return resolved;
  }

  /**
   * Checks whether any zone pegging variants requiring red glow hole highlights are active.
   * @param {Object} [settings=null]
   * @returns {boolean}
   */
  isZoneHighlightActive(settings = null) {
    const s = this.getResolvedSettings(settings);
    return Boolean(
      s.optPegThroughZones ||
      s.optLast5PegOut ||
      s.optBunkers ||
      s.optStinkHole
    );
  }

  /**
   * Returns a Set of hole indices that should be highlighted with a red glow.
   * @param {Object} [settings=null]
   * @returns {Set<number>}
   */
  getHighlightedZoneHoles(settings = null) {
    const holes = new Set();
    const s = this.getResolvedSettings(settings);
    const target = Number(s.target || this.targetHoles || 121) || 121;

    for (let h = 1; h < target; h++) {
      if (s.optPegThroughZones && isInPegThroughZone(h)) {
        holes.add(h);
      } else if (s.optLast5PegOut && isInLast5PegOut(h, target)) {
        holes.add(h);
      } else if (s.optBunkers && isInBunker(h, target)) {
        holes.add(h);
      } else if (s.optStinkHole && isInStinkHole(h, target)) {
        holes.add(h);
      }
    }
    return holes;
  }

  /**
   * Generates or retrieves cached PBR materials for 3D red glow zone hole highlights.
   * Configured as pure ruby red (#d50000 / #ff1100) with zero blue and high roughness
   * so ambient spotlights never cast white specular sheen (which makes it look pink).
   * @private
   */
  _getZoneHighlightMaterials() {
    if (this._zoneMaterials) return this._zoneMaterials;
    const BABYLON = this.BABYLON;
    if (!BABYLON) return null;
    const MatClass = BABYLON.PBRMaterial || BABYLON.StandardMaterial;
    if (!MatClass) return null;

    const redGlow = new MatClass('mat3D_ZoneRedGlow', this.scene);
    const col = BABYLON.Color3 && typeof BABYLON.Color3.FromHexString === 'function' ? BABYLON.Color3.FromHexString('#ff0000') : null;
    const em = BABYLON.Color3 && typeof BABYLON.Color3.FromHexString === 'function' ? BABYLON.Color3.FromHexString('#ff0000') : null;
    if (redGlow.albedoColor !== undefined) redGlow.albedoColor = col;
    else if (redGlow.diffuseColor !== undefined) redGlow.diffuseColor = col;
    if (redGlow.emissiveColor !== undefined && em) redGlow.emissiveColor = em;
    redGlow.metallic = 0.0;
    redGlow.roughness = 0.75; // Matte finish to eliminate warm room lighting specular reflections shifting red to orange
    redGlow.backFaceCulling = false;
    redGlow.alpha = 1.0;

    this._zoneMaterials = { redGlow };
    return this._zoneMaterials;
  }

  /**
   * Sets up 3D red soft gradient glow highlights at the top of active zone pegging holes
   * (Peg Through Zones, Last 5 Peg Out, Bunkers, or Stink Hole).
   * Stands vertically around the hole socket with a feathered alpha gradient.
   */
  setupZoneHighlights() {
    if (!this.scene || !this.BABYLON || this.isDestroyed) return;
    const settings = this.getResolvedSettings();
    const target = Number(settings.target || this.targetHoles || 121) || 121;
    this.targetHoles = target;
    const key = `${Boolean(settings.optPegThroughZones)}_${Boolean(settings.optLast5PegOut)}_${Boolean(settings.optBunkers)}_${Boolean(settings.optStinkHole)}_${target}`;

    if (!this.isZoneHighlightActive(settings)) {
      this.clearZoneHighlights();
      this._lastZoneHighlightKey = key;
      return;
    }

    if (this._lastZoneHighlightKey === key && this.zoneHighlightsRoot) {
      return;
    }

    this.clearZoneHighlights();
    this._lastZoneHighlightKey = key;

    const BABYLON = this.BABYLON;
    this.zoneHighlightsRoot = new BABYLON.TransformNode('zoneHighlightsRoot', this.scene);
    if (this.boardRoot) {
      this.zoneHighlightsRoot.parent = this.boardRoot;
    }
    const hasExplicitCoords = Boolean(this.boardConfig?.coordinatesData || this.boardConfig?.holeData);
    this.zoneHighlightsRoot.position.y = hasExplicitCoords ? 0 : 5.0;

    if (!this.glowLayer && BABYLON.GlowLayer) {
      try {
        this.glowLayer = new BABYLON.GlowLayer('boardObstaclesGlow', this.scene, {
          mainTextureRatio: 0.5,
          blurKernelSize: 16
        });
      } catch (_) {}
    }
    if (this.glowLayer) {
      this.glowLayer.intensity = Math.max(this.glowLayer.intensity || 0, 0.65);
      if (!this.glowLayer._hasZoneGlowSelector) {
        this.glowLayer._hasZoneGlowSelector = true;
        const prevSelector = this.glowLayer.customEmissiveColorSelector;
        this.glowLayer.customEmissiveColorSelector = (mesh, subMesh, material, result) => {
          if (mesh && mesh.metadata && mesh.metadata.isZoneHighlight) {
            result.set(1.0, 0.0, 0.0, 1.0); // Pure ruby red, zero blue/pink washout
          } else if (typeof prevSelector === 'function') {
            prevSelector(mesh, subMesh, material, result);
          } else if (material && material.emissiveColor) {
            result.set(material.emissiveColor.r, material.emissiveColor.g, material.emissiveColor.b, 1.0);
          } else {
            result.set(0, 0, 0, 0);
          }
        };
      }
    }

    const dims = this.getPegDimensions('front');
    const spacing = dims.spacing || 7.75;
    const holeRadius = dims.radiusBottom || (spacing * 0.20) || 1.1;

    // Torus sits directly on the wood material surface framing the peg hole:
    // - collarDiam frames the hole perimeter without overlapping adjacent track columns
    // - ringThickness provides a clean, visible glowing 3D torus resting right on the board
    // - torusCenterY places the center at surfaceY + (ringThickness * 0.45) so the bottom touches the material without hovering
    const collarDiam = Math.min(spacing * 0.58, Math.max(holeRadius * 2.3, 3.2));
    const ringThickness = Math.max(0.38, holeRadius * 0.32);

    const mats = this._getZoneHighlightMaterials();
    if (!mats) return;

    const highlightedHoles = this.getHighlightedZoneHoles(settings);
    const tracksToMark = (Array.isArray(this.activeTracks) && this.activeTracks.length > 0)
      ? this.activeTracks
      : ['p1', 'p2'];

    highlightedHoles.forEach(h => {
      tracksToMark.forEach(who => {
        const pt = this.getHoleCoords(who, h);
        if (!pt) return;
        const surfaceY = (pt && pt.y !== undefined) ? pt.y : 0.8;
        // Positioned just above the top surface of the board at the peg hole opening (mouth)
        // so it remains clearly visible on top of the board material
        const torusCenterY = surfaceY + 2.55;

        // Glowing red torus collar sitting directly on the wood material around the hole
        let ring = null;
        if (typeof BABYLON.MeshBuilder.CreateTorus === 'function') {
          ring = BABYLON.MeshBuilder.CreateTorus(`zoneHoleRing_${who}_${h}`, {
            diameter: collarDiam,
            thickness: ringThickness,
            tessellation: 32
          }, this.scene);
        } else {
          ring = BABYLON.MeshBuilder.CreateCylinder(`zoneHoleRing_${who}_${h}`, {
            diameter: collarDiam,
            height: ringThickness,
            tessellation: 24
          }, this.scene);
        }

        ring.position.set(pt.x, torusCenterY, pt.z);
        ring.material = mats.redGlow;
        ring.parent = this.zoneHighlightsRoot;
        ring.metadata = { isZoneHighlight: true, hole: h, who };
      });
    });
  }

  /**
   * Clears and disposes all 3D red glow zone hole highlight meshes.
   */
  clearZoneHighlights() {
    if (this.zoneHighlightsRoot) {
      try {
        this.zoneHighlightsRoot.dispose(false, true);
      } catch (_) {}
      this.zoneHighlightsRoot = null;
    }
    this._lastZoneHighlightKey = null;
  }

  /**
   * Dynamically updates 3D zone highlight placements when game settings or variants change.
   * @param {Object} [settings=null]
   */
  updateZoneHighlights(settings = null) {
    if (settings && typeof settings === 'object' && Object.keys(settings).length > 0) {
      this.settings = Object.assign(this.settings || {}, settings);
    }
    this.setupZoneHighlights();
  }

  /**
   * Sets up 3D visual markers, socket highlights, and leap trajectories
   * for active Cribbage Clash obstacles on the 3D board model.
   */
  setupClashObstacles() {
    if (!this.scene || !this.BABYLON || this.isDestroyed) return;
    if (!this.isClashMode()) {
      this.clearClashObstacles();
      return;
    }

    this.clearClashObstacles();

    const BABYLON = this.BABYLON;
    this.clashObstaclesRoot = new BABYLON.TransformNode('clashObstaclesRoot', this.scene);
    if (this.boardRoot) {
      this.clashObstaclesRoot.parent = this.boardRoot;
    }
    const hasExplicitCoords = Boolean(this.boardConfig?.coordinatesData || this.boardConfig?.holeData);
    this.clashObstaclesRoot.position.y = hasExplicitCoords ? 0 : 5.0;

    if (!this.glowLayer && BABYLON.GlowLayer) {
      try {
        this.glowLayer = new BABYLON.GlowLayer('boardObstaclesGlow', this.scene, {
          mainTextureRatio: 0.5,
          blurKernelSize: 16
        });
        this.glowLayer.intensity = 0.55;
      } catch (_) {}
    }

    const dims = this.getPegDimensions('front');
    const spacing = dims.spacing || 7.75;
    const markerRadius = spacing * 0.38;
    const padHeight = 0.22;

    const materials = this._getClashObstacleMaterials();

    this._render3DStopSigns(spacing, markerRadius, padHeight, materials);
    this._render3DJumps(spacing, markerRadius, padHeight, materials);
    this._render3DJails(spacing, markerRadius, padHeight, materials);
    this._render3DBars(spacing, markerRadius, padHeight, materials);
    this._render3DTimeouts(spacing, markerRadius, padHeight, materials);
    this._render3DDetours(spacing, markerRadius, padHeight, materials);
    this._render3DRoundAbouts(spacing, markerRadius, padHeight, materials);
    this._render3DNoPassingLanes(spacing, materials);
    this._render3DSignBlocks(spacing, markerRadius, padHeight, materials);
    this._render3DDice(spacing, markerRadius, padHeight, materials);
    this._render3DSpinningWheels(spacing, markerRadius, padHeight, materials);
    this._render3DSkunkMarkers(spacing, markerRadius, padHeight, materials);
  }

  /**
   * Clears and disposes all 3D clash obstacle meshes.
   */
  clearClashObstacles() {
    if (this.clashObstaclesRoot) {
      try {
        this.clashObstaclesRoot.dispose(false, true);
      } catch (_) {}
      this.clashObstaclesRoot = null;
    }
    if (this._diceSharedTexture) {
      try { this._diceSharedTexture.dispose(); } catch (_) {}
      this._diceSharedTexture = null;
    }
    if (this._wheelSharedTexture) {
      try { this._wheelSharedTexture.dispose(); } catch (_) {}
      this._wheelSharedTexture = null;
    }
    if (this._skunkSharedTexture) {
      try { this._skunkSharedTexture.dispose(); } catch (_) {}
      this._skunkSharedTexture = null;
    }
    if (this._wheelRenderObservers && Array.isArray(this._wheelRenderObservers)) {
      this._wheelRenderObservers.forEach(obs => {
        try { this.scene.onBeforeRenderObservable.remove(obs); } catch (_) {}
      });
      this._wheelRenderObservers = null;
    }
  }

  /**
   * Dynamically updates 3D clash obstacle placements when game settings or presets change.
   * @param {Object} [settings=null]
   */
  updateClashObstacles(settings = null) {
    if (settings) {
      this.settings = Object.assign(this.settings || {}, settings);
    }
    this.setupClashObstacles();
  }

  /**
   * Checks whether Pints & Pegs drinking spaces are active on this board.
   * @returns {boolean}
   */
  isPintsMode() {
    const brand = this.options.brand || this.settings?.brand || (typeof window !== 'undefined' && (window.CRAZY_BRAND || (typeof window.getAppBrand === 'function' && window.getAppBrand())));
    if (brand && brand !== 'pints') return false;
    if (brand === 'pints') return true;
    const hasDrinkingSettings = Boolean(
      this.settings && (
        this.settings.optYouDrink ||
        this.settings.optGuysDrink ||
        this.settings.optGalsDrink ||
        this.settings.optLeftDrink ||
        this.settings.optRightDrink ||
        this.settings.optEveryoneDrink ||
        this.settings.optEveryoneButYouDrink ||
        this.settings.optPickWhoDrinks
      )
    );
    const hasPintsConfigs = Boolean(
      this.options.pintsConfigs && this.options.pintsConfigs.length > 0
    );
    return hasDrinkingSettings || hasPintsConfigs;
  }

  /**
   * Sets up 3D visual markers, socket coaster pads, and upright beverages
   * for active Pints & Pegs drinking spaces on the 3D board model.
   */
  setupPintsObjects() {
    if (!this.scene || !this.BABYLON || this.isDestroyed) return;
    if (!this.isPintsMode()) {
      this.clearPintsObjects();
      return;
    }

    this.clearPintsObjects();

    const BABYLON = this.BABYLON;
    this.pintsObjectsRoot = new BABYLON.TransformNode('pintsObjectsRoot', this.scene);
    if (this.boardRoot) {
      this.pintsObjectsRoot.parent = this.boardRoot;
    }
    const hasExplicitCoords = Boolean(this.boardConfig?.coordinatesData || this.boardConfig?.holeData);
    this.pintsObjectsRoot.position.y = hasExplicitCoords ? 0 : 5.0;

    if (!this.glowLayer && BABYLON.GlowLayer) {
      try {
        this.glowLayer = new BABYLON.GlowLayer('boardObstaclesGlow', this.scene, {
          mainTextureRatio: 0.5,
          blurKernelSize: 16
        });
        this.glowLayer.intensity = 0.55;
      } catch (_) {}
    }

    const dims = this.getPegDimensions('front');
    const spacing = dims.spacing || 7.75;
    const markerRadius = spacing * 0.38;
    const padHeight = 0.22;

    const materials = this._getPintsMaterials();
    this._render3DPintsCoastersAndBeverages(spacing, markerRadius, padHeight, materials);
  }

  /**
   * Clears and disposes all 3D pints drinking meshes.
   */
  clearPintsObjects() {
    if (this.pintsObjectsRoot) {
      try {
        this.pintsObjectsRoot.dispose(false, true);
      } catch (_) {}
      this.pintsObjectsRoot = null;
    }
  }

  /**
   * Dynamically updates 3D pints drinking space placements when game settings or presets change.
   * @param {Object} [settings=null]
   */
  updatePintsObjects(settings = null) {
    if (settings) {
      this.settings = Object.assign(this.settings || {}, settings);
    }
    this.setupPintsObjects();
  }

  /**
   * Generates or retrieves cached PBR materials for 3D Pints drinking markers and beverages.
   * @private
   */
  _getPintsMaterials() {
    const BABYLON = this.BABYLON;
    if (this._pintsMaterials) return this._pintsMaterials;

    const createMat = (name, colHex, emHex, emissiveScale = 0.65, metallic = 0.15, roughness = 0.3) => {
      const mat = new BABYLON.PBRMaterial(name, this.scene);
      const col = BABYLON.Color3.FromHexString(colHex);
      const em = BABYLON.Color3.FromHexString(emHex);
      mat.albedoColor = col;
      mat.emissiveColor = em.scale(emissiveScale);
      mat.metallic = metallic;
      mat.roughness = roughness;
      return mat;
    };

    const mats = {
      // Coasters & trim
      coasterWood: createMat('matPints_CoasterWood', '#3e2723', '#27180d', 0.2, 0.05, 0.6),
      coasterSlate: createMat('matPints_CoasterSlate', '#1e293b', '#0f172a', 0.2, 0.2, 0.5),
      coasterMarble: createMat('matPints_CoasterMarble', '#f8fafc', '#e2e8f0', 0.25, 0.1, 0.2),
      coasterGold: createMat('matPints_CoasterGold', '#f59e0b', '#d97706', 0.7, 0.85, 0.2),
      coasterSilver: createMat('matPints_CoasterSilver', '#cbd5e1', '#94a3b8', 0.4, 0.85, 0.25),

      // Beverage Liquids & Accents
      beerAmber: createMat('matPints_BeerAmber', '#f59e0b', '#d97706', 0.75, 0.1, 0.35),
      beerDarkAle: createMat('matPints_BeerDarkAle', '#78350f', '#451a03', 0.6, 0.1, 0.4),
      beerFoam: createMat('matPints_BeerFoam', '#ffffff', '#fef3c7', 0.85, 0.0, 0.7),
      wineRuby: createMat('matPints_WineRuby', '#991b1b', '#7f1d1d', 0.8, 0.1, 0.2),
      cocktailViolet: createMat('matPints_CocktailViolet', '#7c3aed', '#6d28d9', 0.75, 0.1, 0.25),
      cocktailOrange: createMat('matPints_CocktailOrange', '#ea580c', '#c2410c', 0.8, 0.1, 0.25),
      champagneGold: createMat('matPints_ChampagneGold', '#fef08a', '#facc15', 0.85, 0.15, 0.2),
      iceBucketSteel: createMat('matPints_IceBucketSteel', '#e2e8f0', '#94a3b8', 0.35, 0.9, 0.15),
      shakerSteel: createMat('matPints_ShakerSteel', '#f1f5f9', '#cbd5e1', 0.4, 0.92, 0.12),
      bottleGreen: createMat('matPints_BottleGreen', '#14532d', '#052e16', 0.6, 0.2, 0.3),
      bottleFoilGold: createMat('matPints_BottleFoilGold', '#fbbf24', '#f59e0b', 0.85, 0.8, 0.25),
      corkWood: createMat('matPints_CorkWood', '#b45309', '#92400e', 0.2, 0.02, 0.85),
      limeGarnish: createMat('matPints_LimeGarnish', '#84cc16', '#65a30d', 0.7, 0.05, 0.4),
      orangeGarnish: createMat('matPints_OrangeGarnish', '#f97316', '#ea580c', 0.8, 0.05, 0.4),
      strawCyan: createMat('matPints_StrawCyan', '#06b6d4', '#0891b2', 0.85, 0.1, 0.3),
      strawMagenta: createMat('matPints_StrawMagenta', '#ec4899', '#db2777', 0.85, 0.1, 0.3),
      targetRed: createMat('matPints_TargetRed', '#e11d48', '#be123c', 0.9, 0.1, 0.3),
      targetWhite: createMat('matPints_TargetWhite', '#ffffff', '#f1f5f9', 0.6, 0.1, 0.3),

      // Glass & Ice
      glass: (() => {
        const m = new BABYLON.PBRMaterial('matPints_Glass', this.scene);
        m.albedoColor = new BABYLON.Color3(0.88, 0.95, 1.0);
        m.alpha = 0.42;
        m.metallic = 0.08;
        m.roughness = 0.12;
        return m;
      })(),
      iceCube: (() => {
        const m = new BABYLON.PBRMaterial('matPints_IceCube', this.scene);
        m.albedoColor = new BABYLON.Color3(0.92, 0.97, 1.0);
        m.alpha = 0.55;
        m.metallic = 0.05;
        m.roughness = 0.08;
        return m;
      })(),

      // Themed Track Socket Pads
      padYouDrink: createMat('matPints_PadYouDrink', '#f59e0b', '#d97706', 0.85),
      padGuysDrink: createMat('matPints_PadGuysDrink', '#6366f1', '#4f46e5', 0.85),
      padGalsDrink: createMat('matPints_PadGalsDrink', '#ec4899', '#db2777', 0.85),
      padLeftDrink: createMat('matPints_PadLeftDrink', '#3b82f6', '#2563eb', 0.85),
      padRightDrink: createMat('matPints_PadRightDrink', '#06b6d4', '#0891b2', 0.85),
      padEveryoneDrink: createMat('matPints_PadEveryoneDrink', '#10b981', '#059669', 0.85),
      padEveryoneButYouDrink: createMat('matPints_PadEveryoneButYouDrink', '#8b5cf6', '#7c3aed', 0.85),
      padPickWhoDrinks: createMat('matPints_PadPickWhoDrinks', '#e11d48', '#be123c', 0.85),
      padRim: createMat('matPints_PadRim', '#ffffff', '#e2e8f0', 0.5, 0.5, 0.25)
    };

    this._pintsMaterials = mats;
    return mats;
  }

  /**
   * Retrieves active Pints drinking space configurations from settings or presets.
   * @returns {Array<Object>}
   */
  getActivePintsConfigs() {
    const settings = this.settings || {};
    const brand = this.options.brand || settings?.brand || (typeof window !== 'undefined' && (window.CRAZY_BRAND || (typeof window.getAppBrand === 'function' && window.getAppBrand())));
    const isPints = brand === 'pints';

    if (Array.isArray(this.options.pintsConfigs) && this.options.pintsConfigs.length > 0) {
      return this.options.pintsConfigs;
    }

    const drinkingTypes = [
      { key: 'youDrink', name: 'You Drink', icon: '🍺', defHoles: [15, 45, 75, 105] },
      { key: 'guysDrink', name: 'Guys Drink', icon: '🍻', defHoles: [25, 55, 85, 115] },
      { key: 'galsDrink', name: 'Gals Drink', icon: '🍷', defHoles: [35, 65, 95, 105] },
      { key: 'leftDrink', name: 'Left Drinks', icon: '👈', defHoles: [20, 50, 80, 110] },
      { key: 'rightDrink', name: 'Right Drinks', icon: '👉', defHoles: [40, 70, 100, 120] },
      { key: 'everyoneDrink', name: 'Everyone Drinks', icon: '🥂', defHoles: [30, 60, 90, 120] },
      { key: 'everyoneButYouDrink', name: 'Everyone But You', icon: '🎉', defHoles: [10, 50, 90, 110] },
      { key: 'pickWhoDrinks', name: 'Pick Who Drinks', icon: '🎯', defHoles: [25, 55, 85, 115] }
    ];

    const configs = [];
    drinkingTypes.forEach(dt => {
      const chkKey = `opt${dt.key.charAt(0).toUpperCase() + dt.key.slice(1)}`;
      const isChecked = Boolean(settings[chkKey] || settings[dt.key] || (isPints && (settings[`${dt.key}Holes`] || settings[`${chkKey}1Hole`])));
      if (!isChecked) return;

      const arr = settings[`${dt.key}Holes`] || [];
      const rawCnt = settings[`${chkKey}Count`] || settings[`${dt.key}Count`] || arr.length || 2;
      const count = Math.min(4, Math.max(1, parseInt(rawCnt, 10) || 1));
      const rawPegs = settings[`${chkKey}Pegs`] || settings[`${dt.key}Pegs`] || 1;
      const pegs = Math.min(5, Math.max(1, parseInt(rawPegs, 10) || 1));

      for (let i = 1; i <= count; i++) {
        const h = settings[`${chkKey}${i}Hole`] || arr[i - 1] || dt.defHoles[i - 1] || 15;
        if (h && h > 0 && h <= this.targetHoles) {
          configs.push({
            id: `${dt.key}_${i}`,
            type: dt.key,
            name: `${dt.name} ${i}`,
            icon: dt.icon,
            index: i,
            hole: parseInt(h, 10),
            pegs: parseInt(pegs, 10)
          });
        }
      }
    });

    return configs;
  }

  /**
   * Checks whether Skunked and Stripped (Adult Brand) options are active on this board.
   * @returns {boolean}
   */
  isAdultMode() {
    const brand = this.options.brand || this.settings?.brand || (typeof window !== 'undefined' && (window.CRAZY_BRAND || (typeof window.getAppBrand === 'function' && window.getAppBrand())));
    if (brand && brand !== 'adult') return false;
    if (brand === 'adult') return true;
    const hasAdultSettings = Boolean(
      this.settings && (
        this.settings.optLoseClothing ||
        this.settings.optGainClothing ||
        this.settings.optPickWhoLosesClothing ||
        this.settings.optRightLosesClothing ||
        this.settings.optLeftLosesClothing ||
        this.settings.optRemoveClothingChoice ||
        this.settings.optTouchBreast ||
        this.settings.optLickBreast ||
        this.settings.optSuckBreast ||
        this.settings.optTouchGenitals ||
        this.settings.optLickGenitals ||
        this.settings.optSuckGenitals ||
        this.settings.optFingerGenitals ||
        this.settings.optGenitalIntercourse ||
        this.settings.optTouchAnus ||
        this.settings.optLickAnus ||
        this.settings.optFingerAnus ||
        this.settings.optAnalIntercourse
      )
    );
    const hasAdultConfigs = Boolean(
      this.options.adultConfigs && this.options.adultConfigs.length > 0
    );
    return hasAdultSettings || hasAdultConfigs;
  }

  /**
   * Sets up 3D visual markers, socket coaster pads, and sensual sculptures
   * for active Skunked & Stripped (Adult) options on the 3D board model.
   */
  setupAdultObjects() {
    if (!this.scene || !this.BABYLON || this.isDestroyed) return;
    if (!this.isAdultMode()) {
      this.clearAdultObjects();
      return;
    }

    this.clearAdultObjects();

    const BABYLON = this.BABYLON;
    this.adultObjectsRoot = new BABYLON.TransformNode('adultObjectsRoot', this.scene);
    if (this.boardRoot) {
      this.adultObjectsRoot.parent = this.boardRoot;
    }
    const hasExplicitCoords = Boolean(this.boardConfig?.coordinatesData || this.boardConfig?.holeData);
    this.adultObjectsRoot.position.y = hasExplicitCoords ? 0 : 5.0;

    if (!this.glowLayer && BABYLON.GlowLayer) {
      try {
        this.glowLayer = new BABYLON.GlowLayer('boardObstaclesGlow', this.scene, {
          mainTextureRatio: 0.5,
          blurKernelSize: 16
        });
        this.glowLayer.intensity = 0.55;
      } catch (_) {}
    }

    const dims = this.getPegDimensions('front');
    const spacing = dims.spacing || 7.75;
    const markerRadius = spacing * 0.38;
    const padHeight = 0.22;

    const materials = this._getAdultMaterials();
    this._render3DAdultCoastersAndObjects(spacing, markerRadius, padHeight, materials);
  }

  /**
   * Clears and disposes all 3D adult challenge meshes.
   */
  clearAdultObjects() {
    if (this.adultObjectsRoot) {
      try {
        this.adultObjectsRoot.dispose(false, true);
      } catch (_) {}
      this.adultObjectsRoot = null;
    }
  }

  /**
   * Dynamically updates 3D adult challenge placements when game settings or presets change.
   * @param {Object} [settings=null]
   */
  updateAdultObjects(settings = null) {
    if (settings) {
      this.settings = Object.assign(this.settings || {}, settings);
    }
    this.setupAdultObjects();
  }

  /**
   * Generates or retrieves cached PBR materials for 3D Adult markers and sculptures.
   * @private
   */
  _getAdultMaterials() {
    const BABYLON = this.BABYLON;
    if (this._adultMaterials) return this._adultMaterials;

    const createMat = (name, colHex, emHex, emissiveScale = 0.65, metallic = 0.15, roughness = 0.3) => {
      const mat = new BABYLON.PBRMaterial(name, this.scene);
      const col = BABYLON.Color3.FromHexString(colHex);
      const em = BABYLON.Color3.FromHexString(emHex);
      mat.albedoColor = col;
      mat.emissiveColor = em.scale(emissiveScale);
      mat.metallic = metallic;
      mat.roughness = roughness;
      return mat;
    };

    const mats = {
      // Pedestals, plinths & luxury materials
      pedestalObsidian: createMat('matAdult_PedestalObsidian', '#0f172a', '#020617', 0.15, 0.25, 0.35),
      pedestalRoseGold: createMat('matAdult_PedestalRoseGold', '#f43f5e', '#e11d48', 0.7, 0.85, 0.2),
      pedestalGold: createMat('matAdult_PedestalGold', '#f59e0b', '#d97706', 0.75, 0.9, 0.18),
      pedestalSilver: createMat('matAdult_PedestalSilver', '#e2e8f0', '#cbd5e1', 0.45, 0.92, 0.15),
      pedestalWood: createMat('matAdult_PedestalWood', '#451a03', '#27180d', 0.2, 0.05, 0.5),
      velvetRed: createMat('matAdult_VelvetRed', '#991b1b', '#7f1d1d', 0.45, 0.05, 0.85),
      velvetPurple: createMat('matAdult_VelvetPurple', '#6b21a8', '#581c87', 0.45, 0.05, 0.85),
      velvetPink: createMat('matAdult_VelvetPink', '#f43f5e', '#e11d48', 0.5, 0.05, 0.85),
      silkPink: createMat('matAdult_SilkPink', '#fb7185', '#f43f5e', 0.75, 0.1, 0.3),
      satinEmerald: createMat('matAdult_SatinEmerald', '#10b981', '#059669', 0.75, 0.1, 0.3),
      sculptureGold: createMat('matAdult_SculptureGold', '#fbbf24', '#f59e0b', 0.8, 0.9, 0.2),
      lipsRuby: createMat('matAdult_LipsRuby', '#e11d48', '#be123c', 0.9, 0.15, 0.18),
      peachBlush: createMat('matAdult_PeachBlush', '#fb923c', '#f97316', 0.65, 0.05, 0.5),
      strawberryRed: createMat('matAdult_StrawberryRed', '#dc2626', '#b91c1c', 0.75, 0.1, 0.25),
      leafGreen: createMat('matAdult_LeafGreen', '#16a34a', '#15803d', 0.4, 0.05, 0.45),
      candleWax: createMat('matAdult_CandleWax', '#fef08a', '#fde047', 0.6, 0.05, 0.5),
      candleFlame: createMat('matAdult_CandleFlame', '#ffedd5', '#f97316', 1.2, 0.0, 0.1),
      ribbonScarlet: createMat('matAdult_RibbonScarlet', '#e11d48', '#be123c', 0.85, 0.1, 0.3),
      sandGold: createMat('matAdult_SandGold', '#fbbf24', '#f59e0b', 0.85, 0.2, 0.4),

      // Glass for hourglass & votives
      glass: (() => {
        const m = new BABYLON.PBRMaterial('matAdult_Glass', this.scene);
        m.albedoColor = new BABYLON.Color3(0.9, 0.96, 1.0);
        m.alpha = 0.38;
        m.metallic = 0.08;
        m.roughness = 0.12;
        return m;
      })(),

      // Themed Track Socket Pads
      padLoseClothing: createMat('matAdult_PadLoseClothing', '#f43f5e', '#e11d48', 0.85),
      padGainClothing: createMat('matAdult_PadGainClothing', '#10b981', '#059669', 0.85),
      padPickWhoLosesClothing: createMat('matAdult_PadPickWhoLosesClothing', '#ec4899', '#db2777', 0.85),
      padRightLosesClothing: createMat('matAdult_PadRightLosesClothing', '#3b82f6', '#2563eb', 0.85),
      padLeftLosesClothing: createMat('matAdult_PadLeftLosesClothing', '#8b5cf6', '#7c3aed', 0.85),
      padRemoveClothingChoice: createMat('matAdult_PadRemoveClothingChoice', '#e11d48', '#be123c', 0.85),

      padTouchBreast: createMat('matAdult_PadTouchBreast', '#f43f5e', '#e11d48', 0.85),
      padLickBreast: createMat('matAdult_PadLickBreast', '#ec4899', '#db2777', 0.85),
      padSuckBreast: createMat('matAdult_PadSuckBreast', '#d946ef', '#c026d3', 0.85),
      padTouchGenitals: createMat('matAdult_PadTouchGenitals', '#a855f7', '#9333ea', 0.85),
      padLickGenitals: createMat('matAdult_PadLickGenitals', '#f43f5e', '#e11d48', 0.85),
      padSuckGenitals: createMat('matAdult_PadSuckGenitals', '#ec4899', '#db2777', 0.85),
      padFingerGenitals: createMat('matAdult_PadFingerGenitals', '#f59e0b', '#d97706', 0.85),
      padGenitalIntercourse: createMat('matAdult_PadGenitalIntercourse', '#ef4444', '#dc2626', 0.9),
      padTouchAnus: createMat('matAdult_PadTouchAnus', '#8b5cf6', '#7c3aed', 0.85),
      padLickAnus: createMat('matAdult_PadLickAnus', '#ec4899', '#db2777', 0.85),
      padFingerAnus: createMat('matAdult_PadFingerAnus', '#f59e0b', '#d97706', 0.85),
      padAnalIntercourse: createMat('matAdult_PadAnalIntercourse', '#ef4444', '#dc2626', 0.9),

      padRim: createMat('matAdult_PadRim', '#ffffff', '#e2e8f0', 0.5, 0.5, 0.25)
    };

    this._adultMaterials = mats;
    return mats;
  }

  /**
   * Retrieves active Skunked & Stripped (Adult) configurations from settings or presets.
   * @returns {Array<Object>}
   */
  getActiveAdultConfigs() {
    const settings = this.settings || {};
    const brand = this.options.brand || settings?.brand || (typeof window !== 'undefined' && (window.CRAZY_BRAND || (typeof window.getAppBrand === 'function' && window.getAppBrand())));
    const isAdult = brand === 'adult';

    if (Array.isArray(this.options.adultConfigs) && this.options.adultConfigs.length > 0) {
      return this.options.adultConfigs;
    }

    const adultTypes = [
      { key: 'loseClothing', name: 'You Lose Clothing', icon: '👙', color: '#f43f5e', defHoles: [10, 70, 10, 70] },
      { key: 'gainClothing', name: 'You Gain Clothing', icon: '🧥', color: '#10b981', defHoles: [20, 80, 20, 80] },
      { key: 'pickWhoLosesClothing', name: 'Pick Who Strips', icon: '🎯', color: '#ec4899', defHoles: [30, 90, 30, 90] },
      { key: 'rightLosesClothing', name: 'Right Neighbor Strips', icon: '👉', color: '#3b82f6', defHoles: [40, 100, 40, 100] },
      { key: 'leftLosesClothing', name: 'Left Neighbor Strips', icon: '👈', color: '#8b5cf6', defHoles: [50, 110, 50, 110] },
      { key: 'removeClothingChoice', name: 'Remove Clothing Choice', icon: '✂️', color: '#e11d48', defHoles: [60, 120, 60, 120] },
      { key: 'touchBreast', name: 'Touch Breast', icon: '💋', color: '#f43f5e', defHoles: [5, 45, 75, 105], timed: true },
      { key: 'lickBreast', name: 'Lick Breast', icon: '👅', color: '#ec4899', defHoles: [15, 50, 80, 110], timed: true },
      { key: 'suckBreast', name: 'Suck Breast', icon: '👄', color: '#d946ef', defHoles: [25, 55, 85, 115], timed: true },
      { key: 'touchGenitals', name: 'Touch Genitals', icon: '✨', color: '#a855f7', defHoles: [35, 40, 70, 100], timed: true },
      { key: 'lickGenitals', name: 'Lick Genitals', icon: '👅', color: '#f43f5e', defHoles: [45, 60, 90, 120], timed: true },
      { key: 'suckGenitals', name: 'Suck Genitals', icon: '👄', color: '#ec4899', defHoles: [55, 65, 95, 115], timed: true },
      { key: 'fingerGenitals', name: 'Finger Genitals', icon: '👉', color: '#f59e0b', defHoles: [65, 45, 75, 105], timed: true },
      { key: 'genitalIntercourse', name: 'Genital Intercourse', icon: '🔥', color: '#ef4444', defHoles: [75, 55, 85, 115], timed: true },
      { key: 'touchAnus', name: 'Touch Anus', icon: '🍑', color: '#8b5cf6', defHoles: [85, 50, 80, 110], timed: true },
      { key: 'lickAnus', name: 'Lick Anus', icon: '👅', color: '#ec4899', defHoles: [95, 55, 85, 115], timed: true },
      { key: 'fingerAnus', name: 'Finger Anus', icon: '👉', color: '#f59e0b', defHoles: [105, 60, 90, 120], timed: true },
      { key: 'analIntercourse', name: 'Anal Intercourse', icon: '🔥', color: '#ef4444', defHoles: [115, 65, 95, 115], timed: true }
    ];

    const configs = [];
    adultTypes.forEach(at => {
      const chkKey = `opt${at.key.charAt(0).toUpperCase() + at.key.slice(1)}`;
      const isChecked = Boolean(settings[chkKey] || settings[at.key] || (isAdult && (settings[`${at.key}Holes`] || settings[`${chkKey}1Hole`])));
      if (!isChecked) return;

      const arr = settings[`${at.key}Holes`] || [];
      const rawCnt = settings[`${chkKey}Count`] || settings[`${at.key}Count`] || arr.length || 2;
      const count = Math.min(4, Math.max(1, parseInt(rawCnt, 10) || 1));
      const rawPegs = settings[`${chkKey}Pegs`] || settings[`${at.key}Pegs`] || 1;
      const pegs = Math.min(5, Math.max(1, parseInt(rawPegs, 10) || 1));

      for (let i = 1; i <= count; i++) {
        const h = settings[`${chkKey}${i}Hole`] || arr[i - 1] || at.defHoles[i - 1] || 15;
        if (h && h > 0 && h <= this.targetHoles) {
          configs.push({
            id: `${at.key}_${i}`,
            type: at.key,
            name: `${at.name} ${i}`,
            icon: at.icon,
            color: at.color,
            index: i,
            hole: parseInt(h, 10),
            pegs: parseInt(pegs, 10),
            timed: Boolean(at.timed)
          });
        }
      }
    });

    return configs;
  }

  /**
   * Generates or retrieves cached PBR materials for 3D clash obstacle markers.
   * @private
   */
  _getClashObstacleMaterials() {
    const BABYLON = this.BABYLON;
    if (this._obstacleMaterials) return this._obstacleMaterials;

    const createMat = (name, colHex, emHex, emissiveScale = 0.65) => {
      const mat = new BABYLON.PBRMaterial(name, this.scene);
      const col = BABYLON.Color3.FromHexString(colHex);
      const em = BABYLON.Color3.FromHexString(emHex);
      mat.albedoColor = col;
      mat.emissiveColor = em.scale(emissiveScale);
      mat.metallic = 0.15;
      mat.roughness = 0.3;
      return mat;
    };

    const mats = {
      stopSign: createMat('mat3D_StopSign', '#d32f2f', '#ff1744', 0.95),
      stopSignInner: createMat('mat3D_StopSignInner', '#ffffff', '#ffffff', 0.5),
      stopSignPole: createMat('mat3D_StopSignPole', '#b0bec5', '#eceff1', 0.35),
      jumpFwdLaunch: createMat('mat3D_JumpFwdLaunch', '#00e676', '#00ff80', 0.9),
      jumpFwdDest: createMat('mat3D_JumpFwdDest', '#00b0ff', '#00e5ff', 0.85),
      jumpBwdLaunch: createMat('mat3D_JumpBwdLaunch', '#ff3d00', '#ff1744', 0.9),
      jumpBwdDest: createMat('mat3D_JumpBwdDest', '#b71c1c', '#d50000', 0.85),
      jumpArcFwd: createMat('mat3D_JumpArcFwd', '#00e676', '#00ff88', 1.0),
      jumpArcBwd: createMat('mat3D_JumpArcBwd', '#ff1744', '#ff5252', 1.0),
      jail: createMat('mat3D_Jail', '#8e24aa', '#ba68c8', 0.85),
      bar: createMat('mat3D_Bar', '#ffb300', '#ffd54f', 0.85),
      timeout: createMat('mat3D_Timeout', '#fb8c00', '#ffa726', 0.85),
      detour: createMat('mat3D_Detour', '#ffd600', '#ffea00', 0.85),
      roundabout: createMat('mat3D_RoundAbout', '#00bcd4', '#26c6da', 0.85),
      noPassing: createMat('mat3D_NoPassing', '#0091ea', '#00b0ff', 0.95),
      noPassingBarrier: createMat('mat3D_NoPassingBarrier', '#00e5ff', '#80d8ff', 0.95),
      noPassingCone: createMat('mat3D_NoPassingCone', '#0d47a1', '#2979ff', 0.9),
      signs: createMat('mat3D_Signs', '#d81b60', '#f06292', 0.85),
      accentWhite: createMat('mat3D_AccentWhite', '#ffffff', '#e0e0e0', 0.4),
      glass: (() => {
        const m = new BABYLON.PBRMaterial('mat3D_Glass', this.scene);
        m.albedoColor = new BABYLON.Color3(0.85, 0.93, 1.0);
        m.alpha = 0.42;
        m.metallic = 0.08;
        m.roughness = 0.12;
        return m;
      })(),
      beerLiquid: createMat('mat3D_BeerLiquid', '#f59e0b', '#d97706', 0.5),
      beerFoam: createMat('mat3D_BeerFoam', '#fffdf5', '#fff9c4', 0.25),
      wineLiquid: createMat('mat3D_WineLiquid', '#881337', '#9f1239', 0.4),
      shotSpirit: createMat('mat3D_ShotSpirit', '#eab308', '#ca8a04', 0.5),
      barCoaster: createMat('mat3D_BarCoaster', '#3e2723', '#5d4037', 0.25),
      metalCage: (() => {
        const m = new BABYLON.PBRMaterial('mat3D_MetalCage', this.scene);
        m.albedoColor = BABYLON.Color3.FromHexString('#37474f');
        m.emissiveColor = BABYLON.Color3.FromHexString('#263238').scale(0.35);
        m.metallic = 0.88;
        m.roughness = 0.28;
        return m;
      })(),
      metalBars: (() => {
        const m = new BABYLON.PBRMaterial('mat3D_MetalBars', this.scene);
        m.albedoColor = BABYLON.Color3.FromHexString('#78909c');
        m.emissiveColor = BABYLON.Color3.FromHexString('#546e7a').scale(0.3);
        m.metallic = 0.92;
        m.roughness = 0.18;
        return m;
      })(),
      cageLock: (() => {
        const m = new BABYLON.PBRMaterial('mat3D_CageLock', this.scene);
        m.albedoColor = BABYLON.Color3.FromHexString('#fbc02d');
        m.emissiveColor = BABYLON.Color3.FromHexString('#f57f17').scale(0.5);
        m.metallic = 0.85;
        m.roughness = 0.25;
        return m;
      })(),
      dunceWood: createMat('mat3D_DunceWood', '#5d4037', '#3e2723', 0.25),
      dunceCap: createMat('mat3D_DunceCap', '#fffdf5', '#ffffff', 0.2),
      dunceRed: createMat('mat3D_DunceRed', '#d32f2f', '#f44336', 0.5),
      timeoutPlatform: createMat('mat3D_TimeoutPlatform', '#4e342e', '#3e2723', 0.2),
      asphaltRoad: (() => {
        const m = createMat('mat3D_AsphaltRoad', '#242424', '#171717', 0.1);
        m.roughness = 0.88;
        m.metallic = 0.05;
        return m;
      })(),
      concreteCurb: (() => {
        const m = createMat('mat3D_ConcreteCurb', '#eceff1', '#cfd8dc', 0.2);
        m.roughness = 0.55;
        m.metallic = 0.08;
        return m;
      })(),
      grassLawn: (() => {
        const m = createMat('mat3D_GrassLawn', '#2e7d32', '#1b5e20', 0.25);
        m.roughness = 0.85;
        m.metallic = 0.02;
        return m;
      })(),
      roadLineWhite: createMat('mat3D_RoadLineWhite', '#ffffff', '#e0e0e0', 0.5),
      roadSignBlue: createMat('mat3D_RoadSignBlue', '#0277bd', '#0288d1', 0.65),
      chevronYellow: createMat('mat3D_ChevronYellow', '#fbc02d', '#f57f17', 0.7),
      carWheels: createMat('mat3D_CarWheels', '#1c1c1c', '#000000', 0.1),
      headlights: createMat('mat3D_Headlights', '#fffde7', '#ffeb3b', 0.95),
      detourOrange: createMat('mat3D_DetourOrange', '#ff6d00', '#ff9100', 0.85),
      detourBarricade: createMat('mat3D_DetourBarricade', '#ff3d00', '#ff6e40', 0.85),
      highwayGreen: (() => {
        const m = createMat('mat3D_HighwayGreen', '#00703c', '#008751', 0.65);
        m.roughness = 0.35;
        m.metallic = 0.12;
        return m;
      })(),
      highwayGreenBorder: createMat('mat3D_HighwayGreenBorder', '#ffffff', '#e0e0e0', 0.7),
      dicePad: createMat('mat3D_DicePad', '#7c3aed', '#9333ea', 0.85),
      dicePadRing: createMat('mat3D_DicePadRing', '#c084fc', '#e9d5ff', 0.5),
      diceFelt: (() => {
        const m = createMat('mat3D_DiceFelt', '#064e3b', '#047857', 0.15);
        m.roughness = 0.92;
        m.metallic = 0.02;
        return m;
      })(),
      diceTrayRim: createMat('mat3D_DiceTrayRim', '#3e2723', '#27180d', 0.25),
      diceTrayBase: createMat('mat3D_DiceTrayBase', '#27180d', '#1a0b03', 0.2),
      diceGoldTrim: createMat('mat3D_DiceGoldTrim', '#f59e0b', '#d97706', 0.6),
      wheelPad: createMat('mat3D_WheelPad', '#db2777', '#ec4899', 0.85),
      wheelPadRing: createMat('mat3D_WheelPadRing', '#f472b6', '#fbcfe8', 0.5),
      wheelStand: (() => {
        const m = createMat('mat3D_WheelStand', '#1e293b', '#0f172a', 0.15);
        m.metallic = 0.85;
        m.roughness = 0.35;
        return m;
      })(),
      wheelRim: (() => {
        const m = createMat('mat3D_WheelRim', '#f59e0b', '#d97706', 0.6);
        m.metallic = 0.9;
        m.roughness = 0.2;
        return m;
      })(),
      wheelHub: (() => {
        const m = createMat('mat3D_WheelHub', '#fbbf24', '#f59e0b', 0.8);
        m.metallic = 0.95;
        m.roughness = 0.15;
        return m;
      })(),
      wheelPointer: createMat('mat3D_WheelPointer', '#ef4444', '#dc2626', 0.9)
    };
    this._obstacleMaterials = mats;
    return mats;
  }

  /**
   * Renders 3D Octagonal Stop Sign markers and roadside signposts directly at stop sign holes.
   * @private
   */
  _render3DStopSigns(spacing, radius, padHeight, mats) {
    const BABYLON = this.BABYLON;
    const settings = this.settings;
    const isClash = this.isClashMode();
    const hasConfigs = Array.isArray(this.options.obstacleConfigs?.stopSigns) && this.options.obstacleConfigs.stopSigns.length > 0;
    const active = hasConfigs || Boolean(
      settings?.optStopSign ||
      settings?.optStopSigns ||
      (isClash && settings?.optStopSign !== false && settings?.optStopSigns !== false)
    );
    if (!active) return;

    const stopSigns = hasConfigs
      ? this.options.obstacleConfigs.stopSigns
      : ObstacleGeometry.getStopSignConfigs(Object.assign({}, settings, { optStopSign: true, target: this.targetHoles || settings?.target || 481 }));
    if (!Array.isArray(stopSigns) || !stopSigns.length) return;

    const tracksToMark = this.activeTracks.length > 0 ? this.activeTracks : ['p1'];
    const refTrack = tracksToMark[0] || 'p1';

    stopSigns.forEach(s => {
      if (s.hole > this.targetHoles) return;

      // 1. Octagonal socket pads on every active track at this stop hole
      tracksToMark.forEach(who => {
        const pt = this.getHoleCoords(who, s.hole);
        if (!pt) return;
        const surfaceY = (pt && pt.y !== undefined) ? pt.y : 0.8;

        // 8-sided Octagon Stop Sign base pad (raised and bold)
        const oct = BABYLON.MeshBuilder.CreateCylinder(`stopSign_${who}_${s.hole}`, {
          diameter: radius * 2.6,
          height: padHeight * 2.2,
          tessellation: 8
        }, this.scene);
        oct.position.set(pt.x, surfaceY + (padHeight * 2.2) / 2 + 0.08, pt.z);
        oct.material = mats.stopSign;
        oct.parent = this.clashObstaclesRoot;
        oct.metadata = { obstacle: 'stop-sign', hole: s.hole, who };

        // Inner white octagonal accent rim
        const innerOct = BABYLON.MeshBuilder.CreateCylinder(`stopSignInner_${who}_${s.hole}`, {
          diameter: radius * 2.0,
          height: padHeight * 2.2 + 0.06,
          tessellation: 8
        }, this.scene);
        innerOct.position.set(pt.x, surfaceY + (padHeight * 2.2 + 0.06) / 2 + 0.08, pt.z);
        innerOct.material = mats.stopSignInner;
        innerOct.parent = this.clashObstaclesRoot;
        innerOct.metadata = { obstacle: 'stop-sign', hole: s.hole, who };

        // Center red octagonal core
        const centerOct = BABYLON.MeshBuilder.CreateCylinder(`stopSignCenter_${who}_${s.hole}`, {
          diameter: radius * 1.5,
          height: padHeight * 2.2 + 0.12,
          tessellation: 8
        }, this.scene);
        centerOct.position.set(pt.x, surfaceY + (padHeight * 2.2 + 0.12) / 2 + 0.08, pt.z);
        centerOct.material = mats.stopSign;
        centerOct.parent = this.clashObstaclesRoot;
        centerOct.metadata = { obstacle: 'stop-sign', hole: s.hole, who };
      });

      // 2. Upright 3D Roadside STOP Signpost (4x bigger) standing beside the reference track
      const p0 = this.getHoleCoords(refTrack, s.hole);
      const p1 = this.getHoleCoords(refTrack, Math.min(this.targetHoles, s.hole + 1));
      if (p0) {
        let nx = 0;
        let nz = 1;
        if (p1) {
          const dx = p1.x - p0.x;
          const dz = p1.z - p0.z;
          const len = Math.hypot(dx, dz) || 1;
          nx = -dz / len;
          nz = dx / len;
        }
        const postDist = spacing * 1.65;
        const postX = p0.x + nx * postDist;
        const postZ = p0.z + nz * postDist;
        const postY = ((p0 && p0.y !== undefined) ? p0.y : 0.8) + 0.1;
        const poleHeight = 16.8; // 4x taller (was 4.2)
        const poleDiameter = 1.35; // 4x thicker (was 0.35)

        // Vertical silver metal pole
        const pole = BABYLON.MeshBuilder.CreateCylinder(`stopSignPole_${s.hole}`, {
          diameter: poleDiameter,
          height: poleHeight,
          tessellation: 20
        }, this.scene);
        pole.position.set(postX, postY + poleHeight / 2, postZ);
        pole.material = mats.stopSignPole || mats.accentWhite;
        pole.parent = this.clashObstaclesRoot;
        pole.metadata = { obstacle: 'stop-sign', hole: s.hole, who: refTrack };

        // Upright 8-sided Octagon Stop Sign Head (4x bigger: ~11-13 units diameter)
        const signDiameter = Math.max(10.5, spacing * 1.68);
        const signDepth = 1.25;
        const signHead = BABYLON.MeshBuilder.CreateCylinder(`stopSignHead_${s.hole}`, {
          diameter: signDiameter,
          height: signDepth,
          tessellation: 8
        }, this.scene);
        signHead.bakeTransformIntoVertices(BABYLON.Matrix.RotationY(Math.PI / 8));
        signHead.position.set(postX, postY + poleHeight - 0.2, postZ);
        signHead.rotation.x = -Math.PI / 3.4;
        signHead.rotation.y = Math.PI;
        signHead.material = mats.stopSign;
        signHead.parent = this.clashObstaclesRoot;
        signHead.metadata = { obstacle: 'stop-sign', hole: s.hole, who: refTrack };

        // White octagonal border on the sign face
        const signBorder = BABYLON.MeshBuilder.CreateCylinder(`stopSignBorder_${s.hole}`, {
          diameter: signDiameter * 0.85,
          height: signDepth + 0.12,
          tessellation: 8
        }, this.scene);
        signBorder.bakeTransformIntoVertices(BABYLON.Matrix.RotationY(Math.PI / 8));
        signBorder.position.set(postX, postY + poleHeight - 0.2, postZ);
        signBorder.rotation.x = signHead.rotation.x;
        signBorder.rotation.y = signHead.rotation.y;
        signBorder.material = mats.stopSignInner;
        signBorder.parent = this.clashObstaclesRoot;
        signBorder.metadata = { obstacle: 'stop-sign', hole: s.hole, who: refTrack };

        // Inner red octagonal center on the sign face
        const signFace = BABYLON.MeshBuilder.CreateCylinder(`stopSignFace_${s.hole}`, {
          diameter: signDiameter * 0.72,
          height: signDepth + 0.24,
          tessellation: 8
        }, this.scene);
        signFace.bakeTransformIntoVertices(BABYLON.Matrix.RotationY(Math.PI / 8));
        signFace.position.set(postX, postY + poleHeight - 0.2, postZ);
        signFace.rotation.x = signHead.rotation.x;
        signFace.rotation.y = signHead.rotation.y;
        signFace.material = mats.stopSign;
        signFace.parent = this.clashObstaclesRoot;
        signFace.metadata = { obstacle: 'stop-sign', hole: s.hole, who: refTrack };

        // Bold white 3D "STOP" text banner disc on the sign face
        try {
          const dynTex = new BABYLON.DynamicTexture(`stopSignTex_${s.hole}`, { width: 512, height: 512 }, this.scene, true);
          const ctx = dynTex.getContext();
          ctx.fillStyle = '#d32f2f';
          ctx.fillRect(0, 0, 512, 512);
          ctx.strokeStyle = '#ffffff';
          ctx.lineWidth = 28;
          ctx.beginPath();
          const cx = 256;
          const cy = 256;
          const rOct = 224;
          for (let i = 0; i < 8; i++) {
            const ang = (i * Math.PI / 4) + (Math.PI / 8);
            const px = cx + rOct * Math.cos(ang);
            const py = cy + rOct * Math.sin(ang);
            if (i === 0) ctx.moveTo(px, py);
            else ctx.lineTo(px, py);
          }
          ctx.closePath();
          ctx.stroke();
          ctx.fillStyle = '#ffffff';
          ctx.font = '900 136px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText('STOP', 256, 260);
          dynTex.update();

          const labelDisc = BABYLON.MeshBuilder.CreateCylinder(`stopSignLabel_${s.hole}`, {
            diameter: signDiameter * 0.70,
            height: signDepth + 0.32,
            tessellation: 32
          }, this.scene);
          labelDisc.position.set(postX, postY + poleHeight - 0.2, postZ);
          labelDisc.rotation.x = signHead.rotation.x;
          labelDisc.rotation.y = signHead.rotation.y;

          const textMat = new BABYLON.PBRMaterial(`matStopSignText_${s.hole}`, this.scene);
          textMat.albedoTexture = dynTex;
          textMat.emissiveTexture = dynTex;
          textMat.emissiveColor = new BABYLON.Color3(0.9, 0.9, 0.9);
          textMat.roughness = 0.25;
          labelDisc.material = textMat;
          labelDisc.parent = this.clashObstaclesRoot;
          labelDisc.metadata = { obstacle: 'stop-sign', hole: s.hole, who: refTrack };
        } catch (_) {}
      }
    });
  }

  /**
   * Renders 3D Jump launch pads, destination pads, and parabolic trajectory leaping arcs.
   * @private
   */
  _render3DJumps(spacing, radius, padHeight, mats) {
    const BABYLON = this.BABYLON;
    const settings = this.settings;
    const isClash = this.isClashMode();
    const hasConfigs = Array.isArray(this.options.obstacleConfigs?.jumps) && this.options.obstacleConfigs.jumps.length > 0;
    const active = hasConfigs || Boolean(settings?.optJump || (isClash && settings?.optJump !== false));
    if (!active) return;

    const jumps = hasConfigs
      ? this.options.obstacleConfigs.jumps
      : ObstacleGeometry.getJumpConfigs(Object.assign({}, settings, { optJump: true, target: this.targetHoles || settings?.target || 481 }));
    if (!Array.isArray(jumps) || !jumps.length) return;

    const tracksToMark = this.activeTracks.length > 0 ? this.activeTracks : ['p1'];
    const refTrack = tracksToMark[0] || 'p1';

    jumps.forEach(j => {
      if (j.start > this.targetHoles && j.dest > this.targetHoles) return;
      const isFwd = Boolean(j.isForward);
      const sHole = Math.min(this.targetHoles, isFwd ? j.start : j.start + 1);
      const dHole = Math.min(this.targetHoles, j.dest);
      const endHole = Math.min(this.targetHoles, j.end !== undefined ? j.end : (isFwd ? j.start : j.start + 2));

      // Determine all trigger holes (1 hole for forward jumps, 3 holes for backward jumps)
      const triggerHoles = [];
      for (let h = j.start; h <= endHole; h++) {
        if (h <= this.targetHoles) {
          triggerHoles.push(h);
        }
      }
      if (triggerHoles.length === 0) {
        triggerHoles.push(sHole);
      }

      tracksToMark.forEach(who => {
        // Launch / Hazard Pads on all trigger holes (all 3 holes highlighted for jump back)
        triggerHoles.forEach(h => {
          const pStart = this.getHoleCoords(who, h);
          if (pStart) {
            const surfaceY = (pStart && pStart.y !== undefined) ? pStart.y : 0.8;
            const launchPad = BABYLON.MeshBuilder.CreateCylinder(`jumpStart_${who}_${j.id}_${h}`, {
              diameter: radius * 2.0,
              height: padHeight,
              tessellation: 24
            }, this.scene);
            launchPad.position.set(pStart.x, surfaceY + padHeight / 2 + 0.08, pStart.z);
            launchPad.material = isFwd ? mats.jumpFwdLaunch : mats.jumpBwdLaunch;
            launchPad.parent = this.clashObstaclesRoot;
            launchPad.metadata = {
              obstacle: 'jump-arrow',
              jumpId: j.id,
              start: j.start,
              end: endHole,
              dest: j.dest,
              forward: String(isFwd),
              hole: h
            };

            // Luminous hazard ring on backward jump trigger holes (3 holes)
            if (!isFwd) {
              const hazardRing = BABYLON.MeshBuilder.CreateTorus(`jumpHazardRing_${who}_${j.id}_${h}`, {
                diameter: radius * 2.0,
                thickness: 0.22,
                tessellation: 24
              }, this.scene);
              hazardRing.position.set(pStart.x, surfaceY + padHeight + 0.08, pStart.z);
              hazardRing.rotation.x = Math.PI / 2;
              hazardRing.material = mats.jumpArcBwd || mats.jumpBwdLaunch;
              hazardRing.parent = this.clashObstaclesRoot;
              hazardRing.metadata = launchPad.metadata;
            }
          }
        });

        // Destination Landing Pad
        const pDest = this.getHoleCoords(who, dHole);
        if (pDest) {
          const surfaceY = (pDest && pDest.y !== undefined) ? pDest.y : 0.8;
          const destPad = BABYLON.MeshBuilder.CreateCylinder(`jumpDest_${who}_${j.id}`, {
            diameter: radius * 2.0,
            height: padHeight,
            tessellation: 24
          }, this.scene);
          destPad.position.set(pDest.x, surfaceY + padHeight / 2 + 0.08, pDest.z);
          destPad.material = isFwd ? mats.jumpFwdDest : mats.jumpBwdDest;
          destPad.parent = this.clashObstaclesRoot;
          destPad.metadata = {
            obstacle: 'jump-arrow',
            jumpId: j.id,
            start: j.start,
            end: endHole,
            dest: j.dest,
            forward: String(isFwd),
            hole: dHole
          };
        }
      });

      // 3D Parabolic Trajectory Leap Arc
      const arcStart = this.getHoleCoords(refTrack, sHole);
      const arcEnd = this.getHoleCoords(refTrack, dHole);
      if (arcStart && arcEnd) {
        const startY = (arcStart && typeof arcStart.y === 'number') ? arcStart.y : 0.8;
        const endY = (arcEnd && typeof arcEnd.y === 'number') ? arcEnd.y : 0.8;
        const dx = arcEnd.x - arcStart.x;
        const dz = arcEnd.z - arcStart.z;
        const dist = Math.hypot(dx, dz);
        if (dist >= 4) {
          const steps = 32;
          const vertDelta = Math.abs(endY - startY);
          const apexClearance = Math.max(6, Math.min(26, dist * 0.35));
          // When destination is vertically higher or lower, ensure parabolic leap peaks above both start and destination tiers
          const curveHeight = (vertDelta / 2) + apexClearance;
          const path = [];

          for (let s = 0; s <= steps; s++) {
            const t = s / steps;
            const x = arcStart.x + dx * t;
            const z = arcStart.z + dz * t;
            const baseY = startY + (endY - startY) * t;
            const arcY = 4 * curveHeight * t * (1 - t);
            path.push(new BABYLON.Vector3(x, baseY + arcY + 0.2, z));
          }

          const tube = BABYLON.MeshBuilder.CreateTube(`jumpTube_${j.id}`, {
            path,
            radius: 0.42,
            tessellation: 12
          }, this.scene);
          tube.material = isFwd ? mats.jumpArcFwd : mats.jumpArcBwd;
          tube.parent = this.clashObstaclesRoot;
          tube.metadata = {
            obstacle: 'jump-arrow',
            jumpId: j.id,
            start: j.start,
            end: j.end,
            dest: j.dest,
            forward: String(isFwd)
          };

          const coneHeight = 1.8;
          const cone = BABYLON.MeshBuilder.CreateCylinder(`jumpCone_${j.id}`, {
            diameterTop: 0,
            diameterBottom: 1.4,
            height: coneHeight,
            tessellation: 16
          }, this.scene);

          // Calculate incoming landing vector so arrowhead points along trajectory descent into destination
          const lastPt = path[path.length - 1];
          const prevPt = path[path.length - 2];
          const dir = lastPt.subtract(prevPt).normalize();

          // Position cone so tip lands cleanly at the destination peg pad elevation
          cone.position.set(
            arcEnd.x - dir.x * (coneHeight * 0.45),
            (endY + padHeight + 0.08) - dir.y * (coneHeight * 0.45),
            arcEnd.z - dir.z * (coneHeight * 0.45)
          );

          // Orient cone to point in trajectory direction (default cylinder tip is at +Y)
          if (BABYLON.Quaternion && typeof BABYLON.Quaternion.FromUnitVectorsToRef === 'function') {
            cone.rotationQuaternion = BABYLON.Quaternion.FromUnitVectorsToRef(
              new BABYLON.Vector3(0, 1, 0),
              dir,
              new BABYLON.Quaternion()
            );
          } else {
            const pitch = Math.acos(Math.max(-1, Math.min(1, dir.y)));
            const yaw = Math.atan2(dir.x, dir.z);
            cone.rotation.set(pitch, yaw, 0);
          }

          cone.material = isFwd ? mats.jumpFwdDest : mats.jumpBwdDest;
          cone.parent = this.clashObstaclesRoot;
          cone.metadata = tube.metadata;
        }
      }
    });
  }

  /**
   * Helper to render holding areas (Jails, Bars, Timeouts) with trigger pads and holding step studs.
   * @private
   */
  _render3DHoldingArea(items, type, mat, mats, spacing, radius, padHeight) {
    const BABYLON = this.BABYLON;
    if (!Array.isArray(items) || !items.length) return;
    const tracksToMark = this.activeTracks.length > 0 ? this.activeTracks : ['p1'];
    const refTrack = tracksToMark[0] || 'p1';

    items.forEach(item => {
      const hole = item.hole;
      if (hole > this.targetHoles) return;

      tracksToMark.forEach(who => {
        const pt = this.getHoleCoords(who, hole);
        if (!pt) return;
        const surfaceY = (pt && pt.y !== undefined) ? pt.y : 0.8;

        const disc = BABYLON.MeshBuilder.CreateCylinder(`${type}Trigger_${who}_${hole}`, {
          diameter: radius * 2.0,
          height: padHeight,
          tessellation: 24
        }, this.scene);
        disc.position.set(pt.x, surfaceY + padHeight / 2 + 0.08, pt.z);
        disc.material = mat;
        disc.parent = this.clashObstaclesRoot;
        disc.metadata = { obstacle: `${type}-cell`, hole, who };
      });

      const p0 = this.getHoleCoords(refTrack, hole);
      const p1 = this.getHoleCoords(refTrack, Math.min(this.targetHoles, hole + 1));
      if (p0 && p1) {
        const dx = p1.x - p0.x;
        const dz = p1.z - p0.z;
        const len = Math.hypot(dx, dz) || 1;
        const nx = -dz / len;
        const nz = dx / len;

        for (let s = 1; s <= 3; s++) {
          const stepDist = (radius * 1.8) + (s * spacing * 0.85);
          const stepX = p0.x + nx * stepDist;
          const stepZ = p0.z + nz * stepDist;
          const stepY = ((p0 && p0.y !== undefined) ? p0.y : 0.8) + padHeight / 2 + 0.08;

          const stud = BABYLON.MeshBuilder.CreateCylinder(`${type}Step_${hole}_${s}`, {
            diameter: radius * 1.2,
            height: padHeight * 0.85,
            tessellation: 16
          }, this.scene);
          stud.position.set(stepX, stepY, stepZ);
          stud.material = mat;
          stud.parent = this.clashObstaclesRoot;
          stud.metadata = { obstacle: `${type}-step`, hole, step: s };
        }
      }
    });
  }

  /**
   * Renders 3D Jail cells with an authentic 3D metal prison cage standing beside the track
   * instead of the 3 dots.
   * @private
   */
  _render3DJails(spacing, radius, padHeight, mats) {
    const BABYLON = this.BABYLON;
    const settings = this.settings;
    const isClash = this.isClashMode();
    const hasConfigs = Array.isArray(this.options.obstacleConfigs?.jails) && this.options.obstacleConfigs.jails.length > 0;
    const active = hasConfigs || Boolean(settings?.optJail || (isClash && settings?.optJail !== false));
    if (!active) return;
    const jails = hasConfigs
      ? this.options.obstacleConfigs.jails
      : ObstacleGeometry.getJailConfigs(Object.assign({}, settings, { optJail: true, target: this.targetHoles || settings?.target || 481 }));
    if (!Array.isArray(jails) || !jails.length) return;

    const tracksToMark = this.activeTracks.length > 0 ? this.activeTracks : ['p1'];
    const refTrack = tracksToMark[0] || 'p1';

    jails.forEach(item => {
      const hole = item.hole;
      if (hole > this.targetHoles) return;

      // 1. Jail trigger pad on the track(s)
      tracksToMark.forEach(who => {
        const pt = this.getHoleCoords(who, hole);
        if (!pt) return;
        const surfaceY = (pt && pt.y !== undefined) ? pt.y : 0.8;

        const disc = BABYLON.MeshBuilder.CreateCylinder(`jailTrigger_${who}_${hole}`, {
          diameter: radius * 2.0,
          height: padHeight,
          tessellation: 24
        }, this.scene);
        disc.position.set(pt.x, surfaceY + padHeight / 2 + 0.08, pt.z);
        disc.material = mats.jail;
        disc.parent = this.clashObstaclesRoot;
        disc.metadata = { obstacle: 'jail-cell', hole, who };

        // Outer iron shackle border ring
        const shackle = BABYLON.MeshBuilder.CreateCylinder(`jailTriggerRing_${who}_${hole}`, {
          diameter: radius * 2.22,
          height: padHeight * 0.75,
          tessellation: 24
        }, this.scene);
        shackle.position.set(pt.x, surfaceY + (padHeight * 0.75) / 2 + 0.06, pt.z);
        shackle.material = mats.metalCage || mats.jail;
        shackle.parent = this.clashObstaclesRoot;
        shackle.metadata = { obstacle: 'jail-cell', hole, who };
      });

      // 2. 3D Metal Prison Cage beside the track (replaces the 3 dots)
      const p0 = this.getHoleCoords(refTrack, hole);
      const p1 = this.getHoleCoords(refTrack, Math.min(this.targetHoles, hole + 1));
      if (p0 && p1) {
        const dx = p1.x - p0.x;
        const dz = p1.z - p0.z;
        const len = Math.hypot(dx, dz) || 1;
        const nx = -dz / len;
        const nz = dx / len;
        const surfaceY = (p0 && p0.y !== undefined) ? p0.y : 0.8;

        // Position the cage center standing beside the track
        const cageDist = (radius * 1.8) + (spacing * 2.1);
        const cageX = p0.x + nx * cageDist;
        const cageZ = p0.z + nz * cageDist;

        // Parent transform node for the metal cage
        const cageNode = new BABYLON.TransformNode(`jailCageNode_${hole}`, this.scene);
        cageNode.position.set(cageX, surfaceY, cageZ);
        // Face the front of the cage toward the track
        cageNode.rotation.y = Math.atan2(-nx, -nz);
        cageNode.parent = this.clashObstaclesRoot;

        const cageMeta = { obstacle: 'jail-cell', hole, who: refTrack };
        const matCage = mats.metalCage || mats.jail;
        const matBars = mats.metalBars || mats.jail;
        const matLock = mats.cageLock || mats.jail;

        // (a) Heavy Steel Floor Slab
        const floor = BABYLON.MeshBuilder.CreateBox(`jailCage_${hole}_floor`, {
          width: 14.0,
          depth: 14.0,
          height: 0.6
        }, this.scene);
        floor.position.set(0, 0.3, 0);
        floor.material = matCage;
        floor.parent = cageNode;
        floor.metadata = cageMeta;

        // (b) 4 Heavy Corner Upright Posts
        const postRadius = 0.55;
        const postHeight = 14.0;
        const postOffsets = [
          [-5.8, -5.8],
          [5.8, -5.8],
          [-5.8, 5.8],
          [5.8, 5.8]
        ];
        postOffsets.forEach(([px, pz], pIdx) => {
          const post = BABYLON.MeshBuilder.CreateCylinder(`jailCage_${hole}_post_${pIdx}`, {
            diameter: postRadius * 2,
            height: postHeight,
            tessellation: 16
          }, this.scene);
          post.position.set(px, 0.6 + postHeight / 2, pz);
          post.material = matCage;
          post.parent = cageNode;
          post.metadata = cageMeta;
        });

        // (c) Bottom Curb Beams
        const curbWidth = 12.8;
        const curbHeight = 0.8;
        const curbThick = 0.8;
        const curbBeams = [
          { x: 0, z: -5.8, w: curbWidth, d: curbThick }, // back
          { x: -5.8, z: 0, w: curbThick, d: curbWidth }, // left
          { x: 5.8, z: 0, w: curbThick, d: curbWidth },  // right
          { x: 0, z: 5.8, w: curbWidth, d: curbThick }   // front
        ];
        curbBeams.forEach((cb, bIdx) => {
          const curb = BABYLON.MeshBuilder.CreateBox(`jailCage_${hole}_curb_${bIdx}`, {
            width: cb.w,
            depth: cb.d,
            height: curbHeight
          }, this.scene);
          curb.position.set(cb.x, 0.6 + curbHeight / 2, cb.z);
          curb.material = matCage;
          curb.parent = cageNode;
          curb.metadata = cageMeta;
        });

        // (d) Top Header Beams
        curbBeams.forEach((tb, bIdx) => {
          const topBeam = BABYLON.MeshBuilder.CreateBox(`jailCage_${hole}_topBeam_${bIdx}`, {
            width: tb.w,
            depth: tb.d,
            height: curbHeight
          }, this.scene);
          topBeam.position.set(tb.x, 0.6 + postHeight - curbHeight / 2, tb.z);
          topBeam.material = matCage;
          topBeam.parent = cageNode;
          topBeam.metadata = cageMeta;
        });

        // (e) Vertical Iron Prison Bars (Back, Left, Right sides)
        const barHeight = 13.0;
        const barDiameter = 0.46;
        const barOffsets = [-4.2, -2.1, 0, 2.1, 4.2];

        // Back Wall Bars (z = -5.8)
        barOffsets.forEach((bx, bIdx) => {
          const bar = BABYLON.MeshBuilder.CreateCylinder(`jailCage_${hole}_barB_${bIdx}`, {
            diameter: barDiameter,
            height: barHeight,
            tessellation: 14
          }, this.scene);
          bar.position.set(bx, 0.6 + barHeight / 2, -5.8);
          bar.material = matBars;
          bar.parent = cageNode;
          bar.metadata = cageMeta;
        });

        // Left Wall Bars (x = -5.8)
        barOffsets.forEach((bz, bIdx) => {
          const bar = BABYLON.MeshBuilder.CreateCylinder(`jailCage_${hole}_barL_${bIdx}`, {
            diameter: barDiameter,
            height: barHeight,
            tessellation: 14
          }, this.scene);
          bar.position.set(-5.8, 0.6 + barHeight / 2, bz);
          bar.material = matBars;
          bar.parent = cageNode;
          bar.metadata = cageMeta;
        });

        // Right Wall Bars (x = 5.8)
        barOffsets.forEach((bz, bIdx) => {
          const bar = BABYLON.MeshBuilder.CreateCylinder(`jailCage_${hole}_barR_${bIdx}`, {
            diameter: barDiameter,
            height: barHeight,
            tessellation: 14
          }, this.scene);
          bar.position.set(5.8, 0.6 + barHeight / 2, bz);
          bar.material = matBars;
          bar.parent = cageNode;
          bar.metadata = cageMeta;
        });

        // (f) Front Wall with Gate and Heavy Padlock (z = 5.8, facing track)
        [-4.2, 4.2].forEach((bx, bIdx) => {
          const bar = BABYLON.MeshBuilder.CreateCylinder(`jailCage_${hole}_barF_${bIdx}`, {
            diameter: barDiameter,
            height: barHeight,
            tessellation: 14
          }, this.scene);
          bar.position.set(bx, 0.6 + barHeight / 2, 5.8);
          bar.material = matBars;
          bar.parent = cageNode;
          bar.metadata = cageMeta;
        });

        // Door frame uprights at x = -2.6 and x = +2.6
        [-2.6, 2.6].forEach((dx, dIdx) => {
          const dPost = BABYLON.MeshBuilder.CreateCylinder(`jailCage_${hole}_doorPost_${dIdx}`, {
            diameter: 0.7,
            height: barHeight,
            tessellation: 16
          }, this.scene);
          dPost.position.set(dx, 0.6 + barHeight / 2, 5.8);
          dPost.material = matCage;
          dPost.parent = cageNode;
          dPost.metadata = cageMeta;
        });

        // Door inner vertical bars at x = -1.3, 0, 1.3
        [-1.3, 0, 1.3].forEach((dx, dIdx) => {
          const dBar = BABYLON.MeshBuilder.CreateCylinder(`jailCage_${hole}_doorBar_${dIdx}`, {
            diameter: barDiameter,
            height: barHeight,
            tessellation: 14
          }, this.scene);
          dBar.position.set(dx, 0.6 + barHeight / 2, 5.8);
          dBar.material = matBars;
          dBar.parent = cageNode;
          dBar.metadata = cageMeta;
        });

        // Door horizontal cross brace
        const doorBrace = BABYLON.MeshBuilder.CreateBox(`jailCage_${hole}_doorBrace`, {
          width: 5.2,
          depth: 0.7,
          height: 0.7
        }, this.scene);
        doorBrace.position.set(0, 0.6 + barHeight / 2, 5.8);
        doorBrace.material = matCage;
        doorBrace.parent = cageNode;
        doorBrace.metadata = cageMeta;

        // Heavy Brass Padlock & Iron Chain on the gate
        const lockBody = BABYLON.MeshBuilder.CreateBox(`jailCage_${hole}_lockBody`, {
          width: 1.4,
          height: 1.6,
          depth: 0.8
        }, this.scene);
        lockBody.position.set(2.4, 0.6 + barHeight / 2, 6.4);
        lockBody.material = matLock;
        lockBody.parent = cageNode;
        lockBody.metadata = cageMeta;

        const lockShackle = BABYLON.MeshBuilder.CreateTorus(`jailCage_${hole}_lockShackle`, {
          diameter: 1.1,
          thickness: 0.28,
          tessellation: 16
        }, this.scene);
        lockShackle.position.set(2.4, 0.6 + barHeight / 2 + 1.1, 6.4);
        lockShackle.rotation.x = Math.PI / 2;
        lockShackle.material = matBars;
        lockShackle.parent = cageNode;
        lockShackle.metadata = cageMeta;

        // (g) Ceiling / Roof Grid Bars
        barOffsets.forEach((rx, rIdx) => {
          const roofBar = BABYLON.MeshBuilder.CreateCylinder(`jailCage_${hole}_roofBar_${rIdx}`, {
            diameter: 0.42,
            height: 12.0,
            tessellation: 12
          }, this.scene);
          roofBar.rotation.x = Math.PI / 2;
          roofBar.position.set(rx, 0.6 + postHeight + 0.1, 0);
          roofBar.material = matBars;
          roofBar.parent = cageNode;
          roofBar.metadata = cageMeta;
        });

        // Center cross roof bar
        const roofCross = BABYLON.MeshBuilder.CreateCylinder(`jailCage_${hole}_roofCross`, {
          diameter: 0.48,
          height: 12.0,
          tessellation: 12
        }, this.scene);
        roofCross.rotation.z = Math.PI / 2;
        roofCross.position.set(0, 0.6 + postHeight + 0.35, 0);
        roofCross.material = matCage;
        roofCross.parent = cageNode;
        roofCross.metadata = cageMeta;

        // (h) Top Warning Finial / Beacon
        const beacon = BABYLON.MeshBuilder.CreateSphere(`jailCage_${hole}_beacon`, {
          diameter: 1.5,
          segments: 16
        }, this.scene);
        beacon.position.set(0, 0.6 + postHeight + 1.2, 0);
        beacon.material = mats.jail;
        beacon.parent = cageNode;
        beacon.metadata = cageMeta;

        // (i) 3 Escape / Breakout Steps Inside the Cage (Steps 1, 2, 3)
        // These replace the 3 dots with 3 cell breakout step pads inside the cage
        const stepPositions = [
          { s: 1, x: -3.4, z: 0 },
          { s: 2, x: 0, z: 0 },
          { s: 3, x: 3.4, z: 0 }
        ];
        stepPositions.forEach(st => {
          const stepPad = BABYLON.MeshBuilder.CreateCylinder(`jailStep_${hole}_${st.s}`, {
            diameter: 2.2,
            height: 0.28,
            tessellation: 20
          }, this.scene);
          stepPad.position.set(st.x, 0.6 + 0.14, st.z);
          stepPad.material = mats.jail;
          stepPad.parent = cageNode;
          stepPad.metadata = { obstacle: 'jail-step', hole, step: st.s };

          const stepRing = BABYLON.MeshBuilder.CreateCylinder(`jailStep_${hole}_${st.s}_ring`, {
            diameter: 2.6,
            height: 0.16,
            tessellation: 20
          }, this.scene);
          stepRing.position.set(st.x, 0.6 + 0.08, st.z);
          stepRing.material = matCage;
          stepRing.parent = cageNode;
          stepRing.metadata = { obstacle: 'jail-step', hole, step: st.s };
        });
      }
    });
  }

  /**
   * Renders 3D Bar lounges with custom 3D beverages:
   * - Step 1: A glass of beer (mug with golden beer, white foam cap, and curved handle)
   * - Step 2: A glass of wine (stemmed wine glass with rich red wine)
   * - Step 3: A shot glass (heavy base tapered glass with golden spirit)
   * @private
   */
  _render3DBars(spacing, radius, padHeight, mats) {
    const BABYLON = this.BABYLON;
    const settings = this.settings;
    const isClash = this.isClashMode();
    const hasConfigs = Array.isArray(this.options.obstacleConfigs?.bars) && this.options.obstacleConfigs.bars.length > 0;
    const active = hasConfigs || Boolean(settings?.optBar || (isClash && settings?.optBar !== false));
    if (!active) return;
    const bars = hasConfigs
      ? this.options.obstacleConfigs.bars
      : ObstacleGeometry.getBarConfigs(Object.assign({}, settings, { optBar: true, target: this.targetHoles || settings?.target || 481 }));
    if (!Array.isArray(bars) || !bars.length) return;

    const tracksToMark = this.activeTracks.length > 0 ? this.activeTracks : ['p1'];
    const refTrack = tracksToMark[0] || 'p1';

    bars.forEach(item => {
      const hole = item.hole;
      if (hole > this.targetHoles) return;

      // 1. Bar trigger coaster pad on the active track(s)
      tracksToMark.forEach(who => {
        const pt = this.getHoleCoords(who, hole);
        if (!pt) return;
        const surfaceY = (pt && pt.y !== undefined) ? pt.y : 0.8;

        const disc = BABYLON.MeshBuilder.CreateCylinder(`barTrigger_${who}_${hole}`, {
          diameter: radius * 2.0,
          height: padHeight,
          tessellation: 24
        }, this.scene);
        disc.position.set(pt.x, surfaceY + padHeight / 2 + 0.08, pt.z);
        disc.material = mats.bar;
        disc.parent = this.clashObstaclesRoot;
        disc.metadata = { obstacle: 'bar-cell', hole, who };

        // Accent rim for cocktail coaster look
        const coasterRim = BABYLON.MeshBuilder.CreateCylinder(`barTriggerRim_${who}_${hole}`, {
          diameter: radius * 2.22,
          height: padHeight * 0.75,
          tessellation: 24
        }, this.scene);
        coasterRim.position.set(pt.x, surfaceY + (padHeight * 0.75) / 2 + 0.06, pt.z);
        coasterRim.material = mats.barCoaster || mats.bar;
        coasterRim.parent = this.clashObstaclesRoot;
        coasterRim.metadata = { obstacle: 'bar-cell', hole, who };
      });

      // 2. The 3 holding drinks beside the track:
      // Step 1: Glass of Beer (mug with foam head and handle)
      // Step 2: Glass of Wine (stemmed wine glass with deep red wine)
      // Step 3: Shot Glass (thick heavy base shot glass with amber spirit)
      const p0 = this.getHoleCoords(refTrack, hole);
      const p1 = this.getHoleCoords(refTrack, Math.min(this.targetHoles, hole + 1));
      if (p0 && p1) {
        const dx = p1.x - p0.x;
        const dz = p1.z - p0.z;
        const len = Math.hypot(dx, dz) || 1;
        const nx = -dz / len;
        const nz = dx / len;
        const surfaceY = (p0 && p0.y !== undefined) ? p0.y : 0.8;

        const baseOffset = (radius * 1.8) + 4.0;
        const stepSpacing = Math.max(13.5, spacing * 1.6);

        for (let s = 1; s <= 3; s++) {
          const stepDist = baseOffset + (s * stepSpacing);
          const stepX = p0.x + nx * stepDist;
          const stepZ = p0.z + nz * stepDist;
          const stepMeta = { obstacle: 'bar-step', hole, step: s };

          if (s === 1) {
            // STEP 1: GLASS OF BEER 🍺 (4x bigger)
            // Coaster
            const coaster = BABYLON.MeshBuilder.CreateCylinder(`barStep_${hole}_1_coaster`, {
              diameter: 11.2,
              height: 0.48,
              tessellation: 24
            }, this.scene);
            coaster.position.set(stepX, surfaceY + 0.24, stepZ);
            coaster.material = mats.barCoaster || mats.bar;
            coaster.parent = this.clashObstaclesRoot;
            coaster.metadata = stepMeta;

            // Outer beer mug glass cylinder (4x bigger)
            const mugGlass = BABYLON.MeshBuilder.CreateCylinder(`barStep_${hole}_1`, {
              diameter: 9.2,
              height: 14.4,
              tessellation: 24
            }, this.scene);
            mugGlass.position.set(stepX, surfaceY + 0.48 + 7.2, stepZ);
            mugGlass.material = mats.glass || mats.bar;
            mugGlass.parent = this.clashObstaclesRoot;
            mugGlass.metadata = stepMeta;

            // Golden beer liquid inside (4x bigger)
            const beerLiquid = BABYLON.MeshBuilder.CreateCylinder(`barStep_${hole}_1_liquid`, {
              diameter: 7.8,
              height: 12.4,
              tessellation: 24
            }, this.scene);
            beerLiquid.position.set(stepX, surfaceY + 1.1 + 6.2, stepZ);
            beerLiquid.material = mats.beerLiquid || mats.bar;
            beerLiquid.parent = this.clashObstaclesRoot;
            beerLiquid.metadata = stepMeta;

            // White foam head (4x bigger)
            const beerFoam = BABYLON.MeshBuilder.CreateCylinder(`barStep_${hole}_1_foam`, {
              diameter: 8.4,
              height: 3.0,
              tessellation: 24
            }, this.scene);
            beerFoam.position.set(stepX, surfaceY + 13.8, stepZ);
            beerFoam.material = mats.beerFoam || mats.accentWhite;
            beerFoam.parent = this.clashObstaclesRoot;
            beerFoam.metadata = stepMeta;

            // Puffy foam top crown (4x bigger)
            const foamCap = BABYLON.MeshBuilder.CreateCylinder(`barStep_${hole}_1_foamcap`, {
              diameter: 9.0,
              height: 1.2,
              tessellation: 24
            }, this.scene);
            foamCap.position.set(stepX, surfaceY + 15.3, stepZ);
            foamCap.material = mats.beerFoam || mats.accentWhite;
            foamCap.parent = this.clashObstaclesRoot;
            foamCap.metadata = stepMeta;

            // Mug handle (4x bigger and thicker)
            try {
              const handleCurve = [];
              const handleAngle = Math.atan2(nz, nx);
              const cosA = Math.cos(handleAngle);
              const sinA = Math.sin(handleAngle);
              for (let a = -Math.PI / 2; a <= Math.PI / 2; a += Math.PI / 6) {
                const rOut = 4.6 + 2.5 * Math.cos(a);
                const hOut = 7.8 + 4.4 * Math.sin(a);
                handleCurve.push(new BABYLON.Vector3(
                  stepX + cosA * rOut,
                  surfaceY + hOut,
                  stepZ + sinA * rOut
                ));
              }
              const handle = BABYLON.MeshBuilder.CreateTube(`barStep_${hole}_1_handle`, {
                path: handleCurve,
                radius: 0.72,
                tessellation: 12
              }, this.scene);
              handle.material = mats.glass || mats.bar;
              handle.parent = this.clashObstaclesRoot;
              handle.metadata = stepMeta;
            } catch (_) {}

          } else if (s === 2) {
            // STEP 2: GLASS OF WINE 🍷 (4x bigger)
            // Coaster
            const coaster = BABYLON.MeshBuilder.CreateCylinder(`barStep_${hole}_2_coaster`, {
              diameter: 10.4,
              height: 0.48,
              tessellation: 24
            }, this.scene);
            coaster.position.set(stepX, surfaceY + 0.24, stepZ);
            coaster.material = mats.barCoaster || mats.bar;
            coaster.parent = this.clashObstaclesRoot;
            coaster.metadata = stepMeta;

            // Wine glass foot / base (4x bigger)
            const wineBase = BABYLON.MeshBuilder.CreateCylinder(`barStep_${hole}_2`, {
              diameter: 8.4,
              height: 0.72,
              tessellation: 24
            }, this.scene);
            wineBase.position.set(stepX, surfaceY + 0.48 + 0.36, stepZ);
            wineBase.material = mats.glass || mats.bar;
            wineBase.parent = this.clashObstaclesRoot;
            wineBase.metadata = stepMeta;

            // Slender stem (4x bigger)
            const wineStem = BABYLON.MeshBuilder.CreateCylinder(`barStep_${hole}_2_stem`, {
              diameter: 1.28,
              height: 9.2,
              tessellation: 18
            }, this.scene);
            wineStem.position.set(stepX, surfaceY + 1.2 + 4.6, stepZ);
            wineStem.material = mats.glass || mats.bar;
            wineStem.parent = this.clashObstaclesRoot;
            wineStem.metadata = stepMeta;

            // Lower goblet bowl (tapers outward from stem, 4x bigger)
            const bowlLower = BABYLON.MeshBuilder.CreateCylinder(`barStep_${hole}_2_bowl_lower`, {
              diameterBottom: 2.0,
              diameterTop: 9.2,
              height: 6.0,
              tessellation: 24
            }, this.scene);
            bowlLower.position.set(stepX, surfaceY + 10.4 + 3.0, stepZ);
            bowlLower.material = mats.glass || mats.bar;
            bowlLower.parent = this.clashObstaclesRoot;
            bowlLower.metadata = stepMeta;

            // Upper goblet bowl (tapers slightly inward toward rim, 4x bigger)
            const bowlUpper = BABYLON.MeshBuilder.CreateCylinder(`barStep_${hole}_2_bowl_upper`, {
              diameterBottom: 9.2,
              diameterTop: 7.4,
              height: 5.2,
              tessellation: 24
            }, this.scene);
            bowlUpper.position.set(stepX, surfaceY + 16.4 + 2.6, stepZ);
            bowlUpper.material = mats.glass || mats.bar;
            bowlUpper.parent = this.clashObstaclesRoot;
            bowlUpper.metadata = stepMeta;

            // Red wine liquid inside (4x bigger)
            const wineLiquid = BABYLON.MeshBuilder.CreateCylinder(`barStep_${hole}_2_liquid`, {
              diameterBottom: 4.8,
              diameterTop: 8.0,
              height: 5.6,
              tessellation: 24
            }, this.scene);
            wineLiquid.position.set(stepX, surfaceY + 14.2, stepZ);
            wineLiquid.material = mats.wineLiquid || mats.bar;
            wineLiquid.parent = this.clashObstaclesRoot;
            wineLiquid.metadata = stepMeta;

          } else if (s === 3) {
            // STEP 3: SHOT GLASS 🥃 (4x bigger)
            // Coaster
            const coaster = BABYLON.MeshBuilder.CreateCylinder(`barStep_${hole}_3_coaster`, {
              diameter: 9.6,
              height: 0.48,
              tessellation: 24
            }, this.scene);
            coaster.position.set(stepX, surfaceY + 0.24, stepZ);
            coaster.material = mats.barCoaster || mats.bar;
            coaster.parent = this.clashObstaclesRoot;
            coaster.metadata = stepMeta;

            // Solid heavy glass base (sham, 4x bigger)
            const shotBase = BABYLON.MeshBuilder.CreateCylinder(`barStep_${hole}_3`, {
              diameter: 6.8,
              height: 3.2,
              tessellation: 24
            }, this.scene);
            shotBase.position.set(stepX, surfaceY + 0.48 + 1.6, stepZ);
            shotBase.material = mats.glass || mats.bar;
            shotBase.parent = this.clashObstaclesRoot;
            shotBase.metadata = stepMeta;

            // Tapered shot glass body (4x bigger)
            const shotGlass = BABYLON.MeshBuilder.CreateCylinder(`barStep_${hole}_3_glass`, {
              diameterBottom: 6.8,
              diameterTop: 9.2,
              height: 8.4,
              tessellation: 24
            }, this.scene);
            shotGlass.position.set(stepX, surfaceY + 3.68 + 4.2, stepZ);
            shotGlass.material = mats.glass || mats.bar;
            shotGlass.parent = this.clashObstaclesRoot;
            shotGlass.metadata = stepMeta;

            // Amber liquor / spirit inside (4x bigger)
            const shotSpirit = BABYLON.MeshBuilder.CreateCylinder(`barStep_${hole}_3_spirit`, {
              diameterBottom: 5.2,
              diameterTop: 7.4,
              height: 6.4,
              tessellation: 24
            }, this.scene);
            shotSpirit.position.set(stepX, surfaceY + 3.68 + 3.2, stepZ);
            shotSpirit.material = mats.shotSpirit || mats.bar;
            shotSpirit.parent = this.clashObstaclesRoot;
            shotSpirit.metadata = stepMeta;
          }
        }
      }
    });
  }

  /**
   * Renders 3D Timeout penalty boxes with a 3D Dunce Stool, Dunce Cap, and Blackboard
   * standing beside the track instead of the 3 dots.
   * @private
   */
  _render3DTimeouts(spacing, radius, padHeight, mats) {
    const BABYLON = this.BABYLON;
    const settings = this.settings;
    const isClash = this.isClashMode();
    const hasConfigs = Array.isArray(this.options.obstacleConfigs?.timeouts) && this.options.obstacleConfigs.timeouts.length > 0;
    const active = hasConfigs || Boolean(settings?.optTimeout || (isClash && settings?.optTimeout !== false));
    if (!active) return;
    const timeouts = hasConfigs
      ? this.options.obstacleConfigs.timeouts
      : ObstacleGeometry.getTimeoutConfigs(Object.assign({}, settings, { optTimeout: true, target: this.targetHoles || settings?.target || 481 }));
    if (!Array.isArray(timeouts) || !timeouts.length) return;

    const tracksToMark = this.activeTracks.length > 0 ? this.activeTracks : ['p1'];
    const refTrack = tracksToMark[0] || 'p1';

    timeouts.forEach(item => {
      const hole = item.hole;
      if (hole > this.targetHoles) return;

      // 1. Timeout trigger pad on the active track(s)
      tracksToMark.forEach(who => {
        const pt = this.getHoleCoords(who, hole);
        if (!pt) return;
        const surfaceY = (pt && pt.y !== undefined) ? pt.y : 0.8;

        const disc = BABYLON.MeshBuilder.CreateCylinder(`timeoutTrigger_${who}_${hole}`, {
          diameter: radius * 2.0,
          height: padHeight,
          tessellation: 24
        }, this.scene);
        disc.position.set(pt.x, surfaceY + padHeight / 2 + 0.08, pt.z);
        disc.material = mats.timeout;
        disc.parent = this.clashObstaclesRoot;
        disc.metadata = { obstacle: 'timeout-cell', hole, who };

        // Outer corner boundary ring
        const outerRing = BABYLON.MeshBuilder.CreateCylinder(`timeoutTriggerRing_${who}_${hole}`, {
          diameter: radius * 2.22,
          height: padHeight * 0.75,
          tessellation: 24
        }, this.scene);
        outerRing.position.set(pt.x, surfaceY + (padHeight * 0.75) / 2 + 0.06, pt.z);
        outerRing.material = mats.dunceWood || mats.timeout;
        outerRing.parent = this.clashObstaclesRoot;
        outerRing.metadata = { obstacle: 'timeout-cell', hole, who };
      });

      // 2. 3D Timeout Corner Vignette (Penalty Stool, Dunce Cap, Blackboard) beside track
      const p0 = this.getHoleCoords(refTrack, hole);
      const p1 = this.getHoleCoords(refTrack, Math.min(this.targetHoles, hole + 1));
      if (p0 && p1) {
        const dx = p1.x - p0.x;
        const dz = p1.z - p0.z;
        const len = Math.hypot(dx, dz) || 1;
        const nx = -dz / len;
        const nz = dx / len;
        const surfaceY = (p0 && p0.y !== undefined) ? p0.y : 0.8;

        // Position the timeout corner beside the track
        const cornerDist = (radius * 1.8) + (spacing * 2.1);
        const cornerX = p0.x + nx * cornerDist;
        const cornerZ = p0.z + nz * cornerDist;

        // Parent transform node for the timeout vignette
        const timeoutNode = new BABYLON.TransformNode(`timeoutNode_${hole}`, this.scene);
        timeoutNode.position.set(cornerX, surfaceY, cornerZ);
        timeoutNode.rotation.y = Math.atan2(-nx, -nz);
        timeoutNode.parent = this.clashObstaclesRoot;

        const timeoutMeta = { obstacle: 'timeout-box', hole, who: refTrack };
        const matWood = mats.dunceWood || mats.timeout;

        // (a) Wooden Floor Platform / Corner Rug
        const platform = BABYLON.MeshBuilder.CreateBox(`timeoutPlatform_${hole}`, {
          width: 14.0,
          depth: 14.0,
          height: 0.5
        }, this.scene);
        platform.position.set(0, 0.25, 0);
        platform.material = mats.timeoutPlatform || mats.dunceWood || mats.timeout;
        platform.parent = timeoutNode;
        platform.metadata = timeoutMeta;

        // Platform perimeter trim
        const trim = BABYLON.MeshBuilder.CreateBox(`timeoutTrim_${hole}`, {
          width: 14.4,
          depth: 14.4,
          height: 0.2
        }, this.scene);
        trim.position.set(0, 0.1, 0);
        trim.material = matWood;
        trim.parent = timeoutNode;
        trim.metadata = timeoutMeta;

        // (b) The Tall 3-Legged Wooden Penalty Stool
        // Thick circular wooden seat
        const stoolSeat = BABYLON.MeshBuilder.CreateCylinder(`timeoutStoolSeat_${hole}`, {
          diameter: 4.8,
          height: 0.8,
          tessellation: 24
        }, this.scene);
        stoolSeat.position.set(0, 7.0, 0);
        stoolSeat.material = matWood;
        stoolSeat.parent = timeoutNode;
        stoolSeat.metadata = timeoutMeta;

        // Orange velvet cushion top
        const cushion = BABYLON.MeshBuilder.CreateCylinder(`timeoutStoolCushion_${hole}`, {
          diameter: 4.2,
          height: 0.35,
          tessellation: 24
        }, this.scene);
        cushion.position.set(0, 7.45, 0);
        cushion.material = mats.timeout;
        cushion.parent = timeoutNode;
        cushion.metadata = timeoutMeta;

        // 3 Turned Splayed Stool Legs
        for (let i = 0; i < 3; i++) {
          const ang = (i * 2 * Math.PI) / 3 + Math.PI / 6;
          const topX = 1.4 * Math.cos(ang);
          const topZ = 1.4 * Math.sin(ang);
          const botX = 2.4 * Math.cos(ang);
          const botZ = 2.4 * Math.sin(ang);
          const legPath = [
            new BABYLON.Vector3(botX, 0.5, botZ),
            new BABYLON.Vector3((topX + botX) / 2, 3.8, (topZ + botZ) / 2),
            new BABYLON.Vector3(topX, 6.7, topZ)
          ];
          try {
            const leg = BABYLON.MeshBuilder.CreateTube(`timeoutStoolLeg_${hole}_${i}`, {
              path: legPath,
              radius: 0.32,
              tessellation: 12
            }, this.scene);
            leg.material = matWood;
            leg.parent = timeoutNode;
            leg.metadata = timeoutMeta;
          } catch (_) {}
        }

        // Horizontal wooden rungs connecting stool legs
        for (let i = 0; i < 3; i++) {
          const nextI = (i + 1) % 3;
          const a1 = (i * 2 * Math.PI) / 3 + Math.PI / 6;
          const a2 = (nextI * 2 * Math.PI) / 3 + Math.PI / 6;
          const midR = 1.9;
          const pA = new BABYLON.Vector3(midR * Math.cos(a1), 3.6, midR * Math.sin(a1));
          const pB = new BABYLON.Vector3(midR * Math.cos(a2), 3.6, midR * Math.sin(a2));
          try {
            const rung = BABYLON.MeshBuilder.CreateTube(`timeoutStoolRung_${hole}_${i}`, {
              path: [pA, pB],
              radius: 0.2,
              tessellation: 10
            }, this.scene);
            rung.material = matWood;
            rung.parent = timeoutNode;
            rung.metadata = timeoutMeta;
          } catch (_) {}
        }

        // (c) The Conical White Paper Dunce Cap
        const dunceCone = BABYLON.MeshBuilder.CreateCylinder(`timeoutDunceCone_${hole}`, {
          diameterTop: 0.1,
          diameterBottom: 3.8,
          height: 6.8,
          tessellation: 24
        }, this.scene);
        dunceCone.position.set(0, 7.55 + 3.4, 0);
        dunceCone.material = mats.dunceCap || mats.accentWhite;
        dunceCone.parent = timeoutNode;
        dunceCone.metadata = timeoutMeta;

        // Bold red accent band around base of cap
        const dunceBand = BABYLON.MeshBuilder.CreateCylinder(`timeoutDunceBand_${hole}`, {
          diameter: 3.88,
          height: 0.6,
          tessellation: 24
        }, this.scene);
        dunceBand.position.set(0, 7.55 + 0.35, 0);
        dunceBand.material = mats.dunceRed || mats.timeout;
        dunceBand.parent = timeoutNode;
        dunceBand.metadata = timeoutMeta;

        // Red "D" emblem on front of cap
        const dunceD = BABYLON.MeshBuilder.CreateCylinder(`timeoutDunceEmblem_${hole}`, {
          diameter: 1.2,
          height: 0.35,
          tessellation: 16
        }, this.scene);
        dunceD.rotation.x = Math.PI / 7;
        dunceD.position.set(0, 10.2, 1.45);
        dunceD.material = mats.dunceRed || mats.timeout;
        dunceD.parent = timeoutNode;
        dunceD.metadata = timeoutMeta;

        // (d) Classroom Blackboard with Chalk Writing
        let boardMat = matWood;
        try {
          const chalkTex = new BABYLON.DynamicTexture(`chalkboardTex_${hole}`, { width: 512, height: 256 }, this.scene);
          const ctx = chalkTex.getContext();
          if (ctx) {
            ctx.fillStyle = '#1e2d24';
            ctx.fillRect(0, 0, 512, 256);
            ctx.strokeStyle = '#37474f';
            ctx.lineWidth = 6;
            ctx.strokeRect(10, 10, 492, 236);
            ctx.fillStyle = '#ffb74d';
            ctx.font = 'bold 36px monospace';
            ctx.textAlign = 'center';
            ctx.fillText('⏳ TIMEOUT CORNER ⏳', 256, 52);
            ctx.fillStyle = '#ffffff';
            ctx.font = 'bold 28px sans-serif';
            ctx.fillText('I WILL NOT OVER-PEG', 256, 108);
            ctx.fillStyle = '#ff9800';
            ctx.font = 'bold 52px monospace';
            ctx.fillText('| | |', 256, 178);
            ctx.fillStyle = '#cfd8dc';
            ctx.font = '22px sans-serif';
            ctx.fillText('3 TURNS IN THE CORNER', 256, 224);
            chalkTex.update();

            const chalkMat = new BABYLON.PBRMaterial(`matChalk_${hole}`, this.scene);
            chalkMat.albedoTexture = chalkTex;
            chalkMat.roughness = 0.8;
            boardMat = chalkMat;
          }
        } catch (_) {}

        // Chalkboard Frame
        const boardFrame = BABYLON.MeshBuilder.CreateBox(`timeoutBoardFrame_${hole}`, {
          width: 11.2,
          height: 7.2,
          depth: 0.5
        }, this.scene);
        boardFrame.position.set(0, 9.6, -5.2);
        boardFrame.material = matWood;
        boardFrame.parent = timeoutNode;
        boardFrame.metadata = timeoutMeta;

        // Chalkboard Face
        const boardPlate = BABYLON.MeshBuilder.CreateBox(`timeoutBoardPlate_${hole}`, {
          width: 10.2,
          height: 6.2,
          depth: 0.55
        }, this.scene);
        boardPlate.position.set(0, 9.6, -5.2);
        boardPlate.material = boardMat;
        boardPlate.parent = timeoutNode;
        boardPlate.metadata = timeoutMeta;

        // 2 Upright Easel Legs
        [-4.4, 4.4].forEach((ex, eIdx) => {
          const leg = BABYLON.MeshBuilder.CreateCylinder(`timeoutEaselLeg_${hole}_${eIdx}`, {
            diameter: 0.65,
            height: 13.5,
            tessellation: 14
          }, this.scene);
          leg.position.set(ex, 0.5 + 13.5 / 2, -5.4);
          leg.material = matWood;
          leg.parent = timeoutNode;
          leg.metadata = timeoutMeta;
        });

        // Chalk tray
        const chalkTray = BABYLON.MeshBuilder.CreateBox(`timeoutChalkTray_${hole}`, {
          width: 9.6,
          depth: 0.8,
          height: 0.3
        }, this.scene);
        chalkTray.position.set(0, 6.0, -4.8);
        chalkTray.material = matWood;
        chalkTray.parent = timeoutNode;
        chalkTray.metadata = timeoutMeta;

        // (e) 3 Penalty / Breakout Steps (Steps 1, 2, 3)
        // These replace the 3 dots with 3 penalty step stools
        const stepPositions = [
          { s: 1, x: -4.2, z: 3.4 },
          { s: 2, x: 0, z: 4.6 },
          { s: 3, x: 4.2, z: 3.4 }
        ];
        stepPositions.forEach(st => {
          const stepBlock = BABYLON.MeshBuilder.CreateCylinder(`timeoutStep_${hole}_${st.s}`, {
            diameter: 2.6,
            height: 0.6,
            tessellation: 20
          }, this.scene);
          stepBlock.position.set(st.x, 0.5 + 0.3, st.z);
          stepBlock.material = matWood;
          stepBlock.parent = timeoutNode;
          stepBlock.metadata = { obstacle: 'timeout-step', hole, step: st.s };

          const stepPad = BABYLON.MeshBuilder.CreateCylinder(`timeoutStep_${hole}_${st.s}_pad`, {
            diameter: 2.1,
            height: 0.66,
            tessellation: 20
          }, this.scene);
          stepPad.position.set(st.x, 0.5 + 0.33, st.z);
          stepPad.material = mats.timeout;
          stepPad.parent = timeoutNode;
          stepPad.metadata = { obstacle: 'timeout-step', hole, step: st.s };
        });
      }
    });
  }

  /**
   * Renders 3D Detour bypass as an authentic 3D paved road complete with asphalt road bed,
   * concrete curb shoulders, dashed yellow centerline, orange construction barricades,
   * roadside "DETOUR ➔" diamond signs, and embedded road navigation step markers.
   * @private
   */
  _render3DDetours(spacing, radius, padHeight, mats) {
    const BABYLON = this.BABYLON;
    const settings = this.settings;
    const isClash = this.isClashMode();
    const hasConfigs = Array.isArray(this.options.obstacleConfigs?.detours) && this.options.obstacleConfigs.detours.length > 0;
    const active = hasConfigs || Boolean(settings?.optDetour || (isClash && settings?.optDetour !== false));
    if (!active) return;

    const detours = hasConfigs
      ? this.options.obstacleConfigs.detours
      : ObstacleGeometry.getDetourConfigs(Object.assign({}, settings, { optDetour: true, target: this.targetHoles || settings?.target || 481 }));
    if (!Array.isArray(detours) || !detours.length) return;

    const tracksToMark = this.activeTracks.length > 0 ? this.activeTracks : ['p1'];
    const refTrack = tracksToMark[0] || 'p1';

    const bounds = (typeof this.getBoardBounds === 'function' ? this.getBoardBounds() : null) || this.boardBounds;
    const isClashTarget = this.isClashMode() || this.boardConfig?.brand === 'clash' || this.boardConfig?.id === 'board-cribbage-clash' || this.boardConfig?.id === 'board-cribbage-clash-square';

    detours.forEach(d => {
      if (d.hole > this.targetHoles) return;

      // 1. Detour trigger pad on the active track(s)
      tracksToMark.forEach(who => {
        const pt = this.getHoleCoords(who, d.hole);
        if (!pt) return;
        const surfaceY = (pt && pt.y !== undefined) ? pt.y : 0.8;

        // Asphalt entry pad on the track hole
        const pad = BABYLON.MeshBuilder.CreateCylinder(`detourTrigger_${who}_${d.hole}`, {
          diameter: radius * 2.2,
          height: padHeight,
          tessellation: 24
        }, this.scene);
        pad.position.set(pt.x, surfaceY + padHeight / 2 + 0.08, pt.z);
        pad.material = mats.asphaltRoad || mats.detour;
        pad.parent = this.clashObstaclesRoot;
        pad.metadata = { obstacle: 'detour-box', hole: d.hole, who };

        // Yellow warning border ring
        const ring = BABYLON.MeshBuilder.CreateTorus(`detourTriggerRing_${who}_${d.hole}`, {
          diameter: radius * 1.8,
          thickness: 0.26,
          tessellation: 24
        }, this.scene);
        ring.position.set(pt.x, surfaceY + padHeight + 0.08, pt.z);
        ring.rotation.x = Math.PI / 2;
        ring.material = mats.chevronYellow || mats.detour;
        ring.parent = this.clashObstaclesRoot;
        ring.metadata = { obstacle: 'detour-box', hole: d.hole, who };
      });

      // 2. The 3D Paved Road Bypass branching off and rejoining the track as an authentic loop
      const pStart = this.getHoleCoords(refTrack, d.hole);
      const pRejoin = this.getHoleCoords(refTrack, Math.min(this.targetHoles, d.hole + 1));
      if (pStart && pRejoin) {
        const dx = pRejoin.x - pStart.x;
        const dz = pRejoin.z - pStart.z;
        const trackLen = Math.hypot(dx, dz) || 2.5;
        const Tx = dx / trackLen;
        const Tz = dz / trackLen;
        let nx = -Tz;
        let nz = Tx;

        const surfaceY = (pStart && pStart.y !== undefined) ? pStart.y : 0.8;
        const pMidX = (pStart.x + pRejoin.x) / 2;
        const pMidZ = (pStart.z + pRejoin.z) / 2;

        // Ensure normal consistently points outward away from the center of the board
        if (bounds && bounds.min && bounds.max) {
          const bCenterX = (bounds.min.x + bounds.max.x) / 2;
          const bCenterZ = (bounds.min.z + bounds.max.z) / 2;
          const dPos = Math.hypot(pMidX + nx * 10 - bCenterX, pMidZ + nz * 10 - bCenterZ);
          const dNeg = Math.hypot(pMidX - nx * 10 - bCenterX, pMidZ - nz * 10 - bCenterZ);
          if (dPos < dNeg) {
            nx = -nx;
            nz = -nz;
          }
        }

        const detourMeta = { obstacle: 'detour-box', hole: d.hole, who: refTrack };

        // Dimensions of the detour loop road (50% smaller in length & reach)
        const Rdist = Math.max(7.0, spacing * 2.25);
        const Rn = Math.max(6.0, spacing * 1.9);
        const Rt = Math.max(5.25, spacing * 1.7);
        const betaMax = 2.38; // ~136.4 deg
        const roadWidth = Math.max(3.0, radius * 1.8);
        const numSegments = 60;

        const centerPoints = [];
        const tangents = [];
        const leftEdgePoints = [];
        const rightEdgePoints = [];

        // Build continuous smooth teardrop loop centerline
        for (let i = 0; i <= numSegments; i++) {
          const t = i / numSegments;
          const beta = (t - 0.5) * 2 * betaMax;
          const alongT = Math.sin(beta);
          const alongN = Math.cos(beta);
          const blend = Math.pow(Math.sin(t * Math.PI), 0.7);
          const neckT = (t - 0.5) * trackLen;
          const posT = neckT * (1 - blend) + (alongT * Rt) * blend;
          const cos0 = Math.cos(betaMax);
          const normN = (alongN - cos0) / (1 - cos0);
          const posN = normN * (Rdist + Rn - 1.0);

          const midX = pStart.x + Tx * (trackLen / 2 + posT) + nx * posN;
          const midZ = pStart.z + Tz * (trackLen / 2 + posT) + nz * posN;
          const midY = surfaceY + 0.16;

          centerPoints.push(new BABYLON.Vector3(midX, midY, midZ));
        }

        // Calculate smooth tangents and left/right road shoulder edges
        for (let i = 0; i <= numSegments; i++) {
          let tanX, tanZ;
          if (i === 0) {
            tanX = centerPoints[1].x - centerPoints[0].x;
            tanZ = centerPoints[1].z - centerPoints[0].z;
          } else if (i === numSegments) {
            tanX = centerPoints[numSegments].x - centerPoints[numSegments - 1].x;
            tanZ = centerPoints[numSegments].z - centerPoints[numSegments - 1].z;
          } else {
            tanX = centerPoints[i + 1].x - centerPoints[i - 1].x;
            tanZ = centerPoints[i + 1].z - centerPoints[i - 1].z;
          }
          const tLen = Math.hypot(tanX, tanZ) || 1;
          tanX /= tLen;
          tanZ /= tLen;
          tangents.push({ x: tanX, z: tanZ, heading: Math.atan2(tanX, tanZ) });

          // Normal pointing to the left of the road direction
          const normX = -tanZ;
          const normZ = tanX;

          leftEdgePoints.push(new BABYLON.Vector3(
            centerPoints[i].x + normX * (roadWidth / 2),
            centerPoints[i].y,
            centerPoints[i].z + normZ * (roadWidth / 2)
          ));
          rightEdgePoints.push(new BABYLON.Vector3(
            centerPoints[i].x - normX * (roadWidth / 2),
            centerPoints[i].y,
            centerPoints[i].z - normZ * (roadWidth / 2)
          ));
        }

        // Calculate cumulative arc length along the centerline for even step spacing
        const arcLens = [0];
        let totalCurveLen = 0;
        for (let i = 1; i <= numSegments; i++) {
          totalCurveLen += BABYLON.Vector3.Distance(centerPoints[i], centerPoints[i - 1]);
          arcLens.push(totalCurveLen);
        }

        // (a) Paved Dark Asphalt Road Surface Ribbon
        try {
          const roadBed = BABYLON.MeshBuilder.CreateRibbon(`detourRoadBed_${d.hole}`, {
            pathArray: [leftEdgePoints, rightEdgePoints],
            sideOrientation: BABYLON.Mesh.DOUBLESIDE
          }, this.scene);
          roadBed.material = mats.asphaltRoad || mats.detour;
          roadBed.parent = this.clashObstaclesRoot;
          roadBed.metadata = detourMeta;
        } catch (_) {}

        // (b) Raised Concrete Curbs along the Left (Outer) and Right (Inner) Road Shoulders
        try {
          const curbLeft = BABYLON.MeshBuilder.CreateTube(`detourCurbLeft_${d.hole}`, {
            path: leftEdgePoints.map(p => new BABYLON.Vector3(p.x, p.y + 0.10, p.z)),
            radius: 0.18,
            tessellation: 12
          }, this.scene);
          curbLeft.material = mats.concreteCurb || mats.accentWhite;
          curbLeft.parent = this.clashObstaclesRoot;
          curbLeft.metadata = detourMeta;

          const curbRight = BABYLON.MeshBuilder.CreateTube(`detourCurbRight_${d.hole}`, {
            path: rightEdgePoints.map(p => new BABYLON.Vector3(p.x, p.y + 0.10, p.z)),
            radius: 0.18,
            tessellation: 12
          }, this.scene);
          curbRight.material = mats.concreteCurb || mats.accentWhite;
          curbRight.parent = this.clashObstaclesRoot;
          curbRight.metadata = detourMeta;
        } catch (_) {}

        // (c) Central Landscaped Scenic Lawn & Island inside the loop (zero gap to road)
        const islandCenterX = pStart.x + Tx * (trackLen / 2) + nx * (Rdist * 0.96);
        const islandCenterZ = pStart.z + Tz * (trackLen / 2) + nz * (Rdist * 0.96);
        const islandCenterY = surfaceY + 0.18;
        const islandRadius = Rt * 0.62;

        try {
          // 1. Watertight grass lawn filling from center all the way flush to inner road edge (rightEdgePoints)
          const lawnMesh = new BABYLON.Mesh(`detourLawn_${d.hole}`, this.scene);
          const boundary = [...rightEdgePoints];
          const numBaseSegments = 8;
          const pEndBoundary = rightEdgePoints[numSegments];
          const pStartBoundary = rightEdgePoints[0];
          for (let b = 1; b < numBaseSegments; b++) {
            const frac = b / numBaseSegments;
            boundary.push(new BABYLON.Vector3(
              pEndBoundary.x + (pStartBoundary.x - pEndBoundary.x) * frac,
              surfaceY + 0.16,
              pEndBoundary.z + (pStartBoundary.z - pEndBoundary.z) * frac
            ));
          }

          const positions = [islandCenterX, islandCenterY, islandCenterZ];
          const uvs = [0.5, 0.5];
          const normals = [0, 1, 0];

          let minX = islandCenterX, maxX = islandCenterX, minZ = islandCenterZ, maxZ = islandCenterZ;
          for (const p of boundary) {
            minX = Math.min(minX, p.x);
            maxX = Math.max(maxX, p.x);
            minZ = Math.min(minZ, p.z);
            maxZ = Math.max(maxZ, p.z);
          }
          const rangeX = maxX - minX || 1;
          const rangeZ = maxZ - minZ || 1;

          for (const p of boundary) {
            positions.push(p.x, surfaceY + 0.16, p.z);
            uvs.push((p.x - minX) / rangeX, (p.z - minZ) / rangeZ);
            normals.push(0, 1, 0);
          }

          const indices = [];
          const N = boundary.length;
          for (let i = 0; i < N; i++) {
            const next = (i + 1) % N;
            indices.push(0, next + 1, i + 1);
          }

          const vertexData = new BABYLON.VertexData();
          vertexData.positions = positions;
          vertexData.indices = indices;
          vertexData.normals = normals;
          vertexData.uvs = uvs;
          vertexData.applyToMesh(lawnMesh);
          lawnMesh.material = mats.grassLawn || mats.p1;
          lawnMesh.parent = this.clashObstaclesRoot;
          lawnMesh.metadata = detourMeta;

          // 2. Central elevated grass circle mound in center of lawn
          const island = BABYLON.MeshBuilder.CreateCylinder(`detourIsland_${d.hole}`, {
            diameter: islandRadius * 2,
            height: 0.18,
            tessellation: 32
          }, this.scene);
          island.position.set(islandCenterX, surfaceY + 0.19, islandCenterZ);
          island.material = mats.grassLawn || mats.p1;
          island.parent = this.clashObstaclesRoot;
          island.metadata = detourMeta;

          // Retaining curb ring around the central elevated mound
          const islandCurb = BABYLON.MeshBuilder.CreateTorus(`detourIslandCurb_${d.hole}`, {
            diameter: islandRadius * 2,
            thickness: 0.16,
            tessellation: 32
          }, this.scene);
          islandCurb.position.set(islandCenterX, surfaceY + 0.22, islandCenterZ);
          islandCurb.rotation.x = Math.PI / 2;
          islandCurb.material = mats.concreteCurb || mats.accentWhite;
          islandCurb.parent = this.clashObstaclesRoot;
          islandCurb.metadata = detourMeta;

          // 3. Scenic Overlook Sign in center of island
          const scenicPost = BABYLON.MeshBuilder.CreateCylinder(`detourScenicPost_${d.hole}`, {
            diameter: 0.24,
            height: 2.4,
            tessellation: 12
          }, this.scene);
          scenicPost.position.set(islandCenterX, surfaceY + 1.2, islandCenterZ);
          scenicPost.material = mats.dunceWood || mats.asphaltRoad;
          scenicPost.parent = this.clashObstaclesRoot;
          scenicPost.metadata = detourMeta;

          const scenicBoard = BABYLON.MeshBuilder.CreateBox(`detourScenicBoard_${d.hole}`, {
            width: 2.2,
            height: 1.0,
            depth: 0.18
          }, this.scene);
          scenicBoard.position.set(islandCenterX, surfaceY + 2.0, islandCenterZ);
          scenicBoard.rotation.y = Math.atan2(nx, nz);
          scenicBoard.material = mats.dunceWood || mats.detourOrange;
          scenicBoard.parent = this.clashObstaclesRoot;
          scenicBoard.metadata = detourMeta;
        } catch (_) {}

        // (d) Dashed Yellow Centerline Stripes painted down the middle of the road
        const dashInterval = 2.0;
        for (let targetArc = 1.6; targetArc < totalCurveLen - 1.6; targetArc += dashInterval) {
          let idx = 0;
          while (idx < numSegments && arcLens[idx + 1] < targetArc) idx++;
          const frac = (targetArc - arcLens[idx]) / (arcLens[idx + 1] - arcLens[idx]);
          const dX = centerPoints[idx].x + frac * (centerPoints[idx + 1].x - centerPoints[idx].x);
          const dZ = centerPoints[idx].z + frac * (centerPoints[idx + 1].z - centerPoints[idx].z);
          const dHeading = tangents[idx].heading;

          const dash = BABYLON.MeshBuilder.CreateBox(`detourDash_${d.hole}_${Math.round(targetArc * 10)}`, {
            width: 0.24,
            depth: 0.9,
            height: 0.05
          }, this.scene);
          dash.position.set(dX, surfaceY + 0.19, dZ);
          dash.rotation.y = dHeading;
          dash.material = mats.chevronYellow || mats.detour;
          dash.parent = this.clashObstaclesRoot;
          dash.metadata = detourMeta;
        }

        // (e) Roadside "DETOUR ➔" Diamond Highway Warning Sign at Entrance
        const signX = pStart.x + Tx * (-1.0) + nx * 1.6;
        const signZ = pStart.z + Tz * (-1.0) + nz * 1.6;

        const signPost = BABYLON.MeshBuilder.CreateCylinder(`detourSignPost_${d.hole}`, {
          diameter: 0.24,
          height: 5.4,
          tessellation: 12
        }, this.scene);
        signPost.position.set(signX, surfaceY + 2.7, signZ);
        signPost.material = mats.stopSignPole || mats.accentWhite;
        signPost.parent = this.clashObstaclesRoot;
        signPost.metadata = detourMeta;

        const signDiamond = BABYLON.MeshBuilder.CreateBox(`detourSignDiamond_${d.hole}`, {
          width: 2.2,
          height: 2.2,
          depth: 0.18
        }, this.scene);
        signDiamond.position.set(signX, surfaceY + 4.8, signZ);
        signDiamond.rotation.z = Math.PI / 4;
        signDiamond.rotation.y = Math.atan2(Tx, Tz) + Math.PI / 2;
        signDiamond.material = mats.detourOrange || mats.detour;
        signDiamond.parent = this.clashObstaclesRoot;
        signDiamond.metadata = detourMeta;

        const signArrow = BABYLON.MeshBuilder.CreateBox(`detourSignArrow_${d.hole}`, {
          width: 1.2,
          height: 0.32,
          depth: 0.22
        }, this.scene);
        signArrow.position.set(signX, surfaceY + 4.8, signZ);
        signArrow.rotation.y = Math.atan2(Tx, Tz) + Math.PI / 2;
        signArrow.material = mats.asphaltRoad || mats.carWheels;
        signArrow.parent = this.clashObstaclesRoot;
        signArrow.metadata = detourMeta;

        // (f) Orange & White Striped Construction A-Frame Barricade on main track between holes
        const barX = pMidX;
        const barZ = pMidZ;
        const barHeading = Math.atan2(Tx, Tz);

        const barricadeBoard = BABYLON.MeshBuilder.CreateBox(`detourBarricade_${d.hole}`, {
          width: Math.max(3.2, trackLen * 1.1),
          height: 0.7,
          depth: 0.18
        }, this.scene);
        barricadeBoard.position.set(barX, surfaceY + 1.6, barZ);
        barricadeBoard.rotation.y = barHeading;
        barricadeBoard.material = mats.detourBarricade || mats.detourOrange || mats.detour;
        barricadeBoard.parent = this.clashObstaclesRoot;
        barricadeBoard.metadata = detourMeta;

        [-1.3, 1.3].forEach((legOff, lIdx) => {
          const leg = BABYLON.MeshBuilder.CreateCylinder(`detourBarricadeLeg_${d.hole}_${lIdx}`, {
            diameter: 0.22,
            height: 2.4,
            tessellation: 10
          }, this.scene);
          leg.position.set(barX + Math.cos(barHeading) * legOff, surfaceY + 1.2, barZ - Math.sin(barHeading) * legOff);
          leg.material = mats.accentWhite;
          leg.parent = this.clashObstaclesRoot;
          leg.metadata = detourMeta;
        });

        const beacon = BABYLON.MeshBuilder.CreateCylinder(`detourBeacon_${d.hole}`, {
          diameter: 0.6,
          height: 0.5,
          tessellation: 14
        }, this.scene);
        beacon.position.set(barX, surfaceY + 2.2, barZ);
        beacon.material = mats.chevronYellow || mats.detour;
        beacon.parent = this.clashObstaclesRoot;
        beacon.metadata = detourMeta;

        // (g) Orange Traffic Cones guiding into detour
        [-0.5, 0.8].forEach((cOff, cIdx) => {
          const cX = pStart.x + Tx * (cOff - 0.8) + nx * (2.0 + cIdx * 1.4);
          const cZ = pStart.z + Tz * (cOff - 0.8) + nz * (2.0 + cIdx * 1.4);

          const coneBase = BABYLON.MeshBuilder.CreateBox(`detourConeBase_${d.hole}_${cIdx}`, {
            width: 0.75,
            depth: 0.75,
            height: 0.10
          }, this.scene);
          coneBase.position.set(cX, surfaceY + 0.10, cZ);
          coneBase.material = mats.detourOrange || mats.detour;
          coneBase.parent = this.clashObstaclesRoot;
          coneBase.metadata = detourMeta;

          const coneBody = BABYLON.MeshBuilder.CreateCylinder(`detourCone_${d.hole}_${cIdx}`, {
            diameterTop: 0.14,
            diameterBottom: 0.60,
            height: 1.0,
            tessellation: 16
          }, this.scene);
          coneBody.position.set(cX, surfaceY + 0.58, cZ);
          coneBody.material = mats.detourOrange || mats.detour;
          coneBody.parent = this.clashObstaclesRoot;
          coneBody.metadata = detourMeta;

          const coneCollar = BABYLON.MeshBuilder.CreateCylinder(`detourConeCollar_${d.hole}_${cIdx}`, {
            diameterTop: 0.28,
            diameterBottom: 0.40,
            height: 0.22,
            tessellation: 16
          }, this.scene);
          coneCollar.position.set(cX, surfaceY + 0.62, cZ);
          coneCollar.material = mats.accentWhite;
          coneCollar.parent = this.clashObstaclesRoot;
          coneCollar.metadata = detourMeta;
        });

        // (h) 7 Detour Road Navigation Step Markers evenly distributed by arc length around the loop
        for (let s = 1; s <= 7; s++) {
          const targetArc = (s / 8) * totalCurveLen;
          let idx = 0;
          while (idx < numSegments && arcLens[idx + 1] < targetArc) idx++;
          const frac = (targetArc - arcLens[idx]) / (arcLens[idx + 1] - arcLens[idx]);
          const stepX = centerPoints[idx].x + frac * (centerPoints[idx + 1].x - centerPoints[idx].x);
          const stepZ = centerPoints[idx].z + frac * (centerPoints[idx + 1].z - centerPoints[idx].z);
          const stepHeading = tangents[idx].heading;
          const stepMeta = { obstacle: 'detour-step', hole: d.hole, step: s };

          // Embedded Road Step Pad
          const stepPad = BABYLON.MeshBuilder.CreateCylinder(`detourStep_${d.hole}_${s}`, {
            diameter: Math.max(1.8, radius * 1.6),
            height: padHeight * 0.8,
            tessellation: 20
          }, this.scene);
          stepPad.position.set(stepX, surfaceY + 0.18, stepZ);
          stepPad.material = mats.detour;
          stepPad.parent = this.clashObstaclesRoot;
          stepPad.metadata = stepMeta;

          // Glowing Yellow Reflector Outer Ring
          const stepRing = BABYLON.MeshBuilder.CreateTorus(`detourStep_${d.hole}_${s}_ring`, {
            diameter: Math.max(1.9, radius * 1.8),
            thickness: 0.14,
            tessellation: 20
          }, this.scene);
          stepRing.position.set(stepX, surfaceY + 0.21, stepZ);
          stepRing.rotation.x = Math.PI / 2;
          stepRing.material = mats.chevronYellow || mats.detour;
          stepRing.parent = this.clashObstaclesRoot;
          stepRing.metadata = stepMeta;

          // White Road Navigation Arrow painted on the asphalt
          const arrowHead = BABYLON.MeshBuilder.CreateCylinder(`detourStep_${d.hole}_${s}_arrow`, {
            diameter: 0.8,
            height: 0.05,
            tessellation: 3
          }, this.scene);
          arrowHead.position.set(stepX, surfaceY + 0.22, stepZ);
          arrowHead.rotation.x = Math.PI / 2;
          arrowHead.rotation.y = stepHeading + Math.PI / 6;
          arrowHead.material = mats.roadLineWhite || mats.accentWhite;
          arrowHead.parent = this.clashObstaclesRoot;
          arrowHead.metadata = stepMeta;
        }
      }
    });
  }

  /**
   * Renders 3D Round About rotary rings and 8-hole circle loops.
   * @private
   */
  /**
   * Renders 3D Round About as an authentic road traffic roundabout with circular asphalt roadway,
   * outer concrete curb, dashed lane markings, raised green central island with chevron signs and
   * stone fountain/monument, roadside traffic signs, and 8 navigating miniature sports cars.
   * @private
   */
  _render3DRoundAbouts(spacing, radius, padHeight, mats) {
    const BABYLON = this.BABYLON;
    const settings = this.settings;
    const isClash = this.isClashMode();
    const hasConfigs = Array.isArray(this.options.obstacleConfigs?.roundabouts) && this.options.obstacleConfigs.roundabouts.length > 0;
    const active = hasConfigs || Boolean(settings?.optRoundAbout || (isClash && settings?.optRoundAbout !== false));
    if (!active) return;

    const roundabouts = hasConfigs
      ? this.options.obstacleConfigs.roundabouts
      : ObstacleGeometry.getRoundAboutConfigs(Object.assign({}, settings, { optRoundAbout: true, target: this.targetHoles || settings?.target || 481 }));
    if (!Array.isArray(roundabouts) || !roundabouts.length) return;

    const tracksToMark = this.activeTracks.length > 0 ? this.activeTracks : ['p1'];
    const refTrack = tracksToMark[0] || 'p1';

    const carPaints = [
      '#e53935', // red racer
      '#1e88e5', // electric blue
      '#fbc02d', // sports yellow
      '#fb8c00', // orange speedster
      '#43a047', // emerald green
      '#8e24aa', // deep purple
      '#f5f5f5', // silver metallic
      '#00e5ff'  // cyan turbo
    ];

    roundabouts.forEach(r => {
      if (r.hole > this.targetHoles) return;

      // 1. Entry / Trigger road pad on the active track(s)
      tracksToMark.forEach(who => {
        const pt = this.getHoleCoords(who, r.hole);
        if (!pt) return;
        const surfaceY = (pt && pt.y !== undefined) ? pt.y : 0.8;

        // Asphalt trigger pad with circular road markings
        const pad = BABYLON.MeshBuilder.CreateCylinder(`roundaboutTrigger_${who}_${r.hole}`, {
          diameter: radius * 2.2,
          height: padHeight,
          tessellation: 24
        }, this.scene);
        pad.position.set(pt.x, surfaceY + padHeight / 2 + 0.08, pt.z);
        pad.material = mats.asphaltRoad || mats.roundabout;
        pad.parent = this.clashObstaclesRoot;
        pad.metadata = { obstacle: 'roundabout-box', hole: r.hole, who };

        // Grey circular road marking on the trigger pad
        const ring = BABYLON.MeshBuilder.CreateTorus(`roundaboutTriggerRing_${who}_${r.hole}`, {
          diameter: radius * 1.6,
          thickness: 0.28,
          tessellation: 24
        }, this.scene);
        ring.position.set(pt.x, surfaceY + padHeight + 0.08, pt.z);
        ring.rotation.x = Math.PI / 2;
        ring.material = mats.asphaltRoad || mats.roundabout;
        ring.parent = this.clashObstaclesRoot;
        ring.metadata = { obstacle: 'roundabout-box', hole: r.hole, who };
      });

      // 2. The 3D Road Roundabout System beside the track
      const p0 = this.getHoleCoords(refTrack, r.hole);
      const p1 = this.getHoleCoords(refTrack, Math.min(this.targetHoles, r.hole + 1));
      if (p0 && p1) {
        const dx = p1.x - p0.x;
        const dz = p1.z - p0.z;
        const len = Math.hypot(dx, dz) || 1;
        const nx = -dz / len;
        const nz = dx / len;
        const surfaceY = (p0 && p0.y !== undefined) ? p0.y : 0.8;

        // Roundabout Dimensions
        const rotaryRadius = Math.max(13.0, spacing * 1.65); // centerline of the driving lane
        const laneWidth = 7.0;
        const roadOuterRadius = rotaryRadius + laneWidth / 2; // ~16.5
        const islandRadius = rotaryRadius - laneWidth / 2;    // ~9.5

        // Center coordinates of the circular roundabout
        const distFromTrack = roadOuterRadius + 4.5;
        const cx = p0.x + nx * distFromTrack;
        const cz = p0.z + nz * distFromTrack;

        // Root transform node for this roundabout
        const roundaboutNode = new BABYLON.TransformNode(`roundaboutNode_${r.hole}`, this.scene);
        roundaboutNode.position.set(cx, surfaceY, cz);
        roundaboutNode.parent = this.clashObstaclesRoot;

        const roundaboutMeta = { obstacle: 'roundabout-box', hole: r.hole, who: refTrack };
        const islandMeta = { obstacle: 'roundabout-island', hole: r.hole, who: refTrack };

        // (a) Outer Curb (same grey as grey dots)
        const outerCurb = BABYLON.MeshBuilder.CreateCylinder(`roundabout_${r.hole}_outerCurb`, {
          diameter: (roadOuterRadius + 0.6) * 2,
          height: 0.44,
          tessellation: 48
        }, this.scene);
        outerCurb.position.set(0, 0.22, 0);
        outerCurb.material = mats.asphaltRoad || mats.roundabout;
        outerCurb.parent = roundaboutNode;
        outerCurb.metadata = roundaboutMeta;

        // (b) Dark Asphalt Circulating Roadway Bed (same grey as grey dots)
        const roadBed = BABYLON.MeshBuilder.CreateCylinder(`roundabout_${r.hole}_roadBed`, {
          diameter: roadOuterRadius * 2,
          height: 0.45,
          tessellation: 48
        }, this.scene);
        roadBed.position.set(0, 0.225, 0);
        roadBed.material = mats.asphaltRoad || mats.roundabout;
        roadBed.parent = roundaboutNode;
        roadBed.metadata = roundaboutMeta;

        // (c) Outer Road Shoulder Ring (same grey as grey dots)
        const outerStripe = BABYLON.MeshBuilder.CreateTorus(`roundabout_${r.hole}_outerStripe`, {
          diameter: (roadOuterRadius - 0.2) * 2,
          thickness: 0.22,
          tessellation: 44
        }, this.scene);
        outerStripe.position.set(0, 0.46, 0);
        outerStripe.rotation.x = Math.PI / 2;
        outerStripe.material = mats.asphaltRoad || mats.roundabout;
        outerStripe.parent = roundaboutNode;
        outerStripe.metadata = roundaboutMeta;

        // (d) Dashed Centerline Stripes circling the lane (yellow highway dashes like detour)
        const dashCount = 16;
        for (let d = 0; d < dashCount; d++) {
          const dashAngle = (d / dashCount) * 2 * Math.PI;
          const dX = rotaryRadius * Math.cos(dashAngle);
          const dZ = rotaryRadius * Math.sin(dashAngle);
          const dash = BABYLON.MeshBuilder.CreateBox(`roundabout_${r.hole}_dash_${d}`, {
            width: 0.34,
            depth: 1.8,
            height: 0.05
          }, this.scene);
          dash.position.set(dX, 0.465, dZ);
          dash.rotation.y = -dashAngle + Math.PI / 2;
          dash.material = mats.chevronYellow || mats.asphaltRoad;
          dash.parent = roundaboutNode;
          dash.metadata = roundaboutMeta;
        }

        // (e) Raised Curb around Central Island (same grey as grey dots)
        const islandCurb = BABYLON.MeshBuilder.CreateCylinder(`roundabout_${r.hole}_islandCurb`, {
          diameter: islandRadius * 2,
          height: 0.65,
          tessellation: 40
        }, this.scene);
        islandCurb.position.set(0, 0.325, 0);
        islandCurb.material = mats.asphaltRoad || mats.roundabout;
        islandCurb.parent = roundaboutNode;
        islandCurb.metadata = islandMeta;

        // (f) Lush Green Grass Lawn Mound on Central Island
        const lawn = BABYLON.MeshBuilder.CreateCylinder(`roundabout_${r.hole}_lawn`, {
          diameter: (islandRadius - 0.4) * 2,
          height: 0.8,
          tessellation: 40
        }, this.scene);
        lawn.position.set(0, 0.4, 0);
        lawn.material = mats.grassLawn || mats.roundabout;
        lawn.parent = roundaboutNode;
        lawn.metadata = islandMeta;

        // (g) Central Island Monument & Stone Fountain (same grey as grey dots)
        // Stone Plinth
        const plinth = BABYLON.MeshBuilder.CreateCylinder(`roundabout_${r.hole}_plinth`, {
          diameter: 5.2,
          height: 1.2,
          tessellation: 24
        }, this.scene);
        plinth.position.set(0, 0.8 + 0.6, 0);
        plinth.material = mats.asphaltRoad || mats.roundabout;
        plinth.parent = roundaboutNode;
        plinth.metadata = islandMeta;

        // Monument Pedestal
        const pedestal = BABYLON.MeshBuilder.CreateCylinder(`roundabout_${r.hole}_pedestal`, {
          diameter: 3.2,
          height: 2.2,
          tessellation: 20
        }, this.scene);
        pedestal.position.set(0, 2.0 + 1.1, 0);
        pedestal.material = mats.asphaltRoad || mats.roundabout;
        pedestal.parent = roundaboutNode;
        pedestal.metadata = islandMeta;

        // Glowing Cyan Center Finial Globe
        const finial = BABYLON.MeshBuilder.CreateSphere(`roundabout_${r.hole}_finial`, {
          diameter: 2.2,
          segments: 16
        }, this.scene);
        finial.position.set(0, 4.3 + 1.1, 0);
        finial.material = mats.roundabout;
        finial.parent = roundaboutNode;
        finial.metadata = islandMeta;

        // (h) 4 Curved Chevron Arrow Boards (>>>) circling the island
        const chevronRadius = islandRadius * 0.72;
        for (let q = 0; q < 4; q++) {
          const qAng = q * (Math.PI / 2) + Math.PI / 4;
          const qX = chevronRadius * Math.cos(qAng);
          const qZ = chevronRadius * Math.sin(qAng);

          // Signboard with yellow chevron face
          const signBoard = BABYLON.MeshBuilder.CreateBox(`roundabout_${r.hole}_chevronSign_${q}`, {
            width: 3.4,
            height: 1.5,
            depth: 0.25
          }, this.scene);
          signBoard.position.set(qX, 2.0, qZ);
          signBoard.rotation.y = -qAng + Math.PI / 2;
          signBoard.material = mats.chevronYellow || mats.roundabout;
          signBoard.parent = roundaboutNode;
          signBoard.metadata = islandMeta;

          // 2 Silver signposts
          [-1.2, 1.2].forEach((postOffset, pI) => {
            const tangentNormX = -Math.sin(qAng);
            const tangentNormZ = Math.cos(qAng);
            const pX = qX + tangentNormX * postOffset;
            const pZ = qZ + tangentNormZ * postOffset;
            const post = BABYLON.MeshBuilder.CreateCylinder(`roundabout_${r.hole}_chevronPost_${q}_${pI}`, {
              diameter: 0.2,
              height: 2.2,
              tessellation: 10
            }, this.scene);
            post.position.set(pX, 1.1, pZ);
            post.material = mats.asphaltRoad || mats.stopSignPole;
            post.parent = roundaboutNode;
            post.metadata = islandMeta;
          });
        }

        // (i) Roadside "Roundabout Ahead" Blue Highway Sign near the entry
        // Positioned along the track approach side
        const entryAngle = Math.atan2(p0.z - cz, p0.x - cx);
        const signX = (roadOuterRadius + 2.5) * Math.cos(entryAngle + 0.3);
        const signZ = (roadOuterRadius + 2.5) * Math.sin(entryAngle + 0.3);

        const signPole = BABYLON.MeshBuilder.CreateCylinder(`roundabout_${r.hole}_signPole`, {
          diameter: 0.35,
          height: 8.5,
          tessellation: 14
        }, this.scene);
        signPole.position.set(signX, 4.25, signZ);
        signPole.material = mats.asphaltRoad || mats.stopSignPole;
        signPole.parent = roundaboutNode;
        signPole.metadata = roundaboutMeta;

        // Circular Blue Traffic Sign
        const blueSign = BABYLON.MeshBuilder.CreateCylinder(`roundabout_${r.hole}_blueSign`, {
          diameter: 3.4,
          height: 0.25,
          tessellation: 24
        }, this.scene);
        blueSign.position.set(signX, 8.2, signZ);
        blueSign.rotation.x = Math.PI / 2;
        blueSign.rotation.y = entryAngle + Math.PI;
        blueSign.material = mats.roadSignBlue || mats.roundabout;
        blueSign.parent = roundaboutNode;
        blueSign.metadata = roundaboutMeta;

        // Rotary Symbol ring on the sign face (grey matching road)
        const signSymbol = BABYLON.MeshBuilder.CreateTorus(`roundabout_${r.hole}_signSymbol`, {
          diameter: 2.1,
          thickness: 0.24,
          tessellation: 20
        }, this.scene);
        signSymbol.position.set(signX, 8.2, signZ - 0.15);
        signSymbol.rotation.x = Math.PI / 2;
        signSymbol.rotation.y = entryAngle + Math.PI;
        signSymbol.material = mats.asphaltRoad || mats.roundabout;
        signSymbol.parent = roundaboutNode;
        signSymbol.metadata = roundaboutMeta;

        // (j) 8 Rotary Step Markers with Miniature Sports Cars Navigating the Roundabout
        for (let s = 1; s <= 8; s++) {
          const angle = (s / 8) * Math.PI * 2;
          const stepX = rotaryRadius * Math.cos(angle);
          const stepZ = rotaryRadius * Math.sin(angle);
          const stepHeading = -angle + Math.PI / 2; // tangent heading of traffic flow
          const stepMeta = { obstacle: 'roundabout-step', hole: r.hole, step: s };

          // Circular road pad embedded in asphalt
          const stepPad = BABYLON.MeshBuilder.CreateCylinder(`roundaboutStep_${r.hole}_${s}`, {
            diameter: 2.8,
            height: 0.22,
            tessellation: 24
          }, this.scene);
          stepPad.position.set(stepX, 0.4, stepZ);
          stepPad.material = mats.roundabout;
          stepPad.parent = roundaboutNode;
          stepPad.metadata = stepMeta;

          // Glowing cyan border reflector ring
          const stepRing = BABYLON.MeshBuilder.CreateTorus(`roundaboutStep_${r.hole}_${s}_ring`, {
            diameter: 2.9,
            thickness: 0.16,
            tessellation: 20
          }, this.scene);
          stepRing.position.set(stepX, 0.44, stepZ);
          stepRing.rotation.x = Math.PI / 2;
          stepRing.material = mats.roundabout;
          stepRing.parent = roundaboutNode;
          stepRing.metadata = stepMeta;

          // Stylized Miniature Sports Car driving along the lane
          // Car chassis / body
          const carColor = carPaints[(s - 1) % carPaints.length];
          const carMat = new BABYLON.PBRMaterial(`matCar_${r.hole}_${s}`, this.scene);
          carMat.albedoColor = BABYLON.Color3.FromHexString(carColor);
          carMat.emissiveColor = BABYLON.Color3.FromHexString(carColor).scale(0.35);
          carMat.metallic = 0.8;
          carMat.roughness = 0.25;

          const carBody = BABYLON.MeshBuilder.CreateBox(`roundaboutCar_${r.hole}_${s}`, {
            width: 1.6,
            depth: 3.4,
            height: 0.7
          }, this.scene);
          carBody.position.set(stepX, 0.85, stepZ);
          carBody.rotation.y = stepHeading;
          carBody.material = carMat;
          carBody.parent = roundaboutNode;
          carBody.metadata = stepMeta;

          // Car windshield / cabin
          const carCabin = BABYLON.MeshBuilder.CreateBox(`roundaboutCabin_${r.hole}_${s}`, {
            width: 1.3,
            depth: 1.6,
            height: 0.6
          }, this.scene);
          carCabin.position.set(0, 0.6, -0.2);
          carCabin.material = mats.carWheels || mats.asphaltRoad;
          carCabin.parent = carBody;
          carCabin.metadata = stepMeta;

          // Car rear spoiler
          const carSpoiler = BABYLON.MeshBuilder.CreateBox(`roundaboutSpoiler_${r.hole}_${s}`, {
            width: 1.5,
            depth: 0.35,
            height: 0.35
          }, this.scene);
          carSpoiler.position.set(0, 0.45, -1.5);
          carSpoiler.material = carMat;
          carSpoiler.parent = carBody;
          carSpoiler.metadata = stepMeta;

          // Dual glowing headlights on the car front
          [-0.55, 0.55].forEach((hx, hI) => {
            const hl = BABYLON.MeshBuilder.CreateSphere(`roundaboutLight_${r.hole}_${s}_${hI}`, {
              diameter: 0.38,
              segments: 10
            }, this.scene);
            hl.position.set(hx, 0, 1.72);
            hl.material = mats.headlights || mats.accentWhite;
            hl.parent = carBody;
            hl.metadata = stepMeta;
          });
        }
      }
    });
  }

  /**
   * Renders 3D No Passing Lane caution pads, continuous barrier guide rails, and entry/exit warning pylons.
   * @private
   */
  _render3DNoPassingLanes(spacing, mats) {
    const BABYLON = this.BABYLON;
    const settings = this.settings;
    const isClash = this.isClashMode();
    const hasConfigs = Array.isArray(this.options.obstacleConfigs?.noPassingLanes) && this.options.obstacleConfigs.noPassingLanes.length > 0;
    const active = hasConfigs || Boolean(
      settings?.optNoPassingLanes ||
      settings?.optNoPassing ||
      (isClash && settings?.optNoPassingLanes !== false && settings?.optNoPassing !== false)
    );
    if (!active) return;

    const lanes = hasConfigs
      ? this.options.obstacleConfigs.noPassingLanes
      : ObstacleGeometry.getNoPassingLanes(Object.assign({}, settings, { optNoPassingLanes: true, target: this.targetHoles || settings?.target || 481 }));
    if (!Array.isArray(lanes) || !lanes.length) return;

    // Single bottleneck track for No Passing Single Lane
    const singleTrack = this.activeTracks[0] || 'p1';
    const padHeight = 0.35;
    const dims = this.getPegDimensions('front');
    const markerRadius = (dims.spacing || spacing || 7.75) * 0.40;

    lanes.forEach((lane, idx) => {
      const lanePoints = [];
      for (let h = lane.start; h <= Math.min(this.targetHoles, lane.end); h++) {
        const pt = this.getHoleCoords(singleTrack, h);
        if (!pt) continue;
        const surfaceY = (pt && pt.y !== undefined) ? pt.y : 0.8;
        const padY = surfaceY + padHeight / 2 + 0.05;
        lanePoints.push(new BABYLON.Vector3(pt.x, padY, pt.z));

        // Flat glowing blue caution collar pad sitting directly on top of the board surface (1 row)
        const pad = BABYLON.MeshBuilder.CreateCylinder(`noPassingPad_${singleTrack}_${h}`, {
          diameter: markerRadius * 2.2,
          height: padHeight,
          tessellation: 24
        }, this.scene);
        pad.position.set(pt.x, padY, pt.z);
        pad.material = mats.noPassing;
        pad.parent = this.clashObstaclesRoot;
        pad.metadata = { obstacle: 'no-passing', laneIndex: idx + 1, start: lane.start, end: lane.end, hole: h, who: singleTrack };
      }

      // Continuous 3D glowing blue barrier guide rail along the lane holes on top of the board (1 row)
      if (lanePoints.length >= 2) {
        try {
          const railPath = lanePoints.map(p => new BABYLON.Vector3(p.x, p.y + padHeight / 2 + 0.22, p.z));
          const barrierRail = BABYLON.MeshBuilder.CreateTube(`noPassingRail_${singleTrack}_${lane.start}`, {
            path: railPath,
            radius: 0.36,
            tessellation: 16
          }, this.scene);
          barrierRail.material = mats.noPassingBarrier || mats.noPassing;
          barrierRail.parent = this.clashObstaclesRoot;
          barrierRail.metadata = { obstacle: 'no-passing', laneIndex: idx + 1, start: lane.start, end: lane.end, hole: lane.start, who: singleTrack };
        } catch (_) {}
      }

      // Entry & Exit Warning Pylons / Stanchions at lane start and lane end beside the single lane track on top of the board
      [lane.start, lane.end].forEach((coneHole, cIdx) => {
        const pCone = this.getHoleCoords(singleTrack, coneHole);
        if (pCone) {
          const surfaceY = (pCone && pCone.y !== undefined) ? pCone.y : 0.8;
          const pylonHeight = 3.6;
          const cone = BABYLON.MeshBuilder.CreateCylinder(`noPassingPylon_${idx}_${cIdx}`, {
            diameterTop: 0.45,
            diameterBottom: 1.8,
            height: pylonHeight,
            tessellation: 20
          }, this.scene);
          cone.position.set(pCone.x, surfaceY + pylonHeight / 2 + 0.05, pCone.z);
          cone.material = mats.noPassingCone || mats.noPassing;
          cone.parent = this.clashObstaclesRoot;
          cone.metadata = { obstacle: 'no-passing', laneIndex: idx + 1, start: lane.start, end: lane.end, hole: coneHole, who: singleTrack };
        }
      });
    });
  }

  /**
   * Renders 3D Sign Block runway socket highlights.
   * @private
   */
  _render3DSignBlocks(spacing, radius, padHeight, mats) {
    const BABYLON = this.BABYLON;
    const settings = this.settings;
    const isClash = this.isClashMode();
    const hasConfigs = Array.isArray(this.options.obstacleConfigs?.signs) && this.options.obstacleConfigs.signs.length > 0;
    const active = hasConfigs || Boolean(settings?.optSigns || settings?.optSign || (isClash && settings?.optSigns !== false && settings?.optSign !== false));
    if (!active) return;

    const blocks = hasConfigs
      ? this.options.obstacleConfigs.signs
      : ObstacleGeometry.getSignConfigs(Object.assign({}, settings, { optSigns: true, target: this.targetHoles || settings?.target || 481 }));
    if (!Array.isArray(blocks) || !blocks.length) return;

    const tracksToMark = this.activeTracks.length > 0 ? this.activeTracks : ['p1'];

    const bounds = (typeof this.getBoardBounds === 'function' ? this.getBoardBounds() : null) || this.boardBounds;
    const isClashTarget = isClash || this.boardConfig?.brand === 'clash' || this.boardConfig?.id === 'board-cribbage-clash' || this.boardConfig?.id === 'board-cribbage-clash-square';

    blocks.forEach(b => {
      // 1. Socket pads on each track for every hole in the sign block
      tracksToMark.forEach(who => {
        for (let h = b.start; h <= Math.min(this.targetHoles, b.end); h++) {
          const pt = this.getHoleCoords(who, h);
          if (!pt) continue;
          const surfaceY = (pt && pt.y !== undefined) ? pt.y : 0.8;

          // Highway Green base socket pad on the track hole
          const pad = BABYLON.MeshBuilder.CreateCylinder(`signHole_${who}_${h}`, {
            diameter: radius * 2.2,
            height: padHeight,
            tessellation: 24
          }, this.scene);
          pad.position.set(pt.x, surfaceY + padHeight / 2 + 0.08, pt.z);
          pad.material = mats.highwayGreen || mats.signs;
          pad.parent = this.clashObstaclesRoot;
          pad.metadata = { obstacle: 'sign-block', blockId: b.id, start: b.start, end: b.end, hole: h, who };

          // Inset white highway border ring
          const ring = BABYLON.MeshBuilder.CreateTorus(`signHoleRing_${who}_${h}`, {
            diameter: radius * 1.7,
            thickness: 0.22,
            tessellation: 24
          }, this.scene);
          ring.position.set(pt.x, surfaceY + padHeight + 0.08, pt.z);
          ring.rotation.x = Math.PI / 2;
          ring.material = mats.highwayGreenBorder || mats.accentWhite;
          ring.parent = this.clashObstaclesRoot;
          ring.metadata = { obstacle: 'sign-block', blockId: b.id, start: b.start, end: b.end, hole: h, who };
        }
      });

      // 2. Upright Roadside Highway Signs (Rounded Rectangles) standing beside the reference track
      const refTrack = tracksToMark[0] || 'p1';
      const initialSigns = Array.isArray(b.initialSigns) && b.initialSigns.length
        ? b.initialSigns
        : ['Plus 5', 'Roll The Dice'];
      const signCount = Math.max(2, initialSigns.length);
      const effectiveEnd = Math.min(this.targetHoles, b.end);

      // Pre-compute 2D rounded rectangle shape points for extruded 3D sign head
      const signW = Math.max(7.4, spacing * 2.2);
      const signH = Math.max(4.6, spacing * 1.4);
      const signR = 0.8;
      const poleHeight = Math.max(12.0, spacing * 3.6);
      const poleDiameter = 0.42;

      const roundedRectShape = [];
      const segs = 6;
      // Top-right corner
      for (let i = 0; i <= segs; i++) {
        const a = (i / segs) * Math.PI / 2;
        roundedRectShape.push(new BABYLON.Vector3(signW / 2 - signR + Math.cos(a) * signR, signH / 2 - signR + Math.sin(a) * signR, 0));
      }
      // Top-left corner
      for (let i = 0; i <= segs; i++) {
        const a = Math.PI / 2 + (i / segs) * Math.PI / 2;
        roundedRectShape.push(new BABYLON.Vector3(-signW / 2 + signR + Math.cos(a) * signR, signH / 2 - signR + Math.sin(a) * signR, 0));
      }
      // Bottom-left corner
      for (let i = 0; i <= segs; i++) {
        const a = Math.PI + (i / segs) * Math.PI / 2;
        roundedRectShape.push(new BABYLON.Vector3(-signW / 2 + signR + Math.cos(a) * signR, -signH / 2 + signR + Math.sin(a) * signR, 0));
      }
      // Bottom-right corner
      for (let i = 0; i <= segs; i++) {
        const a = 3 * Math.PI / 2 + (i / segs) * Math.PI / 2;
        roundedRectShape.push(new BABYLON.Vector3(signW / 2 - signR + Math.cos(a) * signR, -signH / 2 + signR + Math.sin(a) * signR, 0));
      }
      roundedRectShape.push(roundedRectShape[0]);

      for (let sIdx = 0; sIdx < signCount; sIdx++) {
        const frac = signCount === 1 ? 0.5 : sIdx / (signCount - 1);
        const signHole = Math.round(b.start + frac * (effectiveEnd - b.start));
        const signName = initialSigns[sIdx] || 'BONUS SIGN';

        const p0 = this.getHoleCoords(refTrack, signHole);
        const p1 = this.getHoleCoords(refTrack, Math.min(this.targetHoles, signHole + 1));
        if (!p0) continue;

        let nx = 0, nz = 1, dx = 1, dz = 0;
        if (p1) {
          dx = p1.x - p0.x;
          dz = p1.z - p0.z;
          const len = Math.hypot(dx, dz) || 1;
          dx /= len;
          dz /= len;
          nx = -dz;
          nz = dx;
        }

        // Outward normal check to ensure signpost is positioned on the roadside away from the board center
        if (bounds && bounds.min && bounds.max) {
          const bCenterX = (bounds.min.x + bounds.max.x) / 2;
          const bCenterZ = (bounds.min.z + bounds.max.z) / 2;
          const dPos = Math.hypot(p0.x + nx * 10 - bCenterX, p0.z + nz * 10 - bCenterZ);
          const dNeg = Math.hypot(p0.x - nx * 10 - bCenterX, p0.z - nz * 10 - bCenterZ);
          if (dPos < dNeg) {
            nx = -nx;
            nz = -nz;
          }
        }

        // Alternate post offset distance slightly for clean staggering along the highway
        const staggerDist = spacing * (1.75 + (sIdx % 2) * 0.25);
        const postX = p0.x + nx * staggerDist;
        const postZ = p0.z + nz * staggerDist;
        const postY = ((p0 && p0.y !== undefined) ? p0.y : 0.8) + 0.1;
        const signMeta = { obstacle: 'sign-block', blockId: b.id, signName, hole: signHole, start: b.start, end: effectiveEnd, who: refTrack };

        // Dual silver vertical signposts supporting the highway sign
        [-signW * 0.30, signW * 0.30].forEach((postOff, pI) => {
          const pSideX = postX + dx * postOff;
          const pSideZ = postZ + dz * postOff;
          const post = BABYLON.MeshBuilder.CreateCylinder(`signBlockPost_${b.id}_${sIdx}_${pI}`, {
            diameter: poleDiameter,
            height: poleHeight,
            tessellation: 16
          }, this.scene);
          post.position.set(pSideX, postY + poleHeight / 2, pSideZ);
          post.material = mats.stopSignPole || mats.accentWhite;
          post.parent = this.clashObstaclesRoot;
          post.metadata = signMeta;
        });

        // Horizontal mounting crossbeam behind the sign
        const crossBeam = BABYLON.MeshBuilder.CreateBox(`signBlockBeam_${b.id}_${sIdx}`, {
          width: signW * 0.85,
          height: 0.35,
          depth: 0.35
        }, this.scene);
        crossBeam.position.set(postX, postY + poleHeight - 0.4, postZ);
        crossBeam.rotation.y = Math.atan2(dx, dz);
        crossBeam.material = mats.stopSignPole || mats.accentWhite;
        crossBeam.parent = this.clashObstaclesRoot;
        crossBeam.metadata = signMeta;

        // 3D Extruded Rounded Rectangle Sign Head
        try {
          const extrudePath = [new BABYLON.Vector3(0, 0, -0.16), new BABYLON.Vector3(0, 0, 0.16)];
          const signBody = BABYLON.MeshBuilder.ExtrudeShape(`signBlockHead_${b.id}_${sIdx}`, {
            shape: roundedRectShape,
            path: extrudePath,
            cap: BABYLON.Mesh.CAP_ALL
          }, this.scene);
          signBody.position.set(postX, postY + poleHeight, postZ);
          signBody.rotation.x = -Math.PI / 3.4;
          signBody.rotation.y = Math.atan2(dx, dz) + Math.PI / 2;
          signBody.material = mats.highwayGreen || mats.signs;
          signBody.parent = this.clashObstaclesRoot;
          signBody.metadata = signMeta;

          // Front Sign Face with Authentic Highway Interstate Green Graphics
          const dynTex = new BABYLON.DynamicTexture(`signBlockTex_${b.id}_${sIdx}`, { width: 512, height: 320 }, this.scene, true);
          dynTex.hasAlpha = true;
          const ctx = dynTex.getContext();
          ctx.clearRect(0, 0, 512, 320);

          const drawRoundRect = (x, y, w, h, r) => {
            ctx.beginPath();
            ctx.moveTo(x + r, y);
            ctx.lineTo(x + w - r, y);
            ctx.arcTo(x + w, y, x + w, y + r, r);
            ctx.lineTo(x + w, y + h - r);
            ctx.arcTo(x + w, y + h, x + w - r, y + h, r);
            ctx.lineTo(x + r, y + h);
            ctx.arcTo(x, y + h, x, y + h - r, r);
            ctx.lineTo(x, y + r);
            ctx.arcTo(x, y, x + r, y, r);
            ctx.closePath();
          };

          // Outer Highway Green Base
          drawRoundRect(8, 8, 496, 304, 36);
          ctx.fillStyle = '#00703c';
          ctx.fill();

          // Reflective White Inset Border
          drawRoundRect(22, 22, 468, 276, 26);
          ctx.strokeStyle = '#ffffff';
          ctx.lineWidth = 10;
          ctx.stroke();

          // Top Highway Exit / Route Header
          ctx.fillStyle = '#ffffff';
          ctx.font = '700 30px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'top';
          ctx.fillText(`EXIT ${signHole} • CLASH EXPWY`, 256, 36);

          // Horizontal divider
          ctx.strokeStyle = 'rgba(255, 255, 255, 0.7)';
          ctx.lineWidth = 3;
          ctx.beginPath();
          ctx.moveTo(45, 74);
          ctx.lineTo(467, 74);
          ctx.stroke();

          // Main Sign Title (Action/Reward)
          ctx.fillStyle = '#ffffff';
          const cleanSignName = String(signName || 'SIGN BLOCK').toUpperCase();
          if (cleanSignName.length > 14) {
            ctx.font = '900 44px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif';
          } else if (cleanSignName.length > 9) {
            ctx.font = '900 52px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif';
          } else {
            ctx.font = '900 62px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif';
          }
          ctx.textBaseline = 'middle';
          ctx.fillText(cleanSignName, 256, 150);

          // Bottom Highway Yellow Guide Banner
          ctx.fillStyle = '#ffd600';
          ctx.font = '700 28px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif';
          ctx.fillText('➔  BONUS SIGN  ➔', 256, 240);

          dynTex.update();

          const facePlane = BABYLON.MeshBuilder.CreatePlane(`signBlockFace_${b.id}_${sIdx}`, {
            width: signW * 0.98,
            height: signH * 0.98
          }, this.scene);
          facePlane.position.set(0, 0, 0.18);
          facePlane.parent = signBody;

          const faceMat = new BABYLON.PBRMaterial(`matSignFace_${b.id}_${sIdx}`, this.scene);
          faceMat.albedoTexture = dynTex;
          faceMat.emissiveTexture = dynTex;
          faceMat.emissiveColor = new BABYLON.Color3(0.85, 0.85, 0.85);
          faceMat.roughness = 0.3;
          faceMat.metallic = 0.1;
          facePlane.material = faceMat;
          facePlane.metadata = signMeta;
        } catch (_) {}
      }
    });
  }

  /**
   * Renders 3D physical dice clusters (3 dice in a felt casino tray) and track sockets for Dice challenge spaces.
   * @private
   */
  _render3DDice(spacing, radius, padHeight, mats) {
    const BABYLON = this.BABYLON;
    const settings = this.settings;
    const isClash = this.isClashMode();
    const hasConfigs = Array.isArray(this.options.obstacleConfigs?.dice) && this.options.obstacleConfigs.dice.length > 0;
    const active = hasConfigs || Boolean(settings?.optDice || (isClash && settings?.optDice !== false && [241, 361, 481].includes(Number(settings?.target))));
    if (!active) return;

    const diceList = hasConfigs
      ? this.options.obstacleConfigs.dice
      : ObstacleGeometry.getDiceConfigs(Object.assign({}, settings, { optDice: true, target: this.targetHoles || settings?.target || 481 }));
    if (!Array.isArray(diceList) || !diceList.length) return;

    const tracksToMark = this.activeTracks.length > 0 ? this.activeTracks : ['p1'];

    // 1. Prepare shared 6-face texture atlas for physical 3D dice
    let diceTex = this._diceSharedTexture;
    if (!diceTex && BABYLON.DynamicTexture) {
      try {
        diceTex = new BABYLON.DynamicTexture('diceSharedTex', { width: 1536, height: 256 }, this.scene, false);
        const ctx = diceTex.getContext();
        if (ctx) {
          const pipR = 21;
          const pipRedR = 34;

          const drawDieFace = (fIdx) => {
            const ox = fIdx * 256;
            ctx.fillStyle = '#f8fafc';
            ctx.fillRect(ox, 0, 256, 256);

            ctx.strokeStyle = '#cbd5e1';
            ctx.lineWidth = 10;
            ctx.strokeRect(ox + 5, 5, 246, 246);

            const grad = ctx.createRadialGradient(ox + 128, 128, 10, ox + 128, 128, 140);
            grad.addColorStop(0, 'rgba(255, 255, 255, 0.95)');
            grad.addColorStop(1, 'rgba(226, 232, 240, 0.4)');
            ctx.fillStyle = grad;
            ctx.fillRect(ox + 8, 8, 240, 240);
          };

          const drawPip = (ox, cx, cy, isRed = false) => {
            const r = isRed ? pipRedR : pipR;
            ctx.beginPath();
            ctx.arc(ox + cx, cy + 2, r, 0, Math.PI * 2);
            ctx.fillStyle = isRed ? '#7f1d1d' : '#0f172a';
            ctx.fill();

            ctx.beginPath();
            ctx.arc(ox + cx, cy, r, 0, Math.PI * 2);
            ctx.fillStyle = isRed ? '#dc2626' : '#1e293b';
            ctx.fill();

            ctx.beginPath();
            ctx.arc(ox + cx - r * 0.32, cy - r * 0.32, r * 0.28, 0, Math.PI * 2);
            ctx.fillStyle = isRed ? 'rgba(254, 202, 202, 0.8)' : 'rgba(255, 255, 255, 0.55)';
            ctx.fill();
          };

          for (let f = 0; f < 6; f++) {
            drawDieFace(f);
            const ox = f * 256;
            if (f === 0) {
              drawPip(ox, 128, 128, true);
            } else if (f === 1) {
              [68, 188].forEach(x => {
                [64, 128, 192].forEach(y => drawPip(ox, x, y));
              });
            } else if (f === 2) {
              drawPip(ox, 72, 72);
              drawPip(ox, 184, 184);
            } else if (f === 3) {
              drawPip(ox, 72, 72);
              drawPip(ox, 184, 72);
              drawPip(ox, 128, 128);
              drawPip(ox, 72, 184);
              drawPip(ox, 184, 184);
            } else if (f === 4) {
              drawPip(ox, 72, 72);
              drawPip(ox, 128, 128);
              drawPip(ox, 184, 184);
            } else if (f === 5) {
              drawPip(ox, 72, 72);
              drawPip(ox, 184, 72);
              drawPip(ox, 72, 184);
              drawPip(ox, 184, 184);
            }
          }
          diceTex.update();
          this._diceSharedTexture = diceTex;
        }
      } catch (_) {}
    }

    const diceMat = new BABYLON.PBRMaterial('mat3D_DiePhysical', this.scene);
    if (diceTex) {
      diceMat.albedoTexture = diceTex;
      diceMat.emissiveTexture = diceTex;
      diceMat.emissiveColor = new BABYLON.Color3(0.12, 0.12, 0.12);
    } else {
      diceMat.albedoColor = new BABYLON.Color3(0.96, 0.96, 0.94);
    }
    diceMat.roughness = 0.22;
    diceMat.metallic = 0.04;

    const faceUV = [
      new BABYLON.Vector4(0 / 6, 0, 1 / 6, 1),
      new BABYLON.Vector4(1 / 6, 0, 2 / 6, 1),
      new BABYLON.Vector4(2 / 6, 0, 3 / 6, 1),
      new BABYLON.Vector4(3 / 6, 0, 4 / 6, 1),
      new BABYLON.Vector4(4 / 6, 0, 5 / 6, 1),
      new BABYLON.Vector4(5 / 6, 0, 6 / 6, 1)
    ];

    diceList.forEach(item => {
      const hole = item.hole;
      if (hole > this.targetHoles) return;

      // 1. Purple dice socket pad on active tracks
      tracksToMark.forEach(who => {
        const pt = this.getHoleCoords(who, hole);
        if (!pt) return;
        const surfaceY = (pt && pt.y !== undefined) ? pt.y : 0.8;

        const pad = BABYLON.MeshBuilder.CreateCylinder(`diceTrigger_${who}_${hole}`, {
          diameter: radius * 2.2,
          height: padHeight,
          tessellation: 24
        }, this.scene);
        pad.position.set(pt.x, surfaceY + padHeight / 2 + 0.08, pt.z);
        pad.material = mats.dicePad || mats.timeout;
        pad.parent = this.clashObstaclesRoot;
        pad.metadata = { obstacle: 'dice-box', hole, who };

        // Outer glow/accent ring
        const outerRing = BABYLON.MeshBuilder.CreateCylinder(`diceTriggerRing_${who}_${hole}`, {
          diameter: radius * 2.45,
          height: padHeight * 0.7,
          tessellation: 24
        }, this.scene);
        outerRing.position.set(pt.x, surfaceY + (padHeight * 0.7) / 2 + 0.06, pt.z);
        outerRing.material = mats.dicePadRing || mats.diceGoldTrim || mats.accentWhite;
        outerRing.parent = this.clashObstaclesRoot;
        outerRing.metadata = { obstacle: 'dice-box', hole, who };
      });

      // 2. 3D Dice Display beside track (3 physical dice inside a felt tray)
      const refTrack = tracksToMark[0] || 'p1';
      const p0 = this.getHoleCoords(refTrack, hole);
      const p1 = this.getHoleCoords(refTrack, Math.min(this.targetHoles, hole + 1));
      if (!p0 || !p1) return;

      const dx = p1.x - p0.x;
      const dz = p1.z - p0.z;
      const len = Math.hypot(dx, dz) || 1;
      const nx = -dz / len;
      const nz = dx / len;
      const surfaceY = (p0 && p0.y !== undefined) ? p0.y : 0.8;

      const trayDist = (radius * 1.8) + (spacing * 2.3);
      const trayX = p0.x + nx * trayDist;
      const trayZ = p0.z + nz * trayDist;

      const diceNode = new BABYLON.TransformNode(`diceNode_${hole}`, this.scene);
      diceNode.position.set(trayX, surfaceY, trayZ);
      diceNode.rotation.y = Math.atan2(-nx, -nz);
      diceNode.parent = this.clashObstaclesRoot;

      const diceMeta = { obstacle: 'dice-box', hole, who: refTrack };

      // (a) Casino Felt Dice Tray
      const trayDiam = Math.max(14.0, spacing * 3.4);
      const trayBase = BABYLON.MeshBuilder.CreateCylinder(`diceTrayBase_${hole}`, {
        diameter: trayDiam,
        height: 0.6,
        tessellation: 28
      }, this.scene);
      trayBase.position.set(0, 0.3, 0);
      trayBase.material = mats.diceTrayBase || mats.dunceWood;
      trayBase.parent = diceNode;
      trayBase.metadata = diceMeta;

      // Inner Emerald Felt Lining
      const felt = BABYLON.MeshBuilder.CreateCylinder(`diceFelt_${hole}`, {
        diameter: trayDiam * 0.9,
        height: 0.65,
        tessellation: 28
      }, this.scene);
      felt.position.set(0, 0.32, 0);
      felt.material = mats.diceFelt || mats.grassLawn;
      felt.parent = diceNode;
      felt.metadata = diceMeta;

      // Outer Polished Mahogany Rim
      const rim = BABYLON.MeshBuilder.CreateTorus(`diceTrayRim_${hole}`, {
        diameter: trayDiam * 0.95,
        thickness: 0.7,
        tessellation: 28
      }, this.scene);
      rim.position.set(0, 0.75, 0);
      rim.material = mats.diceTrayRim || mats.dunceWood;
      rim.parent = diceNode;
      rim.metadata = diceMeta;

      // Brass accent trim ring
      const goldTrim = BABYLON.MeshBuilder.CreateTorus(`diceGoldTrim_${hole}`, {
        diameter: trayDiam * 0.88,
        thickness: 0.2,
        tessellation: 28
      }, this.scene);
      goldTrim.position.set(0, 0.7, 0);
      goldTrim.material = mats.diceGoldTrim || mats.cageLock;
      goldTrim.parent = diceNode;
      goldTrim.metadata = diceMeta;

      // (b) 3 Physical 3D Dice!
      const dieSize = Math.max(3.2, spacing * 0.82);

      // Die 1: Resting flat on the felt, angled slightly (showing 3 pips)
      const die1 = BABYLON.MeshBuilder.CreateBox(`die1_${hole}`, { size: dieSize, faceUV }, this.scene);
      die1.position.set(-dieSize * 0.74, 0.62 + dieSize / 2, -dieSize * 0.43);
      die1.rotation.set(0, 0.35, 0);
      die1.material = diceMat;
      die1.parent = diceNode;
      die1.metadata = diceMeta;

      // Die 2: Resting flat on the felt with a small gap, angled (showing 6 pips)
      const die2 = BABYLON.MeshBuilder.CreateBox(`die2_${hole}`, { size: dieSize, faceUV }, this.scene);
      die2.position.set(dieSize * 0.74, 0.62 + dieSize / 2, -dieSize * 0.43);
      die2.rotation.set(-Math.PI / 2, 0, -0.40);
      die2.material = diceMat;
      die2.parent = diceNode;
      die2.metadata = diceMeta;

      // Die 3: Resting in front with a small gap, showing the red 1-pip face upright!
      const die3 = BABYLON.MeshBuilder.CreateBox(`die3_${hole}`, { size: dieSize, faceUV }, this.scene);
      die3.position.set(0, 0.62 + dieSize / 2, dieSize * 0.76);
      die3.rotation.set(Math.PI / 2, 0, 0.60);
      die3.material = diceMat;
      die3.parent = diceNode;
      die3.metadata = diceMeta;
    });
  }

  /**
   * Renders physical 3D Spinning Wheels (pedestal stand, prize wheel disc with dynamic texture,
   * center hub, pointer flipper, and track socket pad) beside the track for Clash mode.
   * @private
   */
  _render3DSpinningWheels(spacing, radius, padHeight, mats) {
    const BABYLON = this.BABYLON;
    const settings = this.settings;
    const isClash = this.isClashMode();
    const hasConfigs = Array.isArray(this.options.obstacleConfigs?.wheels) && this.options.obstacleConfigs.wheels.length > 0;
    const active = hasConfigs || Boolean(
      settings?.optWheel ||
      settings?.optSpinningWheel ||
      (isClash && settings?.optWheel !== false && settings?.optSpinningWheel !== false && [241, 361, 481].includes(Number(settings?.target)))
    );
    if (!active) return;

    const wheelsList = hasConfigs
      ? this.options.obstacleConfigs.wheels
      : ObstacleGeometry.getWheelConfigs(Object.assign({}, settings, { optWheel: true, target: this.targetHoles || settings?.target || 481 }));
    if (!Array.isArray(wheelsList) || !wheelsList.length) return;

    const tracksToMark = this.activeTracks.length > 0 ? this.activeTracks : ['p1'];

    // 1. Prepare shared 8-segment dynamic prize wheel texture
    let wheelTex = this._wheelSharedTexture;
    if (!wheelTex && BABYLON.DynamicTexture) {
      try {
        wheelTex = new BABYLON.DynamicTexture('wheelSharedTex', { width: 512, height: 512 }, this.scene, false);
        const ctx = wheelTex.getContext();
        if (ctx) {
          const cx = 256;
          const cy = 256;
          const outerR = 246;
          const numSlices = 8;
          const sliceAngle = (Math.PI * 2) / numSlices;
          const colors = [
            '#10b981', '#ef4444', '#06b6d4', '#f97316',
            '#6366f1', '#a855f7', '#eab308', '#ec4899'
          ];
          const sliceLabels = ['+10', 'TRAP', 'PEGS', '+15', 'ROLL', 'FREE', '+20', 'SPIN'];

          for (let i = 0; i < numSlices; i++) {
            const startA = i * sliceAngle;
            const endA = startA + sliceAngle;
            ctx.beginPath();
            ctx.moveTo(cx, cy);
            ctx.arc(cx, cy, outerR, startA, endA);
            ctx.closePath();
            ctx.fillStyle = colors[i % colors.length];
            ctx.fill();

            // Inner divider lines
            ctx.strokeStyle = '#f59e0b';
            ctx.lineWidth = 5;
            ctx.stroke();

            // Slice label text
            ctx.save();
            ctx.translate(cx, cy);
            ctx.rotate(startA + sliceAngle / 2);
            ctx.textAlign = 'right';
            ctx.fillStyle = '#ffffff';
            ctx.font = 'bold 26px sans-serif';
            ctx.shadowColor = 'rgba(0,0,0,0.8)';
            ctx.shadowBlur = 6;
            ctx.fillText(sliceLabels[i], outerR - 26, 9);
            ctx.restore();
          }

          // Outer golden rim ring with pins
          ctx.beginPath();
          ctx.arc(cx, cy, outerR, 0, Math.PI * 2);
          ctx.strokeStyle = '#f59e0b';
          ctx.lineWidth = 14;
          ctx.stroke();

          for (let p = 0; p < numSlices * 2; p++) {
            const pinAngle = (p * Math.PI) / numSlices;
            const px = cx + (outerR - 7) * Math.cos(pinAngle);
            const py = cy + (outerR - 7) * Math.sin(pinAngle);
            ctx.beginPath();
            ctx.arc(px, py, 5, 0, Math.PI * 2);
            ctx.fillStyle = '#ffffff';
            ctx.fill();
            ctx.strokeStyle = '#d97706';
            ctx.lineWidth = 2;
            ctx.stroke();
          }

          // Center prize circle
          ctx.beginPath();
          ctx.arc(cx, cy, 52, 0, Math.PI * 2);
          ctx.fillStyle = '#1e1b4b';
          ctx.fill();
          ctx.strokeStyle = '#fbbf24';
          ctx.lineWidth = 6;
          ctx.stroke();

          // Star icon in center
          ctx.fillStyle = '#fbbf24';
          ctx.font = 'bold 36px sans-serif';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText('★', cx, cy);

          wheelTex.update();
          this._wheelSharedTexture = wheelTex;
        }
      } catch (_) {}
    }

    const wheelFaceMat = new BABYLON.PBRMaterial('mat3D_WheelFace', this.scene);
    if (wheelTex) {
      wheelFaceMat.albedoTexture = wheelTex;
      wheelFaceMat.emissiveTexture = wheelTex;
      wheelFaceMat.emissiveColor = new BABYLON.Color3(0.22, 0.22, 0.22);
    } else {
      wheelFaceMat.albedoColor = new BABYLON.Color3(0.92, 0.3, 0.6);
    }
    wheelFaceMat.roughness = 0.28;
    wheelFaceMat.metallic = 0.12;

    if (!this._wheelRenderObservers) {
      this._wheelRenderObservers = [];
    }

    wheelsList.forEach(item => {
      const hole = item.hole;
      if (hole > this.targetHoles) return;

      // 1. Glowing magenta socket pad on active tracks
      tracksToMark.forEach(who => {
        const pt = this.getHoleCoords(who, hole);
        if (!pt) return;
        const surfaceY = (pt && pt.y !== undefined) ? pt.y : 0.8;

        const pad = BABYLON.MeshBuilder.CreateCylinder(`wheelTrigger_${who}_${hole}`, {
          diameter: radius * 2.2,
          height: padHeight,
          tessellation: 24
        }, this.scene);
        pad.position.set(pt.x, surfaceY + padHeight / 2 + 0.08, pt.z);
        pad.material = mats.wheelPad || mats.timeout;
        pad.parent = this.clashObstaclesRoot;
        pad.metadata = { obstacle: 'wheel-box', hole, who };

        const outerRing = BABYLON.MeshBuilder.CreateCylinder(`wheelTriggerRing_${who}_${hole}`, {
          diameter: radius * 2.45,
          height: padHeight * 0.7,
          tessellation: 24
        }, this.scene);
        outerRing.position.set(pt.x, surfaceY + (padHeight * 0.7) / 2 + 0.06, pt.z);
        outerRing.material = mats.wheelPadRing || mats.wheelRim || mats.accentWhite;
        outerRing.parent = this.clashObstaclesRoot;
        outerRing.metadata = { obstacle: 'wheel-box', hole, who };
      });

      // 2. Physical 3D Spinning Wheel Standing Beside Track
      const refTrack = tracksToMark[0] || 'p1';
      const p0 = this.getHoleCoords(refTrack, hole);
      const p1 = this.getHoleCoords(refTrack, Math.min(this.targetHoles, hole + 1));
      if (!p0 || !p1) return;

      const dx = p1.x - p0.x;
      const dz = p1.z - p0.z;
      const len = Math.hypot(dx, dz) || 1;
      const nx = -dz / len;
      const nz = dx / len;
      const surfaceY = (p0 && p0.y !== undefined) ? p0.y : 0.8;

      const wheelDist = (radius * 1.8) + (spacing * 2.4);
      const wheelX = p0.x + nx * wheelDist;
      const wheelZ = p0.z + nz * wheelDist;

      const wheelRootNode = new BABYLON.TransformNode(`wheelRootNode_${hole}`, this.scene);
      wheelRootNode.position.set(wheelX, surfaceY, wheelZ);
      wheelRootNode.rotation.y = Math.atan2(-nx, -nz);
      wheelRootNode.parent = this.clashObstaclesRoot;

      const wheelMeta = { obstacle: 'wheel-box', hole, who: refTrack };

      // (a) Pedestal Base Floor Plate
      const baseDiam = Math.max(10.0, spacing * 2.2);
      const basePlate = BABYLON.MeshBuilder.CreateCylinder(`wheelBasePlate_${hole}`, {
        diameter: baseDiam,
        height: 0.5,
        tessellation: 28
      }, this.scene);
      basePlate.position.set(0, 0.25, 0);
      basePlate.material = mats.wheelStand || mats.dunceWood;
      basePlate.parent = wheelRootNode;
      basePlate.metadata = wheelMeta;

      // Base Golden Bevel Ring
      const baseRing = BABYLON.MeshBuilder.CreateTorus(`wheelBaseRing_${hole}`, {
        diameter: baseDiam * 0.94,
        thickness: 0.25,
        tessellation: 28
      }, this.scene);
      baseRing.position.set(0, 0.48, 0);
      baseRing.material = mats.wheelRim || mats.cageLock;
      baseRing.parent = wheelRootNode;
      baseRing.metadata = wheelMeta;

      // (b) Upright A-Frame Support Legs
      const hubHeight = Math.max(12.5, spacing * 3.2);
      const legRadius = 0.4;
      [-baseDiam * 0.32, baseDiam * 0.32].forEach((lx, lIdx) => {
        const pBottom = new BABYLON.Vector3(lx, 0.5, 0);
        const pTop = new BABYLON.Vector3(0, hubHeight, 0);
        try {
          const leg = BABYLON.MeshBuilder.CreateTube(`wheelLeg_${hole}_${lIdx}`, {
            path: [pBottom, pTop],
            radius: legRadius,
            tessellation: 12
          }, this.scene);
          leg.material = mats.wheelStand;
          leg.parent = wheelRootNode;
          leg.metadata = wheelMeta;
        } catch (_) {}
      });

      // Rear stabilizing strut leg
      try {
        const pRear = new BABYLON.Vector3(0, 0.5, -baseDiam * 0.38);
        const pTop = new BABYLON.Vector3(0, hubHeight, 0);
        const rearStrut = BABYLON.MeshBuilder.CreateTube(`wheelRearStrut_${hole}`, {
          path: [pRear, pTop],
          radius: legRadius * 0.85,
          tessellation: 12
        }, this.scene);
        rearStrut.material = mats.wheelStand;
        rearStrut.parent = wheelRootNode;
        rearStrut.metadata = wheelMeta;
      } catch (_) {}

      // Crossbar between legs
      const crossbar = BABYLON.MeshBuilder.CreateCylinder(`wheelCrossbar_${hole}`, {
        diameter: legRadius * 1.5,
        height: baseDiam * 0.45,
        tessellation: 12
      }, this.scene);
      crossbar.position.set(0, hubHeight * 0.45, 0);
      crossbar.rotation.z = Math.PI / 2;
      crossbar.material = mats.wheelRim;
      crossbar.parent = wheelRootNode;
      crossbar.metadata = wheelMeta;

      // (c) Rotating Prize Wheel Disc
      const wheelDiameter = Math.max(13.0, spacing * 3.1);
      const wheelDiscThickness = 0.55;

      const wheelRotNode = new BABYLON.TransformNode(`wheelRotNode_${hole}`, this.scene);
      wheelRotNode.position.set(0, hubHeight, 0.4);
      wheelRotNode.parent = wheelRootNode;

      const wheelDisc = BABYLON.MeshBuilder.CreateCylinder(`wheelDisc_${hole}`, {
        diameter: wheelDiameter,
        height: wheelDiscThickness,
        tessellation: 48
      }, this.scene);
      wheelDisc.rotation.x = Math.PI / 2;
      wheelDisc.material = wheelFaceMat;
      wheelDisc.parent = wheelRotNode;
      wheelDisc.metadata = wheelMeta;

      // Outer Golden Wheel Rim Ring
      const wheelRimTorus = BABYLON.MeshBuilder.CreateTorus(`wheelRimTorus_${hole}`, {
        diameter: wheelDiameter,
        thickness: 0.5,
        tessellation: 48
      }, this.scene);
      wheelRimTorus.rotation.x = Math.PI / 2;
      wheelRimTorus.material = mats.wheelRim;
      wheelRimTorus.parent = wheelRotNode;
      wheelRimTorus.metadata = wheelMeta;

      // Center Golden Hub Cap
      const hubCap = BABYLON.MeshBuilder.CreateSphere(`wheelHubCap_${hole}`, {
        diameter: wheelDiameter * 0.22,
        segments: 16
      }, this.scene);
      hubCap.position.set(0, 0, wheelDiscThickness / 2 + 0.1);
      hubCap.scaling.set(1, 1, 0.45);
      hubCap.material = mats.wheelHub;
      hubCap.parent = wheelRotNode;
      hubCap.metadata = wheelMeta;

      // (d) Stationary Top Indicator Flipper (Pointer at 12 o'clock)
      const pointer = BABYLON.MeshBuilder.CreateCylinder(`wheelPointer_${hole}`, {
        diameterTop: 0.1,
        diameterBottom: 1.1,
        height: 1.8,
        tessellation: 16
      }, this.scene);
      pointer.position.set(0, hubHeight + (wheelDiameter / 2) - 0.2, 0.7);
      pointer.rotation.x = Math.PI;
      pointer.material = mats.wheelPointer;
      pointer.parent = wheelRootNode;
      pointer.metadata = wheelMeta;

      // (e) Continuous subtle idle spin animation
      const spinSpeed = 0.008 + (Math.random() * 0.004);
      const animObserver = this.scene.onBeforeRenderObservable.add(() => {
        if (wheelRotNode && !wheelRotNode.isDisposed()) {
          wheelRotNode.rotation.z += spinSpeed;
        }
      });
      this._wheelRenderObservers.push(animObserver);
    });
  }

  /**
   * Renders 3D Skunk and Double Skunk Milestone Markers:
   * - Threshold line across the track holes
   * - 1 skunk on each side of the track for Single Skunk Marker
   * - 2 skunks on each side of the track for Double Skunk Marker
   * @private
   */
  _render3DSkunkMarkers(spacing, radius, padHeight, mats) {
    const BABYLON = this.BABYLON;
    const settings = this.settings;
    const isClash = this.isClashMode();
    const active = Boolean(
      settings?.optSkunkMarkers ||
      (isClash && settings?.optSkunkMarkers !== false && [241, 361, 481].includes(Number(settings?.target)))
    );
    if (!active) return;

    const targetHoles = this.targetHoles || settings?.target || 481;
    const skunkHole = settings?.skunkMarkerHole || Math.round(targetHoles * 0.75);
    const doubleSkunkHole = settings?.doubleSkunkMarkerHole || Math.round(targetHoles * 0.5);

    const markers = [
      {
        hole: skunkHole,
        isDouble: false,
        label: 'SKUNK',
        title: 'Skunk Line',
        color: '#38bdf8',
        numSkunksPerSide: 1
      },
      {
        hole: doubleSkunkHole,
        isDouble: true,
        label: 'DBL SKUNK',
        title: 'Double Skunk Line',
        color: '#f59e0b',
        numSkunksPerSide: 2
      }
    ];

    const tracksToMark = this.activeTracks.length > 0 ? this.activeTracks : ['p1'];

    // Prepare shared dynamic texture for skunk medallions
    let skunkTex = this._skunkSharedTexture;
    if (!skunkTex && BABYLON.DynamicTexture) {
      try {
        skunkTex = new BABYLON.DynamicTexture('skunkSharedTex', { width: 512, height: 512 }, this.scene, false);
        const ctx = skunkTex.getContext();
        if (ctx) {
          ctx.clearRect(0, 0, 512, 512);

          // Deep rich dark enamel circle
          ctx.beginPath();
          ctx.arc(256, 256, 238, 0, Math.PI * 2);
          ctx.fillStyle = '#090d16';
          ctx.fill();

          // Outer glowing metallic rim
          ctx.lineWidth = 18;
          ctx.strokeStyle = '#e2e8f0';
          ctx.stroke();

          // Inner gold accent ring
          ctx.beginPath();
          ctx.arc(256, 256, 214, 0, Math.PI * 2);
          ctx.lineWidth = 7;
          ctx.strokeStyle = '#f59e0b';
          ctx.stroke();

          // Skunk emoji centered (enlarged)
          ctx.font = '280px "Segoe UI Emoji", "Apple Color Emoji", "Noto Color Emoji", sans-serif';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText('🦨', 256, 260);

          skunkTex.update();
          this._skunkSharedTexture = skunkTex;
        }
      } catch (_) {}
    }

    const skunkMat = new BABYLON.PBRMaterial('mat3D_SkunkToken', this.scene);
    if (skunkTex) {
      skunkMat.albedoTexture = skunkTex;
      skunkMat.emissiveTexture = skunkTex;
      skunkMat.emissiveColor = new BABYLON.Color3(0.35, 0.35, 0.35);
    } else {
      skunkMat.albedoColor = new BABYLON.Color3(0.12, 0.12, 0.12);
    }
    skunkMat.roughness = 0.25;
    skunkMat.metallic = 0.35;

    markers.forEach(m => {
      const hole = m.hole;
      if (!hole || hole <= 0 || hole > targetHoles) return;

      const pFirst = this.getHoleCoords(tracksToMark[0], hole);
      const pLast = this.getHoleCoords(tracksToMark[tracksToMark.length - 1], hole);
      if (!pFirst) return;

      const pNext = this.getHoleCoords(tracksToMark[0], Math.min(targetHoles, hole + 1));
      const pPrev = this.getHoleCoords(tracksToMark[0], Math.max(1, hole - 1));
      const tdx = (pNext ? pNext.x : pFirst.x) - (pPrev ? pPrev.x : pFirst.x);
      const tdz = (pNext ? pNext.z : pFirst.z) - (pPrev ? pPrev.z : pFirst.z);
      const tLen = Math.hypot(tdx, tdz) || 1;
      const tx = tdx / tLen;
      const tz = tdz / tLen;

      let cx, cz;
      if (pLast && (pLast.x !== pFirst.x || pLast.z !== pFirst.z)) {
        const dx = pLast.x - pFirst.x;
        const dz = pLast.z - pFirst.z;
        const cLen = Math.hypot(dx, dz) || 1;
        cx = dx / cLen;
        cz = dz / cLen;
      } else {
        cx = -tz;
        cz = tx;
      }

      const surfaceY = (pFirst && pFirst.y !== undefined) ? pFirst.y : 0.8;
      const ext = spacing * 0.75;
      const lastPt = pLast || pFirst;

      // 1. Dashed / Threshold Line across the track holes
      const lineP1 = new BABYLON.Vector3(pFirst.x - cx * ext, surfaceY + 0.12, pFirst.z - cz * ext);
      const lineP2 = new BABYLON.Vector3(lastPt.x + cx * ext, surfaceY + 0.12, lastPt.z + cz * ext);

      try {
        const threshLine = BABYLON.MeshBuilder.CreateDashedLines(`skunkThresh_${hole}`, {
          points: [lineP1, lineP2],
          dashSize: 1.5,
          gapSize: 0.8
        }, this.scene);
        threshLine.color = BABYLON.Color3.FromHexString(m.color);
        threshLine.parent = this.clashObstaclesRoot;
        threshLine.metadata = { obstacle: 'skunk-marker', hole, isDouble: m.isDouble };
      } catch (_) {}

      // 2. Draw skunk(s) on each side of the track (doubled size):
      // Single Skunk: 1 skunk on each side
      // Double Skunk: 2 skunks on each side
      const tokenRadius = Math.max(3.0, spacing * 0.76);
      const tokenHeight = 0.55;
      const sideDist = ext + tokenRadius + spacing * 0.4;

      const sides = [
        { x: pFirst.x - cx * sideDist, z: pFirst.z - cz * sideDist, normSign: -1 },
        { x: lastPt.x + cx * sideDist, z: lastPt.z + cz * sideDist, normSign: 1 }
      ];

      sides.forEach((sidePt, sIdx) => {
        const sideNode = new BABYLON.TransformNode(`skunkSideNode_${hole}_${sIdx}`, this.scene);
        sideNode.position.set(sidePt.x, surfaceY, sidePt.z);
        sideNode.parent = this.clashObstaclesRoot;

        // Number of skunks on this side:
        // m.numSkunksPerSide is 1 for single skunk, 2 for double skunk
        const offsets = m.numSkunksPerSide === 1
          ? [0]
          : [-tokenRadius * 1.15, tokenRadius * 1.15];

        offsets.forEach((off, oIdx) => {
          const tokenX = tx * off;
          const tokenZ = tz * off;

          // (a) Polished Wood / Brass Base Pedestal (doubled size)
          const baseCyl = BABYLON.MeshBuilder.CreateCylinder(`skunkBase_${hole}_${sIdx}_${oIdx}`, {
            diameter: tokenRadius * 2.2,
            height: tokenHeight,
            tessellation: 24
          }, this.scene);
          baseCyl.position.set(tokenX, tokenHeight / 2, tokenZ);
          baseCyl.material = mats.diceTrayBase || mats.dunceWood;
          baseCyl.parent = sideNode;
          baseCyl.metadata = { obstacle: 'skunk-marker', hole, isDouble: m.isDouble };

          // (b) Colored Glow Accent Ring around Token
          const accentRing = BABYLON.MeshBuilder.CreateTorus(`skunkRing_${hole}_${sIdx}_${oIdx}`, {
            diameter: tokenRadius * 2.1,
            thickness: 0.28,
            tessellation: 24
          }, this.scene);
          accentRing.position.set(tokenX, tokenHeight, tokenZ);
          accentRing.material = m.isDouble ? (mats.diceGoldTrim || mats.cageLock) : (mats.accentWhite || mats.dicePadRing);
          accentRing.parent = sideNode;
          accentRing.metadata = { obstacle: 'skunk-marker', hole, isDouble: m.isDouble };

          // (c) 3D Skunk Coin / Token Face showing Skunk
          const tokenCyl = BABYLON.MeshBuilder.CreateCylinder(`skunkCoin_${hole}_${sIdx}_${oIdx}`, {
            diameter: tokenRadius * 2.0,
            height: tokenHeight * 0.4,
            tessellation: 24
          }, this.scene);
          tokenCyl.position.set(tokenX, tokenHeight + 0.05, tokenZ);
          tokenCyl.rotation.y = Math.atan2(cx * sidePt.normSign, cz * sidePt.normSign);
          tokenCyl.material = skunkMat;
          tokenCyl.parent = sideNode;
          tokenCyl.metadata = { obstacle: 'skunk-marker', hole, isDouble: m.isDouble };
        });
      });
    });
  }

  /**
   * Renders 3D themed coaster socket pads and upright beverages for active Pints & Pegs drinking spaces.
   * @private
   */
  _render3DPintsCoastersAndBeverages(spacing, radius, padHeight, mats) {
    const BABYLON = this.BABYLON;
    const configs = this.getActivePintsConfigs();
    if (!Array.isArray(configs) || configs.length === 0) return;

    const tracksToMark = this.activeTracks.length > 0 ? this.activeTracks : ['p1'];
    const refTrack = tracksToMark[0] || 'p1';

    configs.forEach(cfg => {
      if (cfg.hole > this.targetHoles) return;

      // 1. Coaster pads on every active track for each hole in the range [hole .. hole + pegs - 1]
      tracksToMark.forEach(who => {
        for (let offset = 0; offset < cfg.pegs; offset++) {
          const currentHole = cfg.hole + offset;
          if (currentHole > this.targetHoles) break;

          const pt = this.getHoleCoords(who, currentHole);
          if (!pt) continue;
          const surfaceY = (pt && pt.y !== undefined) ? pt.y : 0.8;

          const padDiam = radius * 2.3;
          const padH = padHeight * 1.8;
          const padMat = mats[`pad${cfg.type.charAt(0).toUpperCase() + cfg.type.slice(1)}`] || mats.padYouDrink;
          const meta = { obstacle: 'pints-space', type: cfg.type, hole: currentHole, who };

          // Outer beveled themed coaster disc
          const padMesh = BABYLON.MeshBuilder.CreateCylinder(`pintsPad_${who}_${currentHole}_${cfg.type}`, {
            diameter: padDiam,
            height: padH,
            tessellation: 24
          }, this.scene);
          padMesh.position.set(pt.x, surfaceY + padH / 2 + 0.05, pt.z);
          padMesh.material = padMat;
          padMesh.parent = this.pintsObjectsRoot;
          padMesh.metadata = meta;

          // Raised metallic accent rim
          const rimMesh = BABYLON.MeshBuilder.CreateCylinder(`pintsPadRim_${who}_${currentHole}_${cfg.type}`, {
            diameter: padDiam * 0.84,
            height: padH + 0.06,
            tessellation: 24
          }, this.scene);
          rimMesh.position.set(pt.x, surfaceY + (padH + 0.06) / 2 + 0.05, pt.z);
          rimMesh.material = mats.padRim;
          rimMesh.parent = this.pintsObjectsRoot;
          rimMesh.metadata = meta;

          // Inner coaster medallion core
          const coreMesh = BABYLON.MeshBuilder.CreateCylinder(`pintsPadCore_${who}_${currentHole}_${cfg.type}`, {
            diameter: padDiam * 0.58,
            height: padH + 0.12,
            tessellation: 24
          }, this.scene);
          coreMesh.position.set(pt.x, surfaceY + (padH + 0.12) / 2 + 0.05, pt.z);
          coreMesh.material = padMat;
          coreMesh.parent = this.pintsObjectsRoot;
          coreMesh.metadata = meta;
        }
      });

      // 2. Upright 3D Beverage standing on a pub coaster beside the reference track
      const p0 = this.getHoleCoords(refTrack, cfg.hole);
      const p1 = this.getHoleCoords(refTrack, Math.min(this.targetHoles, cfg.hole + 1));
      if (!p0) return;

      let nx = 0;
      let nz = 1;
      if (p1 && (p1.x !== p0.x || p1.z !== p0.z)) {
        const dx = p1.x - p0.x;
        const dz = p1.z - p0.z;
        const len = Math.hypot(dx, dz) || 1;
        nx = -dz / len;
        nz = dx / len;
      }

      const itemDist = spacing * 1.75;
      const itemX = p0.x + nx * itemDist;
      const itemZ = p0.z + nz * itemDist;
      const surfaceY = (p0 && p0.y !== undefined) ? p0.y : 0.8;

      const bevNode = new BABYLON.TransformNode(`pintsBevNode_${cfg.id}`, this.scene);
      bevNode.position.set(itemX, surfaceY, itemZ);
      bevNode.rotation.y = Math.atan2(-nx, -nz);
      bevNode.parent = this.pintsObjectsRoot;

      const bevMeta = { obstacle: 'pints-drink', type: cfg.type, hole: cfg.hole, who: refTrack, id: cfg.id };

      switch (cfg.type) {
        case 'youDrink':
          this._build3DBeerMug(bevNode, mats, bevMeta);
          break;
        case 'guysDrink':
          this._build3DGuysDrink(bevNode, mats, bevMeta);
          break;
        case 'galsDrink':
          this._build3DGalsDrink(bevNode, mats, bevMeta);
          break;
        case 'leftDrink':
          this._build3DLeftDrink(bevNode, mats, bevMeta);
          break;
        case 'rightDrink':
          this._build3DRightDrink(bevNode, mats, bevMeta);
          break;
        case 'everyoneDrink':
          this._build3DEveryoneDrink(bevNode, mats, bevMeta);
          break;
        case 'everyoneButYouDrink':
          this._build3DEveryoneButYouDrink(bevNode, mats, bevMeta);
          break;
        case 'pickWhoDrinks':
          this._build3DShakerAndShot(bevNode, mats, bevMeta);
          break;
        default:
          this._build3DBeerMug(bevNode, mats, bevMeta);
          break;
      }
    });
  }

  /**
   * 1. You Drink: 3D Frosted Beer Mug with Golden Lager & Foam Crown
   * @private
   */
  _build3DBeerMug(parent, mats, meta) {
    const BABYLON = this.BABYLON;

    // Embossed dark oak wooden coaster
    const coaster = BABYLON.MeshBuilder.CreateCylinder(`bevCoaster_${meta.id}`, {
      diameter: 11.0,
      height: 0.5,
      tessellation: 28
    }, this.scene);
    coaster.position.set(0, 0.25, 0);
    coaster.material = mats.coasterWood;
    coaster.parent = parent;
    coaster.metadata = meta;

    // Brass coaster rim
    const coasterRim = BABYLON.MeshBuilder.CreateTorus(`bevCoasterRim_${meta.id}`, {
      diameter: 10.8,
      thickness: 0.25,
      tessellation: 28
    }, this.scene);
    coasterRim.position.set(0, 0.48, 0);
    coasterRim.material = mats.coasterGold;
    coasterRim.parent = parent;
    coasterRim.metadata = meta;

    // Thick glass beer stein body
    const mugGlass = BABYLON.MeshBuilder.CreateCylinder(`bevGlass_${meta.id}`, {
      diameter: 8.8,
      height: 13.0,
      tessellation: 24
    }, this.scene);
    mugGlass.position.set(0, 0.5 + 6.5, 0);
    mugGlass.material = mats.glass;
    mugGlass.parent = parent;
    mugGlass.metadata = meta;

    // Golden amber beer liquid
    const liquid = BABYLON.MeshBuilder.CreateCylinder(`bevLiquid_${meta.id}`, {
      diameter: 7.4,
      height: 11.2,
      tessellation: 24
    }, this.scene);
    liquid.position.set(0, 1.0 + 5.6, 0);
    liquid.material = mats.beerAmber;
    liquid.parent = parent;
    liquid.metadata = meta;

    // Frothy white beer foam head
    const foam = BABYLON.MeshBuilder.CreateCylinder(`bevFoam_${meta.id}`, {
      diameter: 8.0,
      height: 2.6,
      tessellation: 24
    }, this.scene);
    foam.position.set(0, 12.5, 0);
    foam.material = mats.beerFoam;
    foam.parent = parent;
    foam.metadata = meta;

    // Bubbly foam crown cap
    const foamCap = BABYLON.MeshBuilder.CreateCylinder(`bevFoamCap_${meta.id}`, {
      diameter: 8.6,
      height: 1.2,
      tessellation: 24
    }, this.scene);
    foamCap.position.set(0, 13.8, 0);
    foamCap.material = mats.beerFoam;
    foamCap.parent = parent;
    foamCap.metadata = meta;

    // Curved glass mug handle
    try {
      const handlePath = [];
      for (let a = -Math.PI / 2; a <= Math.PI / 2; a += Math.PI / 8) {
        handlePath.push(new BABYLON.Vector3(
          4.4 + 2.2 * Math.cos(a),
          7.0 + 3.8 * Math.sin(a),
          0
        ));
      }
      const handle = BABYLON.MeshBuilder.CreateTube(`bevHandle_${meta.id}`, {
        path: handlePath,
        radius: 0.65,
        tessellation: 12
      }, this.scene);
      handle.material = mats.glass;
      handle.parent = parent;
      handle.metadata = meta;
    } catch (_) {}
  }

  /**
   * 2. Guys Drink: 3D British Nonic Pint Glass with signature bulge band & dark ale
   * @private
   */
  _build3DGuysDrink(parent, mats, meta) {
    const BABYLON = this.BABYLON;

    // Slate pub coaster with cobalt blue rim
    const coaster = BABYLON.MeshBuilder.CreateCylinder(`bevCoaster_${meta.id}`, {
      diameter: 10.6,
      height: 0.48,
      tessellation: 28
    }, this.scene);
    coaster.position.set(0, 0.24, 0);
    coaster.material = mats.coasterSlate;
    coaster.parent = parent;
    coaster.metadata = meta;

    const coasterRim = BABYLON.MeshBuilder.CreateTorus(`bevCoasterRim_${meta.id}`, {
      diameter: 10.4,
      thickness: 0.25,
      tessellation: 28
    }, this.scene);
    coasterRim.position.set(0, 0.46, 0);
    coasterRim.material = mats.padGuysDrink;
    coasterRim.parent = parent;
    coasterRim.metadata = meta;

    // Nonic glass lower body
    const nonicLower = BABYLON.MeshBuilder.CreateCylinder(`bevNonicLower_${meta.id}`, {
      diameterBottom: 6.2,
      diameterTop: 8.2,
      height: 8.0,
      tessellation: 24
    }, this.scene);
    nonicLower.position.set(0, 0.48 + 4.0, 0);
    nonicLower.material = mats.glass;
    nonicLower.parent = parent;
    nonicLower.metadata = meta;

    // Signature Nonic bulge band (curved bulge near upper glass)
    const nonicBulge = BABYLON.MeshBuilder.CreateCylinder(`bevNonicBulge_${meta.id}`, {
      diameterBottom: 8.2,
      diameterTop: 9.4,
      height: 2.8,
      tessellation: 24
    }, this.scene);
    nonicBulge.position.set(0, 8.48 + 1.4, 0);
    nonicBulge.material = mats.glass;
    nonicBulge.parent = parent;
    nonicBulge.metadata = meta;

    const nonicUpper = BABYLON.MeshBuilder.CreateCylinder(`bevNonicUpper_${meta.id}`, {
      diameterBottom: 9.4,
      diameterTop: 8.6,
      height: 3.2,
      tessellation: 24
    }, this.scene);
    nonicUpper.position.set(0, 11.28 + 1.6, 0);
    nonicUpper.material = mats.glass;
    nonicUpper.parent = parent;
    nonicUpper.metadata = meta;

    // Rich dark British ale liquid
    const liquid = BABYLON.MeshBuilder.CreateCylinder(`bevLiquid_${meta.id}`, {
      diameterBottom: 5.4,
      diameterTop: 7.8,
      height: 11.0,
      tessellation: 24
    }, this.scene);
    liquid.position.set(0, 1.0 + 5.5, 0);
    liquid.material = mats.beerDarkAle;
    liquid.parent = parent;
    liquid.metadata = meta;

    // Creamy stout foam head
    const foam = BABYLON.MeshBuilder.CreateCylinder(`bevFoam_${meta.id}`, {
      diameter: 8.4,
      height: 1.8,
      tessellation: 24
    }, this.scene);
    foam.position.set(0, 13.4, 0);
    foam.material = mats.beerFoam;
    foam.parent = parent;
    foam.metadata = meta;
  }

  /**
   * 3. Gals Drink: 3D Stemmed Wine Glass with circular foot, slender stem & ruby wine
   * @private
   */
  _build3DGalsDrink(parent, mats, meta) {
    const BABYLON = this.BABYLON;

    // Polished wood coaster with rose metallic trim
    const coaster = BABYLON.MeshBuilder.CreateCylinder(`bevCoaster_${meta.id}`, {
      diameter: 10.2,
      height: 0.45,
      tessellation: 28
    }, this.scene);
    coaster.position.set(0, 0.225, 0);
    coaster.material = mats.coasterWood;
    coaster.parent = parent;
    coaster.metadata = meta;

    const coasterRim = BABYLON.MeshBuilder.CreateTorus(`bevCoasterRim_${meta.id}`, {
      diameter: 10.0,
      thickness: 0.22,
      tessellation: 28
    }, this.scene);
    coasterRim.position.set(0, 0.42, 0);
    coasterRim.material = mats.padGalsDrink;
    coasterRim.parent = parent;
    coasterRim.metadata = meta;

    // Circular glass foot base
    const foot = BABYLON.MeshBuilder.CreateCylinder(`bevWineFoot_${meta.id}`, {
      diameter: 8.0,
      height: 0.65,
      tessellation: 24
    }, this.scene);
    foot.position.set(0, 0.45 + 0.325, 0);
    foot.material = mats.glass;
    foot.parent = parent;
    foot.metadata = meta;

    // Slender glass stem
    const stem = BABYLON.MeshBuilder.CreateCylinder(`bevWineStem_${meta.id}`, {
      diameter: 1.15,
      height: 8.5,
      tessellation: 18
    }, this.scene);
    stem.position.set(0, 1.1 + 4.25, 0);
    stem.material = mats.glass;
    stem.parent = parent;
    stem.metadata = meta;

    // Tulip goblet lower bowl
    const bowlLower = BABYLON.MeshBuilder.CreateCylinder(`bevWineBowlLower_${meta.id}`, {
      diameterBottom: 1.8,
      diameterTop: 8.6,
      height: 5.5,
      tessellation: 24
    }, this.scene);
    bowlLower.position.set(0, 9.6 + 2.75, 0);
    bowlLower.material = mats.glass;
    bowlLower.parent = parent;
    bowlLower.metadata = meta;

    // Tulip goblet upper bowl
    const bowlUpper = BABYLON.MeshBuilder.CreateCylinder(`bevWineBowlUpper_${meta.id}`, {
      diameterBottom: 8.6,
      diameterTop: 7.0,
      height: 4.8,
      tessellation: 24
    }, this.scene);
    bowlUpper.position.set(0, 15.1 + 2.4, 0);
    bowlUpper.material = mats.glass;
    bowlUpper.parent = parent;
    bowlUpper.metadata = meta;

    // Ruby red wine liquid inside bowl
    const liquid = BABYLON.MeshBuilder.CreateCylinder(`bevLiquid_${meta.id}`, {
      diameterBottom: 4.2,
      diameterTop: 7.6,
      height: 4.8,
      tessellation: 24
    }, this.scene);
    liquid.position.set(0, 13.2, 0);
    liquid.material = mats.wineRuby;
    liquid.parent = parent;
    liquid.metadata = meta;
  }

  /**
   * 4. Left Drink: 3D Frosted Highball with Violet Cocktail, Ice & Lime Wheel pointing Left
   * @private
   */
  _build3DLeftDrink(parent, mats, meta) {
    const BABYLON = this.BABYLON;

    // White marble coaster with sapphire rim
    const coaster = BABYLON.MeshBuilder.CreateCylinder(`bevCoaster_${meta.id}`, {
      diameter: 10.4,
      height: 0.48,
      tessellation: 28
    }, this.scene);
    coaster.position.set(0, 0.24, 0);
    coaster.material = mats.coasterMarble;
    coaster.parent = parent;
    coaster.metadata = meta;

    const coasterRim = BABYLON.MeshBuilder.CreateTorus(`bevCoasterRim_${meta.id}`, {
      diameter: 10.2,
      thickness: 0.24,
      tessellation: 28
    }, this.scene);
    coasterRim.position.set(0, 0.46, 0);
    coasterRim.material = mats.padLeftDrink;
    coasterRim.parent = parent;
    coasterRim.metadata = meta;

    // Heavy glass sham base
    const glassBase = BABYLON.MeshBuilder.CreateCylinder(`bevGlassBase_${meta.id}`, {
      diameter: 7.4,
      height: 2.2,
      tessellation: 24
    }, this.scene);
    glassBase.position.set(0, 0.48 + 1.1, 0);
    glassBase.material = mats.glass;
    glassBase.parent = parent;
    glassBase.metadata = meta;

    // Tall cylindrical highball tumbler
    const glassBody = BABYLON.MeshBuilder.CreateCylinder(`bevGlass_${meta.id}`, {
      diameter: 7.6,
      height: 13.0,
      tessellation: 24
    }, this.scene);
    glassBody.position.set(0, 2.68 + 6.5, 0);
    glassBody.material = mats.glass;
    glassBody.parent = parent;
    glassBody.metadata = meta;

    // Violet cocktail liquid
    const liquid = BABYLON.MeshBuilder.CreateCylinder(`bevLiquid_${meta.id}`, {
      diameter: 6.6,
      height: 10.5,
      tessellation: 24
    }, this.scene);
    liquid.position.set(0, 2.68 + 5.25, 0);
    liquid.material = mats.cocktailViolet;
    liquid.parent = parent;
    liquid.metadata = meta;

    // Floating translucent ice cubes
    const ice1 = BABYLON.MeshBuilder.CreateBox(`bevIce1_${meta.id}`, { size: 2.0 }, this.scene);
    ice1.position.set(-0.8, 7.5, 0.4);
    ice1.rotation.set(0.3, 0.4, 0.2);
    ice1.material = mats.iceCube;
    ice1.parent = parent;
    ice1.metadata = meta;

    const ice2 = BABYLON.MeshBuilder.CreateBox(`bevIce2_${meta.id}`, { size: 1.9 }, this.scene);
    ice2.position.set(0.7, 10.5, -0.3);
    ice2.rotation.set(-0.2, 0.5, -0.3);
    ice2.material = mats.iceCube;
    ice2.parent = parent;
    ice2.metadata = meta;

    // Lime wheel garnish perched on left rim (pointing 👈)
    const lime = BABYLON.MeshBuilder.CreateCylinder(`bevLime_${meta.id}`, {
      diameter: 4.0,
      height: 0.4,
      tessellation: 24
    }, this.scene);
    lime.position.set(-3.6, 15.5, 0);
    lime.rotation.z = -0.45; // Tilted toward -X left
    lime.material = mats.limeGarnish;
    lime.parent = parent;
    lime.metadata = meta;

    // Slender straw leaning toward the left
    const straw = BABYLON.MeshBuilder.CreateCylinder(`bevStraw_${meta.id}`, {
      diameter: 0.42,
      height: 16.0,
      tessellation: 12
    }, this.scene);
    straw.position.set(-1.4, 9.8, 0);
    straw.rotation.z = 0.30; // Angled leaning left
    straw.material = mats.strawCyan;
    straw.parent = parent;
    straw.metadata = meta;
  }

  /**
   * 5. Right Drink: 3D Frosted Highball with Orange Cocktail, Ice & Orange Slice pointing Right
   * @private
   */
  _build3DRightDrink(parent, mats, meta) {
    const BABYLON = this.BABYLON;

    // White marble coaster with vibrant cyan rim
    const coaster = BABYLON.MeshBuilder.CreateCylinder(`bevCoaster_${meta.id}`, {
      diameter: 10.4,
      height: 0.48,
      tessellation: 28
    }, this.scene);
    coaster.position.set(0, 0.24, 0);
    coaster.material = mats.coasterMarble;
    coaster.parent = parent;
    coaster.metadata = meta;

    const coasterRim = BABYLON.MeshBuilder.CreateTorus(`bevCoasterRim_${meta.id}`, {
      diameter: 10.2,
      thickness: 0.24,
      tessellation: 28
    }, this.scene);
    coasterRim.position.set(0, 0.46, 0);
    coasterRim.material = mats.padRightDrink;
    coasterRim.parent = parent;
    coasterRim.metadata = meta;

    // Heavy glass sham base
    const glassBase = BABYLON.MeshBuilder.CreateCylinder(`bevGlassBase_${meta.id}`, {
      diameter: 7.4,
      height: 2.2,
      tessellation: 24
    }, this.scene);
    glassBase.position.set(0, 0.48 + 1.1, 0);
    glassBase.material = mats.glass;
    glassBase.parent = parent;
    glassBase.metadata = meta;

    // Tall cylindrical highball tumbler
    const glassBody = BABYLON.MeshBuilder.CreateCylinder(`bevGlass_${meta.id}`, {
      diameter: 7.6,
      height: 13.0,
      tessellation: 24
    }, this.scene);
    glassBody.position.set(0, 2.68 + 6.5, 0);
    glassBody.material = mats.glass;
    glassBody.parent = parent;
    glassBody.metadata = meta;

    // Sunrise tropical orange cocktail liquid
    const liquid = BABYLON.MeshBuilder.CreateCylinder(`bevLiquid_${meta.id}`, {
      diameter: 6.6,
      height: 10.5,
      tessellation: 24
    }, this.scene);
    liquid.position.set(0, 2.68 + 5.25, 0);
    liquid.material = mats.cocktailOrange;
    liquid.parent = parent;
    liquid.metadata = meta;

    // Floating translucent ice cubes
    const ice1 = BABYLON.MeshBuilder.CreateBox(`bevIce1_${meta.id}`, { size: 2.0 }, this.scene);
    ice1.position.set(0.8, 7.5, 0.4);
    ice1.rotation.set(-0.3, 0.4, -0.2);
    ice1.material = mats.iceCube;
    ice1.parent = parent;
    ice1.metadata = meta;

    const ice2 = BABYLON.MeshBuilder.CreateBox(`bevIce2_${meta.id}`, { size: 1.9 }, this.scene);
    ice2.position.set(-0.7, 10.5, -0.3);
    ice2.rotation.set(0.2, 0.5, 0.3);
    ice2.material = mats.iceCube;
    ice2.parent = parent;
    ice2.metadata = meta;

    // Orange slice garnish perched on right rim (pointing 👉)
    const orange = BABYLON.MeshBuilder.CreateCylinder(`bevOrange_${meta.id}`, {
      diameter: 4.4,
      height: 0.45,
      tessellation: 24
    }, this.scene);
    orange.position.set(3.6, 15.5, 0);
    orange.rotation.z = 0.45; // Tilted toward +X right
    orange.material = mats.orangeGarnish;
    orange.parent = parent;
    orange.metadata = meta;

    // Slender straw leaning toward the right
    const straw = BABYLON.MeshBuilder.CreateCylinder(`bevStraw_${meta.id}`, {
      diameter: 0.42,
      height: 16.0,
      tessellation: 12
    }, this.scene);
    straw.position.set(1.4, 9.8, 0);
    straw.rotation.z = -0.30; // Angled leaning right
    straw.material = mats.strawMagenta;
    straw.parent = parent;
    straw.metadata = meta;
  }

  /**
   * 6. Everyone Drinks: 3D Toasting Clinking Champagne Flutes with bubbly golden champagne
   * @private
   */
  _build3DEveryoneDrink(parent, mats, meta) {
    const BABYLON = this.BABYLON;

    // Double-wide celebratory coaster
    const coaster = BABYLON.MeshBuilder.CreateCylinder(`bevCoaster_${meta.id}`, {
      diameter: 14.0,
      height: 0.48,
      tessellation: 28
    }, this.scene);
    coaster.position.set(0, 0.24, 0);
    coaster.material = mats.coasterSlate;
    coaster.parent = parent;
    coaster.metadata = meta;

    const coasterRim = BABYLON.MeshBuilder.CreateTorus(`bevCoasterRim_${meta.id}`, {
      diameter: 13.8,
      thickness: 0.26,
      tessellation: 28
    }, this.scene);
    coasterRim.position.set(0, 0.46, 0);
    coasterRim.material = mats.padEveryoneDrink;
    coasterRim.parent = parent;
    coasterRim.metadata = meta;

    // Helper for a single champagne flute
    const buildFlute = (sideName, posX, rotZ) => {
      const fluteNode = new BABYLON.TransformNode(`fluteNode_${sideName}_${meta.id}`, this.scene);
      fluteNode.position.set(posX, 0.48, 0);
      fluteNode.rotation.z = rotZ;
      fluteNode.parent = parent;

      const foot = BABYLON.MeshBuilder.CreateCylinder(`fluteFoot_${sideName}_${meta.id}`, {
        diameter: 5.2,
        height: 0.45,
        tessellation: 24
      }, this.scene);
      foot.position.set(0, 0.225, 0);
      foot.material = mats.glass;
      foot.parent = fluteNode;
      foot.metadata = meta;

      const stem = BABYLON.MeshBuilder.CreateCylinder(`fluteStem_${sideName}_${meta.id}`, {
        diameter: 0.85,
        height: 7.5,
        tessellation: 18
      }, this.scene);
      stem.position.set(0, 0.45 + 3.75, 0);
      stem.material = mats.glass;
      stem.parent = fluteNode;
      stem.metadata = meta;

      const bowl = BABYLON.MeshBuilder.CreateCylinder(`fluteBowl_${sideName}_${meta.id}`, {
        diameterBottom: 1.4,
        diameterTop: 4.4,
        height: 8.5,
        tessellation: 24
      }, this.scene);
      bowl.position.set(0, 4.2 + 4.25, 0);
      bowl.material = mats.glass;
      bowl.parent = fluteNode;
      bowl.metadata = meta;

      const liquid = BABYLON.MeshBuilder.CreateCylinder(`fluteLiquid_${sideName}_${meta.id}`, {
        diameterBottom: 1.2,
        diameterTop: 3.8,
        height: 6.5,
        tessellation: 24
      }, this.scene);
      liquid.position.set(0, 4.5 + 3.25, 0);
      liquid.material = mats.champagneGold;
      liquid.parent = fluteNode;
      liquid.metadata = meta;
    };

    // Left flute tilted slightly right (+X)
    buildFlute('left', -2.8, -0.14);
    // Right flute tilted slightly left (-X)
    buildFlute('right', 2.8, 0.14);

    // Toasting Apex Clink Spark
    const spark = BABYLON.MeshBuilder.CreateSphere(`clinkSpark_${meta.id}`, {
      diameter: 1.1,
      segments: 12
    }, this.scene);
    spark.position.set(0, 16.2, 0);
    spark.material = mats.coasterGold;
    spark.parent = parent;
    spark.metadata = meta;
  }

  /**
   * 7. Everyone But You: 3D Champagne Bottle in Chilled Stainless Steel Ice Bucket
   * @private
   */
  _build3DEveryoneButYouDrink(parent, mats, meta) {
    const BABYLON = this.BABYLON;

    // Velvet purple & silver coaster
    const coaster = BABYLON.MeshBuilder.CreateCylinder(`bevCoaster_${meta.id}`, {
      diameter: 13.0,
      height: 0.48,
      tessellation: 28
    }, this.scene);
    coaster.position.set(0, 0.24, 0);
    coaster.material = mats.padEveryoneButYouDrink;
    coaster.parent = parent;
    coaster.metadata = meta;

    const coasterRim = BABYLON.MeshBuilder.CreateTorus(`bevCoasterRim_${meta.id}`, {
      diameter: 12.8,
      thickness: 0.26,
      tessellation: 28
    }, this.scene);
    coasterRim.position.set(0, 0.46, 0);
    coasterRim.material = mats.coasterSilver;
    coasterRim.parent = parent;
    coasterRim.metadata = meta;

    // Stainless steel ice bucket body
    const bucket = BABYLON.MeshBuilder.CreateCylinder(`iceBucket_${meta.id}`, {
      diameterBottom: 9.0,
      diameterTop: 11.6,
      height: 8.5,
      tessellation: 28
    }, this.scene);
    bucket.position.set(0, 0.48 + 4.25, 0);
    bucket.material = mats.iceBucketSteel;
    bucket.parent = parent;
    bucket.metadata = meta;

    // Rolled steel rim
    const bucketRim = BABYLON.MeshBuilder.CreateTorus(`iceBucketRim_${meta.id}`, {
      diameter: 11.6,
      thickness: 0.45,
      tessellation: 28
    }, this.scene);
    bucketRim.position.set(0, 0.48 + 8.5, 0);
    bucketRim.material = mats.iceBucketSteel;
    bucketRim.parent = parent;
    bucketRim.metadata = meta;

    // Side ring handles
    [-5.8, 5.8].forEach((hx, hIdx) => {
      const handle = BABYLON.MeshBuilder.CreateTorus(`bucketHandle_${meta.id}_${hIdx}`, {
        diameter: 2.0,
        thickness: 0.35,
        tessellation: 18
      }, this.scene);
      handle.position.set(hx, 6.8, 0);
      handle.rotation.z = Math.PI / 2;
      handle.material = mats.iceBucketSteel;
      handle.parent = parent;
      handle.metadata = meta;
    });

    // Ice mound inside bucket
    const iceMound = BABYLON.MeshBuilder.CreateCylinder(`bucketIce_${meta.id}`, {
      diameter: 10.8,
      height: 1.2,
      tessellation: 24
    }, this.scene);
    iceMound.position.set(0, 8.2, 0);
    iceMound.material = mats.iceCube;
    iceMound.parent = parent;
    iceMound.metadata = meta;

    // Loose ice cubes around the bottle
    [
      { x: -2.6, y: 9.0, z: 2.2, r: 0.3 },
      { x: 2.4, y: 9.1, z: 2.0, r: -0.4 },
      { x: -1.8, y: 8.9, z: -2.4, r: 0.5 }
    ].forEach((cub, cIdx) => {
      const cube = BABYLON.MeshBuilder.CreateBox(`bucketCube_${meta.id}_${cIdx}`, { size: 1.8 }, this.scene);
      cube.position.set(cub.x, cub.y, cub.z);
      cube.rotation.set(cub.r, cub.r * 1.5, cub.r * 0.7);
      cube.material = mats.iceCube;
      cube.parent = parent;
      cube.metadata = meta;
    });

    // Champagne bottle nestled into the ice (tilted slightly 8°)
    const bottleNode = new BABYLON.TransformNode(`champagneBottle_${meta.id}`, this.scene);
    bottleNode.position.set(0.4, 5.0, 0);
    bottleNode.rotation.z = 0.14;
    bottleNode.parent = parent;

    const bottleBody = BABYLON.MeshBuilder.CreateCylinder(`bottleBody_${meta.id}`, {
      diameter: 4.8,
      height: 7.2,
      tessellation: 24
    }, this.scene);
    bottleBody.position.set(0, 3.6, 0);
    bottleBody.material = mats.bottleGreen;
    bottleBody.parent = bottleNode;
    bottleBody.metadata = meta;

    const bottleShoulder = BABYLON.MeshBuilder.CreateCylinder(`bottleShoulder_${meta.id}`, {
      diameterBottom: 4.8,
      diameterTop: 2.0,
      height: 3.2,
      tessellation: 24
    }, this.scene);
    bottleShoulder.position.set(0, 7.2 + 1.6, 0);
    bottleShoulder.material = mats.bottleGreen;
    bottleShoulder.parent = bottleNode;
    bottleShoulder.metadata = meta;

    const bottleNeck = BABYLON.MeshBuilder.CreateCylinder(`bottleNeck_${meta.id}`, {
      diameter: 1.9,
      height: 3.8,
      tessellation: 24
    }, this.scene);
    bottleNeck.position.set(0, 10.4 + 1.9, 0);
    bottleNeck.material = mats.bottleFoilGold;
    bottleNeck.parent = bottleNode;
    bottleNeck.metadata = meta;

    const bottleCork = BABYLON.MeshBuilder.CreateCylinder(`bottleCork_${meta.id}`, {
      diameter: 2.2,
      height: 1.6,
      tessellation: 24
    }, this.scene);
    bottleCork.position.set(0, 14.2 + 0.8, 0);
    bottleCork.material = mats.corkWood;
    bottleCork.parent = bottleNode;
    bottleCork.metadata = meta;
  }

  /**
   * 8. Pick Who Drinks: 3D Cocktail Shaker, Shot Glass & Bullseye Target Coaster
   * @private
   */
  _build3DShakerAndShot(parent, mats, meta) {
    const BABYLON = this.BABYLON;

    // Bullseye Target Coaster with concentric rings
    const targetBase = BABYLON.MeshBuilder.CreateCylinder(`targetBase_${meta.id}`, {
      diameter: 13.0,
      height: 0.48,
      tessellation: 28
    }, this.scene);
    targetBase.position.set(0, 0.24, 0);
    targetBase.material = mats.targetRed;
    targetBase.parent = parent;
    targetBase.metadata = meta;

    const targetWhite = BABYLON.MeshBuilder.CreateCylinder(`targetWhite_${meta.id}`, {
      diameter: 10.0,
      height: 0.54,
      tessellation: 28
    }, this.scene);
    targetWhite.position.set(0, 0.27, 0);
    targetWhite.material = mats.targetWhite;
    targetWhite.parent = parent;
    targetWhite.metadata = meta;

    const targetRed = BABYLON.MeshBuilder.CreateCylinder(`targetRed_${meta.id}`, {
      diameter: 7.0,
      height: 0.60,
      tessellation: 28
    }, this.scene);
    targetRed.position.set(0, 0.30, 0);
    targetRed.material = mats.targetRed;
    targetRed.parent = parent;
    targetRed.metadata = meta;

    const targetBullseye = BABYLON.MeshBuilder.CreateCylinder(`targetBullseye_${meta.id}`, {
      diameter: 3.8,
      height: 0.66,
      tessellation: 28
    }, this.scene);
    targetBullseye.position.set(0, 0.33, 0);
    targetBullseye.material = mats.coasterGold;
    targetBullseye.parent = parent;
    targetBullseye.metadata = meta;

    // Stainless Steel Cobbler Cocktail Shaker (left side of target)
    const shakerTumbler = BABYLON.MeshBuilder.CreateCylinder(`shakerTumbler_${meta.id}`, {
      diameterBottom: 4.8,
      diameterTop: 6.2,
      height: 8.0,
      tessellation: 24
    }, this.scene);
    shakerTumbler.position.set(-2.6, 0.48 + 4.0, 0.5);
    shakerTumbler.material = mats.shakerSteel;
    shakerTumbler.parent = parent;
    shakerTumbler.metadata = meta;

    const shakerStrainer = BABYLON.MeshBuilder.CreateCylinder(`shakerStrainer_${meta.id}`, {
      diameterBottom: 6.2,
      diameterTop: 3.4,
      height: 3.6,
      tessellation: 24
    }, this.scene);
    shakerStrainer.position.set(-2.6, 8.48 + 1.8, 0.5);
    shakerStrainer.material = mats.shakerSteel;
    shakerStrainer.parent = parent;
    shakerStrainer.metadata = meta;

    const shakerCap = BABYLON.MeshBuilder.CreateCylinder(`shakerCap_${meta.id}`, {
      diameter: 3.2,
      height: 2.6,
      tessellation: 24
    }, this.scene);
    shakerCap.position.set(-2.6, 12.08 + 1.3, 0.5);
    shakerCap.material = mats.shakerSteel;
    shakerCap.parent = parent;
    shakerCap.metadata = meta;

    // Shot Glass (right side of target)
    const shotBase = BABYLON.MeshBuilder.CreateCylinder(`shotBase_${meta.id}`, {
      diameter: 4.2,
      height: 1.6,
      tessellation: 24
    }, this.scene);
    shotBase.position.set(3.2, 0.48 + 0.8, -0.5);
    shotBase.material = mats.glass;
    shotBase.parent = parent;
    shotBase.metadata = meta;

    const shotGlass = BABYLON.MeshBuilder.CreateCylinder(`shotGlass_${meta.id}`, {
      diameterBottom: 4.2,
      diameterTop: 5.4,
      height: 4.8,
      tessellation: 24
    }, this.scene);
    shotGlass.position.set(3.2, 2.08 + 2.4, -0.5);
    shotGlass.material = mats.glass;
    shotGlass.parent = parent;
    shotGlass.metadata = meta;

    const shotLiquid = BABYLON.MeshBuilder.CreateCylinder(`shotLiquid_${meta.id}`, {
      diameterBottom: 3.6,
      diameterTop: 4.8,
      height: 3.6,
      tessellation: 24
    }, this.scene);
    shotLiquid.position.set(3.2, 2.08 + 1.8, -0.5);
    shotLiquid.material = mats.beerAmber;
    shotLiquid.parent = parent;
    shotLiquid.metadata = meta;
  }

  /**
   * Renders 3D themed coaster socket pads and romantic sculptures for active Skunked & Stripped (Adult) spaces.
   * @private
   */
  _render3DAdultCoastersAndObjects(spacing, radius, padHeight, mats) {
    const BABYLON = this.BABYLON;
    const configs = this.getActiveAdultConfigs();
    if (!Array.isArray(configs) || configs.length === 0) return;

    const tracksToMark = this.activeTracks.length > 0 ? this.activeTracks : ['p1'];
    const refTrack = tracksToMark[0] || 'p1';

    configs.forEach(cfg => {
      if (cfg.hole > this.targetHoles) return;

      // 1. Coaster pads on every active track for each hole in range [hole .. hole + pegs - 1]
      tracksToMark.forEach(who => {
        for (let offset = 0; offset < cfg.pegs; offset++) {
          const currentHole = cfg.hole + offset;
          if (currentHole > this.targetHoles) break;

          const pt = this.getHoleCoords(who, currentHole);
          if (!pt) continue;
          const surfaceY = (pt && pt.y !== undefined) ? pt.y : 0.8;

          const padDiam = radius * 2.3;
          const padH = padHeight * 1.8;
          const padMat = mats[`pad${cfg.type.charAt(0).toUpperCase() + cfg.type.slice(1)}`] || mats.padLoseClothing;
          const meta = { obstacle: 'adult-space', type: cfg.type, hole: currentHole, who, timed: cfg.timed };

          // Outer beveled themed coaster disc
          const padMesh = BABYLON.MeshBuilder.CreateCylinder(`adultPad_${who}_${currentHole}_${cfg.type}`, {
            diameter: padDiam,
            height: padH,
            tessellation: 24
          }, this.scene);
          padMesh.position.set(pt.x, surfaceY + padH / 2 + 0.05, pt.z);
          padMesh.material = padMat;
          padMesh.parent = this.adultObjectsRoot;
          padMesh.metadata = meta;

          // Raised metallic accent rim
          const rimMesh = BABYLON.MeshBuilder.CreateCylinder(`adultPadRim_${who}_${currentHole}_${cfg.type}`, {
            diameter: padDiam * 0.84,
            height: padH + 0.06,
            tessellation: 24
          }, this.scene);
          rimMesh.position.set(pt.x, surfaceY + (padH + 0.06) / 2 + 0.05, pt.z);
          rimMesh.material = mats.padRim;
          rimMesh.parent = this.adultObjectsRoot;
          rimMesh.metadata = meta;

          // Inner coaster medallion core
          const coreMesh = BABYLON.MeshBuilder.CreateCylinder(`adultPadCore_${who}_${currentHole}_${cfg.type}`, {
            diameter: padDiam * 0.58,
            height: padH + 0.12,
            tessellation: 24
          }, this.scene);
          coreMesh.position.set(pt.x, surfaceY + (padH + 0.12) / 2 + 0.05, pt.z);
          coreMesh.material = padMat;
          coreMesh.parent = this.adultObjectsRoot;
          coreMesh.metadata = meta;
        }
      });

      // 2. Upright 3D Adult Sculpture standing on a velvet plinth beside the reference track
      const p0 = this.getHoleCoords(refTrack, cfg.hole);
      const p1 = this.getHoleCoords(refTrack, Math.min(this.targetHoles, cfg.hole + 1));
      if (!p0) return;

      let nx = 0;
      let nz = 1;
      if (p1 && (p1.x !== p0.x || p1.z !== p0.z)) {
        const dx = p1.x - p0.x;
        const dz = p1.z - p0.z;
        const len = Math.hypot(dx, dz) || 1;
        nx = -dz / len;
        nz = dx / len;
      }

      const itemDist = spacing * 1.75;
      const itemX = p0.x + nx * itemDist;
      const itemZ = p0.z + nz * itemDist;
      const surfaceY = (p0 && p0.y !== undefined) ? p0.y : 0.8;

      const objNode = new BABYLON.TransformNode(`adultObjNode_${cfg.id}`, this.scene);
      objNode.position.set(itemX, surfaceY, itemZ);
      objNode.rotation.y = Math.atan2(-nx, -nz);
      objNode.parent = this.adultObjectsRoot;

      const objMeta = { obstacle: 'adult-dare', type: cfg.type, hole: cfg.hole, who: refTrack, id: cfg.id, timed: cfg.timed };

      switch (cfg.type) {
        case 'loseClothing':
          this._build3DLingerieMannequin(objNode, mats, objMeta);
          break;
        case 'gainClothing':
          this._build3DCoatValet(objNode, mats, objMeta);
          break;
        case 'pickWhoLosesClothing':
          this._build3DCupidTarget(objNode, mats, objMeta);
          break;
        case 'rightLosesClothing':
          this._build3DPointingHandRight(objNode, mats, objMeta);
          break;
        case 'leftLosesClothing':
          this._build3DPointingHandLeft(objNode, mats, objMeta);
          break;
        case 'removeClothingChoice':
          this._build3DTailorShears(objNode, mats, objMeta);
          break;
        case 'touchBreast':
          this._build3DLipsAndHourglass(objNode, mats, objMeta);
          break;
        case 'lickBreast':
        case 'lickGenitals':
        case 'lickAnus':
          this._build3DLollipopAndHourglass(objNode, mats, objMeta);
          break;
        case 'suckBreast':
        case 'suckGenitals':
          this._build3DStrawberryAndHourglass(objNode, mats, objMeta);
          break;
        case 'touchGenitals':
          this._build3DMagicWandAndHourglass(objNode, mats, objMeta);
          break;
        case 'fingerGenitals':
        case 'fingerAnus':
          this._build3DScentedCandleAndHand(objNode, mats, objMeta);
          break;
        case 'genitalIntercourse':
        case 'analIntercourse':
          this._build3DPassionateFlames(objNode, mats, objMeta);
          break;
        case 'touchAnus':
          this._build3DVelvetPeach(objNode, mats, objMeta);
          break;
        default:
          this._build3DLingerieMannequin(objNode, mats, objMeta);
          break;
      }
    });
  }

  /**
   * Helper: 3D Challenge Timer Hourglass with glass bulbs, sand & golden pillars
   * @private
   */
  _build3DHourglass(parent, mats, meta, posX = 2.8, posZ = 0.2) {
    const BABYLON = this.BABYLON;
    const hgNode = new BABYLON.TransformNode(`hourglassNode_${meta.id}`, this.scene);
    hgNode.position.set(posX, 0, posZ);
    hgNode.parent = parent;

    // Bottom and top golden endcap discs
    const baseDisc = BABYLON.MeshBuilder.CreateCylinder(`hgBase_${meta.id}`, {
      diameter: 3.8,
      height: 0.5,
      tessellation: 20
    }, this.scene);
    baseDisc.position.set(0, 0.48 + 0.25, 0);
    baseDisc.material = mats.sculptureGold;
    baseDisc.parent = hgNode;
    baseDisc.metadata = meta;

    const topDisc = BABYLON.MeshBuilder.CreateCylinder(`hgTop_${meta.id}`, {
      diameter: 3.8,
      height: 0.5,
      tessellation: 20
    }, this.scene);
    topDisc.position.set(0, 0.48 + 7.8, 0);
    topDisc.material = mats.sculptureGold;
    topDisc.parent = hgNode;
    topDisc.metadata = meta;

    // 3 Golden Support Pillars
    for (let p = 0; p < 3; p++) {
      const a = (p * 2 * Math.PI) / 3;
      const px = 1.4 * Math.cos(a);
      const pz = 1.4 * Math.sin(a);
      const pillar = BABYLON.MeshBuilder.CreateCylinder(`hgPillar_${meta.id}_${p}`, {
        diameter: 0.35,
        height: 7.2,
        tessellation: 12
      }, this.scene);
      pillar.position.set(px, 0.48 + 4.0, pz);
      pillar.material = mats.sculptureGold;
      pillar.parent = hgNode;
      pillar.metadata = meta;
    }

    // Lower & Upper Glass Bulbs
    const glassLower = BABYLON.MeshBuilder.CreateCylinder(`hgGlassLower_${meta.id}`, {
      diameterBottom: 3.0,
      diameterTop: 0.6,
      height: 3.4,
      tessellation: 20
    }, this.scene);
    glassLower.position.set(0, 0.48 + 0.5 + 1.7, 0);
    glassLower.material = mats.glass;
    glassLower.parent = hgNode;
    glassLower.metadata = meta;

    const glassUpper = BABYLON.MeshBuilder.CreateCylinder(`hgGlassUpper_${meta.id}`, {
      diameterBottom: 0.6,
      diameterTop: 3.0,
      height: 3.4,
      tessellation: 20
    }, this.scene);
    glassUpper.position.set(0, 0.48 + 0.5 + 5.1, 0);
    glassUpper.material = mats.glass;
    glassUpper.parent = hgNode;
    glassUpper.metadata = meta;

    // Glowing Golden Sand Mounds
    const sandLower = BABYLON.MeshBuilder.CreateCylinder(`hgSandLower_${meta.id}`, {
      diameterBottom: 2.6,
      diameterTop: 0.3,
      height: 1.8,
      tessellation: 20
    }, this.scene);
    sandLower.position.set(0, 0.48 + 0.5 + 0.9, 0);
    sandLower.material = mats.sandGold;
    sandLower.parent = hgNode;
    sandLower.metadata = meta;

    const sandUpper = BABYLON.MeshBuilder.CreateCylinder(`hgSandUpper_${meta.id}`, {
      diameterBottom: 0.4,
      diameterTop: 2.4,
      height: 1.4,
      tessellation: 20
    }, this.scene);
    sandUpper.position.set(0, 0.48 + 0.5 + 5.9, 0);
    sandUpper.material = mats.sandGold;
    sandUpper.parent = hgNode;
    sandUpper.metadata = meta;
  }

  /**
   * 1. You Lose Clothing: 3D Sculpted Lingerie Corset Mannequin on Rose-Gold Plinth
   * @private
   */
  _build3DLingerieMannequin(parent, mats, meta) {
    const BABYLON = this.BABYLON;

    // Obsidian plinth with rose-gold rim
    const plinth = BABYLON.MeshBuilder.CreateCylinder(`adultPlinth_${meta.id}`, {
      diameter: 11.2,
      height: 0.5,
      tessellation: 28
    }, this.scene);
    plinth.position.set(0, 0.25, 0);
    plinth.material = mats.pedestalObsidian;
    plinth.parent = parent;
    plinth.metadata = meta;

    const plinthRim = BABYLON.MeshBuilder.CreateTorus(`adultPlinthRim_${meta.id}`, {
      diameter: 11.0,
      thickness: 0.25,
      tessellation: 28
    }, this.scene);
    plinthRim.position.set(0, 0.48, 0);
    plinthRim.material = mats.pedestalRoseGold;
    plinthRim.parent = parent;
    plinthRim.metadata = meta;

    // Central brass mannequin rod
    const rod = BABYLON.MeshBuilder.CreateCylinder(`mannequinRod_${meta.id}`, {
      diameter: 0.8,
      height: 5.5,
      tessellation: 16
    }, this.scene);
    rod.position.set(0, 0.5 + 2.75, 0);
    rod.material = mats.sculptureGold;
    rod.parent = parent;
    rod.metadata = meta;

    // Sculpted Silver Torso Mannequin
    const hips = BABYLON.MeshBuilder.CreateCylinder(`mannequinHips_${meta.id}`, {
      diameterBottom: 5.6,
      diameterTop: 4.2,
      height: 3.8,
      tessellation: 24
    }, this.scene);
    hips.position.set(0, 5.0, 0);
    hips.material = mats.pedestalSilver;
    hips.parent = parent;
    hips.metadata = meta;

    const bust = BABYLON.MeshBuilder.CreateCylinder(`mannequinBust_${meta.id}`, {
      diameterBottom: 4.2,
      diameterTop: 6.0,
      height: 4.2,
      tessellation: 24
    }, this.scene);
    bust.position.set(0, 8.8, 0);
    bust.material = mats.pedestalSilver;
    bust.parent = parent;
    bust.metadata = meta;

    const neck = BABYLON.MeshBuilder.CreateCylinder(`mannequinNeck_${meta.id}`, {
      diameter: 1.8,
      height: 1.8,
      tessellation: 16
    }, this.scene);
    neck.position.set(0, 11.6, 0);
    neck.material = mats.pedestalSilver;
    neck.parent = parent;
    neck.metadata = meta;

    const finial = BABYLON.MeshBuilder.CreateSphere(`mannequinFinial_${meta.id}`, {
      diameter: 2.2,
      segments: 16
    }, this.scene);
    finial.position.set(0, 13.0, 0);
    finial.material = mats.sculptureGold;
    finial.parent = parent;
    finial.metadata = meta;

    // Draped Pink Silk Lingerie Piece
    const lowerWrap = BABYLON.MeshBuilder.CreateTorus(`lingerieWrap_${meta.id}`, {
      diameter: 5.4,
      thickness: 0.45,
      tessellation: 24
    }, this.scene);
    lowerWrap.position.set(0, 4.4, 0);
    lowerWrap.material = mats.silkPink;
    lowerWrap.parent = parent;
    lowerWrap.metadata = meta;

    // Cups
    [-1.4, 1.4].forEach((cx, cIdx) => {
      const cup = BABYLON.MeshBuilder.CreateSphere(`lingerieCup_${meta.id}_${cIdx}`, {
        diameter: 2.7,
        segments: 16
      }, this.scene);
      cup.position.set(cx, 9.2, 1.6);
      cup.material = mats.silkPink;
      cup.parent = parent;
      cup.metadata = meta;
    });
  }

  /**
   * 2. You Gain Clothing: Tailored Emerald Velvet Jacket on Mahogany Valet Stand
   * @private
   */
  _build3DCoatValet(parent, mats, meta) {
    const BABYLON = this.BABYLON;

    // Polished mahogany plinth with emerald rim
    const plinth = BABYLON.MeshBuilder.CreateCylinder(`valetPlinth_${meta.id}`, {
      diameter: 11.2,
      height: 0.5,
      tessellation: 28
    }, this.scene);
    plinth.position.set(0, 0.25, 0);
    plinth.material = mats.pedestalWood;
    plinth.parent = parent;
    plinth.metadata = meta;

    const plinthRim = BABYLON.MeshBuilder.CreateTorus(`valetPlinthRim_${meta.id}`, {
      diameter: 11.0,
      thickness: 0.25,
      tessellation: 28
    }, this.scene);
    plinthRim.position.set(0, 0.48, 0);
    plinthRim.material = mats.satinEmerald;
    plinthRim.parent = parent;
    plinthRim.metadata = meta;

    // Polished brass valet rod
    const rod = BABYLON.MeshBuilder.CreateCylinder(`valetRod_${meta.id}`, {
      diameter: 0.85,
      height: 12.0,
      tessellation: 16
    }, this.scene);
    rod.position.set(0, 0.5 + 6.0, 0);
    rod.material = mats.sculptureGold;
    rod.parent = parent;
    rod.metadata = meta;

    // Shoulder coat hanger
    const hanger = BABYLON.MeshBuilder.CreateBox(`valetHanger_${meta.id}`, {
      width: 8.5,
      height: 0.65,
      depth: 1.2
    }, this.scene);
    hanger.position.set(0, 12.2, 0);
    hanger.material = mats.pedestalWood;
    hanger.parent = parent;
    hanger.metadata = meta;

    // Tailored Emerald Velvet Coat
    const coat = BABYLON.MeshBuilder.CreateBox(`valetCoat_${meta.id}`, {
      width: 7.2,
      height: 8.0,
      depth: 3.8
    }, this.scene);
    coat.position.set(0, 8.0, 0);
    coat.material = mats.satinEmerald;
    coat.parent = parent;
    coat.metadata = meta;

    // Golden buttons down coat front
    for (let b = 0; b < 3; b++) {
      const btn = BABYLON.MeshBuilder.CreateCylinder(`coatBtn_${meta.id}_${b}`, {
        diameter: 0.6,
        height: 0.35,
        tessellation: 12
      }, this.scene);
      btn.position.set(0, 9.5 - b * 1.5, 2.0);
      btn.rotation.x = Math.PI / 2;
      btn.material = mats.sculptureGold;
      btn.parent = parent;
      btn.metadata = meta;
    }
  }

  /**
   * 3. Pick Who Strips: 3D Archery Dartboard Target with Cupid's Golden Heart Arrow
   * @private
   */
  _build3DCupidTarget(parent, mats, meta) {
    const BABYLON = this.BABYLON;

    // Plinth
    const plinth = BABYLON.MeshBuilder.CreateCylinder(`targetPlinth_${meta.id}`, {
      diameter: 11.2,
      height: 0.5,
      tessellation: 28
    }, this.scene);
    plinth.position.set(0, 0.25, 0);
    plinth.material = mats.pedestalObsidian;
    plinth.parent = parent;
    plinth.metadata = meta;

    const plinthRim = BABYLON.MeshBuilder.CreateTorus(`targetPlinthRim_${meta.id}`, {
      diameter: 11.0,
      thickness: 0.25,
      tessellation: 28
    }, this.scene);
    plinthRim.position.set(0, 0.48, 0);
    plinthRim.material = mats.padPickWhoLosesClothing;
    plinthRim.parent = parent;
    plinthRim.metadata = meta;

    // Upright circular target board
    const board = BABYLON.MeshBuilder.CreateCylinder(`targetDisc_${meta.id}`, {
      diameter: 10.4,
      height: 0.6,
      tessellation: 28
    }, this.scene);
    board.position.set(0, 7.0, 0);
    board.rotation.x = Math.PI / 2;
    board.material = mats.velvetPink;
    board.parent = parent;
    board.metadata = meta;

    const ringWhite = BABYLON.MeshBuilder.CreateCylinder(`targetRingWhite_${meta.id}`, {
      diameter: 7.6,
      height: 0.7,
      tessellation: 28
    }, this.scene);
    ringWhite.position.set(0, 7.0, 0.05);
    ringWhite.rotation.x = Math.PI / 2;
    ringWhite.material = mats.padRim;
    ringWhite.parent = parent;
    ringWhite.metadata = meta;

    const ringPink = BABYLON.MeshBuilder.CreateCylinder(`targetRingPink_${meta.id}`, {
      diameter: 4.8,
      height: 0.8,
      tessellation: 28
    }, this.scene);
    ringPink.position.set(0, 7.0, 0.1);
    ringPink.rotation.x = Math.PI / 2;
    ringPink.material = mats.padPickWhoLosesClothing;
    ringPink.parent = parent;
    ringPink.metadata = meta;

    const bullseye = BABYLON.MeshBuilder.CreateCylinder(`targetBullseye_${meta.id}`, {
      diameter: 2.2,
      height: 0.9,
      tessellation: 24
    }, this.scene);
    bullseye.position.set(0, 7.0, 0.15);
    bullseye.rotation.x = Math.PI / 2;
    bullseye.material = mats.sculptureGold;
    bullseye.parent = parent;
    bullseye.metadata = meta;

    // Cupid's Golden Arrow embedded into bullseye
    const shaft = BABYLON.MeshBuilder.CreateCylinder(`arrowShaft_${meta.id}`, {
      diameter: 0.38,
      height: 6.5,
      tessellation: 12
    }, this.scene);
    shaft.position.set(0, 7.0, 3.5);
    shaft.rotation.x = Math.PI / 2;
    shaft.material = mats.sculptureGold;
    shaft.parent = parent;
    shaft.metadata = meta;

    const heartHead = BABYLON.MeshBuilder.CreateSphere(`arrowHeart_${meta.id}`, {
      diameter: 1.4,
      segments: 12
    }, this.scene);
    heartHead.position.set(0, 7.0, 0.5);
    heartHead.material = mats.lipsRuby;
    heartHead.parent = parent;
    heartHead.metadata = meta;

    const fletch = BABYLON.MeshBuilder.CreateBox(`arrowFletch_${meta.id}`, {
      width: 1.5,
      height: 1.5,
      depth: 0.3
    }, this.scene);
    fletch.position.set(0, 7.0, 6.4);
    fletch.material = mats.sculptureGold;
    fletch.parent = parent;
    fletch.metadata = meta;
  }

  /**
   * 4. Right Neighbor Strips: Sculpted Golden Hand with Sapphire Ring pointing Right
   * @private
   */
  _build3DPointingHandRight(parent, mats, meta) {
    const BABYLON = this.BABYLON;

    // Sapphire marble plinth
    const plinth = BABYLON.MeshBuilder.CreateCylinder(`handPlinth_${meta.id}`, {
      diameter: 11.0,
      height: 0.5,
      tessellation: 28
    }, this.scene);
    plinth.position.set(0, 0.25, 0);
    plinth.material = mats.pedestalObsidian;
    plinth.parent = parent;
    plinth.metadata = meta;

    const plinthRim = BABYLON.MeshBuilder.CreateTorus(`handPlinthRim_${meta.id}`, {
      diameter: 10.8,
      thickness: 0.24,
      tessellation: 28
    }, this.scene);
    plinthRim.position.set(0, 0.48, 0);
    plinthRim.material = mats.padRightLosesClothing;
    plinthRim.parent = parent;
    plinthRim.metadata = meta;

    // Wrist mount
    const wrist = BABYLON.MeshBuilder.CreateSphere(`wrist_${meta.id}`, {
      diameter: 3.2,
      segments: 16
    }, this.scene);
    wrist.position.set(-1.8, 2.2, 0);
    wrist.material = mats.pedestalSilver;
    wrist.parent = parent;
    wrist.metadata = meta;

    // Palm
    const palm = BABYLON.MeshBuilder.CreateBox(`palm_${meta.id}`, {
      width: 3.4,
      height: 2.2,
      depth: 2.8
    }, this.scene);
    palm.position.set(-0.8, 3.8, 0);
    palm.material = mats.sculptureGold;
    palm.parent = parent;
    palm.metadata = meta;

    // Index finger pointing horizontally toward +X right (👉)
    const finger = BABYLON.MeshBuilder.CreateCylinder(`finger_${meta.id}`, {
      diameter: 1.0,
      height: 5.5,
      tessellation: 16
    }, this.scene);
    finger.position.set(2.2, 4.4, 0.6);
    finger.rotation.z = -Math.PI / 2;
    finger.material = mats.sculptureGold;
    finger.parent = parent;
    finger.metadata = meta;

    // Curled lower fingers
    const curled = BABYLON.MeshBuilder.CreateBox(`curled_${meta.id}`, {
      width: 2.4,
      height: 2.0,
      depth: 1.8
    }, this.scene);
    curled.position.set(0.2, 3.2, -0.4);
    curled.material = mats.sculptureGold;
    curled.parent = parent;
    curled.metadata = meta;

    // Sapphire Ring on finger
    const ring = BABYLON.MeshBuilder.CreateTorus(`ring_${meta.id}`, {
      diameter: 1.4,
      thickness: 0.35,
      tessellation: 16
    }, this.scene);
    ring.position.set(1.5, 4.4, 0.6);
    ring.rotation.y = Math.PI / 2;
    ring.material = mats.padRightLosesClothing;
    ring.parent = parent;
    ring.metadata = meta;
  }

  /**
   * 5. Left Neighbor Strips: Sculpted Golden Hand with Amethyst Ring pointing Left
   * @private
   */
  _build3DPointingHandLeft(parent, mats, meta) {
    const BABYLON = this.BABYLON;

    // Amethyst marble plinth
    const plinth = BABYLON.MeshBuilder.CreateCylinder(`handPlinth_${meta.id}`, {
      diameter: 11.0,
      height: 0.5,
      tessellation: 28
    }, this.scene);
    plinth.position.set(0, 0.25, 0);
    plinth.material = mats.pedestalObsidian;
    plinth.parent = parent;
    plinth.metadata = meta;

    const plinthRim = BABYLON.MeshBuilder.CreateTorus(`handPlinthRim_${meta.id}`, {
      diameter: 10.8,
      thickness: 0.24,
      tessellation: 28
    }, this.scene);
    plinthRim.position.set(0, 0.48, 0);
    plinthRim.material = mats.padLeftLosesClothing;
    plinthRim.parent = parent;
    plinthRim.metadata = meta;

    // Wrist mount
    const wrist = BABYLON.MeshBuilder.CreateSphere(`wrist_${meta.id}`, {
      diameter: 3.2,
      segments: 16
    }, this.scene);
    wrist.position.set(1.8, 2.2, 0);
    wrist.material = mats.pedestalSilver;
    wrist.parent = parent;
    wrist.metadata = meta;

    // Palm
    const palm = BABYLON.MeshBuilder.CreateBox(`palm_${meta.id}`, {
      width: 3.4,
      height: 2.2,
      depth: 2.8
    }, this.scene);
    palm.position.set(0.8, 3.8, 0);
    palm.material = mats.sculptureGold;
    palm.parent = parent;
    palm.metadata = meta;

    // Index finger pointing horizontally toward -X left (👈)
    const finger = BABYLON.MeshBuilder.CreateCylinder(`finger_${meta.id}`, {
      diameter: 1.0,
      height: 5.5,
      tessellation: 16
    }, this.scene);
    finger.position.set(-2.2, 4.4, 0.6);
    finger.rotation.z = Math.PI / 2;
    finger.material = mats.sculptureGold;
    finger.parent = parent;
    finger.metadata = meta;

    // Curled lower fingers
    const curled = BABYLON.MeshBuilder.CreateBox(`curled_${meta.id}`, {
      width: 2.4,
      height: 2.0,
      depth: 1.8
    }, this.scene);
    curled.position.set(-0.2, 3.2, -0.4);
    curled.material = mats.sculptureGold;
    curled.parent = parent;
    curled.metadata = meta;

    // Amethyst Ring on finger
    const ring = BABYLON.MeshBuilder.CreateTorus(`ring_${meta.id}`, {
      diameter: 1.4,
      thickness: 0.35,
      tessellation: 16
    }, this.scene);
    ring.position.set(-1.5, 4.4, 0.6);
    ring.rotation.y = Math.PI / 2;
    ring.material = mats.padLeftLosesClothing;
    ring.parent = parent;
    ring.metadata = meta;
  }

  /**
   * 6. Remove Clothing Choice: Golden Tailor's Shears Cutting Scarlet Silk Ribbon
   * @private
   */
  _build3DTailorShears(parent, mats, meta) {
    const BABYLON = this.BABYLON;

    // Crimson and silver plinth
    const plinth = BABYLON.MeshBuilder.CreateCylinder(`shearsPlinth_${meta.id}`, {
      diameter: 11.0,
      height: 0.5,
      tessellation: 28
    }, this.scene);
    plinth.position.set(0, 0.25, 0);
    plinth.material = mats.pedestalObsidian;
    plinth.parent = parent;
    plinth.metadata = meta;

    const plinthRim = BABYLON.MeshBuilder.CreateTorus(`shearsPlinthRim_${meta.id}`, {
      diameter: 10.8,
      thickness: 0.24,
      tessellation: 28
    }, this.scene);
    plinthRim.position.set(0, 0.48, 0);
    plinthRim.material = mats.ribbonScarlet;
    plinthRim.parent = parent;
    plinthRim.metadata = meta;

    // Pivot bolt
    const pivot = BABYLON.MeshBuilder.CreateCylinder(`shearsPivot_${meta.id}`, {
      diameter: 1.2,
      height: 0.8,
      tessellation: 16
    }, this.scene);
    pivot.position.set(0, 4.5, 0);
    pivot.rotation.x = Math.PI / 2;
    pivot.material = mats.sculptureGold;
    pivot.parent = parent;
    pivot.metadata = meta;

    // Left and Right Shears Blades
    const bladeLeft = BABYLON.MeshBuilder.CreateBox(`shearsBladeL_${meta.id}`, {
      width: 0.8,
      height: 8.5,
      depth: 0.3
    }, this.scene);
    bladeLeft.position.set(-1.6, 8.2, 0);
    bladeLeft.rotation.z = -0.32;
    bladeLeft.material = mats.pedestalSilver;
    bladeLeft.parent = parent;
    bladeLeft.metadata = meta;

    const bladeRight = BABYLON.MeshBuilder.CreateBox(`shearsBladeR_${meta.id}`, {
      width: 0.8,
      height: 8.5,
      depth: 0.3
    }, this.scene);
    bladeRight.position.set(1.6, 8.2, 0);
    bladeRight.rotation.z = 0.32;
    bladeRight.material = mats.pedestalSilver;
    bladeRight.parent = parent;
    bladeRight.metadata = meta;

    // Golden scissor finger loops
    const loopL = BABYLON.MeshBuilder.CreateTorus(`shearsLoopL_${meta.id}`, {
      diameter: 3.0,
      thickness: 0.5,
      tessellation: 20
    }, this.scene);
    loopL.position.set(-1.8, 2.0, 0);
    loopL.rotation.z = 0.2;
    loopL.material = mats.sculptureGold;
    loopL.parent = parent;
    loopL.metadata = meta;

    const loopR = BABYLON.MeshBuilder.CreateTorus(`shearsLoopR_${meta.id}`, {
      diameter: 3.0,
      thickness: 0.5,
      tessellation: 20
    }, this.scene);
    loopR.position.set(1.8, 2.0, 0);
    loopR.rotation.z = -0.2;
    loopR.material = mats.sculptureGold;
    loopR.parent = parent;
    loopR.metadata = meta;

    // Fluttering Scarlet Silk Ribbon draped between blades
    const ribbon = BABYLON.MeshBuilder.CreateBox(`shearsRibbon_${meta.id}`, {
      width: 7.0,
      height: 0.8,
      depth: 1.4
    }, this.scene);
    ribbon.position.set(0, 5.8, 0.4);
    ribbon.rotation.y = 0.15;
    ribbon.material = mats.ribbonScarlet;
    ribbon.parent = parent;
    ribbon.metadata = meta;
  }

  /**
   * 7. Touch Breast / Kiss Dares: Sculpted Glossy Ruby Lips with 3D Challenge Hourglass
   * @private
   */
  _build3DLipsAndHourglass(parent, mats, meta) {
    const BABYLON = this.BABYLON;

    // Plinth
    const plinth = BABYLON.MeshBuilder.CreateCylinder(`lipsPlinth_${meta.id}`, {
      diameter: 12.0,
      height: 0.5,
      tessellation: 28
    }, this.scene);
    plinth.position.set(0, 0.25, 0);
    plinth.material = mats.pedestalObsidian;
    plinth.parent = parent;
    plinth.metadata = meta;

    const plinthRim = BABYLON.MeshBuilder.CreateTorus(`lipsPlinthRim_${meta.id}`, {
      diameter: 11.8,
      thickness: 0.25,
      tessellation: 28
    }, this.scene);
    plinthRim.position.set(0, 0.48, 0);
    plinthRim.material = mats.pedestalRoseGold;
    plinthRim.parent = parent;
    plinthRim.metadata = meta;

    // Sculpted Glossy Ruby Lips on pedestal stand (left side)
    const stand = BABYLON.MeshBuilder.CreateCylinder(`lipsStand_${meta.id}`, {
      diameter: 0.65,
      height: 4.8,
      tessellation: 16
    }, this.scene);
    stand.position.set(-2.4, 0.5 + 2.4, 0);
    stand.material = mats.sculptureGold;
    stand.parent = parent;
    stand.metadata = meta;

    const upperLipL = BABYLON.MeshBuilder.CreateSphere(`lipUpperL_${meta.id}`, {
      diameter: 2.2,
      segments: 16
    }, this.scene);
    upperLipL.position.set(-3.2, 6.2, 0.1);
    upperLipL.material = mats.lipsRuby;
    upperLipL.parent = parent;
    upperLipL.metadata = meta;

    const upperLipR = BABYLON.MeshBuilder.CreateSphere(`lipUpperR_${meta.id}`, {
      diameter: 2.2,
      segments: 16
    }, this.scene);
    upperLipR.position.set(-1.6, 6.2, 0.1);
    upperLipR.material = mats.lipsRuby;
    upperLipR.parent = parent;
    upperLipR.metadata = meta;

    const lowerLip = BABYLON.MeshBuilder.CreateCylinder(`lipLower_${meta.id}`, {
      diameterBottom: 2.4,
      diameterTop: 3.4,
      height: 1.4,
      tessellation: 20
    }, this.scene);
    lowerLip.position.set(-2.4, 5.0, 0.2);
    lowerLip.rotation.z = Math.PI / 2;
    lowerLip.material = mats.lipsRuby;
    lowerLip.parent = parent;
    lowerLip.metadata = meta;

    // 3D Challenge Hourglass Timer (right side)
    this._build3DHourglass(parent, mats, meta, 2.8, 0.2);
  }

  /**
   * 8. Lick Dares: Sculpted Spiral Lollipop with Ribbon & 3D Challenge Hourglass
   * @private
   */
  _build3DLollipopAndHourglass(parent, mats, meta) {
    const BABYLON = this.BABYLON;

    // Plinth
    const plinth = BABYLON.MeshBuilder.CreateCylinder(`popPlinth_${meta.id}`, {
      diameter: 12.0,
      height: 0.5,
      tessellation: 28
    }, this.scene);
    plinth.position.set(0, 0.25, 0);
    plinth.material = mats.pedestalObsidian;
    plinth.parent = parent;
    plinth.metadata = meta;

    const plinthRim = BABYLON.MeshBuilder.CreateTorus(`popPlinthRim_${meta.id}`, {
      diameter: 11.8,
      thickness: 0.25,
      tessellation: 28
    }, this.scene);
    plinthRim.position.set(0, 0.48, 0);
    plinthRim.material = mats.velvetPink;
    plinthRim.parent = parent;
    plinthRim.metadata = meta;

    // Sculpted Spiral Lollipop (left side)
    const stick = BABYLON.MeshBuilder.CreateCylinder(`popStick_${meta.id}`, {
      diameter: 0.55,
      height: 6.8,
      tessellation: 16
    }, this.scene);
    stick.position.set(-2.4, 0.5 + 3.4, 0);
    stick.material = mats.pedestalSilver;
    stick.parent = parent;
    stick.metadata = meta;

    const popDisc = BABYLON.MeshBuilder.CreateCylinder(`popDisc_${meta.id}`, {
      diameter: 5.6,
      height: 1.2,
      tessellation: 28
    }, this.scene);
    popDisc.position.set(-2.4, 8.2, 0);
    popDisc.rotation.x = Math.PI / 2;
    popDisc.material = mats.lipsRuby;
    popDisc.parent = parent;
    popDisc.metadata = meta;

    const popSwirl = BABYLON.MeshBuilder.CreateTorus(`popSwirl_${meta.id}`, {
      diameter: 4.0,
      thickness: 0.45,
      tessellation: 24
    }, this.scene);
    popSwirl.position.set(-2.4, 8.2, 0.2);
    popSwirl.rotation.x = Math.PI / 2;
    popSwirl.material = mats.silkPink;
    popSwirl.parent = parent;
    popSwirl.metadata = meta;

    // 3D Challenge Hourglass Timer (right side)
    this._build3DHourglass(parent, mats, meta, 2.8, 0.2);
  }

  /**
   * 9. Suck Dares: Chocolate-Dipped Strawberry with 3D Challenge Hourglass
   * @private
   */
  _build3DStrawberryAndHourglass(parent, mats, meta) {
    const BABYLON = this.BABYLON;

    // Plinth
    const plinth = BABYLON.MeshBuilder.CreateCylinder(`strawbPlinth_${meta.id}`, {
      diameter: 12.0,
      height: 0.5,
      tessellation: 28
    }, this.scene);
    plinth.position.set(0, 0.25, 0);
    plinth.material = mats.pedestalObsidian;
    plinth.parent = parent;
    plinth.metadata = meta;

    const plinthRim = BABYLON.MeshBuilder.CreateTorus(`strawbPlinthRim_${meta.id}`, {
      diameter: 11.8,
      thickness: 0.25,
      tessellation: 28
    }, this.scene);
    plinthRim.position.set(0, 0.48, 0);
    plinthRim.material = mats.pedestalGold;
    plinthRim.parent = parent;
    plinthRim.metadata = meta;

    // Chocolate-Dipped Strawberry (left side)
    const dip = BABYLON.MeshBuilder.CreateCylinder(`chocoDip_${meta.id}`, {
      diameterBottom: 1.2,
      diameterTop: 3.8,
      height: 2.6,
      tessellation: 24
    }, this.scene);
    dip.position.set(-2.4, 0.5 + 1.3, 0);
    dip.material = mats.pedestalWood;
    dip.parent = parent;
    dip.metadata = meta;

    const berryBody = BABYLON.MeshBuilder.CreateCylinder(`berryBody_${meta.id}`, {
      diameterBottom: 3.6,
      diameterTop: 4.8,
      height: 2.8,
      tessellation: 24
    }, this.scene);
    berryBody.position.set(-2.4, 0.5 + 2.6 + 1.4, 0);
    berryBody.material = mats.strawberryRed;
    berryBody.parent = parent;
    berryBody.metadata = meta;

    const berryDome = BABYLON.MeshBuilder.CreateSphere(`berryDome_${meta.id}`, {
      diameter: 4.8,
      segments: 16
    }, this.scene);
    berryDome.position.set(-2.4, 0.5 + 5.0, 0);
    berryDome.material = mats.strawberryRed;
    berryDome.parent = parent;
    berryDome.metadata = meta;

    const stem = BABYLON.MeshBuilder.CreateCylinder(`berryStem_${meta.id}`, {
      diameter: 0.45,
      height: 1.6,
      tessellation: 12
    }, this.scene);
    stem.position.set(-2.4, 0.5 + 7.4, 0);
    stem.material = mats.leafGreen;
    stem.parent = parent;
    stem.metadata = meta;

    // 3D Challenge Hourglass Timer (right side)
    this._build3DHourglass(parent, mats, meta, 2.8, 0.2);
  }

  /**
   * 10. Touch Genitals: Golden Star Scepter Crystal Wand with 3D Challenge Hourglass
   * @private
   */
  _build3DMagicWandAndHourglass(parent, mats, meta) {
    const BABYLON = this.BABYLON;

    // Plinth
    const plinth = BABYLON.MeshBuilder.CreateCylinder(`wandPlinth_${meta.id}`, {
      diameter: 12.0,
      height: 0.5,
      tessellation: 28
    }, this.scene);
    plinth.position.set(0, 0.25, 0);
    plinth.material = mats.velvetPurple;
    plinth.parent = parent;
    plinth.metadata = meta;

    const plinthRim = BABYLON.MeshBuilder.CreateTorus(`wandPlinthRim_${meta.id}`, {
      diameter: 11.8,
      thickness: 0.25,
      tessellation: 28
    }, this.scene);
    plinthRim.position.set(0, 0.48, 0);
    plinthRim.material = mats.pedestalGold;
    plinthRim.parent = parent;
    plinthRim.metadata = meta;

    // Golden Star Scepter (left side)
    const staff = BABYLON.MeshBuilder.CreateCylinder(`wandStaff_${meta.id}`, {
      diameter: 0.7,
      height: 10.0,
      tessellation: 16
    }, this.scene);
    staff.position.set(-2.4, 0.5 + 5.0, 0);
    staff.material = mats.sculptureGold;
    staff.parent = parent;
    staff.metadata = meta;

    const star = BABYLON.MeshBuilder.CreateSphere(`wandStar_${meta.id}`, {
      diameter: 2.6,
      segments: 8
    }, this.scene);
    star.position.set(-2.4, 11.4, 0);
    star.material = mats.padTouchGenitals;
    star.parent = parent;
    star.metadata = meta;

    const orbit = BABYLON.MeshBuilder.CreateTorus(`wandOrbit_${meta.id}`, {
      diameter: 3.6,
      thickness: 0.28,
      tessellation: 20
    }, this.scene);
    orbit.position.set(-2.4, 11.4, 0);
    orbit.rotation.x = 0.5;
    orbit.material = mats.sculptureGold;
    orbit.parent = parent;
    orbit.metadata = meta;

    // 3D Challenge Hourglass Timer (right side)
    this._build3DHourglass(parent, mats, meta, 2.8, 0.2);
  }

  /**
   * 11. Finger Dares: Scented Candle Votive & Golden Touch Hand with 3D Challenge Hourglass
   * @private
   */
  _build3DScentedCandleAndHand(parent, mats, meta) {
    const BABYLON = this.BABYLON;

    // Plinth
    const plinth = BABYLON.MeshBuilder.CreateCylinder(`candlePlinth_${meta.id}`, {
      diameter: 12.0,
      height: 0.5,
      tessellation: 28
    }, this.scene);
    plinth.position.set(0, 0.25, 0);
    plinth.material = mats.pedestalObsidian;
    plinth.parent = parent;
    plinth.metadata = meta;

    const plinthRim = BABYLON.MeshBuilder.CreateTorus(`candlePlinthRim_${meta.id}`, {
      diameter: 11.8,
      thickness: 0.25,
      tessellation: 28
    }, this.scene);
    plinthRim.position.set(0, 0.48, 0);
    plinthRim.material = mats.pedestalGold;
    plinthRim.parent = parent;
    plinthRim.metadata = meta;

    // Scented Candle in Glass Votive
    const votive = BABYLON.MeshBuilder.CreateCylinder(`votiveGlass_${meta.id}`, {
      diameter: 4.4,
      height: 5.2,
      tessellation: 20
    }, this.scene);
    votive.position.set(-0.8, 0.48 + 2.6, -0.6);
    votive.material = mats.glass;
    votive.parent = parent;
    votive.metadata = meta;

    const wax = BABYLON.MeshBuilder.CreateCylinder(`votiveWax_${meta.id}`, {
      diameter: 3.8,
      height: 4.2,
      tessellation: 20
    }, this.scene);
    wax.position.set(-0.8, 0.48 + 2.1, -0.6);
    wax.material = mats.candleWax;
    wax.parent = parent;
    wax.metadata = meta;

    const flame = BABYLON.MeshBuilder.CreateSphere(`votiveFlame_${meta.id}`, {
      diameter: 1.2,
      segments: 12
    }, this.scene);
    flame.position.set(-0.8, 0.48 + 4.9, -0.6);
    flame.material = mats.candleFlame;
    flame.parent = parent;
    flame.metadata = meta;

    // Sculpted Touch Hand
    const hand = BABYLON.MeshBuilder.CreateBox(`touchHand_${meta.id}`, {
      width: 3.0,
      height: 1.6,
      depth: 2.2
    }, this.scene);
    hand.position.set(-2.0, 3.6, 1.4);
    hand.material = mats.sculptureGold;
    hand.parent = parent;
    hand.metadata = meta;

    // 3D Challenge Hourglass Timer
    this._build3DHourglass(parent, mats, meta, 2.9, 0.5);
  }

  /**
   * 12. Intercourse / High Heat Dares: Intertwined Golden Love Rings & Passion Flames
   * @private
   */
  _build3DPassionateFlames(parent, mats, meta) {
    const BABYLON = this.BABYLON;

    // Plinth
    const plinth = BABYLON.MeshBuilder.CreateCylinder(`flamePlinth_${meta.id}`, {
      diameter: 12.0,
      height: 0.5,
      tessellation: 28
    }, this.scene);
    plinth.position.set(0, 0.25, 0);
    plinth.material = mats.pedestalObsidian;
    plinth.parent = parent;
    plinth.metadata = meta;

    const plinthRim = BABYLON.MeshBuilder.CreateTorus(`flamePlinthRim_${meta.id}`, {
      diameter: 11.8,
      thickness: 0.25,
      tessellation: 28
    }, this.scene);
    plinthRim.position.set(0, 0.48, 0);
    plinthRim.material = mats.ribbonScarlet;
    plinthRim.parent = parent;
    plinthRim.metadata = meta;

    // Intertwined Golden Love Rings
    const ring1 = BABYLON.MeshBuilder.CreateTorus(`loveRing1_${meta.id}`, {
      diameter: 4.2,
      thickness: 0.48,
      tessellation: 24
    }, this.scene);
    ring1.position.set(-1.2, 3.0, 0);
    ring1.rotation.y = 0.35;
    ring1.material = mats.sculptureGold;
    ring1.parent = parent;
    ring1.metadata = meta;

    const ring2 = BABYLON.MeshBuilder.CreateTorus(`loveRing2_${meta.id}`, {
      diameter: 4.2,
      thickness: 0.48,
      tessellation: 24
    }, this.scene);
    ring2.position.set(1.2, 3.0, 0);
    ring2.rotation.y = -0.35;
    ring2.material = mats.sculptureGold;
    ring2.parent = parent;
    ring2.metadata = meta;

    // Rising Passion Flame Sculptures
    const flameCore = BABYLON.MeshBuilder.CreateCylinder(`flameCore_${meta.id}`, {
      diameterBottom: 2.6,
      diameterTop: 0.2,
      height: 8.5,
      tessellation: 16
    }, this.scene);
    flameCore.position.set(0, 8.0, 0);
    flameCore.material = mats.candleFlame;
    flameCore.parent = parent;
    flameCore.metadata = meta;

    const flameOuter = BABYLON.MeshBuilder.CreateCylinder(`flameOuter_${meta.id}`, {
      diameterBottom: 3.4,
      diameterTop: 0.3,
      height: 6.2,
      tessellation: 16
    }, this.scene);
    flameOuter.position.set(0, 6.8, 0);
    flameOuter.material = mats.sandGold;
    flameOuter.parent = parent;
    flameOuter.metadata = meta;

    // 3D Challenge Hourglass Timer
    this._build3DHourglass(parent, mats, meta, 3.2, 0.8);
  }

  /**
   * 13. Anus Dares: Sculpted Blush Velvet Peach on Satin Cushion with 3D Challenge Hourglass
   * @private
   */
  _build3DVelvetPeach(parent, mats, meta) {
    const BABYLON = this.BABYLON;

    // Plinth
    const plinth = BABYLON.MeshBuilder.CreateCylinder(`peachPlinth_${meta.id}`, {
      diameter: 12.0,
      height: 0.5,
      tessellation: 28
    }, this.scene);
    plinth.position.set(0, 0.25, 0);
    plinth.material = mats.velvetPurple;
    plinth.parent = parent;
    plinth.metadata = meta;

    const plinthRim = BABYLON.MeshBuilder.CreateTorus(`peachPlinthRim_${meta.id}`, {
      diameter: 11.8,
      thickness: 0.25,
      tessellation: 28
    }, this.scene);
    plinthRim.position.set(0, 0.48, 0);
    plinthRim.material = mats.pedestalSilver;
    plinthRim.parent = parent;
    plinthRim.metadata = meta;

    // Tufted satin cushion
    const cushion = BABYLON.MeshBuilder.CreateCylinder(`peachCushion_${meta.id}`, {
      diameter: 6.8,
      height: 1.4,
      tessellation: 24
    }, this.scene);
    cushion.position.set(-1.8, 0.48 + 0.7, 0);
    cushion.material = mats.velvetPink;
    cushion.parent = parent;
    cushion.metadata = meta;

    // Sculpted Blush Velvet Peach Fruit
    const cheekL = BABYLON.MeshBuilder.CreateSphere(`cheekL_${meta.id}`, {
      diameter: 3.4,
      segments: 16
    }, this.scene);
    cheekL.position.set(-2.5, 3.4, 0);
    cheekL.material = mats.peachBlush;
    cheekL.parent = parent;
    cheekL.metadata = meta;

    const cheekR = BABYLON.MeshBuilder.CreateSphere(`cheekR_${meta.id}`, {
      diameter: 3.4,
      segments: 16
    }, this.scene);
    cheekR.position.set(-1.1, 3.4, 0);
    cheekR.material = mats.peachBlush;
    cheekR.parent = parent;
    cheekR.metadata = meta;

    const stem = BABYLON.MeshBuilder.CreateCylinder(`peachStem_${meta.id}`, {
      diameter: 0.35,
      height: 1.8,
      tessellation: 12
    }, this.scene);
    stem.position.set(-1.8, 5.2, 0);
    stem.material = mats.leafGreen;
    stem.parent = parent;
    stem.metadata = meta;

    // 3D Challenge Hourglass Timer
    this._build3DHourglass(parent, mats, meta, 2.9, 0.4);
  }

  /**
   * Handles user clicks on 3D obstacle markers to display interactive fancy tooltips.
   * @private
   */
  _handleBoard3DClick(e) {
    if (!this.scene) return;
    const pick = this.scene.pick(this.scene.pointerX, this.scene.pointerY, (m) => Boolean(m.metadata && m.metadata.obstacle));
    if (pick && pick.hit && pick.pickedMesh && pick.pickedMesh.metadata) {
      const meta = pick.pickedMesh.metadata;
      const fakeEl = {
        id: pick.pickedMesh.name || '',
        dataset: { ...meta, hole: String(meta.hole || '') },
        closest: (selector) => {
          if (typeof selector !== 'string') return null;
          if (selector.includes('data-obstacle') && meta.obstacle) {
            return fakeEl;
          }
          if (selector.includes('data-hole') && meta.hole !== undefined) {
            return fakeEl;
          }
          if (selector.includes('peg') && meta.pegWho) {
            return fakeEl;
          }
          return null;
        }
      };

      const boardRenderer = (typeof window !== 'undefined' && window.CrazyCribbage?.BoardRenderer) ? window.CrazyCribbage.BoardRenderer : null;
      if (boardRenderer) {
        const liveCtx = (typeof window.getBoardTooltipContext === 'function')
          ? window.getBoardTooltipContext()
          : (boardRenderer.getBoardTooltipContext ? boardRenderer.getBoardTooltipContext({ settings: this.settings }) : { settings: this.settings });
        const data = boardRenderer.getBoardTooltipData(fakeEl, liveCtx);
        if (data && typeof boardRenderer.showFancyTooltip === 'function') {
          boardRenderer.showFancyTooltip(data, e.clientX, e.clientY);
        }
      }
    }
  }

  /**
   * Calculates proportional peg dimensions derived from the board's hole spacing
   * to guarantee the peg shank snugly fills the hole aperture.
   * @param {string} [role='front']
   * @returns {{ spacing: number, radiusBottom: number, radiusTop: number, height: number, capDiameter: number, shankDepth: number }}
   */
  getPegDimensions(role = 'front') {
    const isFront = role === 'front';
    let spacing = 0;

    // 1. Direct configuration override if specified on the board
    if (typeof this.boardConfig?.holeSpacing === 'number' && this.boardConfig.holeSpacing > 0) {
      spacing = this.boardConfig.holeSpacing;
    }

    // 2. Measure actual regular hole-to-hole pitch on the active track
    if (!spacing && typeof this.holeLookup === 'function') {
      const distances = [];
      const track = this.activeTracks[0] || 'p1';
      const sampleLimit = Math.min(35, this.targetHoles || 120);

      // Start evaluation at hole 1 (hole 0 is the off-board start/staging area)
      for (let h = 1; h < sampleLimit; h++) {
        const p0 = this.getHoleCoords(track, h);
        const p1 = this.getHoleCoords(track, h + 1);
        if (p0 && p1) {
          const dist = Math.hypot(p1.x - p0.x, p1.z - p0.z);
          // Only collect realistic step distances (ignoring duplicates and cross-board leaps)
          if (dist > 0.1 && dist < 40) {
            distances.push(dist);
          }
        }
      }

      if (distances.length > 0) {
        // Group step distances into bins to identify the true repeating hole pitch
        // (excluding street gaps between 5-hole blocks or wide corner steps)
        const bins = new Map();
        for (const d of distances) {
          const key = Math.round(d * 2) / 2;
          bins.set(key, (bins.get(key) || 0) + 1);
        }
        let maxCount = 0;
        let bestBin = null;
        for (const [bin, count] of bins.entries()) {
          if (count > maxCount) {
            maxCount = count;
            bestBin = bin;
          }
        }
        if (bestBin !== null) {
          const inBin = distances.filter(d => Math.abs(d - bestBin) <= 0.6);
          spacing = inBin.reduce((sum, v) => sum + v, 0) / inBin.length;
        } else {
          distances.sort((a, b) => a - b);
          spacing = distances[Math.floor(distances.length / 2)];
        }
      }
    }

    if (!spacing && this.boardBounds) {
      spacing = Math.max(3.0, this.boardBounds.size.x / 36);
    }
    if (!spacing) spacing = 7.75;

    // Optional user or board scale multiplier
    const scale = typeof this.boardConfig?.pegScale === 'number' && this.boardConfig.pegScale > 0
      ? this.boardConfig.pegScale
      : 1.0;

    // Cribbage board hole diameter is typically ~40-44% of center-to-center hole spacing (~3.1-3.4mm for 7.75mm pitch).
    // The peg shank slides cleanly into the hole aperture without spilling over the hole rim.
    // - radiusBottom: radius of lower base entering hole (~40% of spacing in diameter = ~3.1mm, fitting ~3.2-3.5mm holes)
    // - radiusTop: radius at top of peg body (~48% of spacing in diameter = ~3.7mm, subtle authentic taper)
    // - capDiameter: diameter of sphere crown (~52% of spacing = ~4.0mm)
    // - height: visible height above board (~1.75x spacing for front = ~13.5mm; ~1.45x for rear = ~11.2mm)
    // - shankDepth: depth inserted into board (~0.24x spacing = ~1.86mm)
    const radiusBottom = spacing * 0.20 * scale;
    const radiusTop = spacing * 0.24 * scale;
    const height = spacing * (isFront ? 1.75 : 1.45) * scale;
    const capDiameter = radiusTop * 2.15;
    const shankDepth = spacing * 0.24 * scale;

    return { spacing, radiusBottom, radiusTop, height, capDiameter, shankDepth };
  }

  /**
   * Creates an authentic 3D tapered metallic peg mesh in Babylon.js that fills the hole.
   */
  /**
   * Creates an authentic 3D tapered metallic peg mesh in Babylon.js that fills the hole,
   * wrapped with custom avatar body and medallion textures when avatar mode is active.
   */
  createPegMesh(who, role) {
    const BABYLON = this.BABYLON;
    const isFront = role === 'front';
    const dims = this.getPegDimensions(role);
    const { radiusBottom, radiusTop, height, capDiameter, shankDepth } = dims;

    // Lower shank that inserts down into the hole (below hole surface y=0)
    const shank = BABYLON.MeshBuilder.CreateCylinder(`pegShank_${who}_${role}`, {
      height: shankDepth,
      diameterTop: radiusBottom * 2,
      diameterBottom: radiusBottom * 1.85,
      tessellation: 24
    }, this.scene);
    shank.position.y = -shankDepth / 2;

    // Tapered cylinder body above the board surface
    const body = BABYLON.MeshBuilder.CreateCylinder(`pegBody_${who}_${role}`, {
      height,
      diameterTop: radiusTop * 2,
      diameterBottom: radiusBottom * 2,
      tessellation: 24
    }, this.scene);
    body.position.y = height / 2;

    const baseMetallicMat = this.createPegMaterial(who, role);
    const isWrapping = this.isAvatarWrappingActive(who);

    shank.material = baseMetallicMat;
    body.material = isWrapping ? this.getOrCreateAvatarBodyWrapMaterial(who, role) : baseMetallicMat;

    const pegStyle = (this.settings && this.settings.pegStyle) || this.pegStyle || 'classic';
    const headMeshes = this.createPegHeadMeshes(pegStyle, who, role, dims, baseMetallicMat);

    // Merge into single optimized peg mesh where y = 0 sits at the hole rim surface
    // 6th arg: multiMultiMaterials = true to preserve submesh materials
    let peg = null;
    if (typeof BABYLON.Mesh?.MergeMeshes === 'function') {
      peg = BABYLON.Mesh.MergeMeshes([shank, body, ...headMeshes], true, true, undefined, false, true);
    }
    if (!peg) {
      peg = shank;
    }
    peg.name = `peg_${who}_${role}`;
    peg.receiveShadows = true;
    if (this.shadowGenerator) this.shadowGenerator.addShadowCaster(peg);

    if (!isWrapping && pegStyle === 'classic') {
      peg.material = baseMetallicMat;
    }

    return peg;
  }

  /**
   * Retrieves active peg style.
   * @returns {'classic'|'crown'|'skull'|'beermug'|'rivet'|'crystal'}
   */
  getPegStyle() {
    return (this.settings && this.settings.pegStyle) || this.pegStyle || 'classic';
  }

  /**
   * Updates peg head style in real-time and recreates peg meshes across all active tracks.
   * @param {string} style - 'classic' | 'crown' | 'skull' | 'beermug' | 'rivet' | 'crystal'
   */
  setPegStyle(style) {
    if (!style) return;
    this.pegStyle = style;
    if (this.settings) this.settings.pegStyle = style;
    if (typeof localStorage !== 'undefined') {
      try { localStorage.setItem('cc_board_3d_peg_style', style); } catch (_) {}
    }
    const domSel = typeof document !== 'undefined' ? document.getElementById('optPegStyle') : null;
    if (domSel && domSel.value !== style) {
      domSel.value = style;
    }

    // Rebuild active peg meshes seamlessly
    this.pegMeshes.forEach(mesh => {
      try { mesh.dispose(false, true); } catch (_) {}
    });
    this.pegMeshes.clear();

    for (const who of this.activeTracks) {
      const st = this.pegState[who] || { front: 0, rear: 0 };
      for (const role of ['rear', 'front']) {
        const pegMesh = this.createPegMesh(who, role);
        if (this.boardRoot) {
          pegMesh.parent = this.boardRoot;
        }
        this.pegMeshes.set(`${who}_${role}`, pegMesh);
        const hole = role === 'front' ? st.front : st.rear;
        const pt = this.getHoleCoords(who, hole);
        if (pt) pegMesh.position.set(pt.x, pt.y, pt.z);
      }
    }
    this.updatePegStyleToolbarUI();
  }

  /**
   * Synchronizes active peg style state with DOM selector or toolbar if present.
   */
  updatePegStyleToolbarUI() {
    const domSel = typeof document !== 'undefined' ? document.getElementById('optPegStyle') : null;
    if (domSel && this.pegStyle && domSel.value !== this.pegStyle) {
      domSel.value = this.pegStyle;
    }
  }

  /**
   * Constructs custom 3D sculpted peg heads (Royal Crown, Pirate Skull, Beer Mug, Rivet, Neon Crystal, Classic).
   * @param {string} style
   * @param {string} who
   * @param {string} role
   * @param {Object} dims
   * @param {BABYLON.PBRMaterial} baseMetallicMat
   * @returns {BABYLON.Mesh[]} Array of submeshes to merge into the peg
   */
  createPegHeadMeshes(style, who, role, dims, baseMetallicMat) {
    const BABYLON = this.BABYLON;
    const { capDiameter, height } = dims;
    const isWrapping = this.isAvatarWrappingActive(who);

    if (style === 'crown') {
      // 1. ROYAL CROWN (Imperial 5-point flared peaks with gemstones and royal dome)
      const crownBaseH = capDiameter * 0.28;
      const crownBase = BABYLON.MeshBuilder.CreateCylinder(`pegHeadBase_${who}_${role}`, {
        height: crownBaseH,
        diameterTop: capDiameter * 1.15,
        diameterBottom: capDiameter * 0.95,
        tessellation: 24
      }, this.scene);
      crownBase.position.y = height + crownBaseH / 2;
      crownBase.material = baseMetallicMat;

      const crownParts = [crownBase];
      const peakCount = 5;
      const peakR = capDiameter * 0.52;
      for (let i = 0; i < peakCount; i++) {
        const angle = (i * 2 * Math.PI) / peakCount;
        const px = Math.cos(angle) * peakR;
        const pz = Math.sin(angle) * peakR;

        const peak = BABYLON.MeshBuilder.CreateCylinder(`pegPeak_${who}_${role}_${i}`, {
          height: capDiameter * 0.55,
          diameterTop: 0.1,
          diameterBottom: capDiameter * 0.28,
          tessellation: 12
        }, this.scene);
        peak.position.set(px, height + crownBaseH + capDiameter * 0.24, pz);
        if (!peak.rotation && BABYLON.Vector3) peak.rotation = new BABYLON.Vector3(0, 0, 0);
        if (peak.rotation) {
          peak.rotation.z = -Math.cos(angle) * 0.15;
          peak.rotation.x = Math.sin(angle) * 0.15;
        }
        peak.material = baseMetallicMat;
        crownParts.push(peak);

        const gem = BABYLON.MeshBuilder.CreateSphere(`pegGem_${who}_${role}_${i}`, {
          diameter: capDiameter * 0.20,
          segments: 10
        }, this.scene);
        gem.position.set(px * 1.05, height + crownBaseH + capDiameter * 0.52, pz * 1.05);
        const gemMat = new BABYLON.PBRMaterial(`matGem_${who}_${role}_${i}`, this.scene);
        const isRed = (i % 2 === 0);
        gemMat.albedoColor = isRed ? new BABYLON.Color3(0.9, 0.1, 0.2) : new BABYLON.Color3(0.1, 0.8, 0.9);
        gemMat.emissiveColor = gemMat.albedoColor.scale(0.5);
        gemMat.metallic = 0.4;
        gemMat.roughness = 0.15;
        gem.material = gemMat;
        crownParts.push(gem);
      }

      const dome = BABYLON.MeshBuilder.CreateSphere(`pegCrownDome_${who}_${role}`, {
        diameter: capDiameter * 0.85,
        segments: 16
      }, this.scene);
      dome.position.y = height + crownBaseH * 0.7;
      const domeMat = new BABYLON.PBRMaterial(`matCrownDome_${who}_${role}`, this.scene);
      domeMat.albedoColor = new BABYLON.Color3(0.6, 0.08, 0.12);
      domeMat.roughness = 0.7;
      dome.material = domeMat;
      crownParts.push(dome);

      return crownParts;
    }

    if (style === 'skull') {
      // 2. PIRATE / RAIDER SKULL (Sculpted cranium, jaw, and hollow eye sockets)
      const cranium = BABYLON.MeshBuilder.CreateSphere(`pegSkullDome_${who}_${role}`, {
        diameter: capDiameter * 1.05,
        segments: 16
      }, this.scene);
      cranium.position.set(0, height + capDiameter * 0.55, 0);
      cranium.scaling.set(1.0, 1.15, 1.05);

      const jaw = BABYLON.MeshBuilder.CreateBox(`pegSkullJaw_${who}_${role}`, {
        width: capDiameter * 0.62,
        height: capDiameter * 0.38,
        depth: capDiameter * 0.58
      }, this.scene);
      jaw.position.set(0, height + capDiameter * 0.20, capDiameter * 0.12);

      const skullMat = new BABYLON.PBRMaterial(`matSkull_${who}_${role}`, this.scene);
      skullMat.albedoColor = new BABYLON.Color3(0.92, 0.90, 0.85);
      skullMat.roughness = 0.45;
      skullMat.metallic = 0.25;
      cranium.material = skullMat;
      jaw.material = skullMat;

      const eyeL = BABYLON.MeshBuilder.CreateSphere(`pegEyeL_${who}_${role}`, { diameter: capDiameter * 0.26, segments: 8 }, this.scene);
      eyeL.position.set(-capDiameter * 0.24, height + capDiameter * 0.52, capDiameter * 0.48);
      const eyeR = BABYLON.MeshBuilder.CreateSphere(`pegEyeR_${who}_${role}`, { diameter: capDiameter * 0.26, segments: 8 }, this.scene);
      eyeR.position.set(capDiameter * 0.24, height + capDiameter * 0.52, capDiameter * 0.48);
      const socketMat = new BABYLON.PBRMaterial(`matEyeSocket_${who}_${role}`, this.scene);
      socketMat.albedoColor = new BABYLON.Color3(0.05, 0.05, 0.06);
      socketMat.roughness = 0.9;
      eyeL.material = socketMat;
      eyeR.material = socketMat;

      return [cranium, jaw, eyeL, eyeR];
    }

    if (style === 'beermug') {
      // 3. BEER MUG (Frosted glass stein with golden brew, foam head, and glass handle)
      const mugH = capDiameter * 1.1;
      const mugGlass = BABYLON.MeshBuilder.CreateCylinder(`pegMugGlass_${who}_${role}`, {
        height: mugH,
        diameter: capDiameter * 0.95,
        tessellation: 20
      }, this.scene);
      mugGlass.position.y = height + mugH / 2;
      const glassMat = new BABYLON.PBRMaterial(`matMugGlass_${who}_${role}`, this.scene);
      glassMat.albedoColor = new BABYLON.Color3(0.9, 0.95, 1.0);
      glassMat.alpha = 0.45;
      glassMat.roughness = 0.12;
      glassMat.metallic = 0.05;
      mugGlass.material = glassMat;

      const beerLiquid = BABYLON.MeshBuilder.CreateCylinder(`pegBeerLiq_${who}_${role}`, {
        height: mugH * 0.78,
        diameter: capDiameter * 0.82,
        tessellation: 18
      }, this.scene);
      beerLiquid.position.y = height + (mugH * 0.78) / 2 + 0.1;
      const beerMat = new BABYLON.PBRMaterial(`matBeerLiq_${who}_${role}`, this.scene);
      beerMat.albedoColor = new BABYLON.Color3(0.95, 0.65, 0.12);
      beerMat.emissiveColor = new BABYLON.Color3(0.4, 0.25, 0.02);
      beerMat.roughness = 0.2;
      beerLiquid.material = beerMat;

      const foam = BABYLON.MeshBuilder.CreateCylinder(`pegMugFoam_${who}_${role}`, {
        height: capDiameter * 0.26,
        diameter: capDiameter * 0.92,
        tessellation: 18
      }, this.scene);
      foam.position.y = height + mugH * 0.88;
      const foamMat = new BABYLON.PBRMaterial(`matFoam_${who}_${role}`, this.scene);
      foamMat.albedoColor = new BABYLON.Color3(0.98, 0.98, 0.95);
      foamMat.roughness = 0.85;
      foam.material = foamMat;

      const handle = BABYLON.MeshBuilder.CreateTorus(`pegMugHandle_${who}_${role}`, {
        diameter: capDiameter * 0.55,
        thickness: capDiameter * 0.14,
        tessellation: 16
      }, this.scene);
      handle.position.set(capDiameter * 0.52, height + mugH * 0.52, 0);
      if (!handle.rotation && BABYLON.Vector3) handle.rotation = new BABYLON.Vector3(0, 0, 0);
      if (handle.rotation) handle.rotation.z = Math.PI / 2;
      handle.material = glassMat;

      return [mugGlass, beerLiquid, foam, handle];
    }

    if (style === 'rivet') {
      // 4. INDUSTRIAL RIVET (Hexagonal machine bolt head with recessed socket)
      const boltH = capDiameter * 0.55;
      const hexBolt = BABYLON.MeshBuilder.CreateCylinder(`pegHexBolt_${who}_${role}`, {
        height: boltH,
        diameter: capDiameter * 1.18,
        tessellation: 6
      }, this.scene);
      hexBolt.position.y = height + boltH / 2;

      const boltDome = BABYLON.MeshBuilder.CreateCylinder(`pegBoltDome_${who}_${role}`, {
        height: boltH * 0.35,
        diameterTop: capDiameter * 0.85,
        diameterBottom: capDiameter * 1.15,
        tessellation: 20
      }, this.scene);
      boltDome.position.y = height + boltH + (boltH * 0.35) / 2;

      const socketHole = BABYLON.MeshBuilder.CreateCylinder(`pegBoltSocket_${who}_${role}`, {
        height: 0.15,
        diameter: capDiameter * 0.44,
        tessellation: 6
      }, this.scene);
      socketHole.position.y = height + boltH + boltH * 0.35;
      const socketMat = new BABYLON.PBRMaterial(`matBoltSocket_${who}_${role}`, this.scene);
      socketMat.albedoColor = new BABYLON.Color3(0.08, 0.08, 0.10);
      socketHole.material = socketMat;

      hexBolt.material = baseMetallicMat;
      boltDome.material = baseMetallicMat;

      return [hexBolt, boltDome, socketHole];
    }

    if (style === 'crystal') {
      // 5. NEON CRYSTAL (Prismatic gemstone bipyramid with vibrant emissive facet glow)
      const crystalH = capDiameter * 1.15;
      const topCone = BABYLON.MeshBuilder.CreateCylinder(`pegCrystTop_${who}_${role}`, {
        height: crystalH * 0.58,
        diameterTop: 0.1,
        diameterBottom: capDiameter * 1.08,
        tessellation: 8
      }, this.scene);
      topCone.position.y = height + (crystalH * 0.58) / 2 + (crystalH * 0.42);

      const botCone = BABYLON.MeshBuilder.CreateCylinder(`pegCrystBot_${who}_${role}`, {
        height: crystalH * 0.42,
        diameterTop: capDiameter * 1.08,
        diameterBottom: capDiameter * 0.55,
        tessellation: 8
      }, this.scene);
      botCone.position.y = height + (crystalH * 0.42) / 2;

      const crystalMat = new BABYLON.PBRMaterial(`matCrystal_${who}_${role}`, this.scene);
      const isP1 = (who === 'p1' || who === 'you' || who === 'team1');
      const crystalCol = isP1 ? new BABYLON.Color3(0.0, 0.95, 0.9) : new BABYLON.Color3(1.0, 0.15, 0.55);
      crystalMat.albedoColor = crystalCol;
      crystalMat.emissiveColor = crystalCol.scale(0.55);
      crystalMat.metallic = 0.15;
      crystalMat.roughness = 0.12;
      crystalMat.alpha = 0.88;

      topCone.material = crystalMat;
      botCone.material = crystalMat;

      return [topCone, botCone];
    }

    // 6. CLASSIC (Spherical rounded cap + avatar medallion coin)
    const cap = BABYLON.MeshBuilder.CreateSphere(`pegCap_${who}_${role}`, {
      diameter: capDiameter,
      segments: 20
    }, this.scene);
    cap.position.y = height;

    const medallionH = capDiameter * 0.16;
    const medallion = BABYLON.MeshBuilder.CreateCylinder(`pegMedallion_${who}_${role}`, {
      height: medallionH,
      diameter: capDiameter * 1.04,
      tessellation: 28
    }, this.scene);
    medallion.position.y = height + capDiameter * 0.44;

    if (isWrapping) {
      cap.material = baseMetallicMat;
      medallion.material = this.getOrCreateAvatarMedallionMaterial(who, role);
    } else {
      cap.material = baseMetallicMat;
      medallion.material = baseMetallicMat;
    }

    return [cap, medallion];
  }

  /**
   * Checks whether custom avatar wrapping should be used on pegs instead of metallic skin override.
   * @param {string} who - Track identifier (p1, p2, etc.)
   * @returns {boolean}
   */
  isAvatarWrappingActive(who) {
    const skin = this.settings?.pegSkin;
    if (skin && skin !== 'brass' && skin !== 'classic' && skin !== 'avatar' && skin !== 'default') {
      return false;
    }
    return true;
  }

  /**
   * Resolves the avatar preset ID for a specific track.
   * @param {string} who - Track identifier (p1, p2, team1, etc.)
   * @returns {string}
   */
  getTrackAvatarId(who) {
    if (typeof window !== 'undefined' && window.BoardRenderer?.getTrackAvatarId) {
      return window.BoardRenderer.getTrackAvatarId(who, this.settings);
    }
    const isP1 = (who === 'p1' || who === 'you' || who === 'team1');
    if (isP1) {
      const avMgr = (typeof window !== 'undefined' && window.AvatarManager) || AvatarManager;
      return avMgr ? avMgr.getSelectedAvatarId() : 'joker';
    }
    if (this.settings?.seats && Array.isArray(this.settings.seats)) {
      const s = this.settings.seats.find(x => x && (x.id === who || (who === 'cpu' && x.id === 'p2')));
      if (s && s.avatar) {
        if (s.avatar === 'custom' && !isP1) {
          return 'custom_' + s.id;
        }
        return s.avatar;
      }
    }
    if (who === 'p2' || who === 'cpu' || who === 'team2') {
      const isVsHuman = Boolean(this.settings?.vsHuman || this.settings?.isHvH || this.settings?.opp === 'human');
      if (isVsHuman) return 'queen';
      const rawProf = this.settings?.optAiProfile || 'shark';
      const avatarMap = {
        master: 'king',
        maverick: 'steampunk',
        regular: 'velvet_fox',
        casual: 'pint_master',
        rookie: 'jack',
        gambler: 'gambler',
        turtler: 'turtler',
        shark: 'shark'
      };
      if (avatarMap[rawProf]) return avatarMap[rawProf];
      const aiProfile = (typeof window !== 'undefined' && window.CribbageAI?.normalizeProfile)
        ? window.CribbageAI.normalizeProfile(rawProf)
        : rawProf;
      return avatarMap[aiProfile] || 'shark';
    }
    const multiplayerAvatars = {
      p3: 'pint_master',
      p4: 'steampunk',
      p5: 'clash_helm',
      p6: 'velvet_fox',
      p7: 'cannon_commander',
      p8: 'lightning_29',
      team3: 'pint_master',
      team4: 'steampunk'
    };
    return multiplayerAvatars[who] || 'jack';
  }

  /**
   * Retrieves or creates a PBRMaterial textured with the avatar cylindrical body wrap.
   * @param {string} who
   * @param {string} role
   * @returns {BABYLON.PBRMaterial}
   */
  getOrCreateAvatarBodyWrapMaterial(who, role) {
    const key = `matPegBodyWrap_${who}_${role}`;
    if (this._avatarMaterials && this._avatarMaterials.has(key)) {
      return this._avatarMaterials.get(key);
    }

    const BABYLON = this.BABYLON;
    const mat = new BABYLON.PBRMaterial(key, this.scene);
    mat.metallic = 0.25;
    mat.roughness = 0.35;

    const avatarId = this.getTrackAvatarId(who);
    AvatarManager.createAvatarBodyWrapCanvas(avatarId, 512, 256).then(canvas => {
      if (!canvas || this.isDestroyed || !this.scene || this.scene.isDisposed) return;
      const texKey = `texPegBody_${who}_${role}`;
      let dynTex = this._avatarTextures?.get(texKey);
      if (!dynTex || dynTex.isDisposed) {
        dynTex = new BABYLON.DynamicTexture(texKey, { width: canvas.width, height: canvas.height }, this.scene, true);
        if (this._avatarTextures) this._avatarTextures.set(texKey, dynTex);
      }
      const ctx = dynTex.getContext();
      ctx.drawImage(canvas, 0, 0);
      dynTex.update();
      mat.albedoTexture = dynTex;
    }).catch(err => {
      console.warn('[BoardRenderer3D] Avatar body wrap texture error:', err);
    });

    if (this._avatarMaterials) this._avatarMaterials.set(key, mat);
    return mat;
  }

  /**
   * Retrieves or creates a PBRMaterial textured with the avatar coin medallion disc.
   * @param {string} who
   * @param {string} role
   * @returns {BABYLON.PBRMaterial}
   */
  getOrCreateAvatarMedallionMaterial(who, role) {
    const key = `matPegMedallion_${who}_${role}`;
    if (this._avatarMaterials && this._avatarMaterials.has(key)) {
      return this._avatarMaterials.get(key);
    }

    const BABYLON = this.BABYLON;
    const mat = new BABYLON.PBRMaterial(key, this.scene);
    mat.metallic = 0.45;
    mat.roughness = 0.22;

    const avatarId = this.getTrackAvatarId(who);
    AvatarManager.createAvatarMedallionCanvas(avatarId, 256).then(canvas => {
      if (!canvas || this.isDestroyed || !this.scene || this.scene.isDisposed) return;
      const texKey = `texPegMed_${who}_${role}`;
      let dynTex = this._avatarTextures?.get(texKey);
      if (!dynTex || dynTex.isDisposed) {
        dynTex = new BABYLON.DynamicTexture(texKey, { width: canvas.width, height: canvas.height }, this.scene, true);
        if (this._avatarTextures) this._avatarTextures.set(texKey, dynTex);
      }
      const ctx = dynTex.getContext();
      ctx.drawImage(canvas, 0, 0);
      dynTex.update();
      mat.albedoTexture = dynTex;
    }).catch(err => {
      console.warn('[BoardRenderer3D] Avatar medallion texture error:', err);
    });

    if (this._avatarMaterials) this._avatarMaterials.set(key, mat);
    return mat;
  }

  /**
   * Synchronizes 3D peg avatar textures in real-time when the player swaps their persona.
   */
  async updatePegAvatars() {
    if (this.isDestroyed || !this.scene || this.scene.isDisposed) return;
    for (const who of this.activeTracks) {
      if (!this.isAvatarWrappingActive(who)) continue;
      const avatarId = this.getTrackAvatarId(who);
      const [bodyCanvas, medCanvas] = await Promise.all([
        AvatarManager.createAvatarBodyWrapCanvas(avatarId, 512, 256),
        AvatarManager.createAvatarMedallionCanvas(avatarId, 256)
      ]);
      if (this.isDestroyed || !this.scene || this.scene.isDisposed) return;

      for (const role of ['front', 'rear']) {
        const bodyTexKey = `texPegBody_${who}_${role}`;
        let dynBody = this._avatarTextures?.get(bodyTexKey);
        if (dynBody && !dynBody.isDisposed && bodyCanvas) {
          const ctx = dynBody.getContext();
          ctx.clearRect(0, 0, bodyCanvas.width, bodyCanvas.height);
          ctx.drawImage(bodyCanvas, 0, 0);
          dynBody.update();
        }

        const medTexKey = `texPegMed_${who}_${role}`;
        let dynMed = this._avatarTextures?.get(medTexKey);
        if (dynMed && !dynMed.isDisposed && medCanvas) {
          const ctx = dynMed.getContext();
          ctx.clearRect(0, 0, medCanvas.width, medCanvas.height);
          ctx.drawImage(medCanvas, 0, 0);
          dynMed.update();
        }
      }
    }
  }

  /**
   * PBR Material generator for metallic peg finishes.
   */
  createPegMaterial(who, role) {
    const BABYLON = this.BABYLON;
    const skin = this.settings.pegSkin || 'brass';
    const isP1 = (who === 'p1' || who === 'team1' || who === 'you');
    const isTeam2 = (who === 'p2' || who === 'team2' || who === 'cpu');
    const isTeam3 = (who === 'p3' || who === 'team3' || who === 'p5');
    const isTeam4 = (who === 'p4' || who === 'team4' || who === 'p6' || who === 'p7' || who === 'p8');

    const mat = new BABYLON.PBRMaterial(`matPeg_${who}_${skin}_${role}`, this.scene);

    if (isP1) {
      if (skin === 'silver') {
        mat.albedoColor = new BABYLON.Color3(0.9, 0.92, 0.95);
        mat.metallic = 0.92;
        mat.roughness = 0.2;
      } else if (skin === 'walnut') {
        mat.albedoColor = new BABYLON.Color3(0.4, 0.25, 0.14);
        mat.metallic = 0.05;
        mat.roughness = 0.55;
      } else if (skin === 'cherry') {
        mat.albedoColor = new BABYLON.Color3(0.55, 0.12, 0.14);
        mat.metallic = 0.15;
        mat.roughness = 0.45;
      } else if (skin === 'glow') {
        const glowCol = new BABYLON.Color3(0, 0.9, 1);
        mat.albedoColor = glowCol;
        mat.emissiveColor = glowCol.scale(0.45);
        mat.metallic = 0.2;
        mat.roughness = 0.2;
      } else {
        // Default: Polished Gold / Brass
        mat.albedoColor = new BABYLON.Color3(0.92, 0.72, 0.18);
        mat.metallic = 0.90;
        mat.roughness = 0.20;
      }
    } else if (isTeam2) {
      if (skin === 'silver') {
        mat.albedoColor = new BABYLON.Color3(0.95, 0.35, 0.35);
        mat.metallic = 0.92;
        mat.roughness = 0.2;
      } else if (skin === 'walnut') {
        mat.albedoColor = new BABYLON.Color3(0.18, 0.1, 0.05);
        mat.metallic = 0.05;
        mat.roughness = 0.55;
      } else if (skin === 'cherry') {
        mat.albedoColor = new BABYLON.Color3(0.42, 0.1, 0.12);
        mat.metallic = 0.15;
        mat.roughness = 0.45;
      } else if (skin === 'glow') {
        const glowCol = new BABYLON.Color3(1, 0.1, 0.4);
        mat.albedoColor = glowCol;
        mat.emissiveColor = glowCol.scale(0.45);
        mat.metallic = 0.2;
        mat.roughness = 0.2;
      } else {
        // Vibrant Crimson Metallic
        mat.albedoColor = new BABYLON.Color3(0.85, 0.2, 0.2);
        mat.metallic = 0.90;
        mat.roughness = 0.20;
      }
    } else if (isTeam3) {
      // Royal Sapphire Blue Metallic
      mat.albedoColor = new BABYLON.Color3(0.15, 0.65, 0.95);
      mat.metallic = 0.88;
      mat.roughness = 0.22;
    } else if (isTeam4) {
      // Royal Amethyst Purple Metallic
      mat.albedoColor = new BABYLON.Color3(0.72, 0.25, 0.92);
      mat.metallic = 0.88;
      mat.roughness = 0.22;
    } else {
      mat.albedoColor = new BABYLON.Color3(0.85, 0.2, 0.2);
      mat.metallic = 0.90;
      mat.roughness = 0.20;
    }

    // Authentic clearcoat lacquer for metallic sheen and light reflection
    if (mat.clearCoat) {
      mat.clearCoat.isEnabled = true;
      mat.clearCoat.intensity = (skin === 'walnut' && isP1) ? 0.3 : 0.7;
      mat.clearCoat.roughness = 0.12;
    }

    return mat;
  }

  /**
   * Retrieves 3D coordinates for a specific hole and player.
   */
  getHoleCoords(who, holeNum) {
    if (typeof this.holeLookup === 'function') {
      const pt = this.holeLookup(who, holeNum);
      if (pt) return pt;
    }
    return { x: 0, y: 0.8, z: 0 };
  }

  /**
   * Checks whether a specific hole number is explicitly defined for a track in coordinate data.
   * @param {string} who - Track identifier (p1, p2, team1, etc.)
   * @param {number} holeNum - Hole index (e.g. -1 for secondary starting peg)
   * @returns {boolean}
   */
  hasHole(who, holeNum) {
    if (typeof this.holeLookup?.has === 'function') {
      return this.holeLookup.has(who, holeNum);
    }
    const data = this.boardConfig?.coordinatesData || this.boardConfig?.holeData;
    if (data) {
      let resolvedWho = who;
      if (who === 'you' || who === 'player1' || who === 'team1') resolvedWho = 'p1';
      else if (who === 'cpu' || who === 'player2' || who === 'team2') resolvedWho = 'p2';
      else if (who === 'player3' || who === 'team3') resolvedWho = 'p3';
      else if (who === 'player4' || who === 'team4') resolvedWho = 'p4';

      if (Array.isArray(data)) {
        return data.some(pt => {
          const track = pt.track || pt.who || 'p1';
          const h = parseInt(pt.hole !== undefined ? pt.hole : pt.index, 10);
          return (track === resolvedWho || track === 'p1') && h === holeNum;
        });
      } else if (typeof data === 'object') {
        const tracks = data.tracks || data;
        const trackPts = tracks[resolvedWho] || tracks['p1'];
        if (Array.isArray(trackPts)) {
          return trackPts.some(pt => {
            const h = parseInt(pt.hole !== undefined ? pt.hole : pt.index, 10);
            return h === holeNum;
          });
        }
      }
    }
    return false;
  }

  /**
   * Resolves the numeric score for a track from a score object,
   * accounting for alternate track identifiers (p1/you/team1, p2/cpu/team2, etc.).
   * @param {Object} scoresObj
   * @param {string} track
   * @returns {number}
   */
  getTrackScore(scoresObj, track) {
    if (!scoresObj) return 0;
    if (scoresObj[track] !== undefined) return Number(scoresObj[track]) || 0;
    let norm = track;
    if (track === 'you' || track === 'player1' || track === 'team1') norm = 'p1';
    else if (track === 'cpu' || track === 'player2' || track === 'team2') norm = 'p2';
    else if (track === 'player3' || track === 'team3') norm = 'p3';
    else if (track === 'player4' || track === 'team4') norm = 'p4';

    if (scoresObj[norm] !== undefined) return Number(scoresObj[norm]) || 0;

    if (norm === 'p1') {
      if (scoresObj.you !== undefined) return Number(scoresObj.you) || 0;
      if (scoresObj.player1 !== undefined) return Number(scoresObj.player1) || 0;
      if (scoresObj.team1 !== undefined) return Number(scoresObj.team1) || 0;
    } else if (norm === 'p2') {
      if (scoresObj.cpu !== undefined) return Number(scoresObj.cpu) || 0;
      if (scoresObj.player2 !== undefined) return Number(scoresObj.player2) || 0;
      if (scoresObj.team2 !== undefined) return Number(scoresObj.team2) || 0;
    } else if (norm === 'p3') {
      if (scoresObj.player3 !== undefined) return Number(scoresObj.player3) || 0;
      if (scoresObj.team3 !== undefined) return Number(scoresObj.team3) || 0;
      if (scoresObj.team1 !== undefined) return Number(scoresObj.team1) || 0;
      if (scoresObj.p1 !== undefined) return Number(scoresObj.p1) || 0;
      if (scoresObj.you !== undefined) return Number(scoresObj.you) || 0;
    } else if (norm === 'p4') {
      if (scoresObj.player4 !== undefined) return Number(scoresObj.player4) || 0;
      if (scoresObj.team4 !== undefined) return Number(scoresObj.team4) || 0;
      if (scoresObj.team2 !== undefined) return Number(scoresObj.team2) || 0;
      if (scoresObj.p2 !== undefined) return Number(scoresObj.p2) || 0;
      if (scoresObj.cpu !== undefined) return Number(scoresObj.cpu) || 0;
    }
    return 0;
  }

  /**
   * Synchronizes 3D peg positions with game score updates.
   * Animates a leaping parabolic arc for the moving front peg.
   */
  updatePegs(scores = {}, prevScores = {}, animate = true, scoringEvent = null) {
    if (this.seatManager && scores) {
      this.seatManager.updateScores(scores);
    }
    for (const who of this.activeTracks) {
      const hasMinusOne = this.hasHole(who, -1);
      const defaultRear = hasMinusOne ? -1 : 0;
      const frontScore = Math.min(this.targetHoles, this.getTrackScore(scores, who));
      const rawRearScore = Math.min(this.targetHoles, this.getTrackScore(prevScores, who));
      const rearScore = (frontScore === 0 && hasMinusOne) ? -1 : rawRearScore;

      const oldState = this.pegState[who] || { front: 0, rear: defaultRear };

      // Update front peg
      if (frontScore !== oldState.front) {
        const pegMesh = this.pegMeshes.get(`${who}_front`);
        const startPt = this.getHoleCoords(who, oldState.front);
        const endPt = this.getHoleCoords(who, frontScore);

        if (pegMesh && startPt && endPt) {
          const delta = Math.abs(frontScore - oldState.front);
          const is15 = delta === 2 && scoringEvent && /fifteen/i.test(scoringEvent.why || '');
          const is31 = scoringEvent && /thirty|31/i.test(scoringEvent.why || '');
          const isSpecial = delta >= 6 || is15 || is31;
          const scoringInfo = { is15, is31, isSpecial, delta, who };

          if (animate && frontScore !== oldState.front) {
            this.animatePegLeap(pegMesh, startPt, endPt, 450, scoringInfo);
          } else {
            pegMesh.position.set(endPt.x, endPt.y, endPt.z);
          }
        }
      }

      // Update rear peg
      if (rearScore !== oldState.rear) {
        const pegMesh = this.pegMeshes.get(`${who}_rear`);
        const endPt = this.getHoleCoords(who, rearScore);
        if (pegMesh && endPt) {
          pegMesh.position.set(endPt.x, endPt.y, endPt.z);
        }
      }

      this.pegState[who] = { front: frontScore, rear: rearScore };
    }

    // If watching in Leading Peg close-up mode, follow the active action
    if (this.activePreset === 'closeup') {
      this.setCameraPreset('closeup', true);
    }
  }

  /**
   * Animates a smooth 3D parabolic leap in Babylon coordinate space
   * emitting luminous particle trail sparks and hole socket landing ripples.
   */
  animatePegLeap(mesh, startPt, endPt, durationMs = 450, scoringInfo = null) {
    const startTime = performance.now();
    const dims = this.getPegDimensions('front');
    const horizDist = Math.hypot(endPt.x - startPt.x, endPt.z - startPt.z);
    const vertDelta = Math.abs(((endPt && endPt.y) || 0) - ((startPt && startPt.y) || 0));
    const baseApex = Math.max(dims.height * 0.9, Math.min(dims.height * 2.2, horizDist * 0.45));
    const leapHeight = (vertDelta / 2) + baseApex;

    const trackColor = this.getTrackAccentColor(mesh?.name || '');
    const isSpecial = Boolean(scoringInfo?.isSpecial);
    let lastSparkTime = 0;

    const anim = {
      update: (now) => {
        const elapsed = now - startTime;
        const t = Math.min(1, elapsed / durationMs);

        // Smooth easeInOutQuad
        const ease = t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;

        const x = startPt.x + (endPt.x - startPt.x) * ease;
        const z = startPt.z + (endPt.z - startPt.z) * ease;
        const arcY = leapHeight * 4 * t * (1 - t);
        const y = startPt.y + (endPt.y - startPt.y) * ease + arcY;

        mesh.position.set(x, y, z);

        // Emit particle trail sparks during flight
        if (elapsed - lastSparkTime > 32 && t < 0.95) {
          lastSparkTime = elapsed;
          this.emitLeapSpark(x, y + dims.height * 0.45, z, trackColor, isSpecial);
        }

        if (t >= 1) {
          // Landing impact ripple & spark burst on hole
          this.emitLandingImpact(endPt, trackColor, isSpecial);
          return false;
        }

        return true;
      }
    };

    this.animatingPegs.push(anim);
  }

  /**
   * Emits a floating radiant spark quad during peg flight.
   */
  emitLeapSpark(x, y, z, color, isSpecial = false) {
    if (!this.scene || !this.BABYLON || !this.BABYLON.MeshBuilder || this.isDestroyed) return;
    try {
      const BABYLON = this.BABYLON;
      const size = isSpecial ? 0.85 : 0.55;
      const spark = BABYLON.MeshBuilder.CreatePlane(`spark_${Math.random()}`, { size }, this.scene);
      spark.position.set(x + (Math.random() - 0.5) * 0.3, y + (Math.random() - 0.5) * 0.3, z + (Math.random() - 0.5) * 0.3);
      spark.billboardMode = BABYLON.Mesh.BILLBOARDMODE_ALL;
      spark.isPickable = false;
      if (this.boardRoot) spark.parent = this.boardRoot;

      // Material pooling: reuse spark material per track color to avoid shader recompilation churn
      if (!this._sparkMaterials) this._sparkMaterials = new Map();
      const colorKey = (color && typeof color.toHexString === 'function') ? color.toHexString() : `${color?.r}_${color?.g}_${color?.b}`;
      let mat = this._sparkMaterials.get(colorKey);
      if (!mat || mat.isDisposed) {
        mat = new BABYLON.PBRMaterial(`matSpark_${colorKey}`, this.scene);
        mat.albedoColor = color;
        mat.emissiveColor = (color && typeof color.scale === 'function') ? color.scale(1.2) : color;
        mat.roughness = 0.1;
        this._sparkMaterials.set(colorKey, mat);
      }
      spark.material = mat;

      const startNow = performance.now();
      const lifeMs = isSpecial ? 360 : 260;

      this.animatingPegs.push({
        update: (now) => {
          const el = now - startNow;
          const prog = Math.min(1, el / lifeMs);
          spark.scaling.setAll(Math.max(0.01, 1 - prog));
          spark.position.y += 0.04 * (1 - prog);
          if (prog >= 1) {
            try { spark.dispose(false, false); } catch (_) {}
            return false;
          }
          return true;
        }
      });
    } catch (_) {}
  }

  /**
   * Spawns an expanding surface ring ripple and outward spark spray when a peg lands.
   */
  emitLandingImpact(endPt, color, isSpecial = false) {
    if (!this.scene || !this.BABYLON || !this.BABYLON.MeshBuilder || this.isDestroyed) return;
    try {
      const BABYLON = this.BABYLON;
      const baseR = isSpecial ? 1.6 : 1.1;
      const ring = BABYLON.MeshBuilder.CreateDisc(`landingRing_${Math.random()}`, {
        radius: baseR,
        tessellation: 20
      }, this.scene);
      const surfaceY = (endPt && endPt.y !== undefined) ? endPt.y : 0.8;
      ring.position.set(endPt.x, surfaceY + 0.06, endPt.z);
      ring.rotation.x = Math.PI / 2;
      ring.isPickable = false;
      if (this.boardRoot) ring.parent = this.boardRoot;

      // Material pooling: reuse landing ring material per track color
      if (!this._ringMaterials) this._ringMaterials = new Map();
      const colorKey = (color && typeof color.toHexString === 'function') ? color.toHexString() : `${color?.r}_${color?.g}_${color?.b}`;
      let mat = this._ringMaterials.get(colorKey);
      if (!mat || mat.isDisposed) {
        mat = new BABYLON.PBRMaterial(`matRing_${colorKey}`, this.scene);
        mat.albedoColor = color;
        mat.emissiveColor = (color && typeof color.scale === 'function') ? color.scale(1.4) : color;
        mat.alpha = 0.85;
        this._ringMaterials.set(colorKey, mat);
      }
      ring.material = mat;

      const startNow = performance.now();
      const lifeMs = isSpecial ? 480 : 340;

      this.animatingPegs.push({
        update: (now) => {
          const el = now - startNow;
          const prog = Math.min(1, el / lifeMs);
          const scale = 1 + (prog * (isSpecial ? 2.2 : 1.6));
          ring.scaling.set(scale, scale, scale);
          mat.alpha = Math.max(0, 0.85 * (1 - prog));
          if (prog >= 1) {
            try { ring.dispose(false, false); } catch (_) {}
            return false;
          }
          return true;
        }
      });

      if (isSpecial) {
        for (let i = 0; i < 6; i++) {
          const angle = (i * Math.PI * 2) / 6;
          const dist = 1.2 + Math.random() * 0.8;
          this.emitLeapSpark(endPt.x + Math.cos(angle) * dist, surfaceY + 0.6 + Math.random() * 0.8, endPt.z + Math.sin(angle) * dist, color, false);
        }
      }
    } catch (_) {}
  }

  /**
   * Resolves the primary track accent color for particle sparks and rings.
   */
  getTrackAccentColor(meshName = '') {
    const BABYLON = this.BABYLON;
    if (!BABYLON || !BABYLON.Color3) {
      return { r: 1, g: 0.84, b: 0, scale: (x) => ({ r: x, g: 0.84 * x, b: 0 }) };
    }
    const n = String(meshName || '').toLowerCase();
    if (n.includes('p1') || n.includes('team1') || n.includes('you')) {
      return new BABYLON.Color3(1.0, 0.84, 0.1);
    }
    if (n.includes('p2') || n.includes('team2') || n.includes('cpu')) {
      return new BABYLON.Color3(0.95, 0.22, 0.22);
    }
    if (n.includes('p3') || n.includes('team3')) {
      return new BABYLON.Color3(0.15, 0.72, 1.0);
    }
    if (n.includes('p4') || n.includes('team4')) {
      return new BABYLON.Color3(0.78, 0.25, 0.95);
    }
    return new BABYLON.Color3(1.0, 0.85, 0.2);
  }

  /**
   * Triggers an automated dynamic cinematic slow-motion orbit around the target peg or hole
   * on major gameplay achievements (24+ point hands, 4-of-a-kind pegging, winning peg 121/481).
   * @param {Object} event
   * @param {'big_hand'|'four_of_a_kind'|'winning_peg'} [event.type]
   * @param {number} [event.score]
   * @param {string} [event.who]
   * @param {number} [event.hole]
   */
  triggerActionCam(event = {}) {
    const opt = this.settings?.optActionCam;
    if (opt !== 'yes') return;
    if (this._isActionCamActive || !this.camera || this.isDestroyed || !this.scene) return;

    this._isActionCamActive = true;
    this._savedActionCamState = {
      target: this.camera.target.clone(),
      alpha: this.camera.alpha,
      beta: this.camera.beta,
      radius: this.camera.radius
    };

    const type = event.type || 'big_hand';
    const who = event.who || 'p1';
    let targetPt = null;
    let title = 'ACTION REPLAY';

    if (type === 'winning_peg') {
      const hole = event.hole || this.targetHoles || 121;
      targetPt = this.getHoleCoords(who, hole);
      title = `WINNING PEG • HOLE ${hole}!`;
    } else if (type === 'four_of_a_kind') {
      const st = this.pegState[who] || { front: 0 };
      targetPt = this.getHoleCoords(who, st.front);
      title = '4-OF-A-KIND PEGGING • 12 PTS!';
    } else {
      const score = event.score || 24;
      const st = this.pegState[who] || { front: 0 };
      targetPt = this.getHoleCoords(who, st.front);
      title = `${score}+ POINT MONSTER HAND!`;
    }

    if (!targetPt) {
      targetPt = { x: 0, y: 1.0, z: 0 };
    }

    this.showActionCamOverlay(title);

    const heroTarget = new this.BABYLON.Vector3(targetPt.x, targetPt.y + 1.2, targetPt.z);
    const heroRadius = Math.max(16, (this.fitRadius || 55) * 0.28);
    const heroBeta = Math.PI / 4.6;
    const heroAlpha = this.camera.alpha + 0.75;

    this.animateCameraTo({
      target: heroTarget,
      alpha: heroAlpha,
      beta: heroBeta,
      radius: heroRadius,
      durationMs: 2500
    });

    const sparkColor = this.getTrackAccentColor(who);
    this.emitLandingImpact(targetPt, sparkColor, true);

    if (this._actionCamTimer) clearTimeout(this._actionCamTimer);
    this._actionCamTimer = setTimeout(() => {
      this.cancelActionCam();
    }, 2800);
  }

  /**
   * Cancels the active Action Cam sequence and smoothly returns to the player's vantage point.
   */
  cancelActionCam() {
    if (!this._isActionCamActive) return;
    this._isActionCamActive = false;
    if (this._actionCamTimer) {
      clearTimeout(this._actionCamTimer);
      this._actionCamTimer = null;
    }
    this.hideActionCamOverlay();

    if (this._savedActionCamState && this.camera && !this.isDestroyed) {
      this.animateCameraTo({
        target: this._savedActionCamState.target,
        alpha: this._savedActionCamState.alpha,
        beta: this._savedActionCamState.beta,
        radius: this._savedActionCamState.radius,
        durationMs: 650
      });
      this._savedActionCamState = null;
    }
  }

  showActionCamOverlay(title) {
    if (!this.container || typeof document === 'undefined') return;
    let overlay = this.container.querySelector('.action-cam-overlay');
    if (!overlay) {
      overlay = document.createElement('div');
      overlay.className = 'action-cam-overlay';
      this.container.appendChild(overlay);
    }
    overlay.innerHTML = `
      <div class="action-cam-badge">
        <span class="action-cam-rec">● REC</span>
        <span class="action-cam-label">ACTION CAM</span>
        <span class="action-cam-sep">|</span>
        <span class="action-cam-title">${title}</span>
      </div>
    `;
    overlay.classList.add('is-visible');
    this.actionCamOverlay = overlay;
  }

  hideActionCamOverlay() {
    if (this.actionCamOverlay) {
      this.actionCamOverlay.classList.remove('is-visible');
      setTimeout(() => {
        if (this.actionCamOverlay && !this._isActionCamActive) {
          try { this.actionCamOverlay.remove(); } catch (_) {}
          this.actionCamOverlay = null;
        }
      }, 350);
    }
  }

  /**
   * Smoothly animates the camera to a target configuration.
   * @param {Object} options
   */
  animateCameraTo({ target, alpha, beta, radius, durationMs = 460 }) {
    if (!this.camera || this.isDestroyed) return;

    const startTarget = this.camera.target.clone();
    const endTarget = target ? target.clone() : startTarget;

    const startAlpha = this.camera.alpha;
    let endAlpha = alpha !== undefined ? alpha : startAlpha;
    let deltaAlpha = (endAlpha - startAlpha) % (2 * Math.PI);
    if (deltaAlpha > Math.PI) deltaAlpha -= 2 * Math.PI;
    if (deltaAlpha < -Math.PI) deltaAlpha += 2 * Math.PI;
    endAlpha = startAlpha + deltaAlpha;

    const startBeta = this.camera.beta;
    const endBeta = beta !== undefined ? beta : startBeta;

    const startRadius = this.camera.radius;
    const endRadius = radius !== undefined ? radius : startRadius;

    const startTime = performance.now();

    const anim = {
      update: (now) => {
        const elapsed = now - startTime;
        const t = Math.min(1, elapsed / durationMs);
        // Smooth easeInOutCubic
        const ease = t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

        this.camera.target.x = startTarget.x + (endTarget.x - startTarget.x) * ease;
        this.camera.target.y = startTarget.y + (endTarget.y - startTarget.y) * ease;
        this.camera.target.z = startTarget.z + (endTarget.z - startTarget.z) * ease;

        this.camera.alpha = startAlpha + (endAlpha - startAlpha) * ease;
        this.camera.beta = startBeta + (endBeta - startBeta) * ease;
        this.camera.radius = startRadius + (endRadius - startRadius) * ease;

        return t < 1;
      }
    };

    if (this._currentCameraAnim) {
      this.animatingPegs = this.animatingPegs.filter(a => a !== this._currentCameraAnim);
    }
    this._currentCameraAnim = anim;
    this.animatingPegs.push(anim);
  }

  /**
   * Zooms camera in towards the 3D board.
   * @param {number} [stepPercent=25] Percentage to zoom in
   */
  zoomIn(stepPercent = 25) {
    if (!this.camera || this.isDestroyed) return;
    this._userHasAdjustedCamera = true;
    this.activePreset = null;
    this.updatePresetButtonsUI(null);

    const minRadius = this.camera.lowerRadiusLimit || (this.fitRadius ? this.fitRadius * 0.15 : 5);
    const factor = Math.max(0.1, 1 - (stepPercent / 100));
    const targetRadius = Math.max(minRadius, this.camera.radius * factor);

    this.animateCameraTo({
      radius: targetRadius,
      durationMs: 220
    });
  }

  /**
   * Zooms camera out away from the 3D board.
   * @param {number} [stepPercent=25] Percentage to zoom out
   */
  zoomOut(stepPercent = 25) {
    if (!this.camera || this.isDestroyed) return;
    this._userHasAdjustedCamera = true;
    this.activePreset = null;
    this.updatePresetButtonsUI(null);

    const maxRadius = this.camera.upperRadiusLimit || (this.fitRadius ? this.fitRadius * 4.5 : 500);
    const factor = 1 + (stepPercent / 100);
    const targetRadius = Math.min(maxRadius, this.camera.radius * factor);

    this.animateCameraTo({
      radius: targetRadius,
      durationMs: 220
    });
  }

  /**
   * Toggles Pan mode on/off. In Pan mode, primary pointer drag pans the board instead of orbiting.
   * @param {boolean} [enable] Explicit state, or toggles if omitted
   * @returns {boolean} New pan mode state
   */
  setPanMode(enable) {
    this.isPanMode = enable !== undefined ? !!enable : !this.isPanMode;

    const ptr = this.camera?.inputs?.attached?.pointers;
    if (ptr) {
      if (this.isPanMode) {
        // Detach Babylon's default pointer listener so dragging does NOT rotate the camera
        ptr.detachControl();
      } else {
        // Re-attach Babylon's default pointer listener for normal camera rotation
        ptr.attachControl(this.canvas, true);
      }
    }

    if (this.canvas) {
      if (this.isPanMode) {
        this.canvas.classList.add('is-pan-mode');
      } else {
        this.canvas.classList.remove('is-pan-mode', 'is-pan-grabbing');
      }
    }

    this.updatePanButtonUI();
    return this.isPanMode;
  }

  /**
   * Toggles Pan mode on/off.
   * @returns {boolean}
   */
  togglePanMode() {
    return this.setPanMode();
  }

  /**
   * Updates visual active state for the Pan Mode button in the toolbar.
   */
  updatePanButtonUI() {
    if (!this.presetsToolbar) return;
    const btn = this.presetsToolbar.querySelector('[data-action="panMode"]');
    if (btn) {
      if (this.isPanMode) {
        btn.classList.add('is-active');
        btn.setAttribute('aria-pressed', 'true');
      } else {
        btn.classList.remove('is-active');
        btn.setAttribute('aria-pressed', 'false');
      }
    }
  }

  /**
   * Pans the 3D board relative to current camera screen orientation.
   * @param {number} deltaX Screen horizontal offset (positive = right, negative = left)
   * @param {number} deltaY Screen vertical offset (positive = up, negative = down)
   * @param {number} [durationMs=180] Animation duration in ms (0 for instant)
   */
  pan(deltaX, deltaY, durationMs = 180) {
    if (this.boardRoot) {
      this.moveBoard(deltaX * 0.25, -deltaY * 0.25, durationMs > 0);
      return;
    }
    if (!this.camera || this.isDestroyed) return;
    this._userHasAdjustedCamera = true;
    this.activePreset = null;
    this.updatePresetButtonsUI(null);

    const invView = this.camera.getViewMatrix().clone().invert();
    const right = new this.BABYLON.Vector3(invView.m[0], invView.m[1], invView.m[2]).normalize();
    const up = new this.BABYLON.Vector3(invView.m[4], invView.m[5], invView.m[6]).normalize();

    const offset = right.scale(deltaX).add(up.scale(deltaY));
    const newTarget = this.camera.target.add(offset);

    if (durationMs > 0) {
      this.animateCameraTo({
        target: newTarget,
        durationMs
      });
    } else {
      this.camera.target.copyFrom(newTarget);
    }
  }

  /**
   * Directional pan helper: moves board left.
   * @param {number} [step]
   */
  panLeft(step) {
    const s = typeof step === 'number' ? step : (this.fitRadius ? this.fitRadius * 0.12 : 20);
    this.pan(-s, 0);
  }

  /**
   * Directional pan helper: moves board right.
   * @param {number} [step]
   */
  panRight(step) {
    const s = typeof step === 'number' ? step : (this.fitRadius ? this.fitRadius * 0.12 : 20);
    this.pan(s, 0);
  }

  /**
   * Directional pan helper: moves board up.
   * @param {number} [step]
   */
  panUp(step) {
    const s = typeof step === 'number' ? step : (this.fitRadius ? this.fitRadius * 0.12 : 20);
    this.pan(0, s);
  }

  /**
   * Directional pan helper: moves board down.
   * @param {number} [step]
   */
  panDown(step) {
    const s = typeof step === 'number' ? step : (this.fitRadius ? this.fitRadius * 0.12 : 20);
    this.pan(0, -s);
  }

  /**
   * Resets pan offset back to board center.
   * @param {number} [durationMs=260]
   */
  resetPan(durationMs = 260) {
    if (this.boardRoot) {
      this.resetBoardPosition(durationMs > 0);
    }
    const bounds = this.boardBounds || this.getBoardBounds();
    const center = bounds ? bounds.center : new this.BABYLON.Vector3(0, 0, 0);
    this.animateCameraTo({
      target: center,
      durationMs
    });
  }

  /**
   * Checks whether a picked mesh belongs to the cribbage board, its pegs, or its obstacles.
   * @param {BABYLON.AbstractMesh} mesh
   * @returns {boolean}
   */
  isBoardMesh(mesh) {
    if (!mesh) return false;
    if (this.boardRoot && mesh === this.boardRoot) return true;
    if (this.boardMeshes && this.boardMeshes.includes(mesh)) return true;
    let curr = mesh;
    while (curr) {
      if (this.boardRoot && curr === this.boardRoot) return true;
      if (curr.name) {
        if (
          curr.name === 'board3DTableTop' ||
          curr.name.startsWith('table') ||
          curr.name.startsWith('chair') ||
          curr.name.startsWith('card_') ||
          curr.name.startsWith('avatarRoot_') ||
          curr.name.startsWith('room3D')
        ) {
          return false;
        }
      }
      curr = curr.parent;
    }
    return false;
  }

  /**
   * Sets the position of the cribbage board on the tabletop in the 3D room.
   * Clamps the position to prevent the board from falling off the table edges.
   * @param {number} x - Horizontal offset on the tabletop (left/right)
   * @param {number} z - Depth offset on the tabletop (forward/backward)
   * @param {boolean} [animate=false] - Whether to smoothly animate the translation
   */
  setBoardPosition(x, z, animate = false) {
    if (!this.boardRoot || this.isDestroyed) return;

    const dims = this.roomData?.dims;
    const tableW = dims?.tableW ?? (this.cardEngine?.tableW || 120);
    const tableD = dims?.tableD ?? (this.cardEngine?.tableD || 100);
    const bounds = this.getBoardBounds();
    const halfBoardW = bounds ? bounds.size.x / 2 : 24;
    const halfBoardD = bounds ? bounds.size.z / 2 : 14;

    // Constrain so the board stays stably on the tabletop
    const maxX = Math.max(12, (tableW / 2) - halfBoardW - 3);
    const maxZ = Math.max(10, (tableD / 2) - halfBoardD - 3);
    const clampedX = Math.max(-maxX, Math.min(maxX, x));
    const clampedZ = Math.max(-maxZ, Math.min(maxZ, z));

    if (animate && this.scene?.onBeforeRenderObservable) {
      const startX = this.boardRoot.position.x;
      const startZ = this.boardRoot.position.z;
      const startTime = performance.now();
      const dur = 220;

      const observer = this.scene.onBeforeRenderObservable.add(() => {
        const elapsed = performance.now() - startTime;
        const t = Math.min(1, elapsed / dur);
        const ease = t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;

        if (this.boardRoot) {
          this.boardRoot.position.x = startX + (clampedX - startX) * ease;
          this.boardRoot.position.z = startZ + (clampedZ - startZ) * ease;
        }

        if (t >= 1) {
          this.scene.onBeforeRenderObservable.remove(observer);
          this.boardBounds = this.getBoardBounds();
          if (this.cardEngine) {
            this.cardEngine.boardBounds = this.boardBounds;
          }
        }
      });
    } else {
      this.boardRoot.position.x = clampedX;
      this.boardRoot.position.z = clampedZ;
      this.boardBounds = this.getBoardBounds();
      if (this.cardEngine) {
        this.cardEngine.boardBounds = this.boardBounds;
      }
    }
  }

  /**
   * Moves the cribbage board relative to its current tabletop position.
   * @param {number} deltaX
   * @param {number} deltaZ
   * @param {boolean} [animate=true]
   */
  moveBoard(deltaX, deltaZ, animate = true) {
    if (!this.boardRoot) return;
    const targetX = this.boardRoot.position.x + deltaX;
    const targetZ = this.boardRoot.position.z + deltaZ;
    this.setBoardPosition(targetX, targetZ, animate);
  }

  /**
   * Rotates the cribbage board on the tabletop around its vertical Y axis.
   * @param {number} deltaRad - Angle in radians
   * @param {boolean} [animate=false]
   */
  rotateBoard(deltaRad, animate = false) {
    if (!this.boardRoot) return;
    this.setBoardRotation((this.boardRoot.rotation.y || 0) + deltaRad, animate);
  }

  /**
   * Sets the absolute Y rotation of the cribbage board.
   * @param {number} rad
   * @param {boolean} [animate=false]
   */
  setBoardRotation(rad, animate = false) {
    if (!this.boardRoot) return;
    if (this._boardRotObserver && this.scene?.onBeforeRenderObservable) {
      try {
        this.scene.onBeforeRenderObservable.remove(this._boardRotObserver);
      } catch (_) {}
      this._boardRotObserver = null;
    }

    if (animate && this.scene?.onBeforeRenderObservable) {
      const startRot = this.boardRoot.rotation.y || 0;
      const targetRot = rad;
      const startTime = performance.now();
      const dur = 180;

      const observer = this.scene.onBeforeRenderObservable.add(() => {
        const elapsed = performance.now() - startTime;
        const t = Math.min(1, elapsed / dur);
        const ease = t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;

        if (this.boardRoot) {
          this.boardRoot.rotation.y = startRot + (targetRot - startRot) * ease;
        }

        if (t >= 1) {
          if (this.scene?.onBeforeRenderObservable) {
            this.scene.onBeforeRenderObservable.remove(observer);
          }
          if (this._boardRotObserver === observer) {
            this._boardRotObserver = null;
          }
          if (this.boardRoot) {
            this.boardRoot.rotation.y = targetRot;
          }
          this.boardBounds = this.getBoardBounds();
          if (this.cardEngine) {
            this.cardEngine.boardBounds = this.boardBounds;
          }
        }
      });
      this._boardRotObserver = observer;
    } else {
      this.boardRoot.rotation.y = rad;
      this.boardBounds = this.getBoardBounds();
      if (this.cardEngine) {
        this.cardEngine.boardBounds = this.boardBounds;
      }
    }
  }

  /**
   * Resets the cribbage board to the center of the table (0, 0, 0) with 0 rotation.
   * @param {boolean} [animate=true]
   */
  resetBoardPosition(animate = true) {
    if (!this.boardRoot || this.isDestroyed) return;

    if (this._boardRotObserver && this.scene?.onBeforeRenderObservable) {
      try {
        this.scene.onBeforeRenderObservable.remove(this._boardRotObserver);
      } catch (_) {}
      this._boardRotObserver = null;
    }

    if (animate && this.scene?.onBeforeRenderObservable) {
      const startX = this.boardRoot.position.x;
      const startZ = this.boardRoot.position.z;
      const startRot = this.boardRoot.rotation.y;
      const startTime = performance.now();
      const dur = 260;

      const observer = this.scene.onBeforeRenderObservable.add(() => {
        const elapsed = performance.now() - startTime;
        const t = Math.min(1, elapsed / dur);
        const ease = t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;

        if (this.boardRoot) {
          this.boardRoot.position.x = startX * (1 - ease);
          this.boardRoot.position.z = startZ * (1 - ease);
          this.boardRoot.rotation.y = startRot * (1 - ease);
        }

        if (t >= 1) {
          this.scene.onBeforeRenderObservable.remove(observer);
          if (this.boardRoot) {
            this.boardRoot.position.set(0, 0, 0);
            this.boardRoot.rotation.y = 0;
          }
          this.boardBounds = this.getBoardBounds();
          if (this.cardEngine) {
            this.cardEngine.boardBounds = this.boardBounds;
          }
        }
      });
    } else {
      this.boardRoot.position.set(0, 0, 0);
      this.boardRoot.rotation.y = 0;
      this.boardBounds = this.getBoardBounds();
      if (this.cardEngine) {
        this.cardEngine.boardBounds = this.boardBounds;
      }
    }
  }

  /**
   * Retrieves the current position and orientation of the cribbage board.
   * @returns {{ x: number, y: number, z: number, rotationY: number }}
   */
  getBoardPosition() {
    if (!this.boardRoot) {
      return { x: 0, y: 0, z: 0, rotationY: 0, scale: this.boardScaleMultiplier || 1.0 };
    }
    return {
      x: Number(this.boardRoot.position.x.toFixed(2)),
      y: Number(this.boardRoot.position.y.toFixed(2)),
      z: Number(this.boardRoot.position.z.toFixed(2)),
      rotationY: Number(this.boardRoot.rotation.y.toFixed(3)),
      scale: Number((this.boardScaleMultiplier || 1.0).toFixed(2))
    };
  }

  /**
   * Sets the scale multiplier of the cribbage board in the 3D room.
   * Clamps between 0.20 (20% size) and 2.5 (250% size).
   * @param {number} scaleMultiplier
   * @param {boolean} [animate=false]
   */
  setBoardScale(scaleMultiplier, animate = false) {
    if (!this.boardRoot || this.isDestroyed) return;
    const clamped = Math.max(0.20, Math.min(2.5, Number(scaleMultiplier) || 1.0));

    if (animate && this.scene?.onBeforeRenderObservable) {
      const startScale = this.boardRoot.scaling.x || 1.0;
      const startTime = performance.now();
      const dur = 200;

      const observer = this.scene.onBeforeRenderObservable.add(() => {
        const elapsed = performance.now() - startTime;
        const t = Math.min(1, elapsed / dur);
        const ease = t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
        const curr = startScale + (clamped - startScale) * ease;

        if (this.boardRoot) {
          this.boardRoot.scaling.set(curr, curr, curr);
        }

        if (t >= 1) {
          this.scene.onBeforeRenderObservable.remove(observer);
          this.boardScaleMultiplier = clamped;
          this.boardBounds = this.getBoardBounds();
          if (this.cardEngine) {
            this.cardEngine.boardBounds = this.boardBounds;
          }
          this.setBoardPosition(this.boardRoot.position.x, this.boardRoot.position.z, false);
          this.updateScaleToolbarUI();
        }
      });
    } else {
      this.boardRoot.scaling.set(clamped, clamped, clamped);
      this.boardScaleMultiplier = clamped;
      this.boardBounds = this.getBoardBounds();
      if (this.cardEngine) {
        this.cardEngine.boardBounds = this.boardBounds;
      }
      this.setBoardPosition(this.boardRoot.position.x, this.boardRoot.position.z, false);
      this.updateScaleToolbarUI();
    }

    try {
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem('cc_board_3d_scale', clamped.toFixed(2));
      }
    } catch (_) {}
  }

  /**
   * Adjusts the cribbage board scale relative to its current size.
   * @param {number} deltaMultiplier
   * @param {boolean} [animate=true]
   */
  scaleBoard(deltaMultiplier, animate = true) {
    if (!this.boardRoot) return;
    this.setBoardScale((this.boardScaleMultiplier || 1.0) + deltaMultiplier, animate);
  }

  /**
   * Resets the cribbage board scale to standard 100% size (1.0).
   * @param {boolean} [animate=true]
   */
  resetBoardScale(animate = true) {
    this.setBoardScale(1.0, animate);
  }

  /**
   * Resets position, rotation, and size of the cribbage board to default tabletop state.
   * @param {boolean} [animate=true]
   */
  resetBoardTransform(animate = true) {
    this.resetBoardPosition(animate);
    this.resetBoardScale(animate);
  }

  /**
   * Retrieves the current scale multiplier of the cribbage board.
   * @returns {number}
   */
  getBoardScale() {
    return this.boardScaleMultiplier || 1.0;
  }

  /**
   * Synchronizes board scale controls in the toolbar if present.
   */
  updateScaleToolbarUI() {
    if (!this.presetsToolbar) return;
    const slider = this.presetsToolbar.querySelector('.slider-3d-scale');
    if (slider) {
      slider.value = String(Math.round((this.boardScaleMultiplier || 1.0) * 100));
    }
    const label = this.presetsToolbar.querySelector('.val-3d-scale');
    if (label) {
      label.textContent = `${Math.round((this.boardScaleMultiplier || 1.0) * 100)}%`;
    }
  }

  /**
   * Instantiates the 3D physical tabletop dealer button token with embossed brass/gold medallion.
   */
  createDealerButton3D() {
    if (!this.scene || !this.BABYLON || this.isDestroyed || this.dealerButtonMesh) return;
    const BABYLON = this.BABYLON;

    // 1. Create cylinder token mesh
    const button = BABYLON.MeshBuilder.CreateCylinder('dealerButton3D', {
      diameter: 7.6,
      height: 1.35,
      tessellation: 48
    }, this.scene);
    button.receiveShadows = true;
    if (this.shadowGenerator) {
      this.shadowGenerator.addShadowCaster(button);
    }

    // 2. High-res dynamic texture with embossed DEALER typography and star accents
    const tex = new BABYLON.DynamicTexture('texDealerButton', { width: 512, height: 512 }, this.scene, true);
    const ctx = tex.getContext();
    if (ctx) {
      const w = 512;
      const h = 512;
      const cx = w / 2;
      const cy = h / 2;

      // Base disc background - ivory / gold gradient
      const bgGrad = ctx.createRadialGradient(cx, cy, 30, cx, cy, 250);
      bgGrad.addColorStop(0, '#fef9c3');
      bgGrad.addColorStop(0.65, '#fde047');
      bgGrad.addColorStop(0.9, '#f59e0b');
      bgGrad.addColorStop(1, '#92400e');
      ctx.fillStyle = bgGrad;
      ctx.beginPath();
      ctx.arc(cx, cy, 252, 0, Math.PI * 2);
      ctx.fill();

      // Concentric decorative rim grooves
      ctx.strokeStyle = '#78350f';
      ctx.lineWidth = 14;
      ctx.beginPath();
      ctx.arc(cx, cy, 240, 0, Math.PI * 2);
      ctx.stroke();

      ctx.strokeStyle = '#fef08a';
      ctx.lineWidth = 6;
      ctx.beginPath();
      ctx.arc(cx, cy, 222, 0, Math.PI * 2);
      ctx.stroke();

      ctx.strokeStyle = 'rgba(120, 53, 15, 0.45)';
      ctx.lineWidth = 4;
      ctx.beginPath();
      ctx.arc(cx, cy, 185, 0, Math.PI * 2);
      ctx.stroke();

      // Stars on outer circle
      ctx.fillStyle = '#78350f';
      ctx.font = 'bold 36px "Segoe UI", Roboto, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('★   ★   ★', cx, 88);
      ctx.fillText('★   ★   ★', cx, h - 88);

      // Center "DEALER" bold lettering
      ctx.fillStyle = '#78350f';
      ctx.font = '900 84px "Segoe UI", Roboto, sans-serif';
      ctx.fillText('DEALER', cx, cy - 6);

      // Subtitle "CRIBBAGE"
      ctx.font = 'bold 32px "Segoe UI", Roboto, sans-serif';
      ctx.fillText('CRIBBAGE', cx, cy + 62);
      tex.update();
    }

    // 3. PBR Material with lacquer sheen
    const mat = new BABYLON.PBRMaterial('matDealerButton', this.scene);
    mat.albedoTexture = tex;
    mat.metallic = 0.55;
    mat.roughness = 0.28;
    if (mat.clearCoat) {
      mat.clearCoat.isEnabled = true;
      mat.clearCoat.intensity = 0.8;
      mat.clearCoat.roughness = 0.15;
    }
    button.material = mat;
    button.isPickable = true;
    button.metadata = { dealerButton: true };

    this.dealerButtonMesh = button;
    this._dealerButtonMat = mat;
    this._dealerButtonTex = tex;

    // Initial position
    const initialDealer = this.dealerSeat || 'p1';
    const coords = this.getDealerButtonCoords(initialDealer);
    button.position.set(coords.x, coords.y, coords.z);
    button.rotation.y = coords.angle || 0;
  }

  /**
   * Computes tabletop surface coordinates for placing the 3D dealer button in front of a specific seat.
   * @param {string} seat - 'p1', 'p2', 'you', 'cpu', etc.
   * @returns {{ x: number, y: number, z: number, angle: number }}
   */
  getDealerButtonCoords(seat) {
    const normSeat = this.seatManager ? this.seatManager.normalizeSeatId(seat) : seat;
    let seatIdx = 0;
    if (normSeat && normSeat.startsWith('p')) {
      const parsed = parseInt(normSeat.slice(1), 10);
      if (!isNaN(parsed) && parsed >= 1) seatIdx = parsed - 1;
    }

    let angle = 0;
    const chairRoots = this.roomData?.chairRoots || this.seatManager?.chairRoots || [];
    if (chairRoots[seatIdx]?.rotation && typeof chairRoots[seatIdx].rotation.y === 'number') {
      angle = chairRoots[seatIdx].rotation.y;
    } else {
      if (seatIdx === 0) angle = 0;
      else if (seatIdx === 1) angle = Math.PI;
      else if (seatIdx === 2) angle = 1.5 * Math.PI;
      else if (seatIdx === 3) angle = 0.5 * Math.PI;
    }

    const tableW = this.roomData?.dims?.tableW ?? (this.cardEngine?.tableW || 120);
    const tableD = this.roomData?.dims?.tableD ?? (this.cardEngine?.tableD || 100);
    const edgeOffset = 18.0;

    // Position slightly to the player's right on the tabletop
    const posX = Math.sin(angle + 0.26) * (tableW / 2 - edgeOffset);
    const posZ = -Math.cos(angle + 0.26) * (tableD / 2 - edgeOffset);
    const surfaceY = (this.roomData?.dims?.tableSurfaceY != null)
      ? this.roomData.dims.tableSurfaceY
      : (this.cardEngine?.tableSurfaceY ?? -0.88);
    const posY = surfaceY + 0.72;

    return { x: posX, y: posY, z: posZ, angle };
  }

  /**
   * Sets the active dealer in the 3D room, updating:
   * 1. The physical 3D tabletop dealer button with smooth gliding animation
   * 2. The seated player avatar 3D floating nameplate HUD badge
   * 3. The 3D in-game action HUD status pill
   * @param {string} dealerSeat - 'p1', 'p2', 'you', 'cpu', etc.
   * @param {boolean} [animate=true]
   */
  setDealer(dealerSeat, animate = true) {
    if (!dealerSeat) return;
    const prev = this.dealerSeat;
    const norm = this.seatManager ? this.seatManager.normalizeSeatId(dealerSeat) : dealerSeat;
    this.dealerSeat = norm;

    if (this.seatManager) {
      this.seatManager.setDealer(norm);
    }

    if (!this.dealerButtonMesh && (this.roomData || this.tableMesh || this.cardEngine) && typeof this.createDealerButton3D === 'function') {
      this.createDealerButton3D();
    }

    if (this.dealerButtonMesh) {
      const coords = this.getDealerButtonCoords(norm);
      if (animate && prev && prev !== norm && this.scene?.onBeforeRenderObservable) {
        if (this._dealerButtonAnim) {
          this.scene.onBeforeRenderObservable.remove(this._dealerButtonAnim);
          this._dealerButtonAnim = null;
        }

        const startX = this.dealerButtonMesh.position.x;
        const startY = this.dealerButtonMesh.position.y;
        const startZ = this.dealerButtonMesh.position.z;
        const startAngle = this.dealerButtonMesh.rotation.y;
        const startTime = performance.now();
        const dur = 420;

        const obs = this.scene.onBeforeRenderObservable.add(() => {
          const elapsed = performance.now() - startTime;
          const t = Math.min(1, elapsed / dur);
          const ease = t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;

          if (this.dealerButtonMesh) {
            const x = startX + (coords.x - startX) * ease;
            const z = startZ + (coords.z - startZ) * ease;
            const hop = Math.sin(t * Math.PI) * 2.2;
            this.dealerButtonMesh.position.set(x, coords.y + hop, z);
            this.dealerButtonMesh.rotation.y = startAngle + (coords.angle - startAngle) * ease + (t * Math.PI * 0.5);
          }

          if (t >= 1) {
            this.scene.onBeforeRenderObservable.remove(obs);
            if (this._dealerButtonAnim === obs) this._dealerButtonAnim = null;
            if (this.dealerButtonMesh) {
              this.dealerButtonMesh.position.set(coords.x, coords.y, coords.z);
              this.dealerButtonMesh.rotation.y = coords.angle || 0;
            }
          }
        });
        this._dealerButtonAnim = obs;
      } else {
        this.dealerButtonMesh.position.set(coords.x, coords.y, coords.z);
        this.dealerButtonMesh.rotation.y = coords.angle || 0;
      }
    }
  }

  /**
   * Retrieves the current dealer seat identifier.
   * @returns {string|null}
   */
  getDealer() {
    return this.dealerSeat;
  }

  /**
   * Cleans up and disposes the 3D dealer button mesh and textures.
   */
  clearDealerButton() {
    if (this._dealerButtonAnim && this.scene?.onBeforeRenderObservable) {
      try { this.scene.onBeforeRenderObservable.remove(this._dealerButtonAnim); } catch (_) {}
      this._dealerButtonAnim = null;
    }
    if (this._dealerButtonTex) {
      try { this._dealerButtonTex.dispose(); } catch (_) {}
      this._dealerButtonTex = null;
    }
    if (this._dealerButtonMat) {
      try { this._dealerButtonMat.dispose(); } catch (_) {}
      this._dealerButtonMat = null;
    }
    if (this.dealerButtonMesh) {
      try { this.dealerButtonMesh.dispose(false, true); } catch (_) {}
      this.dealerButtonMesh = null;
    }
  }

  /**
   * Camera Presets:
   * - 'overview': Full board overview with optimal framing
   * - 'closeup': Leading Peg Close-up focusing dynamically on current frontrunner
   * - 'finish': Homestretch and Finish Line view
   */
  setCameraPreset(presetName, isAutoFollow = false) {
    if (!this.camera) return;

    const bounds = this.getBoardBounds();
    const camDef = this.boardConfig?.cameraDefaults || {};

    if (presetName === 'overview' || presetName === 'perspective') {
      this.activePreset = 'overview';
      this.updatePresetButtonsUI('overview');
      this._userHasAdjustedCamera = false;

      const defaultAlpha = camDef.alpha !== undefined
        ? Number(camDef.alpha)
        : (camDef.alphaDeg !== undefined ? (Number(camDef.alphaDeg) * Math.PI) / 180 : -Math.PI / 2);
      const defaultBeta = camDef.beta !== undefined
        ? Number(camDef.beta)
        : (camDef.betaDeg !== undefined ? (Number(camDef.betaDeg) * Math.PI) / 180 : Math.PI / 5.2);

      const center = bounds ? bounds.center : new this.BABYLON.Vector3(0, 0, 0);

      // Compute ideal framing radius
      let fitRadius = 55;
      if (bounds) {
        const canvas = this.canvas || this.engine.getRenderingCanvas();
        const width = (canvas && canvas.clientWidth > 0) ? canvas.clientWidth : this.viewBoxW;
        const height = (canvas && canvas.clientHeight > 0) ? canvas.clientHeight : this.viewBoxH;
        if (width > 0 && height > 0) {
          const aspect = width / height;
          const fov = this.camera.fov || 0.8;
          const halfVFOV = fov / 2;
          const halfHFOV = Math.atan(Math.tan(halfVFOV) * aspect);
          const beta = defaultBeta;
          const alpha = defaultAlpha;
          const hasRoom = Boolean(this.roomData || this.tableMesh || this.cardEngine);
          const dims = this.roomData?.dims;
          const tableW = dims?.tableW ?? (this.cardEngine?.tableW || 120);
          const tableD = dims?.tableD ?? (this.cardEngine?.tableD || 100);

          let spanTargetX = bounds.size.x;
          let spanTargetZ = bounds.size.z;
          if (hasRoom && tableW && tableD) {
            spanTargetX = Math.max(bounds.size.x, tableW * 0.90);
            spanTargetZ = Math.max(bounds.size.z, tableD * 0.90);
          }

          const sizeX = Math.max(1, spanTargetX);
          const sizeY = Math.max(0.1, bounds.size.y);
          const sizeZ = Math.max(1, spanTargetZ);
          const horizSpan = Math.abs(sizeX * Math.sin(alpha)) + Math.abs(sizeZ * Math.cos(alpha));
          const depthSpan = Math.abs(sizeZ * Math.sin(alpha)) + Math.abs(sizeX * Math.cos(alpha));
          const vertSpan = Math.abs(depthSpan * Math.cos(beta)) + Math.abs(sizeY * Math.sin(beta));
          const distH = (horizSpan / 2) / Math.tan(halfHFOV);
          const distV = (vertSpan / 2) / Math.tan(halfVFOV);
          const padding = hasRoom ? 1.25 : 1.18;
          fitRadius = Math.max(distH, distV) * padding;
        }
      }

      this.animateCameraTo({
        target: center,
        alpha: defaultAlpha,
        beta: defaultBeta,
        radius: fitRadius,
        durationMs: 460
      });
    } else if (presetName === 'top') {
      this.activePreset = 'top';
      this.updatePresetButtonsUI(null);
      this._userHasAdjustedCamera = false;
      const center = bounds ? bounds.center : new this.BABYLON.Vector3(0, 0, 0);
      this.animateCameraTo({
        target: center,
        alpha: -Math.PI / 2,
        beta: 0.05,
        radius: this.camera.radius || 55,
        durationMs: 460
      });
    } else if (presetName === 'closeup' || presetName === 'follow') {
      this.activePreset = 'closeup';
      this.updatePresetButtonsUI('closeup');
      this._userHasAdjustedCamera = false;

      // Identify leading track and front peg
      let leaderTrack = this.activeTracks[0] || 'p1';
      let maxScore = -1;
      for (const who of this.activeTracks) {
        const score = (this.pegState[who] && this.pegState[who].front) || 0;
        if (score > maxScore) {
          maxScore = score;
          leaderTrack = who;
        }
      }
      const leaderHole = Math.max(0, maxScore);
      const pt = this.getHoleCoords(leaderTrack, leaderHole);

      if (pt) {
        const maxDim = bounds ? Math.max(bounds.size.x, bounds.size.z) : 100;
        const targetRadius = Math.max(16, maxDim * 0.16);
        this.animateCameraTo({
          target: new this.BABYLON.Vector3(pt.x, pt.y + 1.2, pt.z),
          alpha: -Math.PI / 2.18,
          beta: Math.PI / 4.4,
          radius: targetRadius,
          durationMs: isAutoFollow ? 380 : 460
        });
      }
    } else if (presetName === 'finish') {
      this.activePreset = 'finish';
      this.updatePresetButtonsUI('finish');
      this._userHasAdjustedCamera = false;

      const finishHole = this.targetHoles || 121;
      let pt = this.getHoleCoords('p1', finishHole);
      if (!pt || (pt.x === 0 && pt.z === 0)) {
        pt = this.getHoleCoords('p1', finishHole - 1);
      }

      if (pt) {
        const maxDim = bounds ? Math.max(bounds.size.x, bounds.size.z) : 100;
        const targetRadius = Math.max(18, maxDim * 0.19);
        this.animateCameraTo({
          target: new this.BABYLON.Vector3(pt.x, pt.y + 1.2, pt.z),
          alpha: -Math.PI / 1.72,
          beta: Math.PI / 4.5,
          radius: targetRadius,
          durationMs: 460
        });
      }
    }
  }

  /**
   * Sets up the interactive game logo badge in the top-left corner of the 3D room container.
   * Clicking this logo invokes the quit match modal ('Return to Main Menu?'), consistent with
   * the 2D game header logo behavior, whether in windowed or fullscreen 3D mode.
   */
  setupTopLeftLogoUI() {
    if (!this.container) return;
    const existing = this.container.querySelector('.board-3d-logo-btn');
    if (existing) existing.remove();

    const LOGO_CONFIG = {
      crazy: {
        src: 'https://cdn.shopify.com/s/files/1/0800/0657/8461/files/logo_-_640_x_640.webp',
        alt: 'Crazy Cribbage',
        title: 'Crazy Cribbage'
      },
      clash: {
        src: 'https://cdn.shopify.com/s/files/1/0800/0657/8461/files/logo-round-online-game-640x640.webp',
        alt: 'Cribbage Clash',
        title: 'Cribbage Clash'
      },
      pints: {
        src: 'https://cdn.shopify.com/s/files/1/0800/0657/8461/files/logo-pints-and-pegs_640x640_f004d2b4-49df-4181-a6d2-11c7f9212645.webp',
        alt: 'Pints and Pegs',
        title: 'Pints & Pegs'
      },
      adult: {
        src: 'https://cdn.shopify.com/s/files/1/0800/0657/8461/files/logo-skunked-and-stripped_-_640_x_640.webp?v=1791397387',
        alt: 'Skunked and Stripped',
        title: 'Skunked & Stripped'
      }
    };

    const getBrand = () => {
      if (typeof window !== 'undefined') {
        if (typeof window.getAppBrand === 'function') {
          const b = window.getAppBrand();
          if (b) return b;
        }
        if (window.currentLogoBrand) return window.currentLogoBrand;
        if (window.currentBrand) return window.currentBrand;
        if (window.settings && window.settings.brand) return window.settings.brand;
      }
      return 'crazy';
    };

    const brand = getBrand();
    const config = LOGO_CONFIG[brand] || LOGO_CONFIG.crazy;

    const logoBtn = document.createElement('button');
    logoBtn.type = 'button';
    logoBtn.className = 'board-3d-logo-btn';
    logoBtn.setAttribute('aria-label', `Return to Main Menu (${config.title})`);
    logoBtn.setAttribute('title', 'Return to Main Menu');

    const img = document.createElement('img');
    img.className = 'board-3d-logo-img';
    img.src = config.src;
    img.alt = config.alt;
    img.width = 38;
    img.height = 38;
    img.decoding = 'async';

    const text = document.createElement('span');
    text.className = 'board-3d-logo-text';
    text.textContent = config.title;

    logoBtn.appendChild(img);
    logoBtn.appendChild(text);

    logoBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      e.preventDefault();
      if (typeof window !== 'undefined') {
        if (typeof window.returnToMenu === 'function') {
          window.returnToMenu();
        } else if (window.crazyCribbageApp && typeof window.crazyCribbageApp.returnToMenu === 'function') {
          window.crazyCribbageApp.returnToMenu();
        } else if (window.CrazyCribbage && window.CrazyCribbage.ModalManager && typeof window.CrazyCribbage.ModalManager.returnToMenu === 'function') {
          window.CrazyCribbage.ModalManager.returnToMenu();
        }
      }
    });

    this.container.appendChild(logoBtn);
    this.logoBtn = logoBtn;
  }

  /**
   * Builds the interactive Camera Presets & Lighting Brightness toolbar overlay.
   */
  setupCameraPresetsUI() {
    if (!this.container) return;
    const existing = this.container.querySelector('.board-3d-cam-toolbar');
    if (existing) existing.remove();

    const toolbar = document.createElement('div');
    toolbar.className = 'board-3d-cam-toolbar';
    toolbar.setAttribute('role', 'toolbar');
    toolbar.setAttribute('aria-label', '3D Camera Presets and Lighting Controls');

    const camGroup = document.createElement('div');
    camGroup.className = 'board-3d-cam-group';

    const presets = [
      {
        id: 'overview',
        label: 'Overview',
        title: 'Full Board Overview',
        icon: '<svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="3"/></svg>'
      }
      /*
      ,
      {
        id: 'closeup',
        label: 'Leading Peg',
        title: 'Leading Peg Close-up',
        icon: '<svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="7"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><circle cx="11" cy="11" r="2.5" fill="currentColor"/></svg>'
      },
      {
        id: 'finish',
        label: 'Finish Line',
        title: 'Homestretch & Finish Line',
        icon: '<svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/><line x1="4" y1="22" x2="4" y2="15"/></svg>'
      }
      */
    ];

    presets.forEach(({ id, label, title, icon }) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = `btn-3d-preset${id === (this.activePreset || 'overview') ? ' is-active' : ''}`;
      btn.setAttribute('data-preset', id);
      btn.setAttribute('title', title);
      btn.setAttribute('aria-label', title);
      btn.setAttribute('aria-pressed', id === (this.activePreset || 'overview') ? 'true' : 'false');
      btn.innerHTML = `${icon}<span>${label}</span>`;
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.setCameraPreset(id);
      });
      camGroup.appendChild(btn);
    });
    toolbar.appendChild(camGroup);

    // Separator between Camera Presets and Zoom Controls
    const sepZoom = document.createElement('div');
    sepZoom.className = 'board-3d-toolbar-sep';
    sepZoom.setAttribute('aria-hidden', 'true');
    toolbar.appendChild(sepZoom);

    // Pan Mode & Zoom In / Out Controls Group
    const navGroup = document.createElement('div');
    navGroup.className = 'board-3d-zoom-group board-3d-cam-group';

    const navBtns = [
      {
        id: 'panMode',
        //label: 'Move',
        label: '',
        title: 'Move Board Mode (drag board across table, P or M)',
        icon: '<svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><polyline points="5 9 2 12 5 15"/><polyline points="9 5 12 2 15 5"/><polyline points="15 19 12 22 9 19"/><polyline points="19 9 22 12 19 15"/><line x1="2" y1="12" x2="22" y2="12"/><line x1="12" y1="2" x2="12" y2="22"/></svg>',
        action: () => this.togglePanMode(),
        isActive: () => !!this.isPanMode
      },
      {
        id: 'centerBoard',
        //label: 'Center',
        label: '',
        title: 'Center Board on Table (R)',
        icon: '<svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="3"/><line x1="12" y1="2" x2="12" y2="6"/><line x1="12" y1="18" x2="12" y2="22"/><line x1="2" y1="12" x2="6" y2="12"/><line x1="18" y1="12" x2="22" y2="12"/></svg>',
        action: () => this.resetBoardPosition(true)
      },
      {
        id: 'rotateLeft',
        label: '',
        title: 'Rotate Board Counter-Clockwise ↺ (Shift+←, Hold to spin)',
        icon: '<svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><polyline points="3 3 3 8 8 8"/></svg>',
        action: () => this.rotateBoard(-Math.PI / 12, true),
        onHold: () => this.rotateBoard(-0.035, false)
      },
      {
        id: 'rotateRight',
        label: '',
        title: 'Rotate Board Clockwise ↻ (Shift+→, Hold to spin)',
        icon: '<svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.85.99 6.57 2.6L21 8"/><polyline points="21 3 21 8 16 8"/></svg>',
        action: () => this.rotateBoard(Math.PI / 12, true),
        onHold: () => this.rotateBoard(0.035, false)
      },
      {
        id: 'scaleDown',
        //label: '- Size',
        label: '',
        title: 'Decrease Board Size ([ or Ctrl+Drag down)',
        icon: '<svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><line x1="8" y1="12" x2="16" y2="12"/></svg>',
        action: () => this.scaleBoard(-0.05, true)
      },
      {
        id: 'scaleUp',
        //label: '+ Size',
        label: '',
        title: 'Increase Board Size (] or Ctrl+Drag up)',
        icon: '<svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/></svg>',
        action: () => this.scaleBoard(0.05, true)
      },
      {
        id: 'zoomIn',
        //label: 'In',
        label: '',
        title: 'Zoom In (+)',
        icon: '<svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="7"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/></svg>',
        action: () => this.zoomIn()
      },
      {
        id: 'zoomOut',
        //label: 'Out',
        label: '',
        title: 'Zoom Out (-)',
        icon: '<svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="7"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="8" y1="11" x2="14" y2="11"/></svg>',
        action: () => this.zoomOut()
      },
      {
        id: 'fullScreen',
        label: '',
        title: 'Toggle Full Screen (F)',
        icon: '<svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" class="icon-fs-expand"><path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"/></svg><svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" class="icon-fs-contract" style="display:none;"><path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3"/></svg>',
        action: () => this.toggleFullScreen(),
        isActive: () => this.isFullScreen()
      }
      /*
      {
        id: 'actionCam',
        label: 'Action Cam',
        title: 'Cinematic Action Cam Replay Orbit',
        icon: '<svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M23 7l-7 5 7 5V7z"/><rect x="1" y="5" width="15" height="14" rx="2" ry="2"/></svg>',
        action: () => this.triggerActionCam({ type: 'big_hand', score: 29 })
      }
      */
    ];

    navBtns.forEach(({ id, label, title, icon, action, isActive, onHold }) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      const activeClass = (isActive && isActive()) ? ' is-active' : '';
      btn.className = `btn-3d-preset btn-3d-nav btn-3d-${id}${activeClass}`;
      btn.setAttribute('data-action', id);
      btn.setAttribute('title', title);
      btn.setAttribute('aria-label', title);
      btn.setAttribute('aria-pressed', (isActive && isActive()) ? 'true' : 'false');
      btn.innerHTML = `${icon}<span>${label}</span>`;

      if (onHold) {
        let holdTimer = null;
        let holdInterval = null;
        let didHold = false;

        const stopHold = () => {
          if (holdTimer) {
            clearTimeout(holdTimer);
            holdTimer = null;
          }
          if (holdInterval) {
            clearInterval(holdInterval);
            holdInterval = null;
          }
        };

        btn.addEventListener('pointerdown', (e) => {
          e.stopPropagation();
          didHold = false;
          stopHold();
          holdTimer = setTimeout(() => {
            didHold = true;
            holdInterval = setInterval(() => {
              onHold();
            }, 30);
          }, 260);
        });

        btn.addEventListener('pointerup', (e) => {
          stopHold();
          if (!didHold) {
            e.stopPropagation();
            action();
          }
        });

        btn.addEventListener('pointerleave', stopHold);
        btn.addEventListener('pointercancel', stopHold);
        btn.addEventListener('contextmenu', (e) => e.preventDefault());
      } else {
        btn.addEventListener('click', (e) => {
          e.stopPropagation();
          action();
        });
      }
      navGroup.appendChild(btn);
    });
    toolbar.appendChild(navGroup);

    // Separator between Zoom/Pan Controls and Lighting Controls
    const sep = document.createElement('div');
    sep.className = 'board-3d-toolbar-sep';
    sep.setAttribute('aria-hidden', 'true');
    toolbar.appendChild(sep);

    // Brightness Control Group
    const brightWrap = document.createElement('div');
    brightWrap.className = 'board-3d-brightness-wrap';
    brightWrap.setAttribute('title', 'Adjust 3D Board Lighting Brightness (Double-click to reset to 100%)');

    const brightIconBtn = document.createElement('button');
    brightIconBtn.type = 'button';
    brightIconBtn.className = 'btn-3d-brightness-icon';
    brightIconBtn.setAttribute('title', 'Reset brightness to 100%');
    brightIconBtn.setAttribute('aria-label', 'Reset lighting brightness to 100%');
    brightIconBtn.innerHTML = `
      <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2">
        <circle cx="12" cy="12" r="4.5"/>
        <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41"/>
      </svg>
    `;
    brightIconBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      this.setBrightness(100);
    });
    brightWrap.appendChild(brightIconBtn);

    const slider = document.createElement('input');
    slider.type = 'range';
    slider.className = 'slider-3d-brightness';
    slider.min = '40';
    slider.max = '180';
    slider.step = '5';
    slider.value = String(Math.round(this.brightnessPercent || 100));
    slider.setAttribute('aria-label', '3D Lighting Brightness Slider');
    slider.addEventListener('input', (e) => {
      this.setBrightness(Number(e.target.value));
    });
    slider.addEventListener('dblclick', (e) => {
      e.stopPropagation();
      this.setBrightness(100);
    });
    brightWrap.appendChild(slider);

    const label = document.createElement('span');
    label.className = 'val-3d-brightness';
    label.textContent = `${Math.round(this.brightnessPercent || 100)}%`;
    brightWrap.appendChild(label);

    toolbar.appendChild(brightWrap);

    // Separator between Lighting Controls and Board Scale Controls
    const sepScale = document.createElement('div');
    sepScale.className = 'board-3d-toolbar-sep';
    sepScale.setAttribute('aria-hidden', 'true');
    toolbar.appendChild(sepScale);

    // Board Scale / Size Control Group
    const scaleWrap = document.createElement('div');
    scaleWrap.className = 'board-3d-scale-wrap';
    scaleWrap.setAttribute('title', 'Adjust 3D Cribbage Board Size (Ctrl+Drag or [ / ] keys, Double-click to reset to 100%)');

    const scaleIconBtn = document.createElement('button');
    scaleIconBtn.type = 'button';
    scaleIconBtn.className = 'btn-3d-scale-icon';
    scaleIconBtn.setAttribute('title', 'Reset board size to 100%');
    scaleIconBtn.setAttribute('aria-label', 'Reset board size to 100%');
    scaleIconBtn.innerHTML = `
      <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7"/>
      </svg>
    `;
    scaleIconBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      this.resetBoardScale(true);
    });
    scaleWrap.appendChild(scaleIconBtn);

    const scaleSlider = document.createElement('input');
    scaleSlider.type = 'range';
    scaleSlider.className = 'slider-3d-scale';
    scaleSlider.min = '20';
    scaleSlider.max = '200';
    scaleSlider.step = '5';
    scaleSlider.value = String(Math.round((this.boardScaleMultiplier || 1.0) * 100));
    scaleSlider.setAttribute('aria-label', '3D Board Size Slider');
    scaleSlider.addEventListener('input', (e) => {
      this.setBoardScale(Number(e.target.value) / 100, false);
    });
    scaleSlider.addEventListener('dblclick', (e) => {
      e.stopPropagation();
      this.resetBoardScale(true);
    });
    scaleWrap.appendChild(scaleSlider);

    const scaleLabel = document.createElement('span');
    scaleLabel.className = 'val-3d-scale';
    scaleLabel.title = 'Click to reset board size to 100%';
    scaleLabel.textContent = `${Math.round((this.boardScaleMultiplier || 1.0) * 100)}%`;
    scaleLabel.addEventListener('click', (e) => {
      e.stopPropagation();
      this.resetBoardScale(true);
    });
    scaleWrap.appendChild(scaleLabel);

    toolbar.appendChild(scaleWrap);

    this.container.appendChild(toolbar);
    this.presetsToolbar = toolbar;

    if (!this._fullscreenListenersBound && typeof document !== 'undefined') {
      this._fullscreenListenersBound = true;
      this._onFullscreenChange = () => {
        const isFS = this.isFullScreen();
        if (!isFS) {
          this._applyCssFullScreen(false);
        }
        this.updateFullScreenUI(isFS);
        this.handleResize();
        if (this.fitBoardToViewBox) {
          this.fitBoardToViewBox(false);
        }
      };
      document.addEventListener('fullscreenchange', this._onFullscreenChange);
      document.addEventListener('webkitfullscreenchange', this._onFullscreenChange);

      this._onGlobalKeydown = (e) => {
        if (this.isDestroyed || !this.container) return;
        const target = e.target;
        const isInput = target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable);
        if (isInput) return;

        if ((e.key === 'f' || e.key === 'F') && !e.ctrlKey && !e.altKey && !e.metaKey) {
          const is3DActive = (window.settings?.boardViewMode === '3d' || window.settings?.boardViewMode === 'room3d' || window.settings?.boardViewMode === 'room3d-fullscreen' || this.isFullScreen());
          if (is3DActive) {
            e.preventDefault();
            this.toggleFullScreen();
          }
        } else if (e.key === 'Escape' && this.isFullScreen()) {
          e.preventDefault();
          this.exitFullScreen();
        }
      };
      window.addEventListener('keydown', this._onGlobalKeydown);
    }
  }

  /**
   * Updates visual active states on the preset toolbar buttons.
   */
  updatePresetButtonsUI(activeId) {
    if (!this.presetsToolbar) return;
    const btns = this.presetsToolbar.querySelectorAll('.btn-3d-preset');
    btns.forEach(btn => {
      const presetAttr = btn.getAttribute('data-preset');
      if (!presetAttr) return; // Keep Pan Mode state intact
      const isMatch = activeId && presetAttr === activeId;
      if (isMatch) {
        btn.classList.add('is-active');
        btn.setAttribute('aria-pressed', 'true');
      } else {
        btn.classList.remove('is-active');
        btn.setAttribute('aria-pressed', 'false');
      }
    });
    this.updateFullScreenUI();
  }

  /**
   * Starts the Babylon.js render loop.
   */
  startRenderLoop() {
    this.engine.runRenderLoop(() => {
      if (this.isDestroyed || !this.scene) return;

      const now = performance.now();
      if (this.animatingPegs.length > 0) {
        this.animatingPegs = this.animatingPegs.filter(a => a.update(now));
      }

      this.scene.render();
    });
  }

  getBabylonWoodColor() {
    const BABYLON = this.BABYLON;
    const finish = this.settings.boardFinish || this.boardConfig?.woodFinish || 'maple';
    if (finish === 'walnut') return new BABYLON.Color3(0.24, 0.15, 0.08);
    if (finish === 'cherry') return new BABYLON.Color3(0.42, 0.14, 0.09);
    if (finish === 'obsidian') return new BABYLON.Color3(0.09, 0.09, 0.1);
    if (finish === 'driftwood') return new BABYLON.Color3(0.47, 0.44, 0.42);
    return new BABYLON.Color3(0.85, 0.64, 0.38); // Maple
  }

  /**
   * Cleans up Babylon Engine, Scene, and Event Listeners.
   */
  destroy() {
    this.isDestroyed = true;
    this.clearTableEnvironment();
    this.cancelActionCam();
    if (this.actionCamOverlay) {
      try { this.actionCamOverlay.remove(); } catch (_) {}
      this.actionCamOverlay = null;
    }
    this.clearClashObstacles();
    this.clearPintsObjects();
    this.clearAdultObjects();
    this.clearZoneHighlights();
    if (this._zoneMaterials) {
      for (const k of Object.keys(this._zoneMaterials)) {
        try { this._zoneMaterials[k].dispose(); } catch (_) {}
      }
      this._zoneMaterials = null;
    }
    if (this.boardRoot) {
      try { this.boardRoot.dispose(false, true); } catch (_) {}
      this.boardRoot = null;
    }
    if (this._obstacleMaterials) {
      for (const k of Object.keys(this._obstacleMaterials)) {
        try { this._obstacleMaterials[k].dispose(); } catch (_) {}
      }
      this._obstacleMaterials = null;
    }
    if (this._pintsMaterials) {
      for (const k of Object.keys(this._pintsMaterials)) {
        try { this._pintsMaterials[k].dispose(); } catch (_) {}
      }
      this._pintsMaterials = null;
    }
    if (this._adultMaterials) {
      for (const k of Object.keys(this._adultMaterials)) {
        try { this._adultMaterials[k].dispose(); } catch (_) {}
      }
      this._adultMaterials = null;
    }
    if (this._sparkMaterials) {
      for (const mat of this._sparkMaterials.values()) {
        try { mat.dispose(); } catch (_) {}
      }
      this._sparkMaterials.clear();
      this._sparkMaterials = null;
    }
    if (this._ringMaterials) {
      for (const mat of this._ringMaterials.values()) {
        try { mat.dispose(); } catch (_) {}
      }
      this._ringMaterials.clear();
      this._ringMaterials = null;
    }
    this.animatingPegs = [];
    if (this.glowLayer) {
      try { this.glowLayer.dispose(); } catch (_) {}
      this.glowLayer = null;
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    if (this._resizeRaf) {
      cancelAnimationFrame(this._resizeRaf);
      this._resizeRaf = null;
    }
    if (this._hoverRaf) {
      cancelAnimationFrame(this._hoverRaf);
      this._hoverRaf = null;
    }
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler);
    }
    if (this._onFullscreenChange) {
      document.removeEventListener('fullscreenchange', this._onFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', this._onFullscreenChange);
      this._onFullscreenChange = null;
    }
    if (this._onGlobalKeydown) {
      window.removeEventListener('keydown', this._onGlobalKeydown);
      this._onGlobalKeydown = null;
    }
    this._fullscreenListenersBound = false;
    this._applyCssFullScreen(false);
    if (this._avatarTextures) {
      for (const tex of this._avatarTextures.values()) {
        try { tex.dispose(); } catch (_) {}
      }
      this._avatarTextures.clear();
    }
    if (this._avatarMaterials) {
      for (const mat of this._avatarMaterials.values()) {
        try { mat.dispose(); } catch (_) {}
      }
      this._avatarMaterials.clear();
    }
    if (this.engine && typeof this.engine.stopRenderLoop === 'function') {
      this.engine.stopRenderLoop();
    }
    if (this.scene && typeof this.scene.dispose === 'function') {
      this.scene.dispose();
    }
    if (this.engine) {
      this.engine.dispose();
    }
    if (this.canvas) {
      try {
        if (typeof this.canvas.remove === 'function') {
          this.canvas.remove();
        } else if (this.canvas.parentNode) {
          this.canvas.parentNode.removeChild(this.canvas);
        }
      } catch (_) {}
    }
    if (this.container && this.container.classList && this.container.classList.contains('board-3d-container')) {
      try {
        if (typeof this.container.remove === 'function') {
          this.container.remove();
        } else if (this.container.parentNode) {
          this.container.parentNode.removeChild(this.container);
        }
      } catch (_) {}
    }
    const resizeHandle = typeof document !== 'undefined' ? document.getElementById('board3DResizeHandle') : null;
    if (resizeHandle) {
      try {
        if (typeof resizeHandle.remove === 'function') {
          resizeHandle.remove();
        } else if (resizeHandle.parentNode) {
          resizeHandle.parentNode.removeChild(resizeHandle);
        }
      } catch (_) {}
    }
    if (this.presetsToolbar) {
      try {
        if (typeof this.presetsToolbar.remove === 'function') {
          this.presetsToolbar.remove();
        } else if (this.presetsToolbar.parentNode) {
          this.presetsToolbar.parentNode.removeChild(this.presetsToolbar);
        }
      } catch (_) {}
      this.presetsToolbar = null;
    }
    if (this.ssaoPipeline) {
      try {
        this.ssaoPipeline.dispose();
      } catch (_) {}
      this.ssaoPipeline = null;
    }
    if (this.fillLight) {
      try {
        this.fillLight.dispose();
      } catch (_) {}
      this.fillLight = null;
    }
    this.scene = null;
    this.engine = null;
    this.camera = null;
    this.canvas = null;
  }

  /**
   * Retrieves the current camera angles, distance, and target of the 3D board.
   * Prints a formatted summary and ready-to-paste cameraDefaults code snippet to the console.
   * @returns {{ boardId: string, boardName: string, alphaDeg: number, betaDeg: number, alpha: number, beta: number, radius: number, fov: number, target: number[], snippet: string } | null}
   */
  getCameraAngles() {
    if (!this.camera) {
      console.warn('[BoardRenderer3D] Camera is not initialized.');
      return null;
    }

    const alphaRad = Number(this.camera.alpha.toFixed(4));
    const betaRad = Number(this.camera.beta.toFixed(4));

    // Normalize horizontal orbit angle (alpha) to [-180, 180] degrees
    const rawAlphaDeg = (this.camera.alpha * 180) / Math.PI;
    let alphaDeg = ((rawAlphaDeg % 360) + 540) % 360 - 180;
    alphaDeg = Number(alphaDeg.toFixed(2));

    // Vertical pitch angle (beta) in degrees
    const betaDeg = Number(((this.camera.beta * 180) / Math.PI).toFixed(2));
    const radius = Number((this.camera.radius || 0).toFixed(2));
    const fov = Number((((this.camera.fov || 0.8) * 180) / Math.PI).toFixed(1));

    const targetPos = this.camera.target
      ? [
          Number(this.camera.target.x.toFixed(2)),
          Number(this.camera.target.y.toFixed(2)),
          Number(this.camera.target.z.toFixed(2))
        ]
      : [0, 0, 0];

    const boardId = this.boardConfig?.id || 'current-board';
    const boardName = this.boardConfig?.name || 'Current 3D Board';
    const camDef = this.boardConfig?.cameraDefaults || {};

    const snippet = `cameraDefaults: {
  fov: ${fov},
  alphaDeg: ${alphaDeg},
  betaDeg: ${betaDeg},
  target: [${targetPos.join(', ')}],
  minDistance: ${camDef.minDistance !== undefined ? camDef.minDistance : 15},
  maxDistance: ${camDef.maxDistance !== undefined ? camDef.maxDistance : 95}
}`;

    const info = {
      boardId,
      boardName,
      alphaDeg,
      betaDeg,
      alpha: alphaRad,
      beta: betaRad,
      radius,
      fov,
      target: targetPos,
      snippet
    };

    if (typeof console !== 'undefined' && console.log) {
      console.log(
        `%c🎲 3D Board Camera Angles [${boardName} (${boardId})]%c\n` +
        `  • alphaDeg: %c${alphaDeg}°%c (horizontal orbit / yaw)\n` +
        `  • betaDeg:  %c${betaDeg}°%c (vertical pitch / tilt)\n` +
        `  • alpha:    ${alphaRad} rad\n` +
        `  • beta:     ${betaRad} rad\n` +
        `  • radius:   ${radius}\n` +
        `  • fov:      ${fov}°\n` +
        `  • target:   [${targetPos.join(', ')}]\n\n` +
        `%c📋 Copy & Paste into cameraDefaults in Board3DCatalog.js:%c\n${snippet}`,
        'color: #00d26a; font-weight: bold; font-size: 13px;',
        'color: inherit;',
        'color: #00e5ff; font-weight: bold;',
        'color: inherit;',
        'color: #ffaa00; font-weight: bold;',
        'color: inherit;',
        'color: #b388ff; font-weight: bold;',
        'color: #00e676; font-family: monospace;'
      );
    }

    return info;
  }

  /**
   * Alias for getCameraAngles().
   */
  getBoardAngles() {
    return this.getCameraAngles();
  }

  /**
   * Alias for getCameraAngles().
   */
  getAngles() {
    return this.getCameraAngles();
  }

  /**
   * Static helper to retrieve angles from the active 3D renderer instance.
   */
  static getCameraAngles() {
    if (typeof window !== 'undefined' && window._currentRenderer3D?.getCameraAngles) {
      return window._currentRenderer3D.getCameraAngles();
    }
    console.warn('[BoardRenderer3D] No active 3D board found. Ensure 3D board view is selected.');
    return null;
  }

  /**
   * Static alias for getCameraAngles().
   */
  static getBoardAngles() {
    return BoardRenderer3D.getCameraAngles();
  }

  /**
   * Checks whether the 3D room/board container is currently full screen.
   * @returns {boolean}
   */
  isFullScreen() {
    if (typeof document === 'undefined') return false;
    const fsEl = document.fullscreenElement || document.webkitFullscreenElement || document.mozFullScreenElement || document.msFullscreenElement;
    if (fsEl && (fsEl === this.container || this.container?.contains(fsEl))) {
      return true;
    }
    return Boolean(this.container && this.container.classList.contains('is-fullscreen'));
  }

  /**
   * Applies or removes CSS-level fullscreen fallback styling on the 3D container.
   * @param {boolean} enable
   */
  _applyCssFullScreen(enable) {
    if (!this.container) return;
    if (enable) {
      const gameEl = (typeof document !== 'undefined') ? document.getElementById('game') : null;
      const menuVariants = (typeof document !== 'undefined') ? document.getElementById('menuVariants') : null;
      if ((gameEl && gameEl.classList.contains('hidden')) || (menuVariants && !menuVariants.classList.contains('hidden'))) {
        return;
      }
      this.container.classList.add('is-fullscreen');
      if (typeof document !== 'undefined' && document.body) {
        document.body.classList.add('is-3d-fullscreen');
      }
    } else {
      this.container.classList.remove('is-fullscreen');
      if (typeof document !== 'undefined' && document.body) {
        document.body.classList.remove('is-3d-fullscreen');
      }
    }
  }

  /**
   * Requests fullscreen mode for the 3D room/board container.
   * Uses HTML5 Fullscreen API with automatic fallback to CSS full-window mode.
   * @returns {Promise<void>}
   */
  async enterFullScreen() {
    if (typeof document === 'undefined' || !this.container) return;
    if (this.isFullScreen()) return;

    // Do not enter fullscreen if the game container is hidden or variants menu is open
    const gameEl = document.getElementById('game');
    if (gameEl && gameEl.classList.contains('hidden')) {
      return;
    }
    const menuVariants = document.getElementById('menuVariants');
    if (menuVariants && !menuVariants.classList.contains('hidden')) {
      return;
    }

    let usedHtml5Fs = false;
    try {
      if (this.container.requestFullscreen) {
        await this.container.requestFullscreen();
        usedHtml5Fs = true;
      } else if (this.container.webkitRequestFullscreen) {
        await this.container.webkitRequestFullscreen();
        usedHtml5Fs = true;
      } else if (this.container.mozRequestFullScreen) {
        await this.container.mozRequestFullScreen();
        usedHtml5Fs = true;
      } else if (this.container.msRequestFullscreen) {
        await this.container.msRequestFullscreen();
        usedHtml5Fs = true;
      } else {
        this._applyCssFullScreen(true);
      }
    } catch (err) {
      console.warn('[BoardRenderer3D] HTML5 requestFullscreen unavailable or denied, falling back to CSS fullscreen:', err);
      this._applyCssFullScreen(true);
    }

    if (!usedHtml5Fs && !this.isFullScreen()) {
      this._applyCssFullScreen(true);
    }
    this.updateFullScreenUI(true);
    this.handleResize();
    if (this.fitBoardToViewBox) {
      setTimeout(() => {
        if (!this.isDestroyed) {
          this.handleResize();
          this.fitBoardToViewBox(false);
        }
      }, 60);
    }
  }

  /**
   * Exits fullscreen mode and restores normal container bounds.
   * @returns {Promise<void>}
   */
  async exitFullScreen() {
    if (typeof document === 'undefined') return;
    if (!this.isFullScreen()) return;

    try {
      const fsEl = document.fullscreenElement || document.webkitFullscreenElement || document.mozFullScreenElement || document.msFullscreenElement;
      if (fsEl && (document.exitFullscreen || document.webkitExitFullscreen)) {
        if (document.exitFullscreen) {
          await document.exitFullscreen();
        } else if (document.webkitExitFullscreen) {
          await document.webkitExitFullscreen();
        }
      }
    } catch (err) {
      console.warn('[BoardRenderer3D] exitFullscreen error:', err);
    }

    this._applyCssFullScreen(false);
    try {
      const overlay = document.getElementById('overlay');
      if (overlay && overlay.parentElement !== document.body) {
        document.body.appendChild(overlay);
      }
    } catch (_) {}
    this.updateFullScreenUI(false);
    this.handleResize();
    if (this.fitBoardToViewBox) {
      setTimeout(() => {
        if (!this.isDestroyed) {
          this.handleResize();
          this.fitBoardToViewBox(false);
        }
      }, 60);
    }
  }

  /**
   * Toggles between fullscreen and standard room view.
   * @returns {Promise<void>}
   */
  toggleFullScreen() {
    if (this.isFullScreen()) {
      return this.exitFullScreen();
    } else {
      return this.enterFullScreen();
    }
  }

  /**
   * Synchronizes visual states of fullscreen buttons across toolbar and in-game UI.
   * @param {boolean} [isFS]
   */
  updateFullScreenUI(isFS = this.isFullScreen()) {
    if (typeof document === 'undefined') return;
    // 1. Presets Toolbar button
    if (this.presetsToolbar) {
      const btn = this.presetsToolbar.querySelector('.btn-3d-fullScreen');
      if (btn) {
        btn.classList.toggle('is-active', !!isFS);
        btn.setAttribute('aria-pressed', isFS ? 'true' : 'false');
        btn.title = isFS ? 'Exit Full Screen (Esc or F)' : 'Toggle Full Screen (F)';
        const iconExpand = btn.querySelector('.icon-fs-expand');
        const iconContract = btn.querySelector('.icon-fs-contract');
        if (iconExpand) iconExpand.style.display = isFS ? 'none' : 'block';
        if (iconContract) iconContract.style.display = isFS ? 'block' : 'none';
      }
    }
    // 2. In-game board window button
    const inGameBtn = document.getElementById('btnFullscreen3D');
    const inGameIcon = document.getElementById('btnFullscreen3DIcon');
    if (inGameBtn) {
      inGameBtn.classList.toggle('active', !!isFS);
      inGameBtn.setAttribute('aria-pressed', isFS ? 'true' : 'false');
      inGameBtn.title = isFS ? 'Exit 3D Full Screen (Esc or F)' : 'Toggle 3D Full Screen (F)';
      if (inGameIcon) inGameIcon.textContent = isFS ? '🗗' : '⛶';
    }
  }

  static isFullScreen() {
    if (typeof window !== 'undefined' && window._currentRenderer3D?.isFullScreen) {
      return window._currentRenderer3D.isFullScreen();
    }
    return false;
  }

  static toggleFullScreen() {
    if (typeof window !== 'undefined' && window._currentRenderer3D?.toggleFullScreen) {
      return window._currentRenderer3D.toggleFullScreen();
    }
  }
}

// Auto-register top-level helpers on window/globalThis for direct console access
if (typeof window !== 'undefined') {
  window.getBoardAngles = () => BoardRenderer3D.getBoardAngles();
  window.getCameraAngles = () => BoardRenderer3D.getCameraAngles();
  window.toggle3DRoomFullScreen = () => BoardRenderer3D.toggleFullScreen();
  window.is3DRoomFullScreen = () => BoardRenderer3D.isFullScreen();
}
